import { MobXProviderContext } from "mobx-react";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  renderDialogFooter,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, RESIDENT_CARE_PLAN_TYPE } from "../../../constants/constants";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import {
  buildMedicationScheduleText,
  deriveEmarOccurrenceStatus,
  formatStructuredScheduleTimeLabel,
  getMedicationScheduleDays,
  getMedicationScheduleMonthDays,
  getMedicationScheduleOccurrences,
  getMedicationScheduleTimes,
  getTodayMedicationTimingMeta,
  inferMedicationScheduleDayPattern,
  normalizeMedicationScheduleDays,
  normalizeMedicationScheduleMonthDays,
  normalizeStructuredScheduleTime,
} from "../utils/emarScheduleUtils";
import { buildEmarEntryDetailRows } from "../utils/emarEntryDisplayUtils";
import {
  buildEmarProtocolRuleAutoLabel,
  createEmptyProtocolRule,
  EMAR_PROTOCOL_RULE_ACTION_OPTIONS,
  getEmarProtocolCategoryOption,
  getEmarProtocolEditorCategoryOptions,
  getEmarProtocolNumericRangeDefaults,
  getEmarProtocolParameterSchema,
  getEmarProtocolSummaries,
  getEmarProtocolSummary,
} from "../utils/emarProtocolUtils";
import {
  PRN_EFFECTIVENESS_OPTIONS,
  PRN_REASON_OPTIONS,
  PRN_SIDE_EFFECT_OPTIONS,
} from "../utils/emarPrnUtils";
import PrnMedicationHistory from "../components/PrnMedicationHistory";
import {
  buildEmarDailyTrackingDefaultName,
  buildEmarDailyTrackingValueSummary,
  getEmarDailyTrackingFieldPlaceholder,
  createEmptyEmarDailyTrackingItem,
  EMAR_DAILY_TRACKING_CATEGORY_OPTIONS,
  getEmarDailyTrackingCategoryOption,
  getEmarDailyTrackingProtocolCategory,
  getEmarDailyTrackingSchema,
  normalizeEmarDailyTrackingCategoryValue,
} from "../utils/emarDailyTrackingUtils";
import {
  ensureResidentBinderBundle,
  getBinderFolderByCode,
  RESIDENT_BINDER_FOLDER_CODES,
  upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { ReferenceType } from "../../../models/document";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import CarePlanContactsDialog, { validateCarePlanContactsForm } from "../components/CarePlanContactsDialog";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const EMPTY_FORM = {
  medicationId: "",
  medicationName: "",
  dosage: "",
  route: "",
  frequency: "",
  schedule: "",
  scheduledClockTime: "",
  scheduleDayPattern: "daily",
  scheduleDays: [],
  scheduleMonthDays: [],
  effectiveStartDate: new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date()),
  effectiveEndDate: "",
  indication: "",
  instructions: "",
  handlingNotes: "",
  marOnlyItem: false,
  discontinued: false,
  discontinuedAt: "",
  discontinueReason: "",
  protocolEnabled: false,
  protocolDefinitions: [],
  protocolCategory: "",
  protocolInstructions: "",
  protocolRules: [],
  psychotropicDrugClass: "",
  prn: false,
  active: true,
};

const createEmptyProtocolParameterSet = (category = "sliding_scale_insulin", index = 0) => ({
  parameterId: `parameter_${index + 1}`,
  category,
  instructions: "",
  rules: [createEmptyProtocolRule(category, 0)],
});

const MAX_PROTOCOL_PARAMETERS_PER_MEDICATION = 5;
const MAX_PROTOCOL_RULES_PER_PARAMETER = 5;
const SYSTEM_GENERATED_PROTOCOL_HOLD_RULE_ID = "__system_hold_fallback__";
const PROTOCOL_CONDITION_OPERATOR_OPTIONS = [
  { label: "Equals To", value: "equals" },
  { label: "List", value: "list" },
  { label: "Required", value: "required" },
  { label: "In Between", value: "between" },
  { label: "Less Than", value: "less_than" },
  { label: "More Than", value: "more_than" },
];

const isSystemGeneratedProtocolRule = (rule: any) => (
  rule?.systemGeneratedFallback === true || String(rule?.ruleId || "").trim() === SYSTEM_GENERATED_PROTOCOL_HOLD_RULE_ID
);

const buildProtocolConditionFromOperator = (
  operator: string,
  values?: { equalsValue?: string; minValue?: string; maxValue?: string },
) => {
  const nextValues = {
    equalsValue: String(values?.equalsValue || ""),
    minValue: String(values?.minValue || ""),
    maxValue: String(values?.maxValue || ""),
  };
  if (operator === "equals") {
    return { operator, equalsValue: nextValues.equalsValue, minValue: "", maxValue: "" };
  }
  if (operator === "list") {
    return { operator, equalsValue: nextValues.equalsValue, minValue: "", maxValue: "" };
  }
  if (operator === "required") {
    return { operator, equalsValue: "", minValue: "", maxValue: "" };
  }
  if (operator === "between") {
    return { operator, equalsValue: "", minValue: nextValues.minValue, maxValue: nextValues.maxValue };
  }
  if (operator === "less_than") {
    return { operator, equalsValue: "", minValue: "", maxValue: nextValues.maxValue };
  }
  if (operator === "more_than") {
    return { operator, equalsValue: "", minValue: nextValues.minValue, maxValue: "" };
  }
  return { operator: "none", equalsValue: "", minValue: "", maxValue: "" };
};

const sanitizeProtocolConditionsForPersistence = (conditions: any) => (
  Object.entries(conditions || {}).reduce((nextConditions: any, [fieldKey, condition]: any) => {
    const normalizedFieldKey = String(fieldKey || "").trim();
    if (!normalizedFieldKey) {
      return nextConditions;
    }
    nextConditions[normalizedFieldKey] = {
      operator: String(condition?.operator || "equals"),
      equalsValue: String(condition?.equalsValue || ""),
      minValue: String(condition?.minValue || ""),
      maxValue: String(condition?.maxValue || ""),
    };
    return nextConditions;
  }, {})
);

const buildProtocolDefinitionFromParameterSet = (parameterSet: any) => {
  const categoryOption = getEmarProtocolCategoryOption(parameterSet?.category);
  const visibleRules = (Array.isArray(parameterSet?.rules) ? parameterSet.rules : []).filter((rule: any) => !isSystemGeneratedProtocolRule(rule));
  const fallbackRule = {
    ruleId: SYSTEM_GENERATED_PROTOCOL_HOLD_RULE_ID,
    label: "Otherwise Hold",
    action: "hold",
    doseValue: "",
    note: "Hold when the configured give conditions do not all match.",
    conditions: {},
    systemGeneratedFallback: true,
  };
  return {
    category: parameterSet?.category || "",
    label: categoryOption?.label || "Parameters / Sliding Scale",
    guidanceAction: categoryOption?.guidanceAction || "",
    parameterSchema: getEmarProtocolParameterSchema(parameterSet?.category, visibleRules),
    requiresDoseInput: categoryOption?.requiresDoseInput === true,
    instructions: String(parameterSet?.instructions || ""),
    rules: [
      ...(visibleRules.length
        ? visibleRules
        : [createEmptyProtocolRule(parameterSet?.category || "sliding_scale_insulin", 0)]
      ).map((rule: any) => ({
        ...rule,
        conditions: sanitizeProtocolConditionsForPersistence(rule?.conditions || {}),
      })),
      fallbackRule,
    ],
  };
};

const getProtocolAssessmentFieldLabel = (field: any) => {
  const baseLabel = String(field?.label || field?.key || "Value").trim() || "Value";
  const unit = String(field?.unit || "").trim();
  return unit ? `${baseLabel} (${unit})` : baseLabel;
};

const getProtocolAssessmentFieldPlaceholder = () => "Value";

const getProtocolAssessmentFields = (protocolSummary: any, parameterValues: Record<string, string> = {}) => {
  const fields = Array.isArray(protocolSummary?.parameterSchema) ? [...protocolSummary.parameterSchema] : [];
  const knownKeys = new Set(fields.map((field: any) => String(field?.key || "").trim()).filter(Boolean));
  Object.keys(parameterValues || {}).forEach((key) => {
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey || knownKeys.has(normalizedKey)) {
      return;
    }
    fields.push({
      key: normalizedKey,
      label: normalizedKey
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/^\w/, (character) => character.toUpperCase()),
      inputType: "text",
    });
  });
  return fields;
};

const normalizeProtocolDefinitions = (item: any) => {
  if (Array.isArray(item?.protocolDefinitions) && item.protocolDefinitions.length) {
    return item.protocolDefinitions.map((definition: any, index: number) => ({
      parameterId: definition?.parameterId || `parameter_${index + 1}`,
      category: String(definition?.category || "sliding_scale_insulin"),
      instructions: String(definition?.instructions || ""),
      rules: Array.isArray(definition?.rules)
        ? definition.rules.filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
        : [createEmptyProtocolRule(definition?.category || "sliding_scale_insulin", 0)],
    }));
  }

  if (item?.protocolEnabled === true) {
    const category = String(item?.protocolCategory || item?.protocolDefinition?.category || "sliding_scale_insulin");
    return [{
      parameterId: "parameter_1",
      category,
      instructions: String(item?.protocolInstructions || item?.protocolDefinition?.instructions || ""),
      rules: Array.isArray(item?.protocolDefinition?.rules)
        ? item.protocolDefinition.rules.filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
        : Array.isArray(item?.protocolRules) && item.protocolRules.length
          ? item.protocolRules.filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
          : [createEmptyProtocolRule(category, 0)],
    }];
  }

  return [];
};

type PsychotropicMedicationSuggestion = {
  productName: string;
  drugClass: string;
};

const compactFieldStyle: React.CSSProperties = {
  width: "100%",
};

const protocolAssessmentDialogStyle: React.CSSProperties = {
  width: "min(56rem, 96vw)",
};

const protocolAssessmentInputStyle: React.CSSProperties = {
  ...compactFieldStyle,
  maxWidth: "10rem",
};

const compactQuickPickFieldStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "16rem",
};

const compactProtocolInlineFieldStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "11rem",
};

const compactProtocolDropdownFieldStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "9rem",
};

const compactProtocolNumericFieldStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "5.5rem",
};

const compactProtocolTextValueFieldStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "8rem",
};

const protocolRulesGridHeaderStyle: React.CSSProperties = {
  fontSize: "0.76rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#9a3412",
};

const protocolManagementGridRowStyle: React.CSSProperties = {
  border: "1px solid #ffedd5",
  borderRadius: "10px",
  background: "#ffffff",
  padding: "0.55rem 0.65rem",
};

const createProtocolEditorDraft = (definition: any, index: number) => ({
  parameterId: definition?.parameterId || `parameter_${index + 1}`,
  category: String(definition?.category || "sliding_scale_insulin"),
  instructions: String(definition?.instructions || ""),
  rules: Array.isArray(definition?.rules) && definition.rules.length
    ? definition.rules
    : [createEmptyProtocolRule(definition?.category || "sliding_scale_insulin", 0)],
});

const medicationFormFieldGroupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const medicationFormPickerRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
};

const medicationDialogHeroStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)",
  border: "1px solid #fed7aa",
  borderRadius: "18px",
  padding: "1rem 1.1rem",
};

const medicationDialogSectionStyle: React.CSSProperties = {
  ...appInnerCardStyle,
  padding: "1rem",
  borderRadius: "18px",
};

const medicationDialogToggleCardStyle: React.CSSProperties = {
  ...medicationDialogSectionStyle,
  minHeight: "100%",
};

const medicationDialogSectionTitleStyle: React.CSSProperties = {
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#9a3412",
};

const medicationDialogSectionHintStyle: React.CSSProperties = {
  ...appMutedTextStyle,
  fontSize: "0.82rem",
};

const medicationDialogFieldHintStyle: React.CSSProperties = {
  ...appMutedTextStyle,
  fontSize: "0.8rem",
  lineHeight: 1.45,
  marginTop: "0.35rem",
};

const medicationDialogInlineTextFieldStyle: React.CSSProperties = {
  width: "100%",
  fontSize: "0.92rem",
};

const medicationDialogSummaryCardStyle: React.CSSProperties = {
  border: "1px solid #fed7aa",
  background: "#fffaf0",
  borderRadius: "16px",
  padding: "0.9rem 1rem",
};

const medPassActionChipStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: "999px",
  background: "#fff",
  color: "#0f172a",
  fontSize: "0.78rem",
  fontWeight: 700,
  padding: "0.35rem 0.65rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const reportRangeButtonStyle: React.CSSProperties = {
  borderRadius: "999px",
  border: "1px solid #cbd5e1",
  padding: "0.35rem 0.65rem",
  fontSize: "0.78rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
  boxShadow: "none",
};

const MAR_SHEET_FILE_ACCEPT = ".docx,.txt,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/pdf";
const EMAR_SUBSCRIPTION_FEATURE_DETAILS = [
  "Keep a current medication list with dosage, route, schedule, and purpose.",
  "Chart daily med pass activity, PRN follow-up, and medication exceptions.",
  "Upload medications from the care plan or MAR sheet and generate eMAR reports.",
];

const getIsoDateOnly = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLosAngelesIsoDateOnly = (value: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const day = parts.find((part) => part.type === "day")?.value || "";
  return year && month && day ? `${year}-${month}-${day}` : getIsoDateOnly(value);
};

const getLosAngelesYesterdayIsoDateOnly = (value: Date = new Date()) => {
  const todayDateOnly = getLosAngelesIsoDateOnly(value);
  const match = todayDateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return todayDateOnly;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 1);
  return getLosAngelesIsoDateOnly(date);
};

const getCalendarDateFromDateKey = (dateKey: string) => {
  const match = String(dateKey || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
};

const getMonthStart = (value: Date) => {
  const copy = new Date(value.getFullYear(), value.getMonth(), 1);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const getInclusiveDateRangeDayCount = (startDate: string, endDate: string) => {
  const start = new Date(`${String(startDate || "").trim()}T00:00:00`);
  const end = new Date(`${String(endDate || "").trim()}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

const getDaysAgo = (value: Date, days: number) => {
  const copy = new Date(value);
  copy.setDate(copy.getDate() - days);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const getMonthsAgo = (value: Date, months: number) => {
  const copy = new Date(value);
  copy.setMonth(copy.getMonth() - months);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const getEntryRecordedLocalDate = (entry: any) => {
  const timestamp = String(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return getIsoDateOnly(parsedDate);
};

const getLocalTimeValueFromIso = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getLocalTimeValueWithSecondsFromIso = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const updateIsoDateTimeParts = (sourceValue: any, nextDate?: string, nextTime?: string) => {
  const baseDate = String(sourceValue || "").trim() ? new Date(String(sourceValue || "").trim()) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return "";
  }

  const dateValue = String(nextDate || getIsoDateOnly(baseDate)).trim();
  const timeValue = String(nextTime || getLocalTimeValueFromIso(baseDate)).trim() || "00:00";
  const [year, month, day] = dateValue.split("-").map((part) => Number(part));
  const [hours, minutes, seconds] = timeValue.split(":").map((part) => Number(part));

  if (!year || !month || !day) {
    return "";
  }

  const nextRecordedAt = new Date(
    year,
    month - 1,
    day,
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
    Number.isFinite(seconds) ? seconds : 0,
    0,
  );
  return Number.isNaN(nextRecordedAt.getTime()) ? "" : nextRecordedAt.toISOString();
};

const formatMedPassTimeChoiceLabel = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return parsedDate.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getMinutesDifferenceBetweenTimestamps = (firstValue: any, secondValue: any) => {
  const firstDate = new Date(String(firstValue || "").trim());
  const secondDate = new Date(String(secondValue || "").trim());
  if (Number.isNaN(firstDate.getTime()) || Number.isNaN(secondDate.getTime())) {
    return null;
  }
  return Math.round((firstDate.getTime() - secondDate.getTime()) / 60000);
};

const buildGivenTimeSelectionState = ({
  entryDate,
  scheduledClockTime,
  baseTimestamp,
  includePromptReason,
}: {
  entryDate: string,
  scheduledClockTime: string,
  baseTimestamp: string,
  includePromptReason?: boolean,
}) => {
  const currentDateTime = new Date();
  const currentTimeValue = `${String(currentDateTime.getHours()).padStart(2, "0")}:${String(currentDateTime.getMinutes()).padStart(2, "0")}:${String(currentDateTime.getSeconds()).padStart(2, "0")}`;
  const fallbackBaseTimestamp = String(baseTimestamp || "").trim() || currentDateTime.toISOString();
  const currentRecordedAt = updateIsoDateTimeParts(fallbackBaseTimestamp, entryDate, currentTimeValue);
  const scheduledRecordedAt = scheduledClockTime
    ? updateIsoDateTimeParts(fallbackBaseTimestamp, entryDate, scheduledClockTime)
    : currentRecordedAt;
  const scheduledIsFuture = !!scheduledClockTime && isFutureChartTimestamp(scheduledRecordedAt);
  const scheduledOffsetMinutes = scheduledClockTime
    ? getMinutesDifferenceBetweenTimestamps(currentRecordedAt, scheduledRecordedAt)
    : null;
  const shouldShowPrompt = !!includePromptReason && !!scheduledClockTime && scheduledOffsetMinutes !== null && Math.abs(scheduledOffsetMinutes) > 60;
  const timingDirection = scheduledOffsetMinutes !== null && scheduledOffsetMinutes < 0 ? "before" : "after";
  const roundedHours = scheduledOffsetMinutes === null
    ? ""
    : (Math.abs(scheduledOffsetMinutes) / 60).toFixed(1).replace(/\.0$/, "");

  return {
    currentRecordedAt,
    scheduledRecordedAt,
    scheduledOffsetMinutes,
    givenTimeChoice: shouldShowPrompt && !scheduledIsFuture ? "scheduled" : (!scheduledIsFuture && scheduledClockTime ? "scheduled" : "current"),
    showGivenTimeChoiceDialog: shouldShowPrompt && !scheduledIsFuture,
    givenTimePromptReason: shouldShowPrompt
      ? `This med is being charted about ${roundedHours} hour${roundedHours === "1" ? "" : "s"} ${timingDirection} the scheduled time. Choose whether to save it at the scheduled time or the current time.`
      : "",
    givenTimeOptions: [
      {
        value: "scheduled",
        label: "Given at scheduled time",
        disabled: !scheduledClockTime || scheduledIsFuture,
        helper: !scheduledClockTime
          ? "No scheduled clock time set"
          : scheduledIsFuture
            ? `${formatMedPassTimeChoiceLabel(scheduledRecordedAt)} is in the future`
            : formatMedPassTimeChoiceLabel(scheduledRecordedAt),
        recordedAt: scheduledRecordedAt,
      },
      {
        value: "current",
        label: "Given at current time",
        helper: formatMedPassTimeChoiceLabel(currentRecordedAt),
        recordedAt: currentRecordedAt,
      },
      {
        value: "custom",
        label: "Exact time",
        helper: "Choose a specific charting time below.",
        recordedAt: currentRecordedAt,
      },
    ],
  };
};

const filterEntriesForTodayPass = (entries: any[], todayDate: string) => (
  (Array.isArray(entries) ? entries : []).filter((entry: any) => {
    const recordedLocalDate = getEntryRecordedLocalDate(entry);
    return !recordedLocalDate || recordedLocalDate === todayDate;
  })
);

const getEmarEntryOccurrenceKey = (entry: any) => (
  `${String(entry?.medicationId || "").trim()}::${(() => {
    const scheduledTimeKey = String(entry?.scheduledTimeKey || (String(entry?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase();
    return scheduledTimeKey.startsWith("prn") ? "prn" : scheduledTimeKey;
  })()}`
);

const isMarOnlyMedication = (item: any) => (
  item?.marOnlyItem === true
  || String(item?.administrationMode || "").trim().toLowerCase() === "continuous_active"
);

const getDailyTrackingEntryOccurrenceKey = (entry: any) => (
  `${String(entry?.trackingItemId || "").trim()}::${String(entry?.scheduledTimeKey || "default").trim().toLowerCase()}`
);

const filterEmarPassRows = (rows: any[], passFilter: "all" | "pending" | "charted") => (
  (Array.isArray(rows) ? rows : [])
    .map((row: any) => ({
      ...row,
      occurrences: (Array.isArray(row?.occurrences) ? row.occurrences : []).filter((occurrence: any) => {
        if (passFilter === "all") {
          return true;
        }
        const isCharted = Boolean(occurrence?.entry && String(occurrence?.entryStatus || "").trim());
        return passFilter === "charted" ? isCharted : !isCharted;
      }),
    }))
    .filter((row: any) => row.occurrences.length > 0)
);

const DOSAGE_OPTIONS = [
  "0.25 tablet",
  "0.5 tablet",
  "1 tablet",
  "2 tablets",
  "5 mg",
  "10 mg",
  "20 mg",
  "25 mg",
  "50 mg",
  "100 mg",
  "250 mg",
  "500 mg",
  "1 mL",
  "5 mL",
  "10 mL",
  "1 puff",
  "2 puffs",
  "1 patch",
  "1 capsule",
  "2 capsules",
].map((value) => ({ label: value, value }));

const ROUTE_OPTIONS = [
  "Oral",
  "Topical",
  "Subcutaneous",
  "Intramuscular",
  "Inhalation",
  "Nebulizer",
  "Ophthalmic",
  "Otic",
  "Nasal",
  "Rectal",
  "Sublingual",
  "Transdermal",
  "PEG tube",
  "G-tube",
].map((value) => ({ label: value, value }));

const FREQUENCY_OPTIONS = [
  { label: "Once daily", value: "once_daily" },
  { label: "Twice daily", value: "twice_daily" },
  { label: "Three times daily", value: "three_times_daily" },
  { label: "Four times daily", value: "four_times_daily" },
  { label: "At bedtime", value: "bedtime" },
  { label: "Weekly", value: "weekly" },
  { label: "Custom", value: "custom" },
];

const SCHEDULE_DAY_PATTERN_OPTIONS = [
  { label: "Every day", value: "daily" },
  { label: "Specific days", value: "specific_days" },
  { label: "Monthly dates", value: "specific_month_days" },
  { label: "Alternate days", value: "alternate_days" },
];

const SCHEDULE_WEEKDAY_OPTIONS = [
  { label: "Mon", value: "mon" },
  { label: "Tue", value: "tue" },
  { label: "Wed", value: "wed" },
  { label: "Thu", value: "thu" },
  { label: "Fri", value: "fri" },
  { label: "Sat", value: "sat" },
  { label: "Sun", value: "sun" },
];

const SCHEDULE_MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return {
    label: String(day),
    value: day,
  };
});

const MED_PASS_CORRECTION_STATUS_OPTIONS = [
  { label: "Given", value: "given" },
  { label: "Family", value: "family" },
  { label: "Refused", value: "refused" },
  { label: "Held", value: "held" },
  { label: "Not Given", value: "not_given" },
  { label: "Hospitalized", value: "hospitalized" },
];

const PRN_MED_PASS_CORRECTION_STATUS_OPTIONS = [
  { label: "Given", value: "prn_given" },
  { label: "Family", value: "family" },
  { label: "Refused", value: "refused" },
  { label: "Held", value: "held" },
  { label: "Not Given", value: "not_given" },
];

const DAILY_TRACKING_ENTRY_STATUS_OPTIONS = [
  { label: "Recorded", value: "recorded" },
  { label: "Refused", value: "refused" },
  { label: "Family", value: "family" },
  { label: "Hospitalized", value: "hospitalized" },
];

const SCHEDULE_TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  return {
    label: formatStructuredScheduleTimeLabel(value) || value,
    value,
  };
});

const DEFAULT_BULK_SCHEDULE_TIMES = ["08:00", "20:00"];
const DEFAULT_FREQUENCY_SCHEDULE_TIMES: Record<string, string[]> = {
  once_daily: ["08:00"],
  twice_daily: ["08:00", "20:00"],
  three_times_daily: ["08:00", "14:00", "20:00"],
  four_times_daily: ["08:00", "12:00", "16:00", "20:00"],
  bedtime: ["20:00"],
  weekly: ["09:00"],
};
const EMAR_WAC_GUIDANCE_URL = "https://app.leg.wa.gov/wac/default.aspx?cite=388-76-10470";

const dedupeAndSortScheduleTimes = (times: any[]) => (
  Array.from(
    new Set(
      (Array.isArray(times) ? times : [])
        .map((time) => normalizeStructuredScheduleTime(time))
        .filter(Boolean),
    ),
  ).sort((first, second) => first.localeCompare(second))
);

const getFirstNonEmptyReportFieldValue = (...values: any[]) => {
  for (const value of values) {
    const normalized = String(value || "").trim();
    if (normalized) {
      return normalized;
    }
  }
  return "";
};

const buildFormScheduleFields = (
  times: string[],
  options?: {
    scheduleDayPattern?: "daily" | "specific_days" | "alternate_days" | "specific_month_days";
    scheduleDays?: string[];
    scheduleMonthDays?: number[];
  },
) => {
  const normalizedTimes = dedupeAndSortScheduleTimes(times);
  const normalizedScheduleDayPattern = options?.scheduleDayPattern || "daily";
  const normalizedScheduleDays = normalizeMedicationScheduleDays(options?.scheduleDays || []);
  const normalizedScheduleMonthDays = normalizeMedicationScheduleMonthDays(options?.scheduleMonthDays || []);
  return {
    schedule: buildMedicationScheduleText(normalizedTimes, {
      dayPattern: normalizedScheduleDayPattern,
      scheduleDays: normalizedScheduleDays,
      scheduleMonthDays: normalizedScheduleMonthDays,
    }),
    scheduledClockTime: normalizedTimes[0] || "",
    scheduleDayPattern: normalizedScheduleDayPattern,
    scheduleDays: normalizedScheduleDays,
    scheduleMonthDays: normalizedScheduleMonthDays,
  };
};

const getExpectedScheduleCountForFrequency = (frequency: string) => {
  const normalizedFrequency = String(frequency || "").trim().toLowerCase();
  if (normalizedFrequency === "once_daily") {
    return 1;
  }
  if (normalizedFrequency === "twice_daily") {
    return 2;
  }
  if (normalizedFrequency === "three_times_daily") {
    return 3;
  }
  if (normalizedFrequency === "four_times_daily") {
    return 4;
  }
  if (normalizedFrequency === "bedtime") {
    return 1;
  }
  if (normalizedFrequency === "weekly") {
    return 1;
  }
  return 0;
};

const inferFrequencyFromMedication = (item: any) => {
  const explicitFrequency = String(item?.frequency || "").trim().toLowerCase();
  if (explicitFrequency) {
    return explicitFrequency;
  }
  if (item?.prn) {
    return "prn";
  }

  const scheduleTimes = getMedicationScheduleTimes(item);
  const scheduleText = String(item?.schedule || "").trim().toLowerCase();
  if (/\b(qhs|hs|bedtime)\b/.test(scheduleText)) {
    return "bedtime";
  }
  if (/\bweekly\b/.test(scheduleText)) {
    return "weekly";
  }
  if (/\b(bid|twice)\b/.test(scheduleText)) {
    return "twice_daily";
  }
  if (/\b(tid|three times)\b/.test(scheduleText)) {
    return "three_times_daily";
  }
  if (/\b(qid|four times)\b/.test(scheduleText)) {
    return "four_times_daily";
  }
  if (/\b(daily|once|every morning|every evening|qam)\b/.test(scheduleText)) {
    return "once_daily";
  }
  if (scheduleTimes.length === 1) {
    return "once_daily";
  }
  if (scheduleTimes.length === 2) {
    return "twice_daily";
  }
  if (scheduleTimes.length === 3) {
    return "three_times_daily";
  }
  if (scheduleTimes.length === 4) {
    return "four_times_daily";
  }
  return "";
};

const reconcileScheduleTimesForFrequency = (frequency: string, currentTimes: string[]) => {
  const normalizedFrequency = String(frequency || "").trim().toLowerCase();
  if (!normalizedFrequency || normalizedFrequency === "custom") {
    return dedupeAndSortScheduleTimes(currentTimes);
  }
  if (normalizedFrequency === "prn") {
    return [];
  }

  const expectedCount = getExpectedScheduleCountForFrequency(normalizedFrequency);
  if (!expectedCount) {
    return dedupeAndSortScheduleTimes(currentTimes);
  }

  const normalizedCurrentTimes = dedupeAndSortScheduleTimes(currentTimes);
  const defaultTimes = DEFAULT_FREQUENCY_SCHEDULE_TIMES[normalizedFrequency] || [];
  const mergedTimes = [...normalizedCurrentTimes];

  for (const time of defaultTimes) {
    if (mergedTimes.length >= expectedCount) {
      break;
    }
    if (!mergedTimes.includes(time)) {
      mergedTimes.push(time);
    }
  }

  if (!mergedTimes.length) {
    return dedupeAndSortScheduleTimes(defaultTimes.slice(0, expectedCount));
  }

  return dedupeAndSortScheduleTimes(mergedTimes).slice(0, expectedCount);
};

const getMedicationsMissingSchedule = (items: any[]) => (
  (Array.isArray(items) ? items : []).filter((item: any) => (
    item?.active !== false
    && item?.prn !== true
    && getMedicationScheduleTimes(item).length === 0
  ))
);

const mergeMedicationLists = (...lists: any[][]) => {
  const byMedicationId = new Map<string, any>();
  lists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((item: any) => {
      const medicationId = String(item?.medicationId || "").trim();
      if (medicationId) {
        byMedicationId.set(medicationId, item);
        return;
      }

      const fallbackKey = [
        String(item?.medicationName || "").trim().toLowerCase(),
        String(item?.dosage || "").trim().toLowerCase(),
        String(item?.route || "").trim().toLowerCase(),
      ].join("::");
      if (fallbackKey !== "::") {
        byMedicationId.set(fallbackKey, item);
      }
    });
  });

  return Array.from(byMedicationId.values());
};


const formatMedicationArchiveReason = (row: any) => (
  String(row?.archiveReasonLabel || row?.archiveReason || "").trim()
);

const normalizeValue = (value: any) => String(value || "").trim();
const renderMedicationUseInstructionsText = (entry: any) => {
  const detailLines = [
    String(entry?.indication || ""),
    String(entry?.instructions || ""),
  ].filter((value) => value !== "");

  return detailLines.length ? detailLines.join("\n") : "None";
};
const parseMedicationExpiryValue = (value: any) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const parsed = new Date(`${normalized}T23:59:59`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
  if (yearMonthMatch) {
    const yearValue = Number(yearMonthMatch[1]);
    const monthIndex = Number(yearMonthMatch[2]) - 1;
    if (monthIndex >= 0 && monthIndex <= 11) {
      return new Date(yearValue, monthIndex + 1, 0, 23, 59, 59, 999);
    }
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})\/(\d{4})$/);
  if (monthYearMatch) {
    const monthIndex = Number(monthYearMatch[1]) - 1;
    const yearValue = Number(monthYearMatch[2]);
    if (monthIndex >= 0 && monthIndex <= 11) {
      return new Date(yearValue, monthIndex + 1, 0, 23, 59, 59, 999);
    }
  }

  return null;
};
const isMedicationExpiringWithinMonth = (value: any) => {
  const parsedExpiry = parseMedicationExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  const now = new Date();
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + 30);
  return parsedExpiry.getTime() >= now.getTime() && parsedExpiry.getTime() <= threshold.getTime();
};
const isMedicationExpired = (value: any) => {
  const parsedExpiry = parseMedicationExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  return parsedExpiry.getTime() < Date.now();
};
const getMedicationExpiryAlert = (value: any) => {
  if (isMedicationExpired(value)) {
    return {
      label: `Expired: ${normalizeValue(value)}`,
      shortLabel: `Expired ${normalizeValue(value)}`,
      color: "#b91c1c",
      severity: "danger" as const,
    };
  }
  if (isMedicationExpiringWithinMonth(value)) {
    return {
      label: `Ends soon: ${normalizeValue(value)}`,
      shortLabel: `Ends soon ${normalizeValue(value)}`,
      color: "#b45309",
      severity: "warning" as const,
    };
  }
  return null;
};
const getMedicationExpiryCalendarValue = (value: any) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const parsed = new Date(`${normalized}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
  if (yearMonthMatch) {
    const parsed = new Date(Number(yearMonthMatch[1]), Number(yearMonthMatch[2]) - 1, 1, 12, 0, 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const monthYearMatch = normalized.match(/^(\d{1,2})\/(\d{4})$/);
  if (monthYearMatch) {
    const parsed = new Date(Number(monthYearMatch[2]), Number(monthYearMatch[1]) - 1, 1, 12, 0, 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};
const formatMedicationExpiryFromCalendar = (value: Date | Date[] | null | undefined) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
const normalizeValueKey = (value: any) => normalizeValue(value).toLowerCase();
const isPastChartDate = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return false;
  }
  const now = new Date();
  const todayDateOnly = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return normalized < todayDateOnly;
};

const isFutureChartDate = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return false;
  }
  return normalized > getLosAngelesIsoDateOnly(new Date());
};

const isFutureChartTimestamp = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return false;
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }
  return parsedDate.getTime() > Date.now();
};

const getScheduledChartUnlockTime = (scheduledDate?: Date | null) => {
  if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
    return null;
  }
  return new Date(scheduledDate.getTime() - (60 * 60 * 1000));
};

const getCurrentDateOnlyLabel = () => (
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date())
);

const resolveOccurrenceEntryDate = (occurrence: any) => (
  String(occurrence?.entryDate || occurrence?.timingMeta?.entryDate || "").trim()
);

const resolveOccurrenceChartDate = (occurrence: any) => {
  const explicitEntryDate = resolveOccurrenceEntryDate(occurrence);
  if (explicitEntryDate) {
    return explicitEntryDate;
  }
  const scheduledDate = occurrence?.timingMeta?.scheduledDate;
  if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(scheduledDate);
};

const canChartScheduledOccurrenceNow = (occurrence: any) => {
  const occurrenceEntryDate = resolveOccurrenceChartDate(occurrence);
  const currentDateOnly = getCurrentDateOnlyLabel();
  if (!occurrenceEntryDate || occurrenceEntryDate < currentDateOnly) {
    return true;
  }
  if (occurrenceEntryDate > currentDateOnly) {
    return false;
  }
  const unlockTime = getScheduledChartUnlockTime(occurrence?.timingMeta?.scheduledDate);
  if (!unlockTime) {
    return true;
  }
  return Date.now() >= unlockTime.getTime();
};

const formatChartUnlockTimeLabel = (scheduledDate?: Date | null) => {
  const unlockTime = getScheduledChartUnlockTime(scheduledDate);
  if (!unlockTime) {
    return "";
  }
  return unlockTime.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getAvailableChartTimeOptions = (entryDate?: string) => {
  const normalizedEntryDate = String(entryDate || "").trim();
  if (!normalizedEntryDate) {
    return SCHEDULE_TIME_OPTIONS;
  }
  if (isFutureChartDate(normalizedEntryDate)) {
    return [];
  }
  const todayDateOnly = getLosAngelesIsoDateOnly(new Date());
  if (normalizedEntryDate !== todayDateOnly) {
    return SCHEDULE_TIME_OPTIONS;
  }

  const currentTimeValue = normalizeStructuredScheduleTime(
    `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
  );
  if (!currentTimeValue) {
    return SCHEDULE_TIME_OPTIONS;
  }
  return SCHEDULE_TIME_OPTIONS.filter((option) => String(option?.value || "") <= currentTimeValue);
};

const hasCompletedPrnFollowUp = (entryLike: any) => {
  const status = normalizeValueKey(entryLike?.entryStatus || entryLike?.status);
  if (status !== "prn_given") {
    return false;
  }

  const source = entryLike?.entry || entryLike;
  return !!normalizeValue(source?.prnReassessedAt)
    && !!normalizeValue(source?.prnEffectiveness)
    && !!normalizeValue(source?.prnSideEffectCode);
};

const isPrnFollowUpPending = (entryLike: any) => (
  normalizeValueKey(entryLike?.entryStatus || entryLike?.status) === "prn_given"
  && !hasCompletedPrnFollowUp(entryLike)
);

const formatCorrectionScheduledTimeLabel = (value: any, isPrnMedication?: boolean) => {
  if (isPrnMedication) {
    return "PRN";
  }
  const normalizedTime = normalizeStructuredScheduleTime(value);
  return formatStructuredScheduleTimeLabel(normalizedTime) || normalizeValue(value);
};

const formatEmarStatusLabel = (value: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "prn_given") {
    return "Given";
  }
  if (normalized === "not_given") {
    return "Not Given";
  }
  if (normalized === "family") {
    return "Family";
  }
  if (!normalized) {
    return "Pending";
  }
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getEmarStatusSeverity = (value: any, entryLike?: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "refused" || normalized === "missed") {
    return "danger";
  }
  if (normalized === "held" || normalized === "hospitalized" || normalized === "not_given") {
    return "warning";
  }
  if (normalized === "prn_given") {
    return hasCompletedPrnFollowUp(entryLike) ? "success" : "info";
  }
  if (normalized === "given" || normalized === "family") {
    return "success";
  }
  return "secondary";
};

const formatDailyTrackingStatusLabel = (value: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "missed") {
    return "Missed";
  }
  if (normalized === "recorded") {
    return "Recorded";
  }
  if (normalized === "refused") {
    return "Refused";
  }
  if (normalized === "family") {
    return "Family";
  }
  if (normalized === "hospitalized") {
    return "Hospitalized";
  }
  if (normalized === "not_needed") {
    return "Not needed";
  }
  return "Pending";
};

const getDailyTrackingStatusSeverity = (value: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "missed") {
    return "danger";
  }
  if (normalized === "recorded") {
    return "success";
  }
  if (normalized === "refused") {
    return "danger";
  }
  if (normalized === "family") {
    return "success";
  }
  if (normalized === "hospitalized") {
    return "warning";
  }
  if (normalized === "not_needed") {
    return "warning";
  }
  return "secondary";
};

const buildOccurrenceSummaryTag = (
  occurrence: any,
  statusLabel?: string,
  statusSeverity?: string,
) => {
  const timingMeta = occurrence?.timingMeta || {};
  const timeLabel = String(occurrence?.occurrenceLabel || timingMeta?.scheduledLabel || "Scheduled").trim() || "Scheduled";
  const isPrnOccurrence = String(timeLabel).trim().toUpperCase() === "PRN";
  const suffix = timeLabel && timeLabel !== "Scheduled" ? ` ${timeLabel}` : "";

  if (occurrence?.rawEntryStatus === "prn_given" && !String(occurrence?.entry?.prnReassessedAt || "").trim()) {
    return {
      label: `Follow-up Pending${suffix}`,
      severity: "warning",
    };
  }
  if (statusLabel) {
    return {
      label: `${statusLabel}${suffix}`,
      severity: statusSeverity || "secondary",
    };
  }
  if (timingMeta?.isOverdue) {
    return {
      label: `Missed${suffix}`,
      severity: "danger",
    };
  }
  if (timingMeta?.isUpcoming) {
    return {
      label: `Upcoming${suffix}`,
      severity: "secondary",
    };
  }
  if (isPrnOccurrence) {
    return null;
  }
  return {
    label: `Pending${suffix}`,
    severity: timingMeta?.isDueSoon ? "info" : "secondary",
  };
};

const isDailyTrackingValueEntryStatus = (value: any) => {
  const normalized = normalizeValueKey(value);
  return normalized === "recorded";
};

const formatEmarDateTime = (value: any) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return "";
  }
  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }
  return parsedDate.toLocaleString();
};

const formatEmarDateOnly = (value: any) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return "";
  }
  const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const parsedDate = new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3]),
      12,
      0,
      0,
      0,
    );
    return Number.isNaN(parsedDate.getTime()) ? normalized : parsedDate.toLocaleDateString();
  }
  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }
  return parsedDate.toLocaleDateString();
};

const formatEmarHistoryEventLabel = (eventType: any) => {
  const normalized = normalizeValueKey(eventType);
  if (normalized === "status_create") {
    return "Created";
  }
  if (normalized === "entry_corrected") {
    return "Corrected entry";
  }
  if (normalized === "status_update") {
    return "Status updated";
  }
  if (normalized === "exception_reviewed") {
    return "Reviewed";
  }
  if (normalized === "follow_up_note") {
    return "Follow-up note";
  }
  if (normalized === "exception_resolved") {
    return "Resolved";
  }
  return "Updated";
};

const sortEmarEntriesByLatest = (items: any[]) => (
  [...items].sort((first: any, second: any) => {
    const firstTime = new Date(first?.recordedAt || first?.modifiedAt || first?.createdAt || 0).getTime();
    const secondTime = new Date(second?.recordedAt || second?.modifiedAt || second?.createdAt || 0).getTime();
    return secondTime - firstTime;
  })
);

const renderPrnDetailBlock = (entry: any, fontSize = "0.82rem") => {
  const detailRows = buildEmarEntryDetailRows(entry);
  if (!detailRows.length) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-column gap-1">
      {detailRows.map((item) => (
        <div key={`${item.label}-${item.value}`} style={{ ...appMutedTextStyle, fontSize, whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>{item.label}:</strong> {item.value}
        </div>
      ))}
    </div>
  );
};

const renderMedicationSupportingDetails = (
  medicationLike: any,
  options?: {
    fontSize?: string;
    scheduleLabel?: string;
    showSchedule?: boolean;
    showExpiry?: boolean;
    expiryAlert?: any;
  },
) => {
  const fontSize = options?.fontSize || "0.84rem";
  const expiryAlert = options?.expiryAlert || getMedicationExpiryAlert(medicationLike?.effectiveEndDate);
  const scheduleLabel = options?.scheduleLabel || buildMedicationScheduleText(medicationLike);
  const showSchedule = options?.showSchedule !== false;
  const showExpiry = options?.showExpiry !== false;

  return (
    <>
      {(medicationLike?.dosage || medicationLike?.route) ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize }}>
          <strong style={{ color: "#334155" }}>Dose/Route:</strong> {[medicationLike?.dosage, medicationLike?.route].filter(Boolean).join(" • ")}
        </div>
      ) : null}
      {showSchedule && scheduleLabel ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize }}>
          <strong style={{ color: "#334155" }}>Schedule:</strong> {scheduleLabel}
        </div>
      ) : null}
      {showExpiry && medicationLike?.effectiveEndDate ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize, color: expiryAlert?.color || appMutedTextStyle.color }}>
          <strong style={{ color: expiryAlert?.color || "#334155" }}>
            {isMedicationExpired(medicationLike?.effectiveEndDate) ? "Expired:" : "Expiration:"}
          </strong> {medicationLike.effectiveEndDate}
        </div>
      ) : null}
      {medicationLike?.indication ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize, whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {medicationLike.indication}
        </div>
      ) : null}
      {medicationLike?.instructions ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize, whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>Instructions:</strong> {medicationLike.instructions}
        </div>
      ) : null}
      {medicationLike?.handlingNotes ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize, whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>Handling notes:</strong> {medicationLike.handlingNotes}
        </div>
      ) : null}
    </>
  );
};

const buildWordFriendlyHtmlDocument = (title: string, htmlContent: string, brandName: string = "AFH Hub") => {
  const reportLabel = title.toLowerCase().includes("psychotropic")
    ? "Psychotropic Medication Compliance"
    : "MAR Compliance Report";
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      .hub-report-header { width:100%; max-width:7.55in; margin:0 auto 16px; border-collapse:collapse; font-family:Arial,Helvetica,sans-serif; }
      .hub-report-header td { background:#fff7ed; border:1px solid #f1d2b6; padding:12px 16px; color:#0f172a; font-size:12pt; font-weight:700; text-align:center; }
      .hub-report-footer { width:100%; max-width:7.55in; margin:16px auto 0; border-collapse:collapse; font-family:Arial,Helvetica,sans-serif; }
      .hub-report-footer td { padding:8px 2px 0; color:#64748b; font-size:7.5pt; text-align:center; }
      .hub-report-footer .hub-report-link { color:#c96a16; text-decoration:underline; }
    </style>
  </head>
  <body>
    <table class="hub-report-header" role="presentation"><tr><td>${reportLabel}</td></tr></table>
    ${htmlContent}
    <table class="hub-report-footer" role="presentation"><tr><td>Generated by ${brandName} &nbsp;|&nbsp; <span class="hub-report-link">www.afhhub.com</span></td></tr></table>
  </body>
</html>`;
};

const ProviderResidentEmarPage = () => {
  const { residentId } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const toast = useRef<Toast>(null);
  const marSheetFileInputRef = useRef<HTMLInputElement>(null);
  const emarCompareFileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const [isUserSubscribed, setIsUserSubscribed] = useState(true);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingMarSheetFile, setImportingMarSheetFile] = useState(false);
  const [residentDetails, setResidentDetails] = useState<any>(null);
  const [carePlanSummaryData, setCarePlanSummaryData] = useState<any>(null);
  const [carePlanResponsiblePartiesData, setCarePlanResponsiblePartiesData] = useState<any>(null);
  const [medications, setMedications] = useState<any[]>([]);
  const [dailyTrackingItems, setDailyTrackingItems] = useState<any[]>([]);
  const [todayEntries, setTodayEntries] = useState<any[]>([]);
  const [todayTrackingEntries, setTodayTrackingEntries] = useState<any[]>([]);
  const [historyEntries, setHistoryEntries] = useState<any[]>([]);
  const [historyDailyTrackingEntries, setHistoryDailyTrackingEntries] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilterDate, setHistoryFilterDate] = useState(getLosAngelesYesterdayIsoDateOnly());
  const [historyStatusFilter, setHistoryStatusFilter] = useState<"all" | "missed">("all");
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState<any>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<any>(null);
  const [deletingEntry, setDeletingEntry] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showProtocolSetupDialog, setShowProtocolSetupDialog] = useState(false);
  const [editingProtocolDefinitionIndex, setEditingProtocolDefinitionIndex] = useState<number>(-1);
  const [protocolDefinitionDraft, setProtocolDefinitionDraft] = useState<any>(createEmptyProtocolParameterSet());
  const [protocolDialogDefinitions, setProtocolDialogDefinitions] = useState<any[]>([]);
  const [expandedProtocolEditorIds, setExpandedProtocolEditorIds] = useState<string[]>([]);
  const [showProtocolRuleEditor, setShowProtocolRuleEditor] = useState(false);
  const protocolEditorSectionRef = useRef<HTMLDivElement | null>(null);
  const [activeProtocolRuleIndex, setActiveProtocolRuleIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [todayPassViewMode, setTodayPassViewMode] = useState<"medication" | "time">("medication");
  const [todayPassFilter, setTodayPassFilter] = useState<"all" | "pending" | "charted">("all");
  const [showMedicationExpiryAlertsExpanded, setShowMedicationExpiryAlertsExpanded] = useState(false);
  const [formData, setFormData] = useState<any>(EMPTY_FORM);
  const [selectedScheduleTimes, setSelectedScheduleTimes] = useState<string[]>([]);
  const [selectedScheduleDays, setSelectedScheduleDays] = useState<string[]>([]);
  const [selectedScheduleMonthDays, setSelectedScheduleMonthDays] = useState<number[]>([]);
  const [savingEntry, setSavingEntry] = useState(false);
  const [savingEntryKey, setSavingEntryKey] = useState("");
  const [generatingMedicationInstructions, setGeneratingMedicationInstructions] = useState(false);
  const [carePlanPreviewDialogVisible, setCarePlanPreviewDialogVisible] = useState(false);
  const [carePlanPreviewEntries, setCarePlanPreviewEntries] = useState<any[]>([]);
  const [marSheetPreviewDialogVisible, setMarSheetPreviewDialogVisible] = useState(false);
  const [marSheetPreviewEntries, setMarSheetPreviewEntries] = useState<any[]>([]);
  const [marSheetPreviewFileName, setMarSheetPreviewFileName] = useState("");
  const [marSheetPreviewWarnings, setMarSheetPreviewWarnings] = useState<string[]>([]);
  const [latestEmarCompareJobId, setLatestEmarCompareJobId] = useState("");
  const [showEmarCompareDialog, setShowEmarCompareDialog] = useState(false);
  const [emarCompareUploadFile, setEmarCompareUploadFile] = useState<File | null>(null);
  const [processingEmarCompare, setProcessingEmarCompare] = useState(false);
  const [applyingEmarCompareChanges, setApplyingEmarCompareChanges] = useState(false);
  const [loadingLatestEmarCompareResult, setLoadingLatestEmarCompareResult] = useState(false);
  const [loadingLatestEmarCompareJob, setLoadingLatestEmarCompareJob] = useState(false);
  const [emarCompareJobId, setEmarCompareJobId] = useState("");
  const [emarCompareCompletedJobId, setEmarCompareCompletedJobId] = useState("");
  const [emarCompareJobStatus, setEmarCompareJobStatus] = useState("");
  const [emarCompareJobSubStatus, setEmarCompareJobSubStatus] = useState("");
  const [emarCompareProcessingStartedAt, setEmarCompareProcessingStartedAt] = useState<number | null>(null);
  const [emarCompareElapsedSeconds, setEmarCompareElapsedSeconds] = useState(0);
  const [emarCompareFileName, setEmarCompareFileName] = useState("");
  const [emarCompareResultData, setEmarCompareResultData] = useState<any>(null);
  const [emarCompareReportHtml, setEmarCompareReportHtml] = useState("");
  const [showPsychotropicCompareDialog, setShowPsychotropicCompareDialog] = useState(false);
  const [loadingPsychotropicCompare, setLoadingPsychotropicCompare] = useState(false);
  const [psychotropicCompareResultData, setPsychotropicCompareResultData] = useState<any>(null);
  const [psychotropicCompareReportHtml, setPsychotropicCompareReportHtml] = useState("");
  const [bulkScheduleDialogVisible, setBulkScheduleDialogVisible] = useState(false);
  const [bulkScheduleRows, setBulkScheduleRows] = useState<any[]>([]);
  const [bulkScheduleApplyTimes, setBulkScheduleApplyTimes] = useState<string[]>([]);
  const [savingBulkSchedule, setSavingBulkSchedule] = useState(false);
  const [marSheetImportJobId, setMarSheetImportJobId] = useState("");
  const [marSheetImportJobStatus, setMarSheetImportJobStatus] = useState("");
  const [marSheetImportJobSubStatus, setMarSheetImportJobSubStatus] = useState("");
  const [marSheetImportDialogTitle, setMarSheetImportDialogTitle] = useState("Preparing MAR Sheet Upload");
  const [marSheetImportProgressDialogVisible, setMarSheetImportProgressDialogVisible] = useState(false);
  const [marSheetImportElapsedTime, setMarSheetImportElapsedTime] = useState(0);
  const [pendingMarSheetImportPayload, setPendingMarSheetImportPayload] = useState<any>(null);
  const [showMedicationSetupDialog, setShowMedicationSetupDialog] = useState(false);
  const [showCarePlanRequiredDialog, setShowCarePlanRequiredDialog] = useState(false);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [exceptionDialogMode, setExceptionDialogMode] = useState<"follow_up" | "resolve">("follow_up");
  const [selectedAttentionRow, setSelectedAttentionRow] = useState<any>(null);
  const [exceptionFollowUpDraft, setExceptionFollowUpDraft] = useState("");
  const [savingExceptionAction, setSavingExceptionAction] = useState(false);
  const [showMedPassActionDialog, setShowMedPassActionDialog] = useState(false);
  const [medPassActionDraft, setMedPassActionDraft] = useState("");
  const [prnReasonCode, setPrnReasonCode] = useState("");
  const [prnReassessedAt, setPrnReassessedAt] = useState("");
  const [prnEffectiveness, setPrnEffectiveness] = useState("");
  const [prnSideEffectCode, setPrnSideEffectCode] = useState("");
  const [prnSideEffectNote, setPrnSideEffectNote] = useState("");
  const [prnFollowUpNote, setPrnFollowUpNote] = useState("");
  const [protocolAssessmentValues, setProtocolAssessmentValues] = useState<Record<string, string>>({});
  const [protocolDoseValue, setProtocolDoseValue] = useState("");
  const [protocolDecisionNote, setProtocolDecisionNote] = useState("");
  const [protocolResolution, setProtocolResolution] = useState<any>(null);
  const [showProtocolConditionsDialog, setShowProtocolConditionsDialog] = useState(false);
  const [resolvingProtocol, setResolvingProtocol] = useState(false);
  const [applyHospitalizedToAllForDay, setApplyHospitalizedToAllForDay] = useState(false);
  const [pendingMedPassAction, setPendingMedPassAction] = useState<any>(null);
  const [resettingMedicationList, setResettingMedicationList] = useState(false);
  const [medicationListFilter, setMedicationListFilter] = useState<"active" | "active_expired" | "discontinued" | "inactive" | "inactive_expired">("active");
  const [dailyTrackingListFilter, setDailyTrackingListFilter] = useState<"active" | "discontinued" | "inactive">("active");
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [blankMarReport, setBlankMarReport] = useState(false);
  const [showReportDetailsDialog, setShowReportDetailsDialog] = useState(false);
  const [reportContactForm, setReportContactForm] = useState<any>({});
  const [reportContactErrors, setReportContactErrors] = useState<Record<string, string>>({});
  const [savingReportDetails, setSavingReportDetails] = useState(false);
  const [medicationSuggestions, setMedicationSuggestions] = useState<PsychotropicMedicationSuggestion[]>([]);
  const [psychotropicMedicationMode, setPsychotropicMedicationMode] = useState(false);
  const [showDailyTrackingEditDialog, setShowDailyTrackingEditDialog] = useState(false);
  const [dailyTrackingFormData, setDailyTrackingFormData] = useState<any>(createEmptyEmarDailyTrackingItem());
  const [dailyTrackingScheduleTimes, setDailyTrackingScheduleTimes] = useState<string[]>([]);
  const [dailyTrackingScheduleDays, setDailyTrackingScheduleDays] = useState<string[]>([]);
  const [dailyTrackingScheduleMonthDays, setDailyTrackingScheduleMonthDays] = useState<number[]>([]);
  const [savingDailyTrackingItem, setSavingDailyTrackingItem] = useState(false);
  const [showDailyTrackingEntryDialog, setShowDailyTrackingEntryDialog] = useState(false);
  const [pendingDailyTrackingEntryAction, setPendingDailyTrackingEntryAction] = useState<any>(null);
  const [dailyTrackingValueDraft, setDailyTrackingValueDraft] = useState<Record<string, string>>({});
  const [dailyTrackingNoteDraft, setDailyTrackingNoteDraft] = useState("");
  const [savingDailyTrackingEntry, setSavingDailyTrackingEntry] = useState(false);
  const [trackingRecommendedActionDialog, setTrackingRecommendedActionDialog] = useState<{ title: string; message: string } | null>(null);
  const [protocolSetupTarget, setProtocolSetupTarget] = useState<"medication" | "tracking">("medication");
  const today = new Date();
  const [reportRangeMode, setReportRangeMode] = useState<"monthly" | "exact_dates">("monthly");
  const [reportMonthSelection, setReportMonthSelection] = useState<Date>(today);
  const todayDate = getLosAngelesIsoDateOnly(today);
  const latestPastEntryDate = todayDate;
  const earliestPastEntryEditDate = getLosAngelesIsoDateOnly(getMonthsAgo(today, 3));
  const monthStartDate = getIsoDateOnly(getMonthStart(today));
  const [reportStartDateSelection, setReportStartDateSelection] = useState(monthStartDate);
  const [reportEndDateSelection, setReportEndDateSelection] = useState(todayDate);
  const [reportExactDateSelection, setReportExactDateSelection] = useState<(Date | null)[] | null>([
    getMonthStart(today),
    today,
  ]);
  const effectiveHistoryDateCandidate = String(historyFilterDate || latestPastEntryDate).trim() || latestPastEntryDate;
  const effectiveHistoryDate = effectiveHistoryDateCandidate < earliestPastEntryEditDate
    ? earliestPastEntryEditDate
    : effectiveHistoryDateCandidate > latestPastEntryDate
      ? latestPastEntryDate
      : effectiveHistoryDateCandidate;
  const getStoredEmarCompareJobId = (targetResidentId: string) => {
    const normalizedResidentId = String(targetResidentId || "").trim();
    if (!normalizedResidentId) {
      return "";
    }

    try {
      return String(window.localStorage.getItem(`emar-last-compare-job:${normalizedResidentId}`) || "").trim();
    } catch (_error) {
      return "";
    }
  };
  const persistEmarCompareJobId = (targetResidentId: string, jobId: string) => {
    const normalizedResidentId = String(targetResidentId || "").trim();
    const normalizedJobId = String(jobId || "").trim();
    if (!normalizedResidentId || !normalizedJobId) {
      return;
    }

    try {
      window.localStorage.setItem(`emar-last-compare-job:${normalizedResidentId}`, normalizedJobId);
    } catch (_error) {
      // Ignore storage failures so the compare workflow still works.
    }
  };

  const activeMedications = useMemo(
    () => medications.filter((item: any) => item?.active !== false),
    [medications],
  );
  const inactiveMedications = useMemo(
    () => medications.filter((item: any) => item?.active === false),
    [medications],
  );
  const activeDisplayMedications = useMemo(
    () => activeMedications.filter((item: any) => !isMarOnlyMedication(item) && item?.discontinued !== true),
    [activeMedications],
  );
  const inactiveDisplayMedications = useMemo(
    () => inactiveMedications.filter((item: any) => !isMarOnlyMedication(item)),
    [inactiveMedications],
  );
  const activeMedicationOrders = useMemo(
    () => activeMedications.filter((item: any) => item?.discontinued !== true),
    [activeMedications],
  );
  const activeNonPrnMedications = useMemo(
    () => activeMedicationOrders.filter((item: any) => item?.prn !== true && !isMarOnlyMedication(item)),
    [activeMedicationOrders],
  );
  const activePrnMedications = useMemo(
    () => activeMedicationOrders.filter((item: any) => item?.prn === true),
    [activeMedicationOrders],
  );
  const activeMarOnlyItems = useMemo(
    () => activeMedications
      .filter((item: any) => isMarOnlyMedication(item))
      .sort((first: any, second: any) => (
        String(first?.medicationName || "").localeCompare(String(second?.medicationName || ""))
      )),
    [activeMedications],
  );
  const discontinuedMedications = useMemo(
    () => activeMedications.filter((item: any) => item?.discontinued === true && !isMarOnlyMedication(item)),
    [activeMedications],
  );
  const activeNonPrnMedicationsForMedPass = useMemo(
    () => activeNonPrnMedications.filter((item: any) => !isMedicationExpired(item?.effectiveEndDate)),
    [activeNonPrnMedications],
  );
  const activePrnMedicationsForMedPass = useMemo(
    () => activePrnMedications.filter((item: any) => !isMedicationExpired(item?.effectiveEndDate)),
    [activePrnMedications],
  );
  const historyPrnMedicationsForCharting = useMemo(
    () => medications.filter((item: any) => {
      const prnText = [item?.frequency, item?.schedule, item?.instructions, item?.indication]
        .map((value: any) => String(value || "").trim().toLowerCase())
        .join(" ");
      const isPrnMedication = item?.prn === true
        || item?.isPrn === true
        || item?.isPRN === true
        || item?.prnMedication === true
        || /\bprn\b|as[- ]needed|pro re nata/.test(prnText);
      const isDeletedMedication = item?.deleted === true
        || item?.isDeleted === true
        || String(item?.status || "").trim().toLowerCase() === "deleted";
      if (isDeletedMedication || !isPrnMedication || isMarOnlyMedication(item)) {
        return false;
      }
      return item?.active !== false && item?.discontinued !== true && !isMedicationExpired(item?.effectiveEndDate);
    }),
    [medications],
  );
  const activeNonPrnMedicationsScheduledToday = useMemo(
    () => activeNonPrnMedicationsForMedPass.filter((medication: any) => getMedicationScheduleOccurrences(medication, todayDate).length > 0),
    [activeNonPrnMedicationsForMedPass, todayDate],
  );
  const activeDailyTrackingItems = useMemo(
    () => dailyTrackingItems.filter((item: any) => item?.active !== false && item?.discontinued !== true),
    [dailyTrackingItems],
  );
  const discontinuedDailyTrackingItems = useMemo(
    () => dailyTrackingItems.filter((item: any) => item?.active !== false && item?.discontinued === true),
    [dailyTrackingItems],
  );
  const inactiveDailyTrackingItems = useMemo(
    () => dailyTrackingItems.filter((item: any) => item?.active === false),
    [dailyTrackingItems],
  );
  const residentDisplayName = useMemo(
    () => [residentDetails?.firstName, residentDetails?.lastName].filter(Boolean).join(" ") || `Resident ${residentId || ""}`,
    [residentDetails?.firstName, residentDetails?.lastName, residentId],
  );
  const expiredActiveMedications = useMemo(
    () => activeDisplayMedications.filter((item: any) => isMedicationExpired(item?.effectiveEndDate)),
    [activeDisplayMedications],
  );
  const medicationExpiryAlerts = useMemo(() => (
    activeMedications
      .filter((item: any) => item?.effectiveEndDate && (
        isMedicationExpired(item.effectiveEndDate) || isMedicationExpiringWithinMonth(item.effectiveEndDate)
      ))
      .map((item: any) => ({
        medicationId: item?.medicationId || item?.residentEmarMedicationId || item?.id || item?.name,
        medicationName: normalizeValue(item?.name)
          || normalizeValue(item?.medicationName)
          || normalizeValue(item?.medicineName)
          || normalizeValue(item?.drugName)
          || normalizeValue(item?.label)
          || "Medication",
        effectiveEndDate: item?.effectiveEndDate,
        status: isMedicationExpired(item?.effectiveEndDate) ? "expired" : "expiring",
      }))
      .sort((left, right) => {
        if (left.status !== right.status) {
          return left.status === "expired" ? -1 : 1;
        }
        const leftTime = Date.parse(String(left.effectiveEndDate || "")) || 0;
        const rightTime = Date.parse(String(right.effectiveEndDate || "")) || 0;
        return leftTime - rightTime;
      })
  ), [activeMedications]);
  const expiredInactiveMedications = useMemo(
    () => inactiveDisplayMedications.filter((item: any) => isMedicationExpired(item?.effectiveEndDate)),
    [inactiveDisplayMedications],
  );
  const showArchivedMedications = medicationListFilter === "inactive" || medicationListFilter === "inactive_expired";
  const showDiscontinuedMedications = medicationListFilter === "discontinued";
  const showExpiredOnlyMedications = medicationListFilter === "active_expired" || medicationListFilter === "inactive_expired";
  const medicationListFilterOptions = useMemo(
    () => [
      { label: `Active meds (${activeDisplayMedications.length})`, value: "active" },
      { label: `Expired meds (${expiredActiveMedications.length})`, value: "active_expired" },
      { label: `Discontinued meds (${discontinuedMedications.length})`, value: "discontinued" },
      { label: `Deleted meds (${inactiveDisplayMedications.length})`, value: "inactive" },
      // { label: `Expired inactive meds (${expiredInactiveMedications.length})`, value: "inactive_expired" },
    ],
    [activeDisplayMedications.length, expiredActiveMedications.length, discontinuedMedications.length, inactiveDisplayMedications.length, expiredInactiveMedications.length],
  );
  const displayedMedications = useMemo(
    () => {
      const baseList = showArchivedMedications
        ? inactiveDisplayMedications
        : showDiscontinuedMedications
          ? discontinuedMedications
          : activeDisplayMedications;
      if (!showExpiredOnlyMedications) {
        return baseList;
      }
      return baseList.filter((item: any) => isMedicationExpired(item?.effectiveEndDate));
    },
    [showArchivedMedications, showDiscontinuedMedications, showExpiredOnlyMedications, inactiveDisplayMedications, discontinuedMedications, activeDisplayMedications],
  );
  const canEditPastEntry = (entry: any) => {
    const entryDate = String(entry?.entryDate || "").trim();
    return !!entryDate && entryDate >= earliestPastEntryEditDate && entryDate <= latestPastEntryDate;
  };
  const dailyTrackingListFilterOptions = useMemo(
    () => [
      { label: `Active items (${activeDailyTrackingItems.length})`, value: "active" },
      { label: `Discontinued items (${discontinuedDailyTrackingItems.length})`, value: "discontinued" },
      { label: `Deleted items (${inactiveDailyTrackingItems.length})`, value: "inactive" },
    ],
    [activeDailyTrackingItems.length, discontinuedDailyTrackingItems.length, inactiveDailyTrackingItems.length],
  );
  const displayedDailyTrackingItems = useMemo(
    () => {
      const baseList = dailyTrackingListFilter === "inactive"
        ? inactiveDailyTrackingItems
        : dailyTrackingListFilter === "discontinued"
          ? discontinuedDailyTrackingItems
          : activeDailyTrackingItems;
      return [...baseList].sort((first: any, second: any) => String(first?.name || "").localeCompare(String(second?.name || "")));
    },
    [dailyTrackingListFilter, activeDailyTrackingItems, discontinuedDailyTrackingItems, inactiveDailyTrackingItems],
  );
  const todayEntryByOccurrenceKey = useMemo(() => {
    const nextEntries: Record<string, any> = {};
    todayEntries.forEach((item: any) => {
      const medicationId = String(item?.medicationId || "").trim();
      const rawScheduledTimeKey = String(item?.scheduledTimeKey || (String(item?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase();
      const scheduledTimeKey = rawScheduledTimeKey.startsWith("prn") ? "prn" : rawScheduledTimeKey;
      const occurrenceKey = `${medicationId}::${scheduledTimeKey}`;
      if (!medicationId) {
        return;
      }
      const existingItem = nextEntries[occurrenceKey];
      if (!existingItem) {
        nextEntries[occurrenceKey] = item;
        return;
      }
      if (scheduledTimeKey === "prn") {
        const existingPending = isPrnFollowUpPending(existingItem);
        const nextPending = isPrnFollowUpPending(item);
        if (nextPending && !existingPending) {
          nextEntries[occurrenceKey] = item;
          return;
        }
        if (nextPending === existingPending) {
          const existingTime = new Date(existingItem?.modifiedAt || existingItem?.recordedAt || existingItem?.createdAt || 0).getTime();
          const nextTime = new Date(item?.modifiedAt || item?.recordedAt || item?.createdAt || 0).getTime();
          if (nextTime >= existingTime) {
            nextEntries[occurrenceKey] = item;
          }
        }
      }
    });
    return nextEntries;
  }, [todayEntries]);
  const todayDailyTrackingEntryByOccurrenceKey = useMemo(() => {
    const nextEntries: Record<string, any> = {};
    todayTrackingEntries.forEach((item: any) => {
      const occurrenceKey = getDailyTrackingEntryOccurrenceKey(item);
      if (!occurrenceKey || nextEntries[occurrenceKey]) {
        return;
      }
      nextEntries[occurrenceKey] = item;
    });
    return nextEntries;
  }, [todayTrackingEntries]);
  const visibleHistoryEntries = useMemo(() => {
    const dedupedEntries = new Map<string, any>();
    historyEntries.forEach((entry: any, index: number) => {
      const entryId = String(entry?.entryId || "").trim();
      const fallbackKey = [
        "emar",
        String(entry?.medicationId || "").trim(),
        String(entry?.entryDate || "").trim(),
        String(entry?.scheduledTimeKey || "").trim().toLowerCase(),
        String(entry?.status || "").trim().toLowerCase(),
        String(index),
      ].join("::");
      const dedupeKey = entryId ? `emar::${entryId}` : fallbackKey;
      const existingEntry = dedupedEntries.get(dedupeKey);
      if (!existingEntry) {
        dedupedEntries.set(dedupeKey, {
          ...entry,
          entryKind: "emar",
        });
        return;
      }

      const existingTime = new Date(existingEntry?.recordedAt || existingEntry?.modifiedAt || existingEntry?.createdAt || 0).getTime();
      const nextTime = new Date(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || 0).getTime();
      if (nextTime >= existingTime) {
        dedupedEntries.set(dedupeKey, {
          ...entry,
          entryKind: "emar",
        });
      }
    });

    historyDailyTrackingEntries.forEach((entry: any, index: number) => {
      const entryId = String(entry?.entryId || "").trim();
      const fallbackKey = [
        "daily_tracking",
        String(entry?.trackingItemId || "").trim(),
        String(entry?.entryDate || "").trim(),
        String(entry?.scheduledTimeKey || "default").trim().toLowerCase(),
        String(entry?.status || "").trim().toLowerCase(),
        String(index),
      ].join("::");
      const dedupeKey = entryId ? `daily_tracking::${entryId}` : fallbackKey;
      const existingEntry = dedupedEntries.get(dedupeKey);
      if (!existingEntry) {
        dedupedEntries.set(dedupeKey, {
          ...entry,
          entryKind: "daily_tracking",
        });
        return;
      }

      const existingTime = new Date(existingEntry?.recordedAt || existingEntry?.modifiedAt || existingEntry?.createdAt || 0).getTime();
      const nextTime = new Date(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || 0).getTime();
      if (nextTime >= existingTime) {
        dedupedEntries.set(dedupeKey, {
          ...entry,
          entryKind: "daily_tracking",
        });
      }
    });

    const savedEntries = sortEmarEntriesByLatest(Array.from(dedupedEntries.values()).filter((entry: any) => entry?.entryKind === "emar"));
    const dailyTrackingSavedEntries = sortEmarEntriesByLatest(Array.from(dedupedEntries.values()).filter((entry: any) => entry?.entryKind === "daily_tracking"));
    const existingDailyTrackingOccurrenceKeys = new Set(
      dailyTrackingSavedEntries.map((entry: any) => getDailyTrackingEntryOccurrenceKey(entry)).filter(Boolean),
    );
    const existingOccurrenceKeys = new Set(
      savedEntries.map((entry: any) => (
        `${String(entry?.medicationId || "").trim()}::${String(entry?.scheduledTimeKey || (String(entry?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase()}`
      )).filter(Boolean),
    );

    const syntheticPendingRows = activeNonPrnMedicationsForMedPass
      .flatMap((medication: any) => {
        const medicationId = String(medication?.medicationId || "").trim();
        if (!medicationId) {
          return [];
        }

        return getMedicationScheduleOccurrences(medication, effectiveHistoryDate).flatMap((occurrence: any) => {
          const scheduledTimeKey = String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase();
          const occurrenceKey = `${medicationId}::${scheduledTimeKey}`;
          if (existingOccurrenceKeys.has(occurrenceKey)) {
            return [];
          }

          return [{
            syntheticPending: true,
            entryKind: "emar",
            residentId,
            medicationId,
            medicationName: medication?.medicationName || "Medication",
            medicationSnapshot: {
              medicationId,
              medicationName: medication?.medicationName || "Medication",
              dosage: medication?.dosage || "",
              route: medication?.route || "",
              schedule: medication?.schedule || "",
              scheduledClockTime: occurrence?.scheduledClockTime || "",
              scheduleDayPattern: medication?.scheduleDayPattern || "daily",
              scheduleDays: Array.isArray(medication?.scheduleDays) ? medication.scheduleDays : [],
              indication: medication?.indication || "",
              instructions: medication?.instructions || "",
              handlingNotes: medication?.handlingNotes || "",
              protocolEnabled: medication?.protocolEnabled === true,
              protocolDefinitions: Array.isArray(medication?.protocolDefinitions) ? medication.protocolDefinitions : [],
              protocolCategory: medication?.protocolCategory || "",
              protocolDefinition: medication?.protocolDefinition || null,
              protocolInstructions: medication?.protocolInstructions || "",
              prn: false,
              active: true,
            },
            entryDate: effectiveHistoryDate,
            scheduledTimeKey,
            scheduledTime: occurrence?.scheduledLabel || medication?.schedule || "",
            status: isPastChartDate(effectiveHistoryDate) ? "missed" : "pending",
            note: "",
            recordedAt: "",
            statusHistory: [],
          }];
        });
      })
      .sort((first: any, second: any) => {
        const medicationCompare = String(first?.medicationName || "").localeCompare(String(second?.medicationName || ""));
        if (medicationCompare !== 0) {
          return medicationCompare;
        }
        return String(first?.scheduledTime || "").localeCompare(String(second?.scheduledTime || ""));
      });

    const syntheticPrnRows = historyPrnMedicationsForCharting
      .flatMap((medication: any) => {
        const medicationId = String(
          medication?.medicationId
          || medication?.residentEmarMedicationId
          || medication?.id
          || "",
        ).trim();
        if (!medicationId || existingOccurrenceKeys.has(`${medicationId}::prn`)) {
          return [];
        }

        return [{
          syntheticPending: true,
          entryKind: "emar",
          residentId,
          medicationId,
          medicationName: medication?.medicationName || "Medication",
          medicationSnapshot: {
            medicationId,
            medicationName: medication?.medicationName || "Medication",
            dosage: medication?.dosage || "",
            route: medication?.route || "",
            schedule: "PRN",
            scheduledClockTime: "",
            scheduleDayPattern: medication?.scheduleDayPattern || "daily",
            scheduleDays: Array.isArray(medication?.scheduleDays) ? medication.scheduleDays : [],
            indication: medication?.indication || "",
            instructions: medication?.instructions || "",
            handlingNotes: medication?.handlingNotes || "",
            protocolEnabled: medication?.protocolEnabled === true,
            protocolDefinitions: Array.isArray(medication?.protocolDefinitions) ? medication.protocolDefinitions : [],
            protocolCategory: medication?.protocolCategory || "",
            protocolDefinition: medication?.protocolDefinition || null,
            protocolInstructions: medication?.protocolInstructions || "",
            prn: true,
            active: true,
          },
          entryDate: effectiveHistoryDate,
          scheduledTimeKey: "prn",
          scheduledTime: "PRN",
          status: "pending",
          note: "",
          recordedAt: "",
          statusHistory: [],
          prn: true,
        }];
      })
      .sort((first: any, second: any) => String(first?.medicationName || "").localeCompare(String(second?.medicationName || "")));

    const syntheticPendingVitalRows = activeDailyTrackingItems
      .filter((item: any) => {
        const effectiveStartDate = String(item?.effectiveStartDate || "").trim();
        const effectiveEndDate = String(item?.effectiveEndDate || "").trim();
        return (!effectiveStartDate || effectiveStartDate <= effectiveHistoryDate)
          && (!effectiveEndDate || effectiveEndDate >= effectiveHistoryDate);
      })
      .flatMap((item: any) => {
        const trackingItemId = String(item?.trackingItemId || "").trim();
        if (!trackingItemId) {
          return [];
        }
        return getMedicationScheduleOccurrences(item, effectiveHistoryDate)
          .filter((occurrence: any) => !existingDailyTrackingOccurrenceKeys.has(
            `${trackingItemId}::${String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase()}`,
          ))
          .map((occurrence: any) => ({
            syntheticPending: true,
            entryKind: "daily_tracking",
            residentId,
            trackingItemId,
            trackingItemName: item?.name || "Vital tracker",
            trackingItemCategory: item?.category || "",
            trackingItemSnapshot: item,
            entryDate: effectiveHistoryDate,
            scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
            scheduledTime: occurrence?.scheduledLabel || item?.schedule || "",
            status: "pending",
            note: "",
            recordedAt: "",
            statusHistory: [],
          }));
      })
      .sort((first: any, second: any) => {
        const nameComparison = String(first?.trackingItemName || "").localeCompare(String(second?.trackingItemName || ""));
        return nameComparison || String(first?.scheduledTime || "").localeCompare(String(second?.scheduledTime || ""));
      });

    const sortedHistoryEntries = sortEmarEntriesByLatest([
      ...savedEntries,
      ...dailyTrackingSavedEntries,
      ...syntheticPendingRows,
      ...syntheticPrnRows,
      ...syntheticPendingVitalRows,
    ]);
    return sortedHistoryEntries.sort((first: any, second: any) => {
      const firstIsPrn = first?.medicationSnapshot?.prn === true
        || String(first?.scheduledTimeKey || "").trim().toLowerCase() === "prn"
        || String(first?.status || "").trim().toLowerCase() === "prn_given";
      const secondIsPrn = second?.medicationSnapshot?.prn === true
        || String(second?.scheduledTimeKey || "").trim().toLowerCase() === "prn"
        || String(second?.status || "").trim().toLowerCase() === "prn_given";
      if (firstIsPrn !== secondIsPrn) {
        return firstIsPrn ? -1 : 1;
      }
      return 0;
    });
  }, [historyEntries, historyDailyTrackingEntries, activeNonPrnMedicationsForMedPass, historyPrnMedicationsForCharting, activeDailyTrackingItems, effectiveHistoryDate, residentId]);
  const filteredHistoryEntries = useMemo(() => (
    historyStatusFilter === "missed"
      ? visibleHistoryEntries.filter((entry: any) => String(entry?.status || "").trim().toLowerCase() === "missed")
      : visibleHistoryEntries
  ), [historyStatusFilter, visibleHistoryEntries]);
  const historySummary = useMemo(() => ({
    total: filteredHistoryEntries.length,
    attention: filteredHistoryEntries.filter((entry: any) => ["refused", "held", "not_given", "missed", "hospitalized"].includes(String(entry?.status || "").toLowerCase())).length,
    prn: filteredHistoryEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "prn_given").length,
    reviewed: filteredHistoryEntries.filter((entry: any) => entry?.exceptionReviewed === true).length,
    resolved: filteredHistoryEntries.filter((entry: any) => entry?.exceptionResolved === true).length,
    pending: filteredHistoryEntries.filter((entry: any) => entry?.syntheticPending === true && String(entry?.status || "").toLowerCase() === "pending").length,
  }), [filteredHistoryEntries]);
  const isDailyTrackingHistoryEntry = (entry: any) => String(entry?.entryKind || "").toLowerCase() === "daily_tracking" || !!entry?.trackingItemId;
  const isPrnHistoryEntry = (entry: any) => (
    !isDailyTrackingHistoryEntry(entry)
    && (entry?.medicationSnapshot?.prn === true
      || String(entry?.scheduledTimeKey || "").trim().toLowerCase() === "prn"
      || String(entry?.status || "").trim().toLowerCase() === "prn_given")
  );
  const getPastEntryName = (entry: any) => {
    if (isDailyTrackingHistoryEntry(entry)) {
      return (
        entry?.trackingItemSnapshot?.name
        || entry?.trackingItemName
        || getEmarDailyTrackingCategoryOption(entry?.trackingItemSnapshot?.category || entry?.trackingItemCategory)?.label
        || "Vital tracker"
      );
    }
    return entry?.medicationSnapshot?.medicationName || entry?.medicationName || "Medication";
  };
  const getPastEntryDateLabel = (entry: any) => {
    if (isDailyTrackingHistoryEntry(entry)) {
      const scheduleText = normalizeValue(entry?.scheduledTime || entry?.scheduledTimeKey || "");
      return `${formatEmarDateOnly(entry?.entryDate)}${scheduleText ? ` • ${scheduleText}` : ""}`;
    }
    return `${formatEmarDateOnly(entry?.entryDate)}${normalizeValue(entry?.scheduledTime) ? ` • ${normalizeValue(entry?.scheduledTime)}` : ""}`;
  };
  const getPastEntryStatusTagLabel = (entry: any) => {
    if (isDailyTrackingHistoryEntry(entry)) {
      return entry?.syntheticPending ? "Not charted" : formatDailyTrackingStatusLabel(entry?.status);
    }
    return entry?.syntheticPending ? (String(entry?.status || "").toLowerCase() === "missed" ? "Missed" : "Not charted") : formatEmarStatusLabel(entry?.status);
  };
  const getPastEntryStatusTagSeverity = (entry: any) => {
    if (isDailyTrackingHistoryEntry(entry)) {
      return getDailyTrackingStatusSeverity(entry?.status);
    }
    const status = String(entry?.status || "").toLowerCase();
    const severity = getEmarStatusSeverity(status, entry);
    return entry?.syntheticPending ? (status === "missed" ? "danger" : "warning") : severity;
  };
  const getPastEntryNotes = (entry: any) => {
    if (isDailyTrackingHistoryEntry(entry)) {
      return buildEmarDailyTrackingValueSummary(entry, entry?.trackingItemSnapshot) || entry?.note || "No recorded values";
    }
    return renderPrnDetailBlock(entry);
  };
  const activeNonPrnMedicationIds = useMemo(
    () => new Set(activeNonPrnMedicationsScheduledToday.map((medication: any) => String(medication?.medicationId || "").trim()).filter(Boolean)),
    [activeNonPrnMedicationsScheduledToday],
  );
  const scheduledTodayEntries = useMemo(
    () => todayEntries.filter((entry: any) => activeNonPrnMedicationIds.has(String(entry?.medicationId || "").trim())),
    [todayEntries, activeNonPrnMedicationIds],
  );
  const todayPassSummary = useMemo(() => ({
    updated: scheduledTodayEntries.length,
    pending: Math.max(
      activeNonPrnMedicationsScheduledToday.reduce((total: number, medication: any) => total + getMedicationScheduleOccurrences(medication, todayDate).length, 0) - scheduledTodayEntries.length,
      0,
    ),
    unscheduledNonPrn: getMedicationsMissingSchedule(activeNonPrnMedicationsScheduledToday).length,
    missedScheduled: activeNonPrnMedicationsScheduledToday.reduce((total: number, medication: any) => (
      total + getMedicationScheduleOccurrences(medication, todayDate).filter((occurrence: any) => {
        const medicationId = String(medication?.medicationId || "").trim();
        if (!medicationId) {
          return false;
        }
        const occurrenceKey = `${medicationId}::${String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase()}`;
        const entry = todayEntryByOccurrenceKey[occurrenceKey];
        if (entry) {
          return false;
        }
        const timingMeta = getTodayMedicationTimingMeta({
          schedule: occurrence?.scheduleText || medication?.schedule || "",
          scheduledClockTime: occurrence?.scheduledClockTime || "",
          prn: medication?.prn,
        }, false);
        return timingMeta?.isOverdue === true;
      }).length
    ), 0),
    given: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "given").length,
    refused: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "refused").length,
    held: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "held").length,
    notGiven: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "not_given").length,
    missed: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "missed").length,
    hospitalized: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "hospitalized").length,
    expired: activeNonPrnMedications.filter((medication: any) => isMedicationExpired(medication?.effectiveEndDate)).length,
    expiringSoon: activeNonPrnMedicationsScheduledToday.filter((medication: any) => isMedicationExpiringWithinMonth(medication?.effectiveEndDate)).length,
  }), [scheduledTodayEntries, activeNonPrnMedications, activeNonPrnMedicationsScheduledToday, todayDate, todayEntryByOccurrenceKey]);
  const loadPage = async () => {
    if (!residentId) {
      return;
    }

    setLoading(true);
    try {
      await backendAPIStore.init();
      if (!isAdminUser) {
        const subscriptionResponse = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER);
        const subscribed = !!subscriptionResponse?.isSubscribed;
        setIsUserSubscribed(subscribed);
        setSubscriptionChecked(true);
        if (!subscribed) {
          setShowSubscriptionDialog(true);
          setResidentDetails(null);
          setMedications([]);
          setDailyTrackingItems([]);
          setTodayEntries([]);
          setTodayTrackingEntries([]);
          setHistoryEntries([]);
          setHistoryDailyTrackingEntries([]);
          setCarePlanSummaryData(null);
          setCarePlanResponsiblePartiesData(null);
          return;
        }
      } else {
        setIsUserSubscribed(true);
        setSubscriptionChecked(true);
      }

      const [residentResponse, medicationResponse, entryResponse, carePlanResponse, lastJobsResponse, dailyTrackingItemResponse, dailyTrackingEntryResponse] = await Promise.all([
        backendAPIStore.getResidentDetails(residentId),
        backendAPIStore.getResidentEmarMedications(residentId),
        backendAPIStore.getResidentEmarEntries(residentId, { entryDate: todayDate, limit: 300 }),
        backendAPIStore.getResidentCarePlan(residentId),
        backendAPIStore.getLastJobs(residentId),
        backendAPIStore.getResidentEmarDailyTrackingItems(residentId).catch(() => ({ items: [] })),
        backendAPIStore.getResidentEmarDailyTrackingEntries(residentId, { entryDate: todayDate, limit: 300 }).catch(() => ({ items: [] })),
      ]);
      setResidentDetails(residentResponse || null);
      setMedications(Array.isArray(medicationResponse?.items) ? medicationResponse.items : []);
      setDailyTrackingItems(Array.isArray(dailyTrackingItemResponse?.items) ? dailyTrackingItemResponse.items : []);
      setTodayEntries(filterEntriesForTodayPass(entryResponse?.items, todayDate));
      setTodayTrackingEntries(filterEntriesForTodayPass(dailyTrackingEntryResponse?.items, todayDate));
      const normalizedCarePlanItems = Array.isArray(carePlanResponse) ? carePlanResponse : [];
      setCarePlanSummaryData(
        normalizedCarePlanItems.find((item: any) => String(item?.carePlanType || "").trim().toLowerCase() === RESIDENT_CARE_PLAN_TYPE.SUMMARY) || null,
      );
      setCarePlanResponsiblePartiesData(
        normalizedCarePlanItems.find((item: any) => String(item?.carePlanType || "").trim().toLowerCase() === RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES) || null,
      );
      const latestEmarCompareJob = Array.isArray(lastJobsResponse?.jobs)
        ? lastJobsResponse.jobs.find((job: any) =>
          job?.jobType === "MAR_SHEET_EMAR_COMPARE"
          && String(job?.jobStatus || "").trim().toUpperCase() === "COMPLETED")
        : null;
      const resolvedLatestEmarCompareJobId = String(
        latestEmarCompareJob?.jobId
        || getStoredEmarCompareJobId(residentId)
        || "",
      ).trim();
      setLatestEmarCompareJobId(resolvedLatestEmarCompareJobId);
      if (resolvedLatestEmarCompareJobId) {
        persistEmarCompareJobId(residentId, resolvedLatestEmarCompareJobId);
      }
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_REQUIRED") {
        setIsUserSubscribed(false);
        setSubscriptionChecked(true);
        setShowSubscriptionDialog(true);
        setResidentDetails(null);
        setMedications([]);
        setDailyTrackingItems([]);
        setTodayEntries([]);
        setTodayTrackingEntries([]);
        setHistoryEntries([]);
        setHistoryDailyTrackingEntries([]);
        setCarePlanSummaryData(null);
        setCarePlanResponsiblePartiesData(null);
      } else {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load eMAR setup.",
          life: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!residentId || (!isAdminUser && !isUserSubscribed)) {
      return;
    }

    setLoadingHistory(true);
    try {
      await backendAPIStore.init();
      const [medicationResult, vitalTrackingResult] = await Promise.allSettled([
        backendAPIStore.getResidentEmarEntries(residentId, {
          startDate: effectiveHistoryDate,
          endDate: effectiveHistoryDate,
          limit: 150,
        }),
        backendAPIStore.getResidentEmarDailyTrackingEntries(residentId, {
          startDate: effectiveHistoryDate,
          endDate: effectiveHistoryDate,
          limit: 150,
        }),
      ]);
      if (medicationResult.status === "rejected") {
        throw medicationResult.reason;
      }
      setHistoryEntries(Array.isArray(medicationResult.value?.items) ? medicationResult.value.items : []);
      setHistoryDailyTrackingEntries(
        vitalTrackingResult.status === "fulfilled" && Array.isArray(vitalTrackingResult.value?.items)
          ? vitalTrackingResult.value.items
          : [],
      );
      if (vitalTrackingResult.status === "rejected") {
        console.error("Unable to load vital tracker history", vitalTrackingResult.reason);
      }
    } catch (error) {
      console.error("Unable to load eMAR history", error);
      setHistoryEntries([]);
      setHistoryDailyTrackingEntries([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadLatestEmarCompareJob = async () => {
    if (!residentId) {
      return;
    }

    setLoadingLatestEmarCompareJob(true);
    try {
      await backendAPIStore.init();
      const lastJobsResponse = await backendAPIStore.getLastJobs(residentId);
      const latestEmarCompareJob = Array.isArray(lastJobsResponse?.jobs)
        ? lastJobsResponse.jobs.find((job: any) =>
          job?.jobType === "MAR_SHEET_EMAR_COMPARE"
          && String(job?.jobStatus || "").trim().toUpperCase() === "COMPLETED")
        : null;
      const resolvedLatestEmarCompareJobId = String(
        latestEmarCompareJob?.jobId
        || getStoredEmarCompareJobId(residentId)
        || "",
      ).trim();
      setLatestEmarCompareJobId(resolvedLatestEmarCompareJobId);
      if (resolvedLatestEmarCompareJobId) {
        persistEmarCompareJobId(residentId, resolvedLatestEmarCompareJobId);
      }
    } catch (error) {
      console.error("Unable to load latest MAR compare job", error);
      const storedLatestEmarCompareJobId = getStoredEmarCompareJobId(residentId);
      if (storedLatestEmarCompareJobId) {
        setLatestEmarCompareJobId(storedLatestEmarCompareJobId);
      }
    } finally {
      setLoadingLatestEmarCompareJob(false);
    }
  };

  useEffect(() => {
    if (residentId) {
      const storedLatestEmarCompareJobId = getStoredEmarCompareJobId(residentId);
      if (storedLatestEmarCompareJobId) {
        setLatestEmarCompareJobId(storedLatestEmarCompareJobId);
      }
    }
    void loadPage();
  }, [residentId]);

  useEffect(() => {
    if (!subscriptionChecked) {
      return;
    }
    void loadHistory();
  }, [residentId, effectiveHistoryDate, subscriptionChecked, isUserSubscribed, isAdminUser]);

  useEffect(() => {
    const resolvedHomeId = String(residentDetails?.homeId || "").trim();
    if (!resolvedHomeId) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("homeId") === resolvedHomeId) {
      return;
    }

    searchParams.set("homeId", resolvedHomeId);
    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true },
    );
  }, [residentDetails?.homeId, location.pathname, location.search, navigate]);

  useEffect(() => {
    const hasNoActiveMedications = activeMedications.length === 0;
    const hasNoActiveTrackingItems = activeDailyTrackingItems.length === 0;

    if (!loading && (isAdminUser || isUserSubscribed) && hasNoActiveMedications && hasNoActiveTrackingItems) {
      setShowMedicationSetupDialog(true);
      return;
    }
    setShowMedicationSetupDialog(false);
  }, [loading, activeMedications.length, activeDailyTrackingItems.length, isAdminUser, isUserSubscribed]);

  const handleEmarTabChange = (nextTabIndex: number) => {
    setActiveTabIndex(nextTabIndex);
    if (nextTabIndex === 3) {
      void loadHistory();
      return;
    }
    if (nextTabIndex === 4) {
      void loadLatestEmarCompareJob();
      return;
    }
    void loadPage();
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("resident-emar:set-report-visibility", {
      detail: { visible: true },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("resident-emar:set-report-visibility", {
        detail: { visible: true },
      }));
    };
  }, []);

  useEffect(() => {
    if (!processingEmarCompare || !emarCompareProcessingStartedAt) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setEmarCompareElapsedSeconds(Math.max(0, Math.floor((Date.now() - emarCompareProcessingStartedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [processingEmarCompare, emarCompareProcessingStartedAt]);

  const hydrateEmarCompareResultFromJob = async (jobDetails: any) => {
    const comparisonPayloadKey = String(jobDetails?.comparisonResultS3ObjectKey || "").trim();
    if (comparisonPayloadKey && residentId) {
      try {
        const payload = await backendAPIStore.getResidentScopedJsonFromObjectKey(
          residentId,
          comparisonPayloadKey,
          `emar-compare-${String(jobDetails?.jobId || "result").trim() || "result"}.json`,
        );
        return {
          comparisonResult: payload?.comparison || null,
          reportHtml: String(payload?.reportHtml || ""),
        };
      } catch (_error) {
        // Fall back to the job detail payload so "Open Last Result" still works
        // even when the persisted JSON artifact is unavailable.
      }
    }

    return {
      comparisonResult: jobDetails?.medicationComparison || jobDetails?.comparisonResultSummary || null,
      reportHtml: String(jobDetails?.medicationComparisonReportHtml || ""),
    };
  };

  useEffect(() => {
    if (!emarCompareJobId || !processingEmarCompare) {
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const jobDetails = await backendAPIStore.getJobDetails(emarCompareJobId);
        if (cancelled) {
          return;
        }
        const jobStatus = String(jobDetails?.jobStatus || "").trim().toUpperCase();
        setEmarCompareJobStatus(String(jobDetails?.jobStatus || "").trim());
        setEmarCompareJobSubStatus(String(jobDetails?.jobSubStatus || "").trim());
        if (jobStatus === "COMPLETED") {
          const hydratedResult = await hydrateEmarCompareResultFromJob(jobDetails);
          if (cancelled) {
            return;
          }
          setProcessingEmarCompare(false);
          setEmarCompareFileName(String(jobDetails?.sourceFileName || emarCompareFileName || "").trim());
          const completedJobId = String(jobDetails?.jobId || emarCompareJobId || "").trim();
          setEmarCompareCompletedJobId(completedJobId);
          setLatestEmarCompareJobId(completedJobId);
          if (residentId && completedJobId) {
            persistEmarCompareJobId(residentId, completedJobId);
          }
          setEmarCompareResultData(hydratedResult.comparisonResult);
          setEmarCompareReportHtml(hydratedResult.reportHtml);
          return;
        }
        if (jobStatus === "FAILED") {
          setProcessingEmarCompare(false);
          toast.current?.show({
            severity: "error",
            summary: "Comparison failed",
            detail: String(jobDetails?.errorMessage || "Unable to compare this MAR sheet."),
            life: 4500,
          });
          return;
        }
        window.setTimeout(() => { void poll(); }, 2500);
      } catch (error: any) {
        if (cancelled) {
          return;
        }
        setProcessingEmarCompare(false);
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to refresh the eMAR comparison status.",
          life: 4500,
        });
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, emarCompareFileName, emarCompareJobId, processingEmarCompare, residentId]);

  const openCreateDialog = (overrideValues?: Record<string, any>) => {
    setProtocolSetupTarget("medication");
    setFormData({
      ...EMPTY_FORM,
      effectiveStartDate: getLosAngelesIsoDateOnly(),
      ...(overrideValues || {}),
    });
    setSelectedScheduleTimes([]);
    setSelectedScheduleDays([]);
    setSelectedScheduleMonthDays([]);
    setMedicationSuggestions([]);
    setPsychotropicMedicationMode(false);
    setProtocolDefinitionDraft(createEmptyProtocolParameterSet());
    setEditingProtocolDefinitionIndex(-1);
    setShowProtocolSetupDialog(false);
    setShowEditDialog(true);
  };

  const openCreateMarOnlyDialog = () => {
    openCreateDialog({
      marOnlyItem: true,
      medicationName: "",
      dosage: "",
      route: "",
      frequency: "",
      schedule: "",
      scheduledClockTime: "",
      scheduleDayPattern: "daily",
      scheduleDays: [],
      scheduleMonthDays: [],
      prn: false,
      protocolDefinitions: [],
      protocolEnabled: false,
      protocolCategory: "",
      protocolInstructions: "",
      protocolRules: [],
      psychotropicDrugClass: "",
    });
  };

  const openCarePlanRequiredDialog = () => {
    setShowCarePlanRequiredDialog(true);
  };

  const closeCarePlanRequiredDialog = () => {
    setShowCarePlanRequiredDialog(false);
  };

  const downloadHtmlContent = (title: string, htmlContent: string, fileName: string) => {
    const documentHtml = buildWordFriendlyHtmlDocument(title, htmlContent, String(authExperience.brandName || "AFH Hub"));
    const blob = new Blob([documentHtml], { type: "application/msword" });
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(blobUrl);
  };

  const closeEmarCompareDialog = () => {
    if (processingEmarCompare || applyingEmarCompareChanges) {
      return;
    }
    setShowEmarCompareDialog(false);
    setEmarCompareUploadFile(null);
    setEmarCompareJobId("");
    setEmarCompareJobStatus("");
    setEmarCompareJobSubStatus("");
    setEmarCompareProcessingStartedAt(null);
    setEmarCompareElapsedSeconds(0);
    setEmarCompareResultData(null);
    setEmarCompareReportHtml("");
    setEmarCompareFileName("");
    if (emarCompareFileInputRef.current) {
      emarCompareFileInputRef.current.value = "";
    }
  };

  const startEmarCompareReview = async (file: File, useExistingCredit: boolean = false) => {
    if (!residentId) {
      return;
    }
    const extension = String(file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "png", "jpg", "jpeg", "tif", "tiff", "doc", "docx", "txt"].includes(extension)) {
      toast.current?.show({
        severity: "warn",
        summary: "Unsupported file",
        detail: "Use a PDF, DOCX, or TXT MAR sheet file.",
        life: 4000,
      });
      return;
    }

    setProcessingEmarCompare(true);
    setEmarCompareUploadFile(file);
    setEmarCompareFileName(file.name);
    setEmarCompareResultData(null);
    setEmarCompareReportHtml("");
    setEmarCompareCompletedJobId("");
    setEmarCompareJobStatus("UPLOADING");
    setEmarCompareJobSubStatus("Uploading MAR sheet");
    setEmarCompareProcessingStartedAt(Date.now());
    setEmarCompareElapsedSeconds(0);
    try {
      const objectKey = `resident/${residentId}/emar-compare/${crypto.randomUUID()}-${file.name}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        residentId,
        ReferenceType.RESIDENT,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, file);

      setEmarCompareJobStatus("SUBMITTING");
      setEmarCompareJobSubStatus("Preparing eMAR comparison");
      const response = await backendAPIStore.compareResidentEmarMedicationsFromMarSheetFile({
        residentId,
        fileName: file.name,
        s3ObjectKey: objectKey,
        previewOnly: true,
        useExistingCredit,
      });
      setEmarCompareJobId(String(response?.jobId || "").trim());
      setEmarCompareJobStatus(String(response?.jobStatus || "SUBMITTED").trim());
      setEmarCompareJobSubStatus(String(response?.jobSubStatus || "Preparing eMAR comparison").trim());
    } catch (error: any) {
      setProcessingEmarCompare(false);
      setEmarCompareProcessingStartedAt(null);
      setEmarCompareElapsedSeconds(0);
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        const availableCredits = Number(
          error?.automationCreditsAvailable
          || error?.details?.automationCreditsAvailable
          || error?.details?.featureCreditBalances?.ncp_generation
          || 0,
        );
        if (availableCredits > 0 && !useExistingCredit) {
          confirmDialog({
            message: renderConfirmMessage(
              "Use 1 automation credit",
              "You have used the 2 included eMAR MAR comparison runs for this resident in the current billing period. Continue now and use 1 automation credit?",
            ),
            icon: "pi pi-wallet",
            ...appConfirmDialogClasses,
            acceptLabel: "Use 1 Credit",
            acceptClassName: "p-button-warning",
            rejectLabel: "Cancel",
            accept: () => {
              void startEmarCompareReview(file, true);
            },
          });
          return;
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to review this MAR sheet.",
        life: 4500,
      });
    }
  };

  const applyEmarCompareChanges = async () => {
    if (!residentId || !emarCompareCompletedJobId) {
      return;
    }

    setApplyingEmarCompareChanges(true);
    try {
      const response = await backendAPIStore.applyResidentEmarMedicationCompareJob({
        residentId,
        jobId: emarCompareCompletedJobId,
      });
      await loadPage();
      closeEmarCompareDialog();
      const appliedMedicationCount = emarCompareAffectedMedicationCount || Number(response?.appliedCount || 0);
      toast.current?.show({
        severity: "success",
        summary: "eMAR list updated",
        detail: `${appliedMedicationCount} medication ${appliedMedicationCount === 1 ? "change was" : "changes were"} applied from the uploaded MAR sheet.`,
        life: 4500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update the active eMAR list.",
        life: 4500,
      });
    } finally {
      setApplyingEmarCompareChanges(false);
    }
  };

  const openLatestEmarCompareResult = async () => {
    const jobId = String(latestEmarCompareJobId || emarCompareCompletedJobId || "").trim();
    if (!jobId) {
      toast.current?.show({
        severity: "warn",
        summary: "No saved result",
        detail: "Run a MAR comparison first so there is a result to reopen.",
        life: 3500,
      });
      return;
    }

    setLoadingLatestEmarCompareResult(true);
    setShowEmarCompareDialog(true);
    setProcessingEmarCompare(false);
    setEmarCompareResultData(null);
    setEmarCompareReportHtml("");
    setEmarCompareFileName("");
    setEmarCompareJobStatus("LOADING");
    setEmarCompareJobSubStatus("Loading saved comparison result");

    try {
      const jobDetails = await backendAPIStore.getJobDetails(jobId);
      const jobStatus = String(jobDetails?.jobStatus || "").trim();
      if (jobStatus.toUpperCase() !== "COMPLETED") {
        throw new Error("The last MAR comparison is not ready yet.");
      }
      const hydratedResult = await hydrateEmarCompareResultFromJob(jobDetails);
      setEmarCompareUploadFile(null);
      setEmarCompareJobId(jobId);
      setEmarCompareCompletedJobId(jobId);
      setLatestEmarCompareJobId(jobId);
      if (residentId && jobId) {
        persistEmarCompareJobId(residentId, jobId);
      }
      setEmarCompareJobStatus(jobStatus);
      setEmarCompareJobSubStatus(String(jobDetails?.jobSubStatus || "").trim());
      setEmarCompareFileName(String(jobDetails?.sourceFileName || "").trim());
      setEmarCompareResultData(hydratedResult.comparisonResult);
      setEmarCompareReportHtml(hydratedResult.reportHtml);
      if (emarCompareFileInputRef.current) {
        emarCompareFileInputRef.current.value = "";
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load the last eMAR comparison result.",
        life: 4000,
      });
      setShowEmarCompareDialog(false);
    } finally {
      setLoadingLatestEmarCompareResult(false);
    }
  };

  const isMissingCarePlanMedicationError = (error: any) => {
    const normalizedMessage = String(error?.message || "").trim().toLowerCase();
    return normalizedMessage === "no medication entries were found in the resident care plan.".toLowerCase();
  };

  const isMissingCarePlanMedicationResponse = (response: any) => {
    const normalizedCode = String(response?.code || "").trim().toUpperCase();
    const normalizedMessage = String(response?.message || "").trim().toLowerCase();
    return normalizedCode === "NO_CARE_PLAN_MEDICATIONS"
      || normalizedMessage === "no medication entries were found in the resident care plan.".toLowerCase();
  };

  const handleCarePlanImportError = (error: any) => {
    if (isMissingCarePlanMedicationError(error)) {
      openCarePlanRequiredDialog();
      return;
    }

    if (error?.code === "SUBSCRIPTION_REQUIRED") {
      setIsUserSubscribed(false);
      setShowSubscriptionDialog(true);
      return;
    }

    toast.current?.show({
      severity: "error",
      summary: getErrorTitle(error),
      detail: error?.message || "Unable to import care plan medications.",
      life: 4000,
    });
  };

  const showEmarActionError = (error: any, fallbackMessage: string) => {
    if (error?.code === "SUBSCRIPTION_REQUIRED") {
      setIsUserSubscribed(false);
      setShowSubscriptionDialog(true);
      return;
    }

    toast.current?.show({
      severity: "error",
      summary: getErrorTitle(error),
      detail: error?.message || fallbackMessage,
      life: 4000,
    });
  };

  const openEditDialog = (item: any) => {
    setProtocolSetupTarget("medication");
    const existingScheduleTimes = getMedicationScheduleTimes(item);
    const existingScheduleDays = getMedicationScheduleDays(item);
    const scheduleDayPattern = inferMedicationScheduleDayPattern(item);
    const scheduleFields = buildFormScheduleFields(existingScheduleTimes, {
      scheduleDayPattern,
      scheduleDays: existingScheduleDays,
    });
    const resolvedFrequency = inferFrequencyFromMedication(item);
    const protocolDefinitions = normalizeProtocolDefinitions(item);
    setFormData({
      medicationId: item?.medicationId || "",
      medicationName: item?.medicationName || "",
      dosage: item?.dosage || "",
      route: item?.route || "",
      frequency: resolvedFrequency,
      schedule: scheduleFields.schedule || item?.schedule || "",
      scheduledClockTime: scheduleFields.scheduledClockTime || item?.scheduledClockTime || "",
      scheduleDayPattern,
      scheduleDays: existingScheduleDays,
      effectiveStartDate: item?.effectiveStartDate || getLosAngelesIsoDateOnly(),
      effectiveEndDate: item?.effectiveEndDate || "",
      indication: item?.indication || "",
      instructions: item?.instructions || "",
      handlingNotes: item?.handlingNotes || "",
      marOnlyItem: isMarOnlyMedication(item),
      discontinued: item?.discontinued === true,
      discontinuedAt: item?.discontinuedAt || "",
      discontinueReason: item?.discontinueReason || "",
      protocolEnabled: protocolDefinitions.length > 0,
      protocolDefinitions,
      protocolCategory: item?.protocolCategory || "",
      protocolInstructions: item?.protocolInstructions || item?.protocolDefinition?.instructions || "",
      protocolRules: Array.isArray(item?.protocolDefinition?.rules) ? item.protocolDefinition.rules : Array.isArray(item?.protocolRules) ? item.protocolRules : [],
      psychotropicDrugClass: item?.psychotropicDrugClass || "",
      prn: !!item?.prn,
      active: item?.active !== false,
    });
    setSelectedScheduleTimes(existingScheduleTimes);
    setSelectedScheduleDays(existingScheduleDays);
    setSelectedScheduleMonthDays(getMedicationScheduleMonthDays(item));
    setMedicationSuggestions([]);
    setPsychotropicMedicationMode(!!item?.psychotropicDrugClass);
    setProtocolDefinitionDraft(createEmptyProtocolParameterSet());
    setEditingProtocolDefinitionIndex(-1);
    setShowProtocolSetupDialog(false);
    setShowEditDialog(true);
  };

  const generateMedicationInstructions = async () => {
    const medicationName = String(formData?.medicationName || "").trim();
    if (!medicationName || generatingMedicationInstructions) {
      return;
    }

    setGeneratingMedicationInstructions(true);
    try {
      const response = await backendAPIStore.formatAiText({
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain: "emar-medication-instructions",
        outputStyle: "plain_text",
        metadata: {
          medicationName,
          currentInstructions: String(formData?.instructions || "").trim(),
          currentIndication: String(formData?.indication || "").trim(),
          currentHandlingNotes: String(formData?.handlingNotes || "").trim(),
        },
      });
      const generatedText = String(response?.formattedText || "").trim();
      const readGeneratedField = (label: string) => {
        const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match = generatedText.match(new RegExp(`${escapedLabel}\\s*:\\s*([\\s\\S]*?)(?=\\n(?:Reason or indication|Instructions|Handling notes)\\s*:|$)`, "i"));
        return String(match?.[1] || "").trim();
      };
      const generatedIndication = readGeneratedField("Reason or indication");
      const generatedInstructions = readGeneratedField("Instructions");
      const generatedHandlingNotes = readGeneratedField("Handling notes");
      if (generatedIndication || generatedInstructions || generatedHandlingNotes) {
        setFormData((current: any) => ({
          ...current,
          ...(generatedIndication ? { indication: generatedIndication } : {}),
          ...(generatedInstructions ? { instructions: generatedInstructions } : {}),
          ...(generatedHandlingNotes ? { handlingNotes: generatedHandlingNotes } : {}),
        }));
      }
    } catch (error: any) {
      showEmarActionError(error, "Unable to generate medication instructions with AI.");
    } finally {
      setGeneratingMedicationInstructions(false);
    }
  };

  const openDailyTrackingProtocolManagementDialog = () => {
    const trackingProtocolCategory = dailyTrackingFormData?.protocolCategory || getEmarDailyTrackingProtocolCategory(dailyTrackingFormData?.category);
    const existingDefinitions = Array.isArray(dailyTrackingFormData?.protocolDefinitions) ? dailyTrackingFormData.protocolDefinitions : [];
    const seededDefinitions = existingDefinitions.length
      ? existingDefinitions.map((definition: any, index: number) => ({
        ...definition,
        category: String(definition?.category || trackingProtocolCategory),
        parameterId: definition?.parameterId || `parameter_${index + 1}`,
      }))
      : [createEmptyProtocolParameterSet(trackingProtocolCategory, 0)];
    setProtocolSetupTarget("tracking");
    setEditingProtocolDefinitionIndex(-1);
    setProtocolDefinitionDraft(createEmptyProtocolParameterSet(
      trackingProtocolCategory,
      seededDefinitions.length,
    ));
    setProtocolDialogDefinitions(buildProtocolDialogDefinitions(seededDefinitions));
    setExpandedProtocolEditorIds([String(seededDefinitions[0]?.parameterId || "parameter_1")]);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(false);
    setShowProtocolSetupDialog(true);
  };

  const searchMedicationSuggestions = async (event: AutoCompleteCompleteEvent) => {
    const query = normalizeValue(event.query);
    if (query.length < 1) {
      setMedicationSuggestions([]);
      return;
    }

    try {
      const response = await backendAPIStore.searchPsychotropicMedicationList(query, 8);
      setMedicationSuggestions(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setMedicationSuggestions([]);
    }
  };

  const renderMedicationSuggestion = (suggestion: PsychotropicMedicationSuggestion) => (
    <div className="flex flex-column">
      <span className="font-medium">{suggestion.productName}</span>
      <small style={{ color: "#6b7280" }}>{suggestion.drugClass}</small>
    </div>
  );

  const getProtocolSummaryForMedication = (medicationLike: any) => (
    getEmarProtocolSummary(medicationLike?.medicationSnapshot || medicationLike || {})
  );

  const getProtocolSummariesForMedication = (medicationLike: any) => (
    getEmarProtocolSummaries(medicationLike?.medicationSnapshot || medicationLike || {})
  );

  const getTrackingRecommendedActionLabel = (entryLike: any) => {
    const normalizedActionLabel = String(
      entryLike?.protocolAssessment?.resolution?.recommendedActionLabel
      || entryLike?.protocolAssessment?.resolution?.recommendedAction
      || "",
    ).trim();
    if (!normalizedActionLabel) {
      return "";
    }
    if (normalizedActionLabel.includes("_")) {
      return normalizedActionLabel
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    }
    return normalizedActionLabel;
  };

  const getTrackingRecommendedActionMessage = (entryLike: any) => {
    const actionLabel = getTrackingRecommendedActionLabel(entryLike);
    if (!actionLabel) {
      return "";
    }
    const decisionNote = String(
      entryLike?.protocolAssessment?.resolution?.decisionNote
      || entryLike?.protocolAssessment?.decisionNote
      || "",
    ).trim();
    return decisionNote
      ? `Based on the reading and rules, please ${actionLabel.toLowerCase()}. ${decisionNote}`
      : `Based on the reading and rules, please ${actionLabel.toLowerCase()}.`;
  };

  const getProtocolRuleActionLabel = (value: any) => (
    EMAR_PROTOCOL_RULE_ACTION_OPTIONS.find((option) => option.value === String(value || "").trim())?.label
    || String(value || "").trim().replaceAll("_", " ")
    || "Recommendation"
  );

  const getProtocolConditionSummary = (field: any, condition: any) => {
    const label = String(field?.label || field?.key || "").trim();
    const unit = String(field?.unit || "").trim();
    const equalsValue = String(condition?.equalsValue || "").trim();
    const minValue = String(condition?.minValue || "").trim();
    const maxValue = String(condition?.maxValue || "").trim();

    if (equalsValue) {
      return `${label} = ${equalsValue}${unit ? ` ${unit}` : ""}`;
    }
    if (minValue && maxValue) {
      return `${label} ${minValue}-${maxValue}${unit ? ` ${unit}` : ""}`;
    }
    if (minValue) {
      return `${label} >= ${minValue}${unit ? ` ${unit}` : ""}`;
    }
    if (maxValue) {
      return `${label} <= ${maxValue}${unit ? ` ${unit}` : ""}`;
    }
    return "";
  };

  const getProtocolNumberFieldConfig = (field: any) => (
    getEmarProtocolNumericRangeDefaults(field)
  );

  const getProtocolListDefaultValue = (field: any) => {
    const fieldKey = String(field?.key || "").trim();
    if (fieldKey === "painScore") {
      return "1, 2, 3, 4, 5, 6, 7, 8, 9, 10";
    }
    if (fieldKey === "painAssessment") {
      return "Mild, Moderate, Severe";
    }
    return "";
  };

  const getProtocolFieldOptions = (category: any) => (
    (getEmarProtocolCategoryOption(category)?.parameterSchema || []).map((field: any) => ({
      label: `${field?.label || field?.key}${field?.unit ? ` (${field.unit})` : ""}`,
      value: String(field?.key || "").trim(),
    }))
  );

  const getProtocolConditionOperator = (condition: any) => {
    const explicitOperator = String(condition?.operator || "").trim();
    if (["equals", "list", "required", "between", "less_than", "more_than"].includes(explicitOperator)) {
      return explicitOperator;
    }
    const equalsValue = String(condition?.equalsValue || "").trim();
    const minValue = String(condition?.minValue || "").trim();
    const maxValue = String(condition?.maxValue || "").trim();
    if (equalsValue) {
      return "equals";
    }
    if (minValue && maxValue) {
      return "between";
    }
    if (maxValue) {
      return "less_than";
    }
    if (minValue) {
      return "more_than";
    }
    return "equals";
  };

  const hasAnyProtocolCondition = (conditions: any) => (
    Object.values(conditions || {}).some((condition: any) => (
      ["equals", "list", "required", "between", "less_than", "more_than"].includes(String(condition?.operator || "").trim())
      ||
      !!String(condition?.equalsValue || "").trim()
      || !!String(condition?.minValue || "").trim()
      || !!String(condition?.maxValue || "").trim()
    ))
  );

  const getProtocolConditionRows = (rule: any, category: any) => {
    const storedRows = Array.isArray(rule?.conditionRows) ? rule.conditionRows : [];
    if (storedRows.length) {
      return storedRows.map((row: any) => ({
        ...row,
        operator: String(row?.operator || "").trim() === "none" ? "equals" : String(row?.operator || "equals").trim(),
      }));
    }
    const fieldOptions = getProtocolFieldOptions(category);
    const rowsFromConditions = Object.entries(rule?.conditions || {})
      .map(([fieldKey, condition]: any, index: number) => ({
        rowId: `row_${index + 1}`,
        fieldKey: String(fieldKey || "").trim(),
        operator: getProtocolConditionOperator(condition),
      }))
      .filter((row: any) => row.fieldKey && row.operator !== "none");
    if (rowsFromConditions.length) {
      return rowsFromConditions;
    }
    return [{
      rowId: "row_1",
      fieldKey: String(fieldOptions[0]?.value || ""),
      operator: "equals",
    }];
  };

  const rebuildProtocolConditionsFromRows = (rows: any[], existingConditions: any) => (
    rows.reduce((nextConditions: any, row: any) => {
      const fieldKey = String(row?.fieldKey || "").trim();
      if (!fieldKey) {
        return nextConditions;
      }
      const previousCondition = existingConditions?.[fieldKey] || {};
      nextConditions[fieldKey] = buildProtocolConditionFromOperator(
        String(row?.operator || "none"),
        row?.values || previousCondition,
      );
      return nextConditions;
    }, {})
  );

  const updateActiveProtocolRuleRows = (updater: (rows: any[], current: any, item: any) => any[]) => {
    setProtocolDefinitionDraft((current: any) => ({
      ...current,
      rules: (Array.isArray(current?.rules) ? current.rules : [createEmptyProtocolRule(current?.category || "sliding_scale_insulin", 0)]).map((item: any, index: number) => {
        if (index !== 0) {
          return item;
        }
        const currentRows = getProtocolConditionRows(item, current?.category || "sliding_scale_insulin");
        const nextRows = updater(currentRows, current, item);
        const nextConditions = rebuildProtocolConditionsFromRows(nextRows, item?.conditions || {});
        return {
          ...item,
          action: "give",
          label: updateRuleAutoLabelIfNeeded(current?.category || "sliding_scale_insulin", item, nextConditions, 0),
          conditionRows: nextRows,
          conditions: nextConditions,
        };
      }),
    }));
  };

  const updateActiveProtocolRuleFieldCondition = (
    rowId: string,
    fieldKey: string,
    nextOperator: string,
    nextValues?: { equalsValue?: string; minValue?: string; maxValue?: string },
  ) => {
    const normalizedFieldKey = String(fieldKey || "").trim();
    updateActiveProtocolRuleRows((rows, _current, item) => {
      const currentRows = rows.map((row: any) => {
        if (String(row?.rowId || "") !== rowId) {
          return row;
        }
        const previousFieldKey = String(row?.fieldKey || "").trim();
        const previousCondition = item?.conditions?.[normalizedFieldKey || previousFieldKey] || item?.conditions?.[previousFieldKey] || {};
        const mergedValues = {
          equalsValue: String(nextValues?.equalsValue ?? previousCondition?.equalsValue ?? ""),
          minValue: String(nextValues?.minValue ?? previousCondition?.minValue ?? ""),
          maxValue: String(nextValues?.maxValue ?? previousCondition?.maxValue ?? ""),
        };
        return {
          ...row,
          fieldKey: normalizedFieldKey,
          operator: nextOperator,
          values: buildProtocolConditionFromOperator(nextOperator, mergedValues),
        };
      });
      return currentRows;
    });
  };

  const updateActiveProtocolSchemaFieldCondition = (
    fieldKey: string,
    nextOperator: string,
    nextValues?: { equalsValue?: string; minValue?: string; maxValue?: string },
  ) => {
    const normalizedFieldKey = String(fieldKey || "").trim();
    if (!normalizedFieldKey) {
      return;
    }
    setProtocolDefinitionDraft((current: any) => ({
      ...current,
      rules: (Array.isArray(current?.rules) ? current.rules : [createEmptyProtocolRule(current?.category || "sliding_scale_insulin", 0)]).map((item: any, index: number) => {
        if (index !== 0) {
          return item;
        }
        const previousCondition = item?.conditions?.[normalizedFieldKey] || {};
        const mergedValues = {
          equalsValue: String(nextValues?.equalsValue ?? previousCondition?.equalsValue ?? ""),
          minValue: String(nextValues?.minValue ?? previousCondition?.minValue ?? ""),
          maxValue: String(nextValues?.maxValue ?? previousCondition?.maxValue ?? ""),
        };
        const nextConditions = {
          ...(item?.conditions || {}),
          [normalizedFieldKey]: buildProtocolConditionFromOperator(nextOperator, mergedValues),
        };
        return {
          ...item,
          action: "give",
          label: updateRuleAutoLabelIfNeeded(current?.category || "sliding_scale_insulin", item, nextConditions, 0),
          conditions: nextConditions,
        };
      }),
    }));
  };

  const addProtocolConditionRow = () => {
    updateActiveProtocolRuleRows((rows, current) => {
      const fieldOptions = getProtocolFieldOptions(current?.category || "sliding_scale_insulin");
      const usedFieldKeys = new Set(rows.map((row: any) => String(row?.fieldKey || "").trim()).filter(Boolean));
      const nextFieldKey = String(fieldOptions.find((option: any) => !usedFieldKeys.has(String(option?.value || "").trim()))?.value || "");
      return [
        ...rows,
        {
          rowId: `row_${rows.length + 1}_${Date.now()}`,
          fieldKey: nextFieldKey,
          operator: "equals",
        },
      ];
    });
  };

  const removeProtocolConditionRow = (rowId: string) => {
    updateActiveProtocolRuleRows((rows, current) => {
      const nextRows = rows.filter((row: any) => String(row?.rowId || "") !== rowId);
      if (nextRows.length) {
        return nextRows;
      }
      const fieldOptions = getProtocolFieldOptions(current?.category || "sliding_scale_insulin");
      return [{
        rowId: "row_1",
        fieldKey: String(fieldOptions[0]?.value || ""),
        operator: "equals",
      }];
    });
  };

  const updateRuleAutoLabelIfNeeded = (category: any, rule: any, nextConditions: any, index: number) => {
    const previousAutoLabel = buildEmarProtocolRuleAutoLabel(category, rule?.conditions || {}, index);
    const nextAutoLabel = buildEmarProtocolRuleAutoLabel(category, nextConditions, index);
    const currentLabel = String(rule?.label || "").trim();
    if (!currentLabel || currentLabel === previousAutoLabel) {
      return nextAutoLabel;
    }
    return rule?.label || "";
  };

  const buildProtocolDialogDefinitions = (definitions: any[]) => {
    const mergedDefinitions = (definitions || []).reduce((nextDefinitions: any[], definition: any) => {
      const category = String(definition?.category || "sliding_scale_insulin");
      const existingIndex = nextDefinitions.findIndex((item: any) => String(item?.category || "") === category);
      const definitionDraft = createProtocolEditorDraft(definition, nextDefinitions.length);
      if (existingIndex === -1) {
        return [...nextDefinitions, definitionDraft];
      }
      const existingDefinition = nextDefinitions[existingIndex];
      const existingRules = Array.isArray(existingDefinition?.rules) ? existingDefinition.rules : [];
      const additionalRules = Array.isArray(definitionDraft?.rules) ? definitionDraft.rules : [];
      return nextDefinitions.map((item: any, index: number) => (
        index === existingIndex
          ? {
            ...item,
            instructions: String(item?.instructions || definitionDraft?.instructions || ""),
            rules: [...existingRules, ...additionalRules].slice(0, MAX_PROTOCOL_RULES_PER_PARAMETER),
          }
          : item
      ));
    }, []);
    return mergedDefinitions.map((definition: any, index: number) => ({
      ...definition,
      parameterId: definition?.parameterId || `parameter_${index + 1}`,
    }));
  };

  const getVisibleProtocolRuleForDraft = (draft: any) => (
    (Array.isArray(draft?.rules) ? draft.rules : [])
      .filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
      .slice(0, 1)[0] || createEmptyProtocolRule(draft?.category || "sliding_scale_insulin", 0)
  );

  const getVisibleProtocolRulesForDraft = (draft: any) => {
    const visibleRules = (Array.isArray(draft?.rules) ? draft.rules : [])
      .filter((rule: any) => !isSystemGeneratedProtocolRule(rule));
    return visibleRules.length ? visibleRules : [createEmptyProtocolRule(draft?.category || "sliding_scale_insulin", 0)];
  };

  const isProtocolDialogDefinitionReady = (draft: any) => {
    const visibleRules = getVisibleProtocolRulesForDraft(draft);
    return visibleRules.every((rule: any) => hasAnyProtocolCondition(rule?.conditions || {}));
  };

  const updateProtocolDialogDefinition = (definitionIndex: number, updater: (current: any) => any) => {
    setProtocolDialogDefinitions((current: any[]) => current.map((item: any, index: number) => (
      index === definitionIndex ? updater(item) : item
    )));
  };

  const updateProtocolDialogFieldCondition = (
    definitionIndex: number,
    ruleIndex: number,
    fieldKey: string,
    nextOperator: string,
    nextValues?: { equalsValue?: string; minValue?: string; maxValue?: string },
  ) => {
    const normalizedFieldKey = String(fieldKey || "").trim();
    if (!normalizedFieldKey) {
      return;
    }
    updateProtocolDialogDefinition(definitionIndex, (draft: any) => {
      const visibleRules = getVisibleProtocolRulesForDraft(draft);
      const targetRule = visibleRules[ruleIndex] || createEmptyProtocolRule(draft?.category || "sliding_scale_insulin", ruleIndex);
      const previousCondition = targetRule?.conditions?.[normalizedFieldKey] || {};
      const mergedValues = {
        equalsValue: String(nextValues?.equalsValue ?? previousCondition?.equalsValue ?? ""),
        minValue: String(nextValues?.minValue ?? previousCondition?.minValue ?? ""),
        maxValue: String(nextValues?.maxValue ?? previousCondition?.maxValue ?? ""),
      };
      const nextConditions = {
        ...(targetRule?.conditions || {}),
        [normalizedFieldKey]: buildProtocolConditionFromOperator(nextOperator, mergedValues),
      };
      return {
        ...draft,
        rules: visibleRules.map((rule: any, currentRuleIndex: number) => (
          currentRuleIndex === ruleIndex
            ? {
              ...rule,
              label: updateRuleAutoLabelIfNeeded(draft?.category || "sliding_scale_insulin", rule, nextConditions, currentRuleIndex),
              conditions: nextConditions,
            }
            : rule
        )),
      };
    });
  };

  const openNewProtocolDefinitionDialog = () => {
    const sourceFormData = protocolSetupTarget === "tracking" ? dailyTrackingFormData : formData;
    setEditingProtocolDefinitionIndex(-1);
    setProtocolDefinitionDraft(createEmptyProtocolParameterSet(
      sourceFormData?.protocolCategory || "sliding_scale_insulin",
      Array.isArray(sourceFormData?.protocolDefinitions) ? sourceFormData.protocolDefinitions.length : 0,
    ));
    setProtocolDialogDefinitions(buildProtocolDialogDefinitions(Array.isArray(sourceFormData?.protocolDefinitions) ? sourceFormData.protocolDefinitions : []));
    setExpandedProtocolEditorIds([]);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(false);
    setShowProtocolSetupDialog(true);
  };

  const openProtocolManagementDialog = () => {
    const sourceFormData = protocolSetupTarget === "tracking" ? dailyTrackingFormData : formData;
    setEditingProtocolDefinitionIndex(-1);
    setProtocolDefinitionDraft(createEmptyProtocolParameterSet(
      sourceFormData?.protocolCategory || "sliding_scale_insulin",
      Array.isArray(sourceFormData?.protocolDefinitions) ? sourceFormData.protocolDefinitions.length : 0,
    ));
    setProtocolDialogDefinitions(buildProtocolDialogDefinitions(Array.isArray(sourceFormData?.protocolDefinitions) ? sourceFormData.protocolDefinitions : []));
    setExpandedProtocolEditorIds([]);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(false);
    setShowProtocolSetupDialog(true);
  };

  const openEditProtocolDefinitionDialog = (definition: any, index: number) => {
    setEditingProtocolDefinitionIndex(index);
    setProtocolDefinitionDraft(createProtocolEditorDraft(definition, index));
    setProtocolDialogDefinitions(buildProtocolDialogDefinitions(Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions : []));
    setExpandedProtocolEditorIds([String(definition?.parameterId || `parameter_${index + 1}`)]);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(true);
    setShowProtocolSetupDialog(true);
  };

  const saveProtocolDefinitionDraft = () => {
    const nextDefinitions = (Array.isArray(protocolDialogDefinitions) ? protocolDialogDefinitions : []).map((draft: any, index: number) => {
      const visibleRules = (Array.isArray(draft?.rules) ? draft.rules : [])
        .filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
        .slice(0, MAX_PROTOCOL_RULES_PER_PARAMETER);
      return {
        parameterId: draft?.parameterId || `parameter_${index + 1}`,
        category: String(draft?.category || "sliding_scale_insulin"),
        instructions: String(draft?.instructions || ""),
        rules: visibleRules,
      };
    });
    if (nextDefinitions.some((definition: any) => !(Array.isArray(definition?.rules) && definition.rules.length))) {
      toast.current?.show({
        severity: "warn",
        summary: "Rules required",
        detail: "Each parameter needs at least one matching rule before updating.",
        life: 3000,
      });
      return;
    }
    if (nextDefinitions.some((definition: any) => (
      (Array.isArray(definition?.rules) ? definition.rules : [])
        .some((rule: any) => !hasAnyProtocolCondition(rule?.conditions || {}))
    ))) {
      toast.current?.show({
        severity: "warn",
        summary: "Conditions required",
        detail: "Each parameter needs at least one condition before updating.",
        life: 3000,
      });
      return;
    }

    const applyProtocolDefinitions = (current: any) => {
      const firstDefinition = nextDefinitions[0] || null;
      return {
        ...current,
        protocolEnabled: nextDefinitions.length > 0,
        protocolDefinitions: nextDefinitions,
        protocolCategory: firstDefinition?.category || current?.protocolCategory || "",
        protocolInstructions: firstDefinition?.instructions || "",
        protocolRules: Array.isArray(firstDefinition?.rules) ? firstDefinition.rules : [],
      };
    };
    if (protocolSetupTarget === "tracking") {
      setDailyTrackingFormData((current: any) => applyProtocolDefinitions(current));
    } else {
      setFormData((current: any) => applyProtocolDefinitions(current));
    }
    setShowProtocolSetupDialog(false);
    setEditingProtocolDefinitionIndex(-1);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(false);
    setExpandedProtocolEditorIds([]);
  };

  const syncProtocolDefinitionsToForm = (definitions: any[]) => {
    const normalizedDefinitions = (definitions || []).map((draft: any, index: number) => {
      const visibleRules = (Array.isArray(draft?.rules) ? draft.rules : [])
        .filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
        .slice(0, MAX_PROTOCOL_RULES_PER_PARAMETER);
      return {
        parameterId: draft?.parameterId || `parameter_${index + 1}`,
        category: String(draft?.category || "sliding_scale_insulin"),
        instructions: String(draft?.instructions || ""),
        rules: visibleRules,
      };
    });
    const applyProtocolDefinitions = (current: any) => {
      const firstDefinition = normalizedDefinitions[0] || null;
      return {
        ...current,
        protocolEnabled: normalizedDefinitions.length > 0,
        protocolDefinitions: normalizedDefinitions,
        protocolCategory: firstDefinition?.category || current?.protocolCategory || "",
        protocolInstructions: firstDefinition?.instructions || "",
        protocolRules: Array.isArray(firstDefinition?.rules) ? firstDefinition.rules : [],
      };
    };
    if (protocolSetupTarget === "tracking") {
      setDailyTrackingFormData((current: any) => applyProtocolDefinitions(current));
    } else {
      setFormData((current: any) => applyProtocolDefinitions(current));
    }
  };

  const removeProtocolDefinition = (definitionIndex: number) => {
    confirmDialog({
      ...appConfirmDialogClasses,
      header: "Remove Parameter",
      message: renderConfirmMessage(
        "Remove this parameter?",
        "This will delete the parameter and all of its matching rules for this medication.",
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      rejectClassName: "p-button-text p-button-rounded",
      accept: () => {
        const updateCurrentForm = (current: any) => {
          const nextDefinitions = (Array.isArray(current?.protocolDefinitions) ? current.protocolDefinitions : []).filter((_: any, index: number) => index !== definitionIndex);
          const firstDefinition = nextDefinitions[0] || null;
          return {
            ...current,
            protocolEnabled: nextDefinitions.length > 0,
            protocolDefinitions: nextDefinitions,
            protocolCategory: firstDefinition?.category || "",
            protocolInstructions: firstDefinition?.instructions || "",
            protocolRules: Array.isArray(firstDefinition?.rules) ? firstDefinition.rules : [],
          };
        };
        if (protocolSetupTarget === "tracking") {
          setDailyTrackingFormData((current: any) => updateCurrentForm(current));
        } else {
          setFormData((current: any) => updateCurrentForm(current));
        }
        if (editingProtocolDefinitionIndex === definitionIndex) {
          setEditingProtocolDefinitionIndex(-1);
          setProtocolDefinitionDraft(createEmptyProtocolParameterSet("sliding_scale_insulin", 0));
          setActiveProtocolRuleIndex(0);
          setShowProtocolRuleEditor(false);
        }
        setProtocolDialogDefinitions((current: any[]) => current.filter((_: any, index: number) => index !== definitionIndex));
        setExpandedProtocolEditorIds((current: string[]) => current.filter((id: string) => {
          const removedDefinition = protocolDialogDefinitions?.[definitionIndex];
          const removedId = String(removedDefinition?.parameterId || `parameter_${definitionIndex + 1}`);
          return id !== removedId;
        }));
      },
    });
  };

  const scrollToProtocolEditor = () => {
    window.setTimeout(() => {
      protocolEditorSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const startNewProtocolDefinition = () => {
    if (protocolSetupTarget === "tracking") {
      return;
    }
    const nextDefinition = createEmptyProtocolParameterSet("sliding_scale_insulin", protocolDialogDefinitions.length);
    setProtocolDialogDefinitions((current: any[]) => [...current, nextDefinition]);
    setExpandedProtocolEditorIds((current: string[]) => [...current, String(nextDefinition?.parameterId || `parameter_${protocolDialogDefinitions.length + 1}`)]);
    setEditingProtocolDefinitionIndex(protocolDialogDefinitions.length);
    setProtocolDefinitionDraft(nextDefinition);
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(true);
    scrollToProtocolEditor();
  };

  const startEditProtocolDefinition = (definition: any, index: number) => {
    setEditingProtocolDefinitionIndex(index);
    setProtocolDefinitionDraft(createProtocolEditorDraft(definition, index));
    setExpandedProtocolEditorIds((current: string[]) => {
      const nextId = String(definition?.parameterId || `parameter_${index + 1}`);
      return current.includes(nextId) ? current : [...current, nextId];
    });
    setActiveProtocolRuleIndex(0);
    setShowProtocolRuleEditor(true);
    scrollToProtocolEditor();
  };

  const saveProtocolDialogSection = (definitionIndex: number) => {
    const draft = protocolDialogDefinitions?.[definitionIndex];
    if (!isProtocolDialogDefinitionReady(draft)) {
      return;
    }
    syncProtocolDefinitionsToForm(protocolDialogDefinitions);
    const parameterId = String(draft?.parameterId || `parameter_${definitionIndex + 1}`);
    setExpandedProtocolEditorIds((current: string[]) => current.filter((id: string) => id !== parameterId));
    setShowProtocolRuleEditor((current) => {
      const remainingIds = expandedProtocolEditorIds.filter((id: string) => id !== parameterId);
      return current && remainingIds.length > 0;
    });
  };

  const addProtocolDialogRule = (definitionIndex: number) => {
    updateProtocolDialogDefinition(definitionIndex, (draft: any) => {
      const nextRules = getVisibleProtocolRulesForDraft(draft);
      if (nextRules.length >= MAX_PROTOCOL_RULES_PER_PARAMETER) {
        return draft;
      }
      return {
        ...draft,
        rules: [
          ...nextRules,
          createEmptyProtocolRule(draft?.category || "sliding_scale_insulin", nextRules.length),
        ],
      };
    });
  };

  const removeProtocolDialogRule = (definitionIndex: number, ruleIndex: number) => {
    updateProtocolDialogDefinition(definitionIndex, (draft: any) => {
      const nextRules = getVisibleProtocolRulesForDraft(draft).filter((_: any, currentRuleIndex: number) => currentRuleIndex !== ruleIndex);
      return {
        ...draft,
        rules: nextRules.length ? nextRules : [createEmptyProtocolRule(draft?.category || "sliding_scale_insulin", 0)],
      };
    });
  };

  const updateProtocolDialogRuleMeta = (
    definitionIndex: number,
    ruleIndex: number,
    fields: Record<string, any>,
  ) => {
    updateProtocolDialogDefinition(definitionIndex, (draft: any) => ({
      ...draft,
      rules: getVisibleProtocolRulesForDraft(draft).map((rule: any, currentRuleIndex: number) => (
        currentRuleIndex === ruleIndex ? { ...rule, ...fields } : rule
      )),
    }));
  };

  const protocolEditorCategoryOptions = useMemo(
    () => getEmarProtocolEditorCategoryOptions(protocolDefinitionDraft?.category),
    [protocolDefinitionDraft?.category],
  );

  const activeProtocolRules = (Array.isArray(protocolDefinitionDraft?.rules) ? protocolDefinitionDraft.rules : [])
    .filter((rule: any) => !isSystemGeneratedProtocolRule(rule))
    .slice(0, 1);
  const activeProtocolRule = activeProtocolRules[0] || null;

  const addProtocolRuleToDraft = () => {
    if (activeProtocolRules.length >= MAX_PROTOCOL_RULES_PER_PARAMETER) {
      toast.current?.show({
        severity: "warn",
        summary: "Rule limit reached",
        detail: `You can add up to ${MAX_PROTOCOL_RULES_PER_PARAMETER} rules in one parameter.`,
        life: 3000,
      });
      return;
    }
    setProtocolDefinitionDraft((current: any) => {
      const nextRules = Array.isArray(current?.rules) ? current.rules : [];
      const nextRuleIndex = nextRules.length;
      return {
        ...current,
        rules: [
          ...nextRules,
          createEmptyProtocolRule(current?.category || "sliding_scale_insulin", nextRuleIndex),
        ],
      };
    });
    setActiveProtocolRuleIndex(activeProtocolRules.length);
  };

  const duplicateActiveProtocolRule = () => {
    if (activeProtocolRules.length >= MAX_PROTOCOL_RULES_PER_PARAMETER) {
      toast.current?.show({
        severity: "warn",
        summary: "Rule limit reached",
        detail: `You can add up to ${MAX_PROTOCOL_RULES_PER_PARAMETER} rules in one parameter.`,
        life: 3000,
      });
      return;
    }
    if (!activeProtocolRule) {
      addProtocolRuleToDraft();
      return;
    }
    const nextRuleIndex = activeProtocolRules.length;
    const clonedRule = {
      ...activeProtocolRule,
      ruleId: `rule_${nextRuleIndex + 1}`,
      label: `${String(activeProtocolRule?.label || `Rule ${activeProtocolRuleIndex + 1}`).trim()} Copy`,
      conditions: { ...(activeProtocolRule?.conditions || {}) },
    };
    setProtocolDefinitionDraft((current: any) => ({
      ...current,
      rules: [
        ...(Array.isArray(current?.rules) ? current.rules : []),
        clonedRule,
      ],
    }));
    setActiveProtocolRuleIndex(nextRuleIndex);
  };

  const removeActiveProtocolRule = () => {
    if (!activeProtocolRules.length) {
      return;
    }
    const nextActiveIndex = Math.max(0, activeProtocolRuleIndex - 1);
    setProtocolDefinitionDraft((current: any) => ({
      ...current,
      rules: (Array.isArray(current?.rules) ? current.rules : []).filter((_: any, index: number) => index !== activeProtocolRuleIndex),
    }));
    setActiveProtocolRuleIndex(nextActiveIndex);
  };

  const getProtocolRuleSummaryParts = (rule: any, category: any) => {
    const categoryOption = getEmarProtocolCategoryOption(category);
    const parameterSchema = Array.isArray(categoryOption?.parameterSchema)
      ? (categoryOption?.parameterSchema || [])
      : [];
    const conditionParts = parameterSchema
      .map((field: any) => getProtocolConditionSummary(field, rule?.conditions?.[field.key] || {}))
      .filter(Boolean);
    const actionLabel = getProtocolRuleActionLabel(rule?.action);
    const doseLabel = String(rule?.doseValue || "").trim();
    const noteLabel = String(rule?.note || "").trim();

    return [
      conditionParts.length ? `Match all: ${conditionParts.join(" AND ")}` : "",
      actionLabel && actionLabel.toLowerCase() !== "give" ? `Then ${actionLabel}` : "",
      doseLabel ? `Suggested dosage: ${doseLabel}` : "",
      noteLabel ? noteLabel : "",
    ].filter(Boolean);
  };

  const renderProtocolDefinitionsOverview = (protocolDefinitions: any[]) => {
    if (!protocolDefinitions.length) {
      return null;
    }
    return (
      <div className="col-12">
        <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: "12px", padding: "0.7rem 0.8rem" }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>All conditions</div>
          <div className="mt-2 flex flex-column gap-2">
            {protocolDefinitions.map((definition: any, definitionIndex: number) => (
              <div key={`${String(definition?.category || "protocol")}-${definitionIndex}`} style={{ borderTop: definitionIndex === 0 ? "none" : "1px solid #e2e8f0", paddingTop: definitionIndex === 0 ? 0 : "0.6rem" }}>
                <div className="flex align-items-center gap-2 flex-wrap">
                  <Tag value={String(definition?.label || "Protocol")} severity="info" rounded />
                  {String(definition?.guidanceAction || "").trim() ? (
                    <Tag value={String(definition.guidanceAction).replaceAll("_", " ")} severity="secondary" rounded />
                  ) : null}
                </div>
                {String(definition?.instructions || "").trim() ? (
                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                    <strong style={{ color: "#334155" }}>Setup:</strong> {definition.instructions}
                  </div>
                ) : null}
                {(Array.isArray(definition?.rules) ? definition.rules.filter((rule: any) => !isSystemGeneratedProtocolRule(rule)) : []).length ? (
                  <div className="mt-2 flex flex-column gap-1">
                    {(definition.rules || []).filter((rule: any) => !isSystemGeneratedProtocolRule(rule)).map((rule: any, ruleIndex: number) => (
                      <div key={`${String(rule?.ruleId || "rule")}-${ruleIndex}`} style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                        {getProtocolRuleSummaryParts(rule, definition?.category).join(" | ")}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const initializeProtocolAssessmentState = (medicationLike: any, entryLike?: any) => {
    const protocolSummary = getProtocolSummaryForMedication(medicationLike);
    const assessment = entryLike?.protocolAssessment;
    if (!protocolSummary) {
      setProtocolAssessmentValues({});
      setProtocolDoseValue("");
      setProtocolDecisionNote("");
      setProtocolResolution(null);
      return;
    }
    const parameterSchema = Array.isArray(protocolSummary.parameterSchema) ? protocolSummary.parameterSchema : [];
    const parameterValues = assessment?.parameterValues && typeof assessment.parameterValues === "object"
      ? assessment.parameterValues
      : {};
    const nextValues = Object.fromEntries(
      parameterSchema.map((field: any) => {
        const key = String(field?.key || "").trim();
        return [key, String(parameterValues[key] || "")];
      }),
    );
    Object.entries(parameterValues).forEach(([key, value]) => {
      const normalizedKey = String(key || "").trim();
      if (normalizedKey && !Object.prototype.hasOwnProperty.call(nextValues, normalizedKey)) {
        nextValues[normalizedKey] = String(value || "");
      }
    });
    setProtocolAssessmentValues(nextValues);
    setProtocolDoseValue(String(assessment?.doseValue || ""));
    setProtocolDecisionNote(String(assessment?.decisionNote || ""));
    setProtocolResolution(assessment?.resolution || null);
  };

  const resolvePendingProtocol = async () => {
    if (!residentId || !pendingMedPassAction?.medicationId) {
      return;
    }
    const protocolSummary = getProtocolSummaryForMedication({
      protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
      protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
    });
    if (!protocolSummary) {
      return;
    }
    const missingField = (protocolSummary.parameterSchema || []).find((field: any) => field?.required && !String(protocolAssessmentValues[String(field?.key || "").trim()] || "").trim()) as any;
    if (missingField) {
      toast.current?.show({
        severity: "warn",
        summary: "Protocol assessment required",
        detail: `Add ${missingField.label || "the required assessment value"} before resolving.`,
        life: 3000,
      });
      return;
    }

    setResolvingProtocol(true);
    try {
      const response = await backendAPIStore.resolveResidentEmarProtocol({
        residentId,
        medicationId: pendingMedPassAction.medicationId,
        entryDate: pendingMedPassAction?.entryDate || todayDate,
        scheduledTimeKey: pendingMedPassAction?.scheduledTimeKey || "default",
        protocolAssessment: {
          parameterValues: protocolAssessmentValues,
        },
      });
      if (!hasProtocolRecommendation(response, pendingMedPassAction?.status, response?.resolvedDose)) {
        setProtocolResolution(null);
        setProtocolDoseValue("");
        setProtocolDecisionNote("");
        setMedPassActionDraft("");
        toast.current?.show({
          severity: "warn",
          summary: "Reach out to provider",
          detail: "The entered parameters do not produce a valid recommendation. Reach out to the provider to check the parameters.",
          life: 3500,
        });
        return;
      }
      setProtocolResolution(response);
      const resolvedDose = String(response?.resolvedDose || "").trim();
      setProtocolDoseValue(["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDose.toLowerCase()) ? "" : resolvedDose);
      setProtocolDecisionNote(String(response?.decisionNote || ""));
      const actionMeta = getProtocolResolvedActionMeta(response, pendingMedPassAction?.status);
      setPendingMedPassAction((current: any) => current ? {
        ...current,
        status: actionMeta.nextStatus,
      } : current);
      toast.current?.show({
        severity: response?.matched ? "success" : "warn",
        summary: response?.matched ? "Protocol resolved" : "No matching rule",
        detail: response?.matched
          ? [
            String(actionMeta.actionLabel || "").trim().toLowerCase() !== "give" ? `Recommended action: ${actionMeta.actionLabel}` : "",
            String(response?.resolvedDose || "").trim() ? `Suggested dosage: ${String(response.resolvedDose).trim()}` : "",
          ].filter(Boolean).join(" • ")
          : "Recommended action: No rule matched - Contact provider",
        life: 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to resolve protocol guidance.");
    } finally {
      setResolvingProtocol(false);
    }
  };

  const updateProtocolAssessmentFieldValue = (fieldKey: string, nextValue: string) => {
    setProtocolAssessmentValues((current) => ({ ...current, [fieldKey]: nextValue }));
    setProtocolResolution(null);
  };

  const protocolResolutionRequiredForSave = (protocolSummary: any, status: any) => (
    !!protocolSummary && ["given", "prn_given"].includes(String(status || "").toLowerCase())
  );

  const getProtocolResolvedActionMeta = (resolution: any, currentStatus?: string) => {
    const currentStatusKey = String(currentStatus || "").trim().toLowerCase();
    const recommendedAction = String(resolution?.recommendedAction || "").trim().toLowerCase();
    const resolvedDoseValue = String(resolution?.resolvedDose || "").trim();
    const resolvedDoseAction = resolvedDoseValue.toLowerCase();
    const resolvedDoseIsAction = ["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDoseAction);
    const effectiveRecommendedAction = recommendedAction || (resolvedDoseIsAction ? resolvedDoseAction : "");
    const hasMatchedRecommendation = !!effectiveRecommendedAction
      && (resolution?.matched !== false || resolvedDoseIsAction);
    const isHoldAction = ["hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(effectiveRecommendedAction);
    const actionVerb = isHoldAction ? "Hold" : "Give";
    const matchedRuleLabel = String(resolution?.matchedRuleLabel || "").trim();
    const decisionReason = String(resolution?.decisionNote || "").trim();
    const suggestedDose = resolvedDoseIsAction ? "" : resolvedDoseValue;
    const getStatusButtonLabel = (status: string) => ({
      given: "Mark as Given",
      family: "Mark as Given by Family",
      held: "Mark as Held",
      refused: "Mark as Refused",
      not_given: "Mark as Not Given",
      missed: "Mark as Missed",
      hospitalized: "Mark as Hospitalized",
      prn_given: "Mark as Given",
    } as Record<string, string>)[String(status || "").trim().toLowerCase()] || "Save Action";
    if (!hasMatchedRecommendation) {
      const nextStatus = currentStatusKey === "held"
        ? "held"
        : currentStatusKey === "prn_given"
          ? "prn_given"
          : currentStatusKey === "family"
            ? "family"
            : "given";
      return {
        nextStatus,
        note: "No rule matched. Contact provider.",
        buttonLabel: getStatusButtonLabel(nextStatus),
        actionLabel: "No rule matched - Contact provider",
        dosageLabel: "",
      };
    }
    const explanatoryNote = matchedRuleLabel
      ? `${actionVerb} because the reading matched ${matchedRuleLabel}.${decisionReason ? ` ${decisionReason}` : ""}`
      : decisionReason
        ? `${actionVerb} because ${decisionReason.charAt(0).toLowerCase()}${decisionReason.slice(1)}`
        : actionVerb;
    const noteWithDose = suggestedDose && !isHoldAction
      ? `${explanatoryNote} Suggested dose: ${suggestedDose}.`
      : explanatoryNote;
    if (isHoldAction) {
      return {
        nextStatus: "held",
        note: explanatoryNote,
        buttonLabel: getStatusButtonLabel("held"),
        actionLabel: actionVerb,
        dosageLabel: suggestedDose,
      };
    }
    if (currentStatusKey === "prn_given") {
      return {
        nextStatus: "prn_given",
        note: noteWithDose,
        buttonLabel: getStatusButtonLabel("prn_given"),
        actionLabel: actionVerb,
        dosageLabel: suggestedDose,
      };
    }
    return {
      nextStatus: currentStatusKey === "family" ? "family" : "given",
      note: noteWithDose,
      buttonLabel: getStatusButtonLabel(currentStatusKey === "family" ? "family" : "given"),
      actionLabel: actionVerb,
      dosageLabel: suggestedDose,
    };
  };

  const hasProtocolRecommendation = (resolution: any, currentStatus?: string, doseValue = "") => {
    if (!resolution) {
      return false;
    }
    const resolvedDose = String(resolution?.resolvedDose || doseValue || "").trim().toLowerCase();
    const hasActionLikeDose = ["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDose);
    if (resolution?.matched === false && !hasActionLikeDose) return false;
    const actionMeta = getProtocolResolvedActionMeta(resolution, currentStatus);
    const actionLabel = String(actionMeta?.actionLabel || "").trim().toLowerCase();
    return Boolean(
      String(actionMeta?.dosageLabel || doseValue || "").trim()
      || String(resolution?.decisionNote || "").trim()
      || actionLabel,
    );
  };

  const shouldShowProtocolAction = (resolution: any, currentStatus?: string, doseValue = "") => {
    const actionMeta = getProtocolResolvedActionMeta(resolution, currentStatus);
    const actionLabel = String(actionMeta?.actionLabel || "").trim();
    const dosageLabel = String(actionMeta?.dosageLabel || doseValue || "").trim().toLowerCase();
    const decisionNote = String(resolution?.decisionNote || "").trim();
    return Boolean(actionLabel)
      && (actionLabel.toLowerCase() !== "give" || (!dosageLabel && !decisionNote))
      && !(actionLabel.toLowerCase() === "give" && /\b(do not give|don't give|hold)\b/.test(dosageLabel));
  };

  const removeScheduleTimeFromForm = (timeToRemove: string) => {
    const nextScheduleTimes = selectedScheduleTimes.filter((time) => time !== timeToRemove);
    setSelectedScheduleTimes(nextScheduleTimes);
    setFormData((current: any) => ({
      ...current,
      ...buildFormScheduleFields(nextScheduleTimes, {
        scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
        scheduleDays: selectedScheduleDays,
        scheduleMonthDays: selectedScheduleMonthDays,
      }),
    }));
  };

  const saveMedication = async (overrideValues?: Record<string, any>) => {
    if (!residentId) {
      return;
    }
    const looksLikeUiEvent = !!overrideValues
      && typeof overrideValues === "object"
      && (
        "preventDefault" in overrideValues
        || "stopPropagation" in overrideValues
        || "nativeEvent" in overrideValues
        || "currentTarget" in overrideValues
        || "target" in overrideValues
      );
    const safeOverrideValues = looksLikeUiEvent ? undefined : overrideValues;
    const currentFormData = safeOverrideValues ? { ...formData, ...safeOverrideValues } : formData;
    const existingMedication = String(currentFormData?.medicationId || "").trim()
      ? medications.find((item: any) => String(item?.medicationId || "").trim() === String(currentFormData?.medicationId || "").trim()) || null
      : null;
    // The edit dialog state is authoritative. In particular, an empty schedule
    // is an intentional change and must not fall back to the existing item.
    const effectiveScheduleTimes = Array.isArray(selectedScheduleTimes)
      ? selectedScheduleTimes
      : getMedicationScheduleTimes(existingMedication);
    const effectiveScheduleDays = Array.isArray(selectedScheduleDays)
      ? selectedScheduleDays
      : getMedicationScheduleDays(existingMedication);
    const effectiveScheduleMonthDays = Array.isArray(selectedScheduleMonthDays)
      ? selectedScheduleMonthDays
      : getMedicationScheduleMonthDays(existingMedication);

    if (!String(currentFormData?.medicationName || "").trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Medication name required",
        detail: "Add the medication name before saving.",
        life: 3000,
      });
      return;
    }

    const duplicateActiveMedication = medications.find((item: any) => {
      if (item?.active === false || currentFormData?.active === false) {
        return false;
      }
      if (String(item?.medicationId || "").trim() === String(currentFormData?.medicationId || "").trim()) {
        return false;
      }
      return normalizeValueKey(item?.medicationName) === normalizeValueKey(currentFormData?.medicationName);
    });

    if (duplicateActiveMedication) {
      toast.current?.show({
        severity: "warn",
        summary: "Possible duplicate active medication",
        detail: "An active medication with this same name already exists. Delete or update the existing one instead.",
        life: 3500,
      });
      return;
    }

    if (
      /^\d{4}-\d{2}-\d{2}$/.test(normalizeValue(currentFormData?.effectiveStartDate))
      && /^\d{4}-\d{2}-\d{2}$/.test(normalizeValue(currentFormData?.effectiveEndDate))
      && normalizeValue(currentFormData?.effectiveEndDate) < normalizeValue(currentFormData?.effectiveStartDate)
    ) {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid medication dates",
        detail: "The effective end date cannot be before the start date.",
        life: 3000,
      });
      return;
    }

    const isMarOnlyItem = currentFormData?.marOnlyItem === true;
    const resolvedFrequency = isMarOnlyItem
      ? ""
      : currentFormData?.prn
        ? "prn"
        : String(currentFormData?.frequency || inferFrequencyFromMedication(existingMedication) || "").trim().toLowerCase();
    const reconciledScheduleTimes = dedupeAndSortScheduleTimes(effectiveScheduleTimes);
    const resolvedScheduleDayPattern = isMarOnlyItem
      ? "daily"
      : currentFormData?.prn
        ? "daily"
        : String(currentFormData?.scheduleDayPattern || inferMedicationScheduleDayPattern(existingMedication) || "daily").trim().toLowerCase();
    const resolvedScheduleDays = isMarOnlyItem || currentFormData?.prn ? [] : normalizeMedicationScheduleDays(effectiveScheduleDays);
    const resolvedScheduleMonthDays = isMarOnlyItem || currentFormData?.prn ? [] : normalizeMedicationScheduleMonthDays(effectiveScheduleMonthDays);
    const protocolDefinitions = Array.isArray(currentFormData?.protocolDefinitions) ? currentFormData.protocolDefinitions : [];

    if (!isMarOnlyItem && !currentFormData?.prn && resolvedScheduleDayPattern === "specific_days" && !resolvedScheduleDays.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Select schedule days",
        detail: "Choose at least one weekday for medications that are not taken every day.",
        life: 3500,
      });
      return;
    }

    if (!isMarOnlyItem && !currentFormData?.prn && resolvedScheduleDayPattern === "specific_month_days" && !resolvedScheduleMonthDays.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Select monthly dates",
        detail: "Choose at least one month date for medications that are not scheduled every day.",
        life: 3500,
      });
      return;
    }

    if (protocolDefinitions.length > 0 && protocolDefinitions.some((definition: any) => !(Array.isArray(definition?.rules) && definition.rules.length))) {
      toast.current?.show({
        severity: "warn",
        summary: "Parameter rules required",
        detail: "Each parameter set needs at least one rule before saving this medication.",
        life: 3500,
      });
      return;
    }

    setSaving(true);
    try {
      const scheduleFields = buildFormScheduleFields(reconciledScheduleTimes, {
        scheduleDayPattern: resolvedScheduleDayPattern as any,
        scheduleDays: resolvedScheduleDays,
        scheduleMonthDays: resolvedScheduleMonthDays,
      });
      const isInactiveItem = currentFormData?.active === false;
      const payload = {
        residentId,
        ...currentFormData,
        marOnlyItem: isMarOnlyItem,
        discontinued: !isInactiveItem && currentFormData?.discontinued === true,
        discontinuedAt: !isInactiveItem && currentFormData?.discontinued === true ? normalizeValue(currentFormData?.discontinuedAt) : "",
        discontinueReason: !isInactiveItem && currentFormData?.discontinued === true ? String(currentFormData?.discontinueReason || "").trim() : "",
        protocolEnabled: protocolDefinitions.length > 0,
        protocolDefinitions,
        protocolDefinition: protocolDefinitions.length ? buildProtocolDefinitionFromParameterSet(protocolDefinitions[0]) : null,
        protocolCategory: protocolDefinitions[0]?.category || "",
        protocolInstructions: protocolDefinitions[0]?.instructions || "",
        protocolRules: Array.isArray(protocolDefinitions[0]?.rules) ? protocolDefinitions[0].rules : [],
        frequency: resolvedFrequency,
        ...(isMarOnlyItem ? {
          schedule: "",
          scheduledClockTime: "",
          scheduleDayPattern: "daily",
          scheduleDays: [],
          scheduleMonthDays: [],
          prn: false,
        } : scheduleFields),
      };
      if (currentFormData?.medicationId) {
        await backendAPIStore.updateResidentEmarMedication(payload);
      } else {
        await backendAPIStore.createResidentEmarMedication(payload);
      }
      setShowProtocolSetupDialog(false);
      setShowEditDialog(false);
      await loadPage();
      toast.current?.show({
        severity: "success",
        summary: currentFormData?.medicationId ? "Medication updated" : "Medication added",
        detail: "The eMAR medication list was updated.",
        life: 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to save medication.");
    } finally {
      setSaving(false);
    }
  };

  const openCreateDailyTrackingDialog = () => {
    const nextForm = createEmptyEmarDailyTrackingItem();
    setDailyTrackingFormData(nextForm);
    setDailyTrackingScheduleTimes([]);
    setDailyTrackingScheduleDays([]);
    setDailyTrackingScheduleMonthDays([]);
    setShowDailyTrackingEditDialog(true);
  };

  const openEditDailyTrackingDialog = (item: any) => {
    const nextCategory = normalizeEmarDailyTrackingCategoryValue(item?.category);
    const targetSchema = getEmarDailyTrackingSchema(item);
    const protocolDefinitions = normalizeProtocolDefinitions(item);
    setDailyTrackingFormData({
      ...createEmptyEmarDailyTrackingItem(nextCategory),
      ...item,
      category: nextCategory,
      name: String(item?.name || buildEmarDailyTrackingDefaultName(nextCategory)),
      targetSchema,
      protocolEnabled: protocolDefinitions.length > 0,
      protocolDefinitions,
      protocolCategory: item?.protocolCategory || getEmarDailyTrackingProtocolCategory(nextCategory),
      protocolInstructions: item?.protocolInstructions || item?.protocolDefinition?.instructions || "",
      protocolRules: Array.isArray(item?.protocolDefinition?.rules) ? item.protocolDefinition.rules : Array.isArray(item?.protocolRules) ? item.protocolRules : [],
      active: item?.active !== false,
    });
    setDailyTrackingScheduleTimes(getMedicationScheduleTimes(item));
    setDailyTrackingScheduleDays(getMedicationScheduleDays(item));
    setDailyTrackingScheduleMonthDays(getMedicationScheduleMonthDays(item));
    setShowDailyTrackingEditDialog(true);
  };

  const closeDailyTrackingEditDialog = () => {
    if (savingDailyTrackingItem) {
      return;
    }
    setShowDailyTrackingEditDialog(false);
    setDailyTrackingFormData(createEmptyEmarDailyTrackingItem());
    setDailyTrackingScheduleTimes([]);
    setDailyTrackingScheduleDays([]);
    setDailyTrackingScheduleMonthDays([]);
  };

  const saveDailyTrackingItem = async () => {
    if (!residentId) {
      return;
    }

    if (!String(dailyTrackingFormData?.name || "").trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Vital tracker name required",
        detail: "Add the vital tracker name before saving.",
        life: 3000,
      });
      return;
    }

    const resolvedFrequency = String(dailyTrackingFormData?.frequency || "").trim().toLowerCase();
    const reconciledScheduleTimes = reconcileScheduleTimesForFrequency(resolvedFrequency, dailyTrackingScheduleTimes);
    const expectedScheduleCount = getExpectedScheduleCountForFrequency(resolvedFrequency);
    const resolvedScheduleDayPattern = String(dailyTrackingFormData?.scheduleDayPattern || "daily").trim().toLowerCase();
    const resolvedScheduleDays = normalizeMedicationScheduleDays(dailyTrackingScheduleDays);
    const resolvedScheduleMonthDays = normalizeMedicationScheduleMonthDays(dailyTrackingScheduleMonthDays);
    const targetSchema = getEmarDailyTrackingSchema(dailyTrackingFormData);
    const normalizedTrackingProtocolDefinitions = normalizeProtocolDefinitions(dailyTrackingFormData);
    const primaryTrackingProtocolDefinition = normalizedTrackingProtocolDefinitions[0] || null;

    if (resolvedScheduleDayPattern === "specific_days" && !resolvedScheduleDays.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Select tracking days",
        detail: "Choose at least one weekday for tracking items that are not every day.",
        life: 3000,
      });
      return;
    }

    if (resolvedScheduleDayPattern === "specific_month_days" && !resolvedScheduleMonthDays.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Select monthly dates",
        detail: "Choose at least one month date for tracking items that are not scheduled every day.",
        life: 3000,
      });
      return;
    }

    if (expectedScheduleCount > 0 && reconciledScheduleTimes.length !== expectedScheduleCount) {
      toast.current?.show({
        severity: "warn",
        summary: "Schedule does not match frequency",
        detail: `Select ${expectedScheduleCount} schedule time${expectedScheduleCount === 1 ? "" : "s"} before saving this tracking item.`,
        life: 3000,
      });
      return;
    }

    setSavingDailyTrackingItem(true);
    try {
      const isInactiveItem = dailyTrackingFormData?.active === false;
      const payload = {
        residentId,
        ...dailyTrackingFormData,
        discontinued: !isInactiveItem && dailyTrackingFormData?.discontinued === true,
        discontinuedAt: !isInactiveItem && dailyTrackingFormData?.discontinued === true ? normalizeValue(dailyTrackingFormData?.discontinuedAt) : "",
        discontinueReason: !isInactiveItem && dailyTrackingFormData?.discontinued === true ? String(dailyTrackingFormData?.discontinueReason || "").trim() : "",
        protocolEnabled: normalizedTrackingProtocolDefinitions.length > 0,
        protocolDefinitions: normalizedTrackingProtocolDefinitions,
        protocolCategory: primaryTrackingProtocolDefinition?.category || "",
        protocolInstructions: primaryTrackingProtocolDefinition?.instructions || "",
        protocolRules: Array.isArray(primaryTrackingProtocolDefinition?.rules) ? primaryTrackingProtocolDefinition.rules : [],
        targetSchema,
        ...buildFormScheduleFields(reconciledScheduleTimes, {
          scheduleDayPattern: resolvedScheduleDayPattern as any,
          scheduleDays: resolvedScheduleDays,
          scheduleMonthDays: resolvedScheduleMonthDays,
        }),
      };
      if (dailyTrackingFormData?.trackingItemId) {
        await backendAPIStore.updateResidentEmarDailyTrackingItem(payload);
      } else {
        await backendAPIStore.createResidentEmarDailyTrackingItem(payload);
      }
      closeDailyTrackingEditDialog();
      await loadPage();
      toast.current?.show({
        severity: "success",
        summary: dailyTrackingFormData?.trackingItemId ? "Vital tracker updated" : "Vital tracker added",
        detail: "The vital tracker list was updated.",
        life: 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to save the vital tracker.");
    } finally {
      setSavingDailyTrackingItem(false);
    }
  };

  const deleteDailyTrackingItem = (item: any) => {
    if (!residentId || !item?.trackingItemId) {
      return;
    }
    confirmDialog({
      ...appConfirmDialogClasses,
      message: renderConfirmMessage(
        "Delete Vital Tracker",
        `Remove ${item?.name || "this vital tracker"} from the active vital tracker list?`,
      ),
      header: "Delete Vital Tracker",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await backendAPIStore.deleteResidentEmarDailyTrackingItem(residentId, item.trackingItemId);
          await loadPage();
          toast.current?.show({
            severity: "success",
            summary: "Vital tracker deleted",
            detail: "The vital tracker was moved out of the active list.",
            life: 2500,
          });
        } catch (error: any) {
          showEmarActionError(error, "Unable to delete the vital tracker.");
        }
      },
    });
  };

  const openDailyTrackingEntryDialog = (row: any, occurrence: any, status = "recorded", entryDate = todayDate, allowStatusChange = false) => {
    if (["recorded", "refused"].includes(String(status || "").trim().toLowerCase()) && !canChartScheduledOccurrenceNow(occurrence)) {
      toast.current?.show({
        severity: "warn",
        summary: "Charting not available yet",
        detail: `Record and Refused are available 1 hour before the scheduled tracking time (${formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate)}).`,
        life: 3000,
      });
      return;
    }
    const schema = getEmarDailyTrackingSchema(row?.item);
    const existingValues = occurrence?.entry?.values && typeof occurrence.entry.values === "object" ? occurrence.entry.values : {};
    const nextDraft: Record<string, string> = {};
    const scheduledClockTime = String(occurrence?.scheduledClockTime || row?.item?.scheduledClockTime || "").trim();
    const existingRecordedAt = String(occurrence?.entry?.recordedAt || occurrence?.entry?.modifiedAt || occurrence?.entry?.createdAt || "").trim();
    const nextTimeState = isDailyTrackingValueEntryStatus(status)
      ? buildGivenTimeSelectionState({
        entryDate,
        scheduledClockTime,
        baseTimestamp: existingRecordedAt || new Date().toISOString(),
        includePromptReason: true,
      })
      : null;
    const defaultRecordedAt = existingRecordedAt || (nextTimeState
      ? (nextTimeState.givenTimeChoice === "scheduled" ? nextTimeState.scheduledRecordedAt : nextTimeState.currentRecordedAt)
      : "");
    const scheduledRecordedAt = nextTimeState?.scheduledRecordedAt || "";
    const currentRecordedAt = nextTimeState?.currentRecordedAt || "";
    const initialGivenTimeChoice = !nextTimeState
      ? ""
      : defaultRecordedAt === scheduledRecordedAt
        ? "scheduled"
        : defaultRecordedAt === currentRecordedAt
          ? "current"
          : "custom";
    schema.forEach((field: any) => {
      nextDraft[String(field?.key || "").trim()] = String(existingValues?.[field?.key] || "");
    });
    setDailyTrackingValueDraft(nextDraft);
    setDailyTrackingNoteDraft(String(occurrence?.entry?.note || ""));
    setPendingDailyTrackingEntryAction({
      item: row?.item,
      trackingItemId: row?.trackingItemId,
      occurrenceLabel: occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || "",
      scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
      scheduledClockTime: occurrence?.scheduledClockTime || "",
      entryDate,
      recordedAt: defaultRecordedAt,
      givenTimeChoice: initialGivenTimeChoice,
      showGivenTimeChoiceDialog: nextTimeState?.showGivenTimeChoiceDialog || false,
      givenTimePromptReason: nextTimeState?.givenTimePromptReason || "",
      givenTimeOptions: nextTimeState?.givenTimeOptions || [],
      status,
      entry: occurrence?.entry || null,
      allowStatusChange,
    });
    setShowDailyTrackingEntryDialog(true);
  };

  const openDailyTrackingHistoryFixDialog = (entry: any) => {
    const trackingItemId = String(entry?.trackingItemId || "").trim();
    const matchedItem = activeDailyTrackingItems.find((item: any) => String(item?.trackingItemId || "").trim() === trackingItemId);
    const item = entry?.trackingItemSnapshot || matchedItem || {
      trackingItemId,
      name: entry?.trackingItemName || "Vital tracker",
      category: entry?.trackingItemCategory || "",
    };
    openDailyTrackingEntryDialog(
      {
        item,
        trackingItemId,
      },
      {
        entry,
        occurrenceLabel: entry?.scheduledTime || entry?.scheduledClockTime || entry?.scheduledTimeKey || "",
        scheduledTimeKey: entry?.scheduledTimeKey || "default",
        scheduledClockTime: entry?.scheduledClockTime || "",
      },
      String(entry?.status || "recorded").trim().toLowerCase() || "recorded",
      String(entry?.entryDate || todayDate).trim() || todayDate,
      true,
    );
  };

  const runPsychotropicComplianceComparison = async () => {
    if (!residentId) {
      return;
    }
    setLoadingPsychotropicCompare(true);
    setPsychotropicCompareResultData(null);
    setPsychotropicCompareReportHtml("");
    setShowPsychotropicCompareDialog(true);
    try {
      const response = await backendAPIStore.compareResidentEmarPsychotropicMedicationsWithCarePlan({ residentId });
      setPsychotropicCompareResultData(response?.comparison || null);
      setPsychotropicCompareReportHtml(String(response?.reportHtml || ""));
    } catch (error: any) {
      setShowPsychotropicCompareDialog(false);
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to compare psychotropic medications.",
        life: 4500,
      });
    } finally {
      setLoadingPsychotropicCompare(false);
    }
  };

  const openDailyTrackingHistoryChartDialog = (entry: any) => {
    const trackingItemId = String(entry?.trackingItemId || "").trim();
    const matchedItem = activeDailyTrackingItems.find((item: any) => String(item?.trackingItemId || "").trim() === trackingItemId);
    const item = entry?.trackingItemSnapshot || matchedItem || {
      trackingItemId,
      name: entry?.trackingItemName || "Vital tracker",
      category: entry?.trackingItemCategory || "",
    };
    openDailyTrackingEntryDialog(
      { item, trackingItemId },
      {
        occurrenceLabel: entry?.scheduledTime || entry?.scheduledClockTime || entry?.scheduledTimeKey || "",
        scheduledTimeKey: entry?.scheduledTimeKey || "default",
        scheduledClockTime: entry?.scheduledClockTime || "",
      },
      "recorded",
      String(entry?.entryDate || todayDate).trim() || todayDate,
      false,
    );
  };

  const closeDailyTrackingEntryDialog = () => {
    if (savingDailyTrackingEntry) {
      return;
    }
    setShowDailyTrackingEntryDialog(false);
    setPendingDailyTrackingEntryAction(null);
    setDailyTrackingValueDraft({});
    setDailyTrackingNoteDraft("");
  };

  const saveDailyTrackingEntry = async () => {
    if (!residentId || !pendingDailyTrackingEntryAction?.trackingItemId) {
      return;
    }
    const status = String(pendingDailyTrackingEntryAction?.status || "recorded").trim().toLowerCase();
    const schema = getEmarDailyTrackingSchema(pendingDailyTrackingEntryAction?.item);
    if (isDailyTrackingValueEntryStatus(status)) {
      const missingField = schema.find((field: any) => field?.required && !String(dailyTrackingValueDraft[String(field?.key || "").trim()] || "").trim());
      if (missingField) {
        toast.current?.show({
          severity: "warn",
          summary: "Reading required",
          detail: `Add ${missingField.label || "the required value"} before saving this tracking entry.`,
          life: 3000,
        });
        return;
      }
    } else if (!dailyTrackingNoteDraft.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Note required",
        detail: "Add a short note before saving this tracking status.",
        life: 3000,
      });
      return;
    }

    setSavingDailyTrackingEntry(true);
    try {
      const payload = {
        residentId,
        trackingItemId: pendingDailyTrackingEntryAction.trackingItemId,
        entryDate: pendingDailyTrackingEntryAction.entryDate || todayDate,
        status,
        values: isDailyTrackingValueEntryStatus(status) ? dailyTrackingValueDraft : {},
        note: dailyTrackingNoteDraft.trim(),
        scheduledTimeKey: pendingDailyTrackingEntryAction.scheduledTimeKey || "default",
        scheduledTime: pendingDailyTrackingEntryAction.occurrenceLabel || "",
        recordedAt: String(pendingDailyTrackingEntryAction?.recordedAt || "").trim(),
        expectedVersion: Number(pendingDailyTrackingEntryAction?.entry?.version || 0),
      };
      const savedEntry = await backendAPIStore.upsertResidentEmarDailyTrackingEntry(payload);
      const recommendedActionLabel = getTrackingRecommendedActionLabel(savedEntry);
      setTodayTrackingEntries((current: any[]) => {
        const nextOccurrenceKey = getDailyTrackingEntryOccurrenceKey(savedEntry || payload);
        return [savedEntry, ...current.filter((entry: any) => getDailyTrackingEntryOccurrenceKey(entry) !== nextOccurrenceKey)];
      });
      await loadHistory();
      closeDailyTrackingEntryDialog();
      if (recommendedActionLabel) {
        setTrackingRecommendedActionDialog({
          title: recommendedActionLabel,
          message: getTrackingRecommendedActionMessage(savedEntry),
        });
      }
      toast.current?.show({
        severity: "success",
        summary: "Tracking saved",
        detail: recommendedActionLabel
          ? getTrackingRecommendedActionMessage(savedEntry)
          : "The vital tracker entry was saved.",
        life: recommendedActionLabel ? 4500 : 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to save the vital tracker entry.");
    } finally {
      setSavingDailyTrackingEntry(false);
    }
  };

  const resetMedicationList = () => {
    const activeVitalTrackerCount = activeDailyTrackingItems.length;
    if (!residentId || (activeMedications.length === 0 && activeVitalTrackerCount === 0)) {
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        "Reset the active eMAR list?",
        `This will remove ${activeMedications.length} active medication${activeMedications.length === 1 ? "" : "s"} and ${activeVitalTrackerCount} active vital tracker${activeVitalTrackerCount === 1 ? "" : "s"} so you can start fresh.`,
      ),
      header: "Reset List",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        setResettingMedicationList(true);
        try {
          await Promise.all([
            ...activeMedications.map((item: any) => backendAPIStore.archiveResidentEmarMedication(residentId, item.medicationId)),
            ...activeDailyTrackingItems.map((item: any) => backendAPIStore.deleteResidentEmarDailyTrackingItem(residentId, item.trackingItemId)),
          ]);
          await loadPage();
          toast.current?.show({
            severity: "success",
            summary: "List reset",
            detail: `${activeMedications.length} medication${activeMedications.length === 1 ? "" : "s"} and ${activeVitalTrackerCount} vital tracker${activeVitalTrackerCount === 1 ? "" : "s"} removed.`,
            life: 3000,
          });
        } catch (error: any) {
          showEmarActionError(error, "Unable to reset the list.");
        } finally {
          setResettingMedicationList(false);
        }
      },
      reject: () => { },
    });
  };

  const importFromCarePlan = async () => {
    if (!residentId) {
      return;
    }

    setImporting(true);
    try {
      const response = await backendAPIStore.importResidentEmarMedicationsFromCarePlan({
        residentId,
        previewOnly: true,
      });
      if (isMissingCarePlanMedicationResponse(response)) {
        openCarePlanRequiredDialog();
        return;
      }
      const detectedEntries = Array.isArray(response?.detectedEntries) ? response.detectedEntries : [];
      setCarePlanPreviewEntries(detectedEntries);
      setCarePlanPreviewDialogVisible(true);
    } catch (error: any) {
      handleCarePlanImportError(error);
    } finally {
      setImporting(false);
    }
  };

  const closeCarePlanPreviewDialog = () => {
    setCarePlanPreviewDialogVisible(false);
    setCarePlanPreviewEntries([]);
  };

  const confirmCarePlanImport = async () => {
    if (!residentId) {
      return;
    }

    setImporting(true);
    try {
      const readyPreviewKeys = carePlanPreviewEntries
        .filter((item: any) => !item?.duplicate)
        .map((item: any) => String(item?.previewKey || "").trim())
        .filter(Boolean);
      const response = await backendAPIStore.importResidentEmarMedicationsFromCarePlan({
        residentId,
        selectedPreviewKeys: readyPreviewKeys,
      });
      if (isMissingCarePlanMedicationResponse(response)) {
        closeCarePlanPreviewDialog();
        openCarePlanRequiredDialog();
        return;
      }
      closeCarePlanPreviewDialog();
      const medicationResponse = await backendAPIStore.getResidentEmarMedications(residentId);
      const importedMedications = mergeMedicationLists(
        Array.isArray(medicationResponse?.items) ? medicationResponse.items : [],
        Array.isArray(response?.importedItems) ? response.importedItems : [],
      );
      setMedications(importedMedications);
      await loadPage();

      const reviewScheduleSourceItems = carePlanPreviewEntries.filter((item: any) => !item?.duplicate);
      const missingScheduleMeds = getMedicationsMissingSchedule(reviewScheduleSourceItems);
      const openedBulkSchedule = openBulkScheduleDialog(importedMedications, reviewScheduleSourceItems);
      toast.current?.show({
        severity: openedBulkSchedule ? "warn" : "success",
        summary: openedBulkSchedule ? "Review med schedules" : "Care plan import complete",
        detail: openedBulkSchedule
          ? `${missingScheduleMeds.length} medication${missingScheduleMeds.length === 1 ? "" : "s"} opened with default times for review.`
          : `${response?.importedCount || 0} medication${response?.importedCount === 1 ? "" : "s"} imported. ${response?.skippedCount || 0} skipped.`,
        life: 4500,
      });
    } catch (error: any) {
      handleCarePlanImportError(error);
    } finally {
      setImporting(false);
    }
  };

  const triggerMarSheetFileImport = () => {
    marSheetFileInputRef.current?.click();
  };

  const closeMarSheetPreviewDialog = () => {
    setMarSheetPreviewDialogVisible(false);
    setMarSheetPreviewEntries([]);
    setMarSheetPreviewFileName("");
    setMarSheetPreviewWarnings([]);
    setMarSheetImportJobId("");
    setMarSheetImportJobStatus("");
    setMarSheetImportJobSubStatus("");
    setPendingMarSheetImportPayload(null);
  };

  const closeBulkScheduleDialog = (force = false) => {
    if (savingBulkSchedule && !force) {
      return;
    }
    setBulkScheduleDialogVisible(false);
    setBulkScheduleRows([]);
    setBulkScheduleApplyTimes([]);
  };

  const openBulkScheduleDialog = (items: any[], sourceItems?: any[]) => {
    const candidateItems = Array.isArray(items) ? items : [];
    const sourceCandidates = Array.isArray(sourceItems) && sourceItems.length ? sourceItems : candidateItems;
    const rows = getMedicationsMissingSchedule(sourceCandidates).map((sourceItem: any) => {
      const matchedItem = candidateItems.find((item: any) => {
        const itemMedicationId = String(item?.medicationId || "").trim();
        const sourceMedicationId = String(sourceItem?.medicationId || "").trim();
        if (itemMedicationId && sourceMedicationId && itemMedicationId === sourceMedicationId) {
          return true;
        }

        const itemName = String(item?.medicationName || "").trim().toLowerCase();
        const sourceName = String(sourceItem?.medicationName || "").trim().toLowerCase();
        const itemDosage = String(item?.dosage || "").trim().toLowerCase();
        const sourceDosage = String(sourceItem?.dosage || "").trim().toLowerCase();
        return itemName && sourceName && itemName === sourceName && itemDosage === sourceDosage;
      }) || sourceItem;

      return {
        medication: matchedItem,
        medicationId: matchedItem?.medicationId,
        medicationName: matchedItem?.medicationName || sourceItem?.medicationName || "Medication",
        dosage: matchedItem?.dosage || sourceItem?.dosage || "",
        route: matchedItem?.route || sourceItem?.route || "",
        effectiveEndDate: matchedItem?.effectiveEndDate || sourceItem?.effectiveEndDate || "",
        selectedTimes: getMedicationScheduleTimes(matchedItem).length
          ? getMedicationScheduleTimes(matchedItem)
          : DEFAULT_BULK_SCHEDULE_TIMES,
      };
    });

    if (!rows.length) {
      return false;
    }

    setBulkScheduleRows(rows);
    setBulkScheduleApplyTimes(DEFAULT_BULK_SCHEDULE_TIMES);
    setBulkScheduleDialogVisible(true);
    return true;
  };

  const closeExceptionDialog = () => {
    setShowExceptionDialog(false);
    setSelectedAttentionRow(null);
    setExceptionFollowUpDraft("");
    setSavingExceptionAction(false);
  };

  const openMedPassActionDialog = (row: any, status: "refused" | "held" | "not_given" | "missed" | "hospitalized" | "prn_given") => {
    if (["refused", "held", "not_given"].includes(status) && !canChartScheduledOccurrenceNow(row)) {
      toast.current?.show({
        severity: "warn",
        summary: "Charting not available yet",
        detail: `This action is available 1 hour before the scheduled pass time (${formatChartUnlockTimeLabel(row?.timingMeta?.scheduledDate)}).`,
        life: 3000,
      });
      return;
    }
    const isPrnMedication = row?.medication?.prn === true;
    const shouldUseExistingPrnEntry = false;
    setApplyHospitalizedToAllForDay(false);
    setPendingMedPassAction({
      medicationId: row?.medicationId,
      medicationName: row?.medication?.medicationName || row?.medicationName || "Medication",
      protocolEnabled: row?.medication?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(row?.medication?.protocolDefinitions) ? row.medication.protocolDefinitions : Array.isArray(row?.entry?.medicationSnapshot?.protocolDefinitions) ? row.entry.medicationSnapshot.protocolDefinitions : [],
      protocolDefinition: row?.medication?.protocolDefinition || row?.entry?.medicationSnapshot?.protocolDefinition || null,
      targetEntryId: shouldUseExistingPrnEntry ? (row?.entry?.entryId || "") : "",
      status,
      scheduledTimeKey: row?.scheduledTimeKey || "default",
      expectedVersion: shouldUseExistingPrnEntry ? Number(row?.entry?.version || 0) : 0,
      scheduledTime: row?.occurrenceLabel || row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
      entryDate: String(row?.entryDate || todayDate).trim() || todayDate,
      prnWorkflowStage: shouldUseExistingPrnEntry ? "follow_up" : "administer",
    });
    setMedPassActionDraft(shouldUseExistingPrnEntry ? String(row?.entry?.note || "") : "");
    setPrnReasonCode(shouldUseExistingPrnEntry ? String(row?.entry?.prnReasonCode || "") : "");
    setPrnReassessedAt(shouldUseExistingPrnEntry ? String(row?.entry?.prnReassessedAt || "") : "");
    setPrnEffectiveness(shouldUseExistingPrnEntry ? String(row?.entry?.prnEffectiveness || "") : "");
    setPrnSideEffectCode(shouldUseExistingPrnEntry ? String(row?.entry?.prnSideEffectCode || "") : "");
    setPrnSideEffectNote(shouldUseExistingPrnEntry ? String(row?.entry?.prnSideEffectNote || "") : "");
    setPrnFollowUpNote(shouldUseExistingPrnEntry ? String(row?.entry?.prnFollowUpNote || "") : "");
    initializeProtocolAssessmentState(row?.medication || row?.entry?.medicationSnapshot, shouldUseExistingPrnEntry ? row?.entry : null);
    setShowMedPassActionDialog(true);
  };

  const openAdministeredMedPassDialog = async (row: any, status: "given" | "family") => {
    const entryDate = String(row?.entryDate || todayDate).trim() || todayDate;
    const scheduledClockTime = String(row?.scheduledClockTime || row?.medication?.scheduledClockTime || "").trim();
    const givenTimeState = buildGivenTimeSelectionState({
      entryDate,
      scheduledClockTime,
      baseTimestamp: new Date().toISOString(),
      includePromptReason: true,
    });

    if (!givenTimeState.showGivenTimeChoiceDialog) {
      if (row?.medication?.protocolEnabled === true) {
        setPendingMedPassAction({
          medicationId: row?.medicationId,
          medicationName: row?.medication?.medicationName || row?.medicationName || "Medication",
          protocolEnabled: true,
          protocolDefinitions: Array.isArray(row?.medication?.protocolDefinitions) ? row.medication.protocolDefinitions : [],
          protocolDefinition: row?.medication?.protocolDefinition || null,
          status,
          scheduledTimeKey: row?.scheduledTimeKey || "default",
          expectedVersion: Number(row?.entry?.version || 0),
          scheduledTime: row?.occurrenceLabel || row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
          scheduledClockTime,
          entryDate,
          correctionMode: false,
          givenTimeChoice: givenTimeState.givenTimeChoice,
          recordedAt: givenTimeState.currentRecordedAt,
          showGivenTimeChoiceDialog: false,
        });
        setMedPassActionDraft(String(row?.entry?.note || ""));
        setApplyHospitalizedToAllForDay(false);
        setPrnReasonCode("");
        setPrnReassessedAt("");
        setPrnEffectiveness("");
        setPrnSideEffectCode("");
        setPrnSideEffectNote("");
        setPrnFollowUpNote("");
        initializeProtocolAssessmentState(row?.medication, row?.entry);
        setShowMedPassActionDialog(true);
        return;
      }
      await saveEntry({
        medicationId: row?.medicationId,
        status,
        scheduledTimeKey: row?.scheduledTimeKey || "default",
        scheduledTime: row?.occurrenceLabel || row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
        expectedVersion: Number(row?.entry?.version || 0),
        entryDate,
        recordedAt: givenTimeState.currentRecordedAt,
      });
      return;
    }

    setPendingMedPassAction({
      medicationId: row?.medicationId,
      medicationName: row?.medication?.medicationName || row?.medicationName || "Medication",
      protocolEnabled: row?.medication?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(row?.medication?.protocolDefinitions) ? row.medication.protocolDefinitions : [],
      protocolDefinition: row?.medication?.protocolDefinition || null,
      status,
      scheduledTimeKey: row?.scheduledTimeKey || "default",
      expectedVersion: Number(row?.entry?.version || 0),
      scheduledTime: row?.occurrenceLabel || row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
      scheduledClockTime,
      entryDate,
      correctionMode: false,
      givenTimeChoice: givenTimeState.givenTimeChoice,
      recordedAt: givenTimeState.givenTimeChoice === "scheduled" ? givenTimeState.scheduledRecordedAt : givenTimeState.currentRecordedAt,
      showGivenTimeChoiceDialog: givenTimeState.showGivenTimeChoiceDialog,
      givenTimePromptReason: givenTimeState.givenTimePromptReason,
      givenTimeOptions: givenTimeState.givenTimeOptions,
    });
    setMedPassActionDraft(String(row?.entry?.note || ""));
    setApplyHospitalizedToAllForDay(false);
    setPrnReasonCode("");
    setPrnReassessedAt("");
    setPrnEffectiveness("");
    setPrnSideEffectCode("");
    setPrnSideEffectNote("");
    setPrnFollowUpNote("");
    initializeProtocolAssessmentState(row?.medication, row?.entry);
    setShowMedPassActionDialog(true);
  };

  const openGivenMedPassDialog = async (row: any) => {
    if (!canChartScheduledOccurrenceNow(row)) {
      toast.current?.show({
        severity: "warn",
        summary: "Charting not available yet",
        detail: `Given is available 1 hour before the scheduled pass time (${formatChartUnlockTimeLabel(row?.timingMeta?.scheduledDate)}).`,
        life: 3000,
      });
      return;
    }
    await openAdministeredMedPassDialog(row, "given");
  };

  const openFamilyMedPassDialog = async (row: any) => {
    await openAdministeredMedPassDialog(row, "family");
  };

  const openMedPassCorrectionDialog = (entry: any) => {
    if (entry?.syntheticPending) {
      const entryDate = String(entry?.entryDate || effectiveHistoryDate || todayDate).trim() || todayDate;
      const isPrnMedication = entry?.medicationSnapshot?.prn === true || String(entry?.scheduledTimeKey || "").trim().toLowerCase() === "prn";
      const scheduledClockTime = String(entry?.scheduledClockTime || entry?.medicationSnapshot?.scheduledClockTime || "").trim();
      const givenTimeState = buildGivenTimeSelectionState({
        entryDate,
        scheduledClockTime,
        baseTimestamp: new Date().toISOString(),
      });
      setPendingMedPassAction({
        medicationId: entry?.medicationId,
        medicationName: entry?.medicationSnapshot?.medicationName || entry?.medicationName || "Medication",
        protocolEnabled: entry?.medicationSnapshot?.protocolEnabled === true,
        protocolDefinitions: Array.isArray(entry?.medicationSnapshot?.protocolDefinitions) ? entry.medicationSnapshot.protocolDefinitions : [],
        protocolDefinition: entry?.medicationSnapshot?.protocolDefinition || null,
        status: isPrnMedication ? "prn_given" : "given",
        scheduledTimeKey: String(entry?.scheduledTimeKey || (isPrnMedication ? "prn" : "default")).trim().toLowerCase(),
        expectedVersion: 0,
        scheduledTime: entry?.scheduledTime || entry?.medicationSnapshot?.schedule || "",
        scheduledClockTime,
        entryDate,
        correctionMode: false,
        historyChartMode: !isPrnMedication,
        isPrnMedication,
        prnWorkflowStage: "administer",
        givenTimeChoice: givenTimeState.givenTimeChoice,
        recordedAt: givenTimeState.givenTimeChoice === "scheduled" ? givenTimeState.scheduledRecordedAt : givenTimeState.currentRecordedAt,
        showGivenTimeChoiceDialog: !isPrnMedication,
        givenTimePromptReason: "",
        givenTimeOptions: givenTimeState.givenTimeOptions,
      });
      setMedPassActionDraft("");
      setApplyHospitalizedToAllForDay(false);
      setPrnReasonCode("");
      setPrnReassessedAt("");
      setPrnEffectiveness("");
      setPrnSideEffectCode("");
      setPrnSideEffectNote("");
      setPrnFollowUpNote("");
      initializeProtocolAssessmentState(entry?.medicationSnapshot, entry);
      setShowMedPassActionDialog(true);
      return;
    }

    const entryStatus = String(entry?.status || "").toLowerCase();
    const isPrnMedication = !!entry?.medicationSnapshot?.prn || entryStatus === "prn_given";
    const recordedAt = String(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || "");
    setPendingMedPassAction({
      originalEntryId: entry?.entryId,
      medicationId: entry?.medicationId,
      medicationName: entry?.medicationSnapshot?.medicationName || entry?.medicationName || "Medication",
      protocolEnabled: entry?.medicationSnapshot?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(entry?.medicationSnapshot?.protocolDefinitions) ? entry.medicationSnapshot.protocolDefinitions : [],
      protocolDefinition: entry?.medicationSnapshot?.protocolDefinition || null,
      status: entryStatus || (isPrnMedication ? "prn_given" : "given"),
      scheduledTimeKey: String(entry?.scheduledTimeKey || (entryStatus === "prn_given" ? "prn" : "default")).trim().toLowerCase(),
      expectedVersion: Number(entry?.version || 0),
      scheduledTime: entry?.scheduledTime || entry?.medicationSnapshot?.schedule || "",
      entryDate: getEntryRecordedLocalDate(entry) || String(entry?.entryDate || ""),
      recordedAt,
      recordedByName: String(entry?.recordedByName || entry?.recordedByRole || ""),
      correctionMode: true,
      isPrnMedication,
      prnWorkflowStage: isPrnMedication && entryStatus === "prn_given" && normalizeValue(entry?.prnReassessedAt)
        ? "follow_up"
        : "administer",
    });
    setMedPassActionDraft(String(entry?.note || ""));
    setApplyHospitalizedToAllForDay(false);
    setPrnReasonCode(String(entry?.prnReasonCode || ""));
    setPrnReassessedAt(String(entry?.prnReassessedAt || ""));
    setPrnEffectiveness(String(entry?.prnEffectiveness || ""));
    setPrnSideEffectCode(String(entry?.prnSideEffectCode || ""));
    setPrnSideEffectNote(String(entry?.prnSideEffectNote || ""));
    setPrnFollowUpNote(String(entry?.prnFollowUpNote || ""));
    initializeProtocolAssessmentState(entry?.medicationSnapshot, entry);
    setShowMedPassActionDialog(true);
  };

  const closeMedPassActionDialog = () => {
    setShowMedPassActionDialog(false);
    setMedPassActionDraft("");
    setApplyHospitalizedToAllForDay(false);
    setPrnReasonCode("");
    setPrnReassessedAt("");
    setPrnEffectiveness("");
    setPrnSideEffectCode("");
    setPrnSideEffectNote("");
    setPrnFollowUpNote("");
    setProtocolAssessmentValues({});
    setProtocolDoseValue("");
    setProtocolDecisionNote("");
    setProtocolResolution(null);
    setPendingMedPassAction(null);
  };

  const saveHospitalizedForAllScheduledMedsForDay = async () => {
    if (!residentId || !pendingMedPassAction?.entryDate) {
      return false;
    }

    const targetEntryDate = String(pendingMedPassAction.entryDate || todayDate).trim() || todayDate;
    const note = medPassActionDraft.trim();
    const targetRecordedAt = String(pendingMedPassAction?.recordedAt || "").trim();
    const hospitalizedTargets = medPassRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
      medicationId: row?.medicationId,
      scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
      scheduledTime: occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
      expectedVersion: Number(occurrence?.entry?.version || 0),
      recordedAt: targetRecordedAt,
    })));

    if (!hospitalizedTargets.length) {
      await saveEntry({
        originalEntryId: pendingMedPassAction.originalEntryId,
        targetEntryId: pendingMedPassAction.targetEntryId,
        medicationId: pendingMedPassAction.medicationId,
        status: "hospitalized",
        scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
        note,
        scheduledTime: pendingMedPassAction.scheduledTime || "",
        recordedAt: targetRecordedAt,
        expectedVersion: pendingMedPassAction.expectedVersion || 0,
        entryDate: targetEntryDate,
        correctionMode: false,
      });
      return true;
    }

    setSavingEntry(true);
    setSavingEntryKey("");
    try {
      await Promise.all(hospitalizedTargets.map((target: any) => backendAPIStore.upsertResidentEmarEntry({
        residentId,
        entryDate: targetEntryDate,
        medicationId: target.medicationId,
        status: "hospitalized",
        scheduledTimeKey: target.scheduledTimeKey,
        note,
        scheduledTime: target.scheduledTime,
        recordedAt: target.recordedAt,
        expectedVersion: target.expectedVersion || 0,
      })));
      await loadPage();
      void loadHistory();
      toast.current?.show({
        severity: "success",
        summary: "Hospital status saved",
        detail: `Marked ${hospitalizedTargets.length} scheduled medication pass${hospitalizedTargets.length === 1 ? "" : "es"} as hospitalized for ${formatEmarDateOnly(targetEntryDate)}.`,
        life: 2500,
      });
      return true;
    } catch (error: any) {
      if (error?.statusCode === 409) {
        await loadPage();
      }
      showEmarActionError(error, "Unable to save hospital status for all medications.");
      return false;
    } finally {
      setSavingEntry(false);
      setSavingEntryKey("");
    }
  };

  const saveMedPassActionDialog = async () => {
    if (!pendingMedPassAction?.medicationId || !pendingMedPassAction?.status) {
      return;
    }

    const currentStatus = String(pendingMedPassAction?.status || "").toLowerCase();
    const isCorrectionMode = pendingMedPassAction?.correctionMode === true;
    const protocolSummary = getProtocolSummaryForMedication({
      protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
      protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
    });
    const protocolAssessment = protocolSummary ? {
      parameterValues: protocolAssessmentValues,
      doseValue: protocolDoseValue.trim(),
      decisionNote: protocolDecisionNote.trim(),
      resolution: protocolResolution,
    } : undefined;

    if (protocolSummary && ["given", "prn_given"].includes(currentStatus)) {
      const missingField = (protocolSummary.parameterSchema || []).find((field: any) => field?.required && !String(protocolAssessmentValues[String(field?.key || "").trim()] || "").trim()) as any;
      if (missingField) {
        toast.current?.show({
          severity: "warn",
          summary: "Protocol assessment required",
          detail: `Add ${missingField.label || "the required assessment value"} before saving.`,
          life: 3000,
        });
        return;
      }
      if (!protocolResolution) {
        toast.current?.show({
          severity: "warn",
          summary: "Given action required",
          detail: "Enter the protocol values and show the given action before saving this charting entry.",
          life: 3000,
        });
        return;
      }
      if (protocolSummary.requiresDoseInput && currentStatus !== "held" && !protocolDoseValue.trim()) {
        toast.current?.show({
          severity: "warn",
          summary: "Resolved dose required",
          detail: "Add the resolved dose before saving this protocol medication.",
          life: 3000,
        });
        return;
      }
    }

    if (currentStatus === "prn_given") {
      const shouldValidatePrnFollowUp = !!prnReassessedAt.trim()
        || !!prnEffectiveness
        || !!prnSideEffectCode
        || !!prnSideEffectNote.trim()
        || !!prnFollowUpNote.trim();
      if (shouldValidatePrnFollowUp && (!prnReassessedAt.trim() || !prnEffectiveness || !prnSideEffectCode)) {
        toast.current?.show({
          severity: "warn",
          summary: "PRN follow-up required",
          detail: "Add the PRN reason, reassessment time, effectiveness, and side effects before saving.",
          life: 3000,
        });
        return;
      }
      if (!prnReasonCode) {
        toast.current?.show({
          severity: "warn",
          summary: "PRN reason required",
          detail: "Select the reason for this PRN medication before saving.",
          life: 3000,
        });
        return;
      }
      if (prnReasonCode === "other" && !medPassActionDraft.trim()) {
        toast.current?.show({
          severity: "warn",
          summary: "PRN note required",
          detail: "Add a note when the PRN reason is Other.",
          life: 3000,
        });
        return;
      }
      if (prnSideEffectCode === "other" && !prnSideEffectNote.trim()) {
        toast.current?.show({
          severity: "warn",
          summary: "Side effect note required",
          detail: "Add a side effect note when Side effects is Other.",
          life: 3000,
        });
        return;
      }

      await saveEntry({
        originalEntryId: pendingMedPassAction.originalEntryId,
        targetEntryId: pendingMedPassAction.targetEntryId,
        medicationId: pendingMedPassAction.medicationId,
        status: currentStatus,
        scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
        note: medPassActionDraft.trim(),
        scheduledTime: pendingMedPassAction.scheduledTime || "",
        recordedAt: pendingMedPassAction.recordedAt || "",
        expectedVersion: pendingMedPassAction.expectedVersion || 0,
        entryDate: pendingMedPassAction.entryDate || todayDate,
        correctionMode: isCorrectionMode,
        prnReasonCode,
        prnReasonLabel: PRN_REASON_OPTIONS.find((item) => item.value === prnReasonCode)?.label || "",
        prnReassessedAt: prnReassessedAt.trim(),
        prnEffectiveness,
        prnEffectivenessLabel: PRN_EFFECTIVENESS_OPTIONS.find((item) => item.value === prnEffectiveness)?.label || "",
        prnSideEffectCode,
        prnSideEffectLabel: PRN_SIDE_EFFECT_OPTIONS.find((item) => item.value === prnSideEffectCode)?.label || "",
        prnSideEffectNote: prnSideEffectNote.trim(),
        prnFollowUpNote: prnFollowUpNote.trim(),
        protocolAssessment,
      });
      closeMedPassActionDialog();
      return;
    }

    if (!["given", "family"].includes(currentStatus) && !medPassActionDraft.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Note required",
        detail: pendingMedPassAction?.status === "prn_given"
          ? "Add the PRN reason or note before saving."
          : "Add a short note before saving this action.",
        life: 3000,
      });
      return;
    }

    if (
      currentStatus === "hospitalized"
      && applyHospitalizedToAllForDay
      && pendingMedPassAction?.correctionMode !== true
      && pendingMedPassAction?.historyChartMode !== true
    ) {
      const savedAll = await saveHospitalizedForAllScheduledMedsForDay();
      if (savedAll) {
        closeMedPassActionDialog();
      }
      return;
    }

    await saveEntry({
      originalEntryId: pendingMedPassAction.originalEntryId,
      targetEntryId: pendingMedPassAction.targetEntryId,
      medicationId: pendingMedPassAction.medicationId,
      status: currentStatus,
      scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
      note: medPassActionDraft.trim(),
      scheduledTime: pendingMedPassAction.scheduledTime || "",
      recordedAt: pendingMedPassAction.recordedAt || "",
      expectedVersion: pendingMedPassAction.expectedVersion || 0,
      entryDate: pendingMedPassAction.entryDate || todayDate,
      correctionMode: isCorrectionMode,
      protocolAssessment,
    });
    closeMedPassActionDialog();
  };

  const importFromMarSheetFile = async (event: any) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";

    if (!residentId || !selectedFile) {
      return;
    }

    const extension = String(selectedFile.name.split(".").pop() || "").toLowerCase();
    if (!["docx", "txt", "pdf", "png", "jpg", "jpeg", "tif", "tiff"].includes(extension)) {
      toast.current?.show({
        severity: "warn",
        summary: "Unsupported MAR sheet",
        detail: "Use a PDF, DOCX, or TXT MAR sheet file.",
        life: 3500,
      });
      return;
    }

    setImportingMarSheetFile(true);
    setMarSheetPreviewFileName(selectedFile.name);
    setMarSheetPreviewEntries([]);
    setMarSheetPreviewWarnings([]);
    setMarSheetImportJobId("");
    setMarSheetImportJobStatus("UPLOADING");
    setMarSheetImportJobSubStatus("Uploading file");
    setMarSheetImportDialogTitle("Uploading MAR Sheet");
    setMarSheetImportProgressDialogVisible(true);
    try {
      const objectKey = `resident/${residentId}/emar-mar-import/${crypto.randomUUID()}-${selectedFile.name}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        residentId,
        ReferenceType.RESIDENT,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedFile);

      const importPayload = {
        residentId,
        fileName: selectedFile.name,
        s3ObjectKey: objectKey,
      };
      setMarSheetImportJobStatus("SUBMITTING");
      setMarSheetImportJobSubStatus("Preparing medication list");
      setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
      const response = await backendAPIStore.importResidentEmarMedicationsFromMarSheetFile({
        ...importPayload,
        previewOnly: true,
      });
      setPendingMarSheetImportPayload(importPayload);
      setMarSheetImportJobId(String(response?.jobId || "").trim());
      setMarSheetImportJobStatus(String(response?.jobStatus || "SUBMITTED").trim());
      setMarSheetImportJobSubStatus(String(response?.jobSubStatus || "Preparing medication list").trim());
      setMarSheetImportDialogTitle("Processing MAR Sheet");
    } catch (error: any) {
      setMarSheetImportProgressDialogVisible(false);
      showEmarActionError(error, "Unable to import the MAR sheet.");
    } finally {
      setImportingMarSheetFile(false);
    }
  };

  const confirmMarSheetImport = async () => {
    if (!pendingMarSheetImportPayload) {
      return;
    }

    setImportingMarSheetFile(true);
    try {
      const readyPreviewKeys = marSheetPreviewEntries
        .filter((item: any) => !item?.duplicate)
        .map((item: any) => String(item?.previewKey || "").trim())
        .filter(Boolean);
      if (!readyPreviewKeys.length) {
        toast.current?.show({
          severity: "warn",
          summary: "Nothing to import",
          detail: "This MAR sheet does not have any new medications ready to import.",
          life: 3500,
        });
        return;
      }
      const response = await backendAPIStore.importResidentEmarMedicationsFromMarSheetFile({
        ...pendingMarSheetImportPayload,
        selectedPreviewKeys: readyPreviewKeys,
      });
      closeMarSheetPreviewDialog();
      const medicationResponse = await backendAPIStore.getResidentEmarMedications(residentId!);
      const importedMedications = mergeMedicationLists(
        Array.isArray(medicationResponse?.items) ? medicationResponse.items : [],
        Array.isArray(response?.importedItems) ? response.importedItems : [],
      );
      setMedications(importedMedications);
      await loadPage();

      const openedBulkSchedule = openBulkScheduleDialog(importedMedications);
      toast.current?.show({
        severity: openedBulkSchedule ? "warn" : "success",
        summary: openedBulkSchedule ? "Review med schedules" : "MAR sheet import complete",
        detail: openedBulkSchedule
          ? `${getMedicationsMissingSchedule(importedMedications).length} medication${getMedicationsMissingSchedule(importedMedications).length === 1 ? "" : "s"} opened with default times for review.`
          : `${response?.importedCount || 0} medication${response?.importedCount === 1 ? "" : "s"} imported. ${response?.skippedCount || 0} skipped.`,
        life: 4500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to import the MAR sheet.");
    } finally {
      setImportingMarSheetFile(false);
    }
  };

  const closeMarSheetImportProgressDialog = () => {
    if (importingMarSheetFile) {
      return;
    }
    setMarSheetImportProgressDialogVisible(false);
    setMarSheetImportJobId("");
    setMarSheetImportJobStatus("");
    setMarSheetImportJobSubStatus("");
    setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
    setMarSheetImportElapsedTime(0);
  };

  const buildReportContactFormDraft = () => ({
    primaryPhysicianName: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianName,
      carePlanSummaryData?.primaryPhysicianName,
      residentDetails?.primaryPhysicianName,
    ),
    primaryPhysicianClinic: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianClinic,
      carePlanSummaryData?.primaryPhysicianClinic,
      residentDetails?.primaryPhysicianClinic,
    ),
    primaryPhysicianPhone: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianPhone,
      carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
      carePlanSummaryData?.primaryPhysicianPhone,
      carePlanSummaryData?.primaryPhysicianPhoneNumber,
      residentDetails?.primaryPhysicianPhone,
      residentDetails?.primaryPhysicianPhoneNumber,
    ),
    primaryPhysicianFax: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianFax,
      carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
      carePlanSummaryData?.primaryPhysicianFax,
      carePlanSummaryData?.primaryPhysicianFaxNumber,
      residentDetails?.primaryPhysicianFax,
      residentDetails?.primaryPhysicianFaxNumber,
    ),
    primaryPhysicianEmail: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianEmail,
      carePlanSummaryData?.primaryPhysicianEmail,
      residentDetails?.primaryPhysicianEmail,
    ),
    primaryPhysicianAddress: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.primaryPhysicianAddress,
      carePlanSummaryData?.primaryPhysicianAddress,
      residentDetails?.primaryPhysicianAddress,
    ),
    pharmacyName: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.pharmacyName,
      carePlanSummaryData?.pharmacyName,
      residentDetails?.pharmacyName,
    ),
    pharmacyPhone: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.pharmacyPhone,
      carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
      carePlanSummaryData?.pharmacyPhone,
      carePlanSummaryData?.pharmacyPhoneNumber,
      residentDetails?.pharmacyPhone,
      residentDetails?.pharmacyPhoneNumber,
    ),
    pharmacyFax: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.pharmacyFax,
      carePlanResponsiblePartiesData?.pharmacyFaxNumber,
      carePlanSummaryData?.pharmacyFax,
      carePlanSummaryData?.pharmacyFaxNumber,
      residentDetails?.pharmacyFax,
      residentDetails?.pharmacyFaxNumber,
    ),
    pharmacyEmail: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.pharmacyEmail,
      carePlanSummaryData?.pharmacyEmail,
      residentDetails?.pharmacyEmail,
    ),
    pharmacyAddress: getFirstNonEmptyReportFieldValue(
      carePlanResponsiblePartiesData?.pharmacyAddress,
      carePlanSummaryData?.pharmacyAddress,
      residentDetails?.pharmacyAddress,
    ),
  });

  const buildResolvedReportContactState = (source: any) => ({
    primaryPhysicianName: String(source?.primaryPhysicianName || "").trim(),
    primaryPhysicianClinic: String(source?.primaryPhysicianClinic || "").trim(),
    primaryPhysicianPhone: String(source?.primaryPhysicianPhone || source?.primaryPhysicianPhoneNumber || "").trim(),
    primaryPhysicianPhoneNumber: String(source?.primaryPhysicianPhone || source?.primaryPhysicianPhoneNumber || "").trim(),
    primaryPhysicianFax: String(source?.primaryPhysicianFax || source?.primaryPhysicianFaxNumber || "").trim(),
    primaryPhysicianFaxNumber: String(source?.primaryPhysicianFax || source?.primaryPhysicianFaxNumber || "").trim(),
    primaryPhysicianEmail: String(source?.primaryPhysicianEmail || "").trim(),
    primaryPhysicianAddress: String(source?.primaryPhysicianAddress || "").trim(),
    pharmacyName: String(source?.pharmacyName || "").trim(),
    pharmacyPhone: String(source?.pharmacyPhone || source?.pharmacyPhoneNumber || "").trim(),
    pharmacyPhoneNumber: String(source?.pharmacyPhone || source?.pharmacyPhoneNumber || "").trim(),
    pharmacyFax: String(source?.pharmacyFax || source?.pharmacyFaxNumber || "").trim(),
    pharmacyFaxNumber: String(source?.pharmacyFax || source?.pharmacyFaxNumber || "").trim(),
    pharmacyEmail: String(source?.pharmacyEmail || "").trim(),
    pharmacyAddress: String(source?.pharmacyAddress || "").trim(),
  });

  const saveBulkScheduleAssignments = async () => {
    if (!residentId || !bulkScheduleRows.length) {
      closeBulkScheduleDialog(true);
      return;
    }

    const rowsToSave = bulkScheduleRows.filter((row: any) => row?.medicationId && Array.isArray(row?.selectedTimes) && row.selectedTimes.length > 0);
    if (!rowsToSave.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Schedule needed",
        detail: "Select one or more med times for at least one medication before saving.",
        life: 3000,
      });
      return;
    }

    setSavingBulkSchedule(true);
    try {
      await Promise.all(rowsToSave.map((row: any) => {
        const scheduleFields = buildFormScheduleFields(row.selectedTimes, {
          scheduleDayPattern: inferMedicationScheduleDayPattern(row.medication),
          scheduleDays: getMedicationScheduleDays(row.medication),
        });
        return backendAPIStore.updateResidentEmarMedication({
          ...row.medication,
          residentId,
          medicationId: row.medicationId,
          effectiveEndDate: row.effectiveEndDate,
          ...scheduleFields,
        });
      }));

      closeBulkScheduleDialog();
      await loadPage();
      toast.current?.show({
        severity: "success",
        summary: "Medication times saved",
        detail: `${rowsToSave.length} medication${rowsToSave.length === 1 ? "" : "s"} updated with schedule times.`,
        life: 3500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to save medication schedules.");
    } finally {
      setSavingBulkSchedule(false);
    }
  };

  useEffect(() => {
    if (!marSheetImportJobId || !marSheetImportProgressDialogVisible) {
      return;
    }

    let cancelled = false;

    const pollMarSheetImportJob = async () => {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (cancelled) {
          return;
        }

        try {
          const jobDetails = await backendAPIStore.getJobDetails(marSheetImportJobId);
          const nextStatus = String(jobDetails?.jobStatus || "").trim();
          const normalizedStatus = nextStatus.toUpperCase();
          const nextSubStatus = String(jobDetails?.jobSubStatus || "").trim();
          if (!cancelled) {
            setMarSheetImportJobStatus(nextStatus);
            setMarSheetImportJobSubStatus(nextSubStatus);
          }

          if (normalizedStatus === "COMPLETED") {
            if (!cancelled) {
              const previewEntries = Array.isArray(jobDetails?.marSheetPreviewEntries) ? jobDetails.marSheetPreviewEntries : [];
              const warnings = Array.isArray(jobDetails?.marSheetPreviewWarnings) ? jobDetails.marSheetPreviewWarnings : [];
              setPendingMarSheetImportPayload((current: any) => current ? ({ ...current, preparedEntries: previewEntries }) : current);
              setMarSheetPreviewEntries(previewEntries);
              setMarSheetPreviewWarnings(warnings);
              setMarSheetPreviewFileName(String(jobDetails?.sourceFileName || pendingMarSheetImportPayload?.fileName || "").trim());
              setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
              setMarSheetImportProgressDialogVisible(false);
              setMarSheetPreviewDialogVisible(true);
              setImportingMarSheetFile(false);
            }
            return;
          }

          if (normalizedStatus === "FAILED") {
            if (!cancelled) {
              setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
              setMarSheetImportProgressDialogVisible(false);
              setImportingMarSheetFile(false);
              toast.current?.show({
                severity: "error",
                summary: "MAR sheet import failed",
                detail: String(jobDetails?.errorMessage || "Unable to process the MAR sheet."),
                life: 5000,
              });
            }
            return;
          }
        } catch (error: any) {
          if (!cancelled) {
            setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
            setMarSheetImportProgressDialogVisible(false);
            setImportingMarSheetFile(false);
            toast.current?.show({
              severity: "error",
              summary: getErrorTitle(error),
              detail: error?.message || "Unable to check the MAR sheet import progress.",
              life: 4000,
            });
          }
          return;
        }

        await sleep(attempt < 10 ? 2000 : 3000);
      }

      if (!cancelled) {
        setMarSheetImportDialogTitle("Preparing MAR Sheet Upload");
        setMarSheetImportProgressDialogVisible(false);
        setImportingMarSheetFile(false);
        toast.current?.show({
          severity: "warn",
          summary: "MAR sheet is still processing",
          detail: "The import is taking longer than expected. Check back in a moment and try again.",
          life: 5000,
        });
      }
    };

    void pollMarSheetImportJob();

    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, marSheetImportJobId, marSheetImportProgressDialogVisible, pendingMarSheetImportPayload?.fileName]);

  useEffect(() => {
    if (!marSheetImportProgressDialogVisible) {
      setMarSheetImportElapsedTime(0);
      return;
    }

    const startTime = Date.now();
    setMarSheetImportElapsedTime(0);

    const timer = window.setInterval(() => {
      setMarSheetImportElapsedTime(Math.round((Date.now() - startTime) / 1000));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [marSheetImportProgressDialogVisible]);

  const saveEntry = async (payload: any) => {
    if (!residentId) {
      return;
    }

    const matchingMedication = activeMedications.find((item: any) => String(item?.medicationId || "").trim() === String(payload?.medicationId || "").trim());
    if (matchingMedication?.effectiveEndDate && isMedicationExpired(matchingMedication.effectiveEndDate)) {
      toast.current?.show({
        severity: "warn",
        summary: "Medication expired",
        detail: `This medication expired on ${matchingMedication.effectiveEndDate}. Update the medication before charting another med pass.`,
        life: 4000,
      });
      return;
    }

    const targetEntryDate = String(payload?.entryDate || todayDate).trim() || todayDate;
    const targetRecordedAt = String(payload?.recordedAt || "").trim();
    if (isFutureChartDate(targetEntryDate)) {
      toast.current?.show({
        severity: "warn",
        summary: "Future charting not allowed",
        detail: "Choose today or a past date before saving this medication entry.",
        life: 3000,
      });
      return;
    }
    if (targetRecordedAt && isFutureChartTimestamp(targetRecordedAt)) {
      toast.current?.show({
        severity: "warn",
        summary: "Future charting not allowed",
        detail: "Choose a current or past charting time before saving this medication entry.",
        life: 3000,
      });
      return;
    }
    const nextSavingKey = `${String(payload?.medicationId || "").trim()}::${String(payload?.scheduledTimeKey || (String(payload?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase()}`;
    setSavingEntryKey(nextSavingKey);
    setSavingEntry(true);
    try {
      const savedEntry = await backendAPIStore.upsertResidentEmarEntry({
        residentId,
        entryDate: targetEntryDate,
        ...payload,
      });
      const shouldReloadTodayEntries = String(savedEntry?.status || payload?.status || "").trim().toLowerCase() === "prn_given";
      if (payload?.correctionMode === true || targetEntryDate !== todayDate || shouldReloadTodayEntries) {
        await loadPage();
      } else if (targetEntryDate === todayDate) {
        setTodayEntries((previousEntries: any[]) => {
          const nextOccurrenceKey = getEmarEntryOccurrenceKey(savedEntry || payload);
          const normalizedEntryId = String(savedEntry?.entryId || "").trim();
          const savedIsPrnEntry = String(savedEntry?.status || payload?.status || "").trim().toLowerCase() === "prn_given";
          const filteredEntries = previousEntries.filter((item: any) => {
            if (normalizedEntryId && String(item?.entryId || "").trim() === normalizedEntryId) {
              return false;
            }
            if (savedIsPrnEntry) {
              return true;
            }
            return getEmarEntryOccurrenceKey(item) !== nextOccurrenceKey;
          });
          return sortEmarEntriesByLatest([savedEntry, ...filteredEntries]);
        });
      }
      setSavingEntry(false);
      setSavingEntryKey("");
      void loadHistory();
      toast.current?.show({
        severity: "success",
        summary: payload?.correctionMode === true || targetEntryDate !== todayDate ? "Entry corrected" : "Medication updated",
        detail: "Today’s eMAR entry was saved.",
        life: 2000,
      });
    } catch (error: any) {
      if (error?.statusCode === 409) {
        await loadPage();
      }
      showEmarActionError(error, "Unable to save medication entry.");
    } finally {
      setSavingEntry(false);
      setSavingEntryKey("");
    }
  };

  const markAttentionReviewed = async (row: any) => {
    if (!residentId || !row?.entryId) {
      return;
    }

    setSavingExceptionAction(true);
    try {
      await backendAPIStore.updateResidentEmarEntryException({
        residentId,
        entryId: row.entryId,
        action: "review",
      });
      await loadPage();
      await loadHistory();
      toast.current?.show({
        severity: "success",
        summary: "Marked reviewed",
        detail: "This medication exception was marked as reviewed.",
        life: 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to update this medication exception.");
    } finally {
      setSavingExceptionAction(false);
    }
  };

  const openExceptionDialog = (row: any, mode: "follow_up" | "resolve") => {
    setSelectedAttentionRow(row);
    setExceptionDialogMode(mode);
    setExceptionFollowUpDraft(String(row?.followUpNote || "").trim());
    setShowExceptionDialog(true);
  };

  const openHistoryDialog = (entry: any) => {
    setSelectedHistoryEntry(entry);
    setShowHistoryDialog(true);
  };

  const markPastEntryDeleted = async () => {
    if (!residentId || !entryToDelete?.entryId || deletingEntry) {
      return;
    }
    setDeletingEntry(true);
    try {
      await backendAPIStore.updateResidentEmarEntryException({
        residentId,
        entryId: entryToDelete.entryId,
        action: "delete",
      });
      setEntryToDelete(null);
      await loadPage();
      await loadHistory();
      toast.current?.show({
        severity: "success",
        summary: "Entry marked deleted",
        detail: "This entry was soft-deleted and will be excluded from the eMAR report.",
        life: 3000,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to mark this eMAR entry deleted.");
    } finally {
      setDeletingEntry(false);
    }
  };

  const deletePastEntryButton = (entry: any) => (
    !entry?.syntheticPending && !isDailyTrackingHistoryEntry(entry) ? (
      <Button
        type="button"
        label="Delete"
        size="small"
        rounded
        severity="danger"
        outlined
        icon="pi pi-trash"
        disabled={!canEditPastEntry(entry)}
        onClick={() => setEntryToDelete(entry)}
      />
    ) : null
  );

  const saveExceptionDialogAction = async () => {
    if (!residentId || !selectedAttentionRow?.entryId) {
      return;
    }

    if (!exceptionFollowUpDraft.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: exceptionDialogMode === "resolve" ? "Resolution note required" : "Follow-up note required",
        detail: "Add a short provider follow-up note before saving.",
        life: 3000,
      });
      return;
    }

    setSavingExceptionAction(true);
    try {
      await backendAPIStore.updateResidentEmarEntryException({
        residentId,
        entryId: selectedAttentionRow.entryId,
        action: exceptionDialogMode,
        followUpNote: exceptionFollowUpDraft,
      });
      closeExceptionDialog();
      await loadPage();
      await loadHistory();
      toast.current?.show({
        severity: "success",
        summary: exceptionDialogMode === "resolve" ? "Exception resolved" : "Follow-up note saved",
        detail: exceptionDialogMode === "resolve"
          ? "This medication exception was resolved."
          : "The provider follow-up note was saved.",
        life: 2500,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to update this medication exception.");
    } finally {
      setSavingExceptionAction(false);
    }
  };

  const saveGeneratedObjectKeyToResidentBinder = async (input: {
    folderCode: string;
    title: string;
    fileName: string;
    objectKey: string;
    mimeType: string;
  }) => {
    if (!residentId) {
      return;
    }

    const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
    const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
    if (!bundle.binder?.binderId || !targetFolder?.folderId) {
      throw new Error("Resident binder folder is not available.");
    }

    await upsertBinderDocumentWithVersion({
      backendAPIStore,
      binderId: bundle.binder.binderId,
      folderId: targetFolder.folderId,
      folderName: targetFolder.folderName || input.title,
      referenceId: residentId,
      title: input.title,
      fileName: input.fileName,
      mimeType: input.mimeType,
      existingObjectKey: input.objectKey,
      existingDocuments: bundle.documents,
    });
  };

  const generateReport = async (addToBinder: boolean) => {
    if (!residentId) {
      return;
    }

    setGeneratingReport(true);
    try {
      await backendAPIStore.init();
      const selectedMonth = reportMonthSelection instanceof Date && !Number.isNaN(reportMonthSelection.getTime())
        ? reportMonthSelection
        : today;
      const monthlyStartDate = getIsoDateOnly(getMonthStart(selectedMonth));
      const monthlyEndDate = getIsoDateOnly(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0));
      const chartStartDate = reportRangeMode === "monthly"
        ? monthlyStartDate
        : String(reportStartDateSelection || "").trim();
      const chartEndDate = reportRangeMode === "monthly"
        ? monthlyEndDate
        : String(reportEndDateSelection || "").trim();
      const totalDays = getInclusiveDateRangeDayCount(chartStartDate, chartEndDate);
      if (!chartStartDate || !chartEndDate) {
        throw new Error("Select both a start date and end date for this eMAR report.");
      }
      if (!totalDays) {
        throw new Error("The report end date must be the same as or after the start date.");
      }
      if (totalDays > 31) {
        throw new Error("The eMAR report range must be 31 days or less.");
      }
      const resolvedHomeId = String(residentDetails?.homeId || new URLSearchParams(location.search).get("homeId") || "").trim();

      if (!resolvedHomeId) {
        throw new Error("Home ID is required before generating this eMAR report.");
      }

      const response = await backendAPIStore.generateResidentEmarMarReportDocument({
        residentId,
        homeId: resolvedHomeId,
        chartStartDate,
        chartEndDate,
        includeDailyTracking: true,
        blankReport: blankMarReport,
      });

      if (!response?.documentUrl) {
        throw new Error("The eMAR report was generated, but no download link was returned.");
      }

      if (addToBinder) {
        if (!response?.outputKey) {
          throw new Error("The eMAR report is missing binder save details.");
        }
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.MEDICATION_LOGS,
          title: "eMAR Medication Administration Report",
          fileName: response.fileName || `EmarMarReport_${new Date().toISOString()}.docx`,
          objectKey: response.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        toast.current?.show({
          severity: "success",
          summary: "Saved",
          detail: `The ${blankMarReport ? "blank " : ""}eMAR report was saved to the resident binder.`,
          life: 3000,
        });
      } else {
        window.open(response.documentUrl, "_blank", "noopener,noreferrer");
        toast.current?.show({
          severity: "success",
          summary: "Report ready",
          detail: response?.fileName || `The ${blankMarReport ? "blank " : ""}eMAR report is ready to download.`,
          life: 3000,
        });
      }
      window.setTimeout(() => {
        window.location.reload();
      }, 250);
    } catch (error: any) {
      showEmarActionError(error, "Unable to generate this report.");
    } finally {
      setGeneratingReport(false);
    }
  };

  const showGenerateReportConfirmation = () => {
    const missingProfileFields = getMissingReportProfileFields();
    if (missingProfileFields.length > 0) {
      setReportContactForm(buildReportContactFormDraft());
      setReportContactErrors({});
      setShowReportDetailsDialog(true);
      return;
    }

    setReportRangeMode("monthly");
    setReportMonthSelection(today);
    setReportStartDateSelection(monthStartDate);
    setReportEndDateSelection(todayDate);
    setReportExactDateSelection([getMonthStart(today), today]);
    setShowGenerateReportDialog(true);
  };

  useEffect(() => {
    const handleOpenAddItemDialog = () => {
      setShowAddItemDialog(true);
    };
    const handleImportFromCarePlan = () => {
      void importFromCarePlan();
    };
    const handleImportFromMarSheet = () => {
      triggerMarSheetFileImport();
    };
    const handleGenerateReport = () => {
      showGenerateReportConfirmation();
    };

    window.addEventListener("resident-emar:open-add-item-dialog", handleOpenAddItemDialog as EventListener);
    window.addEventListener("resident-emar:import-care-plan", handleImportFromCarePlan as EventListener);
    window.addEventListener("resident-emar:import-mar-sheet", handleImportFromMarSheet as EventListener);
    window.addEventListener("resident-emar:request-generate-report", handleGenerateReport as EventListener);

    return () => {
      window.removeEventListener("resident-emar:open-add-item-dialog", handleOpenAddItemDialog as EventListener);
      window.removeEventListener("resident-emar:import-care-plan", handleImportFromCarePlan as EventListener);
      window.removeEventListener("resident-emar:import-mar-sheet", handleImportFromMarSheet as EventListener);
      window.removeEventListener("resident-emar:request-generate-report", handleGenerateReport as EventListener);
    };
  }, [showGenerateReportConfirmation, importFromCarePlan, triggerMarSheetFileImport]);

  const getMissingReportProfileFields = () => {
    const resolvedContactState = buildResolvedReportContactState(buildReportContactFormDraft());
    const physicianFields = [
      ["Physician name", resolvedContactState.primaryPhysicianName],
    ];
    const pharmacyFields = [
      ["Pharmacy name", resolvedContactState.pharmacyName],
    ];

    return [...physicianFields, ...pharmacyFields]
      .filter(([, value]) => !String(value || "").trim())
      .map(([label]) => label);
  };

  const saveReportDetailsAndContinue = async () => {
    if (!residentId || !residentDetails) {
      return;
    }

    const { normalizedContacts, errors: nextContactErrors } = validateCarePlanContactsForm(reportContactForm);
    if (Object.keys(nextContactErrors).length > 0) {
      setReportContactErrors(nextContactErrors);
      toast.current?.show({
        severity: "warn",
        summary: "Complete required fields",
        detail: "Add Physician Name and Pharmacy Name, then fix any highlighted contact fields before saving.",
        life: 3500,
      });
      return;
    }
    setReportContactErrors({});

    setSavingReportDetails(true);
    try {
      const payload = {
        ...residentDetails,
        primaryPhysicianName: String(reportContactForm?.primaryPhysicianName || "").trim(),
        primaryPhysicianClinic: String(reportContactForm?.primaryPhysicianClinic || "").trim(),
        primaryPhysicianPhone: normalizedContacts.primaryPhysicianPhone,
        primaryPhysicianPhoneNumber: normalizedContacts.primaryPhysicianPhone,
        primaryPhysicianFax: normalizedContacts.primaryPhysicianFax,
        primaryPhysicianFaxNumber: normalizedContacts.primaryPhysicianFax,
        primaryPhysicianEmail: normalizedContacts.primaryPhysicianEmail,
        primaryPhysicianAddress: String(reportContactForm?.primaryPhysicianAddress || "").trim(),
        pharmacyName: String(reportContactForm?.pharmacyName || "").trim(),
        pharmacyPhone: normalizedContacts.pharmacyPhone,
        pharmacyPhoneNumber: normalizedContacts.pharmacyPhone,
        pharmacyFax: normalizedContacts.pharmacyFax,
        pharmacyFaxNumber: normalizedContacts.pharmacyFax,
        pharmacyEmail: normalizedContacts.pharmacyEmail,
        pharmacyAddress: String(reportContactForm?.pharmacyAddress || "").trim(),
      };

      const updatedResident = await backendAPIStore.updateResident(payload);
      const resolvedContactState = buildResolvedReportContactState(updatedResident || payload);
      setResidentDetails((current: any) => ({
        ...(current || {}),
        ...(updatedResident || payload),
        ...resolvedContactState,
      }));
      setCarePlanResponsiblePartiesData((current: any) => ({
        ...(current || {}),
        ...resolvedContactState,
      }));
      setCarePlanSummaryData((current: any) => ({
        ...(current || {}),
        ...resolvedContactState,
      }));
      setReportContactForm((current: any) => ({
        ...(current || {}),
        primaryPhysicianName: resolvedContactState.primaryPhysicianName,
        primaryPhysicianClinic: resolvedContactState.primaryPhysicianClinic,
        primaryPhysicianPhone: resolvedContactState.primaryPhysicianPhone,
        primaryPhysicianFax: resolvedContactState.primaryPhysicianFax,
        primaryPhysicianEmail: resolvedContactState.primaryPhysicianEmail,
        primaryPhysicianAddress: resolvedContactState.primaryPhysicianAddress,
        pharmacyName: resolvedContactState.pharmacyName,
        pharmacyPhone: resolvedContactState.pharmacyPhone,
        pharmacyFax: resolvedContactState.pharmacyFax,
        pharmacyEmail: resolvedContactState.pharmacyEmail,
        pharmacyAddress: resolvedContactState.pharmacyAddress,
      }));
      setShowReportDetailsDialog(false);
      setReportRangeMode("monthly");
      setReportMonthSelection(today);
      setReportStartDateSelection(monthStartDate);
      setReportEndDateSelection(todayDate);
      setReportExactDateSelection([getMonthStart(today), today]);
      setShowGenerateReportDialog(true);
      toast.current?.show({
        severity: "success",
        summary: "Details saved",
        detail: "The missing MAR report details were saved. Continue with the report below.",
        life: 3000,
      });
    } catch (error: any) {
      showEmarActionError(error, "Unable to save the report details.");
    } finally {
      setSavingReportDetails(false);
    }
  };

  const statusBodyTemplate = (row: any) => (
    <Tag
      value={
        row?.active === false
          ? "Archived"
          : row?.discontinued === true
            ? "Discontinued"
            : isMedicationExpired(row?.effectiveEndDate)
              ? "Expired"
              : isMarOnlyMedication(row)
                ? "MAR-Only"
                : "Active"
      }
      severity={
        row?.active === false
          ? "secondary"
          : row?.discontinued === true
            ? "warning"
            : isMedicationExpired(row?.effectiveEndDate)
              ? "danger"
              : isMarOnlyMedication(row)
                ? "info"
                : "success"
      }
      rounded
    />
  );

  const sourceBodyTemplate = (row: any) => (
    <span style={{ color: "#475569", fontWeight: 600 }}>
      {row?.sourceLabel || (row?.sourceType === "care_plan" ? "Care Plan import" : "Manual")}
    </span>
  );

  const detailsBodyTemplate = (row: any) => {
    const protocolSummary = getEmarProtocolSummary(row);

    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.medicationName || "Medication"}</div>
        {[row?.dosage, row?.route].filter(Boolean).length > 0 ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
            {[row?.dosage, row?.route].filter(Boolean).join(" • ") || "Add dose or route"}
          </div>
        ) : null}
        {row?.dosage ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Dose:</strong> {row.dosage}
          </div>
        ) : null}
        {row?.route ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Route:</strong> {row.route}
          </div>
        ) : null}
        {getMedicationScheduleTimes(row).length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Schedule:</strong> {buildMedicationScheduleText(row)}
          </div>
        ) : null}
        {row?.effectiveEndDate ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", color: getMedicationExpiryAlert(row?.effectiveEndDate)?.color || appMutedTextStyle.color }}>
            <strong style={{ color: getMedicationExpiryAlert(row?.effectiveEndDate)?.color || "#334155" }}>
              {isMedicationExpired(row?.effectiveEndDate) ? "Expired:" : "Expiration:"}
            </strong> {row.effectiveEndDate}
          </div>
        ) : null}
        {isMarOnlyMedication(row) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Type:</strong> MAR-Only item
          </div>
        ) : null}
        {row?.discontinued === true && row?.discontinuedAt ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", color: "#92400e" }}>
            <strong style={{ color: "#92400e" }}>Discontinued:</strong> {row.discontinuedAt}
          </div>
        ) : null}
        {row?.discontinued === true && row?.discontinueReason ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Discontinue reason:</strong> {row.discontinueReason}
          </div>
        ) : null}
        {row?.indication ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {row.indication}
          </div>
        ) : null}
        {row?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Instructions:</strong> {row.instructions}
          </div>
        ) : null}
        {row?.handlingNotes ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Handling notes:</strong> {row.handlingNotes}
          </div>
        ) : null}
        {protocolSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <span style={{ color: "#334155", fontWeight: 700 }}>
              {`Protocol: ${String(protocolSummary.label || "Protocol")}${protocolSummary.guidanceAction ? ` | ${String(protocolSummary.guidanceAction || "").replaceAll("_", " ")}` : ""}`}
            </span>
          </div>
        ) : null}
        {protocolSummary?.parameterSchema?.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Required assessment:</strong> {protocolSummary.parameterSchema.map((field: any) => field?.label || field?.key).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {protocolSummary?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Protocol instructions:</strong> {protocolSummary.instructions}
          </div>
        ) : null}
        {row?.psychotropicDrugClass ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Psychotropic:</strong> {row.psychotropicDrugClass}
          </div>
        ) : null}
        {String(row?.modifiedAt || row?.createdAt || "").trim() ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Last updated:</strong> {formatEmarDateTime(row?.modifiedAt || row?.createdAt)}
          </div>
        ) : null}
        {row?.active === false && formatMedicationArchiveReason(row) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            <strong style={{ color: "#334155" }}>Delete reason:</strong> {formatMedicationArchiveReason(row)}
          </div>
        ) : null}
        {row?.active === false && row?.archiveNote ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Delete note:</strong> {row.archiveNote}
          </div>
        ) : null}
        {row?.prn ? (
          <div className="mt-2">
            <Tag value="PRN" severity="warning" rounded />
          </div>
        ) : null}
        {isMarOnlyMedication(row) ? (
          <div className="mt-2">
            <Tag value="MAR-Only" severity="info" rounded />
          </div>
        ) : null}
        {row?.discontinued === true ? (
          <div className="mt-2">
            <Tag value="Discontinued" severity="warning" rounded />
          </div>
        ) : null}
        {row?.psychotropicDrugClass ? (
          <div className="mt-2">
            <Tag value="Psychotropic" severity="danger" rounded />
          </div>
        ) : null}
        {protocolSummary ? (
          <div className="mt-2">
            <Tag value={String(protocolSummary.label || "Protocol")} severity="info" rounded />
          </div>
        ) : null}
        {getMedicationExpiryAlert(row?.effectiveEndDate) ? (
          <div className="mt-2">
            <Tag value={getMedicationExpiryAlert(row?.effectiveEndDate)?.label || ""} severity={getMedicationExpiryAlert(row?.effectiveEndDate)?.severity as any} rounded />
          </div>
        ) : null}
      </div>
    );
  };

  const actionBodyTemplate = (row: any) => (
    <div className="flex gap-2 flex-wrap justify-content-end">
      <Button
        type="button"
        label={row?.systemDefault === true ? "Set up" : "Edit"}
        rounded
        outlined
        severity="info"
        icon="pi pi-pencil"
        onClick={() => openEditDialog(row)}
      />
      {row?.active === false || !String(row?.medicationId || "").trim() ? null : (
        <Button
          type="button"
          label="Delete"
          rounded
          outlined
          severity="danger"
          icon="pi pi-trash"
          tooltip={deleteItemTooltipMessage}
          tooltipOptions={{ position: "top" }}
          onClick={() => {
            confirmDialog({
              ...appConfirmDialogClasses,
              message: renderConfirmMessage(
                "Delete Medication",
                `Move ${row?.medicationName || "this medication"} out of the active med list?`,
              ),
              header: "Delete Medication",
              icon: "pi pi-trash",
              acceptClassName: "p-button-danger",
              accept: async () => {
                try {
                  await backendAPIStore.archiveResidentEmarMedication(residentId, row.medicationId);
                  await loadPage();
                  toast.current?.show({
                    severity: "success",
                    summary: "Medication deleted",
                    detail: `${row?.medicationName || "Medication"} was moved out of the active list.`,
                    life: 2500,
                  });
                } catch (error: any) {
                  showEmarActionError(error, "Unable to delete medication.");
                }
              },
            });
          }}
        />
      )}
    </div>
  );

  const buildMedPassRows = (medicationsForPass: any[]) => {
    const rows = medicationsForPass.map((medication: any) => {
      const medicationId = String(medication?.medicationId || "").trim();
      const occurrences = getMedicationScheduleOccurrences(medication, todayDate).map((occurrence: any) => {
        const entry = todayEntryByOccurrenceKey[`${medicationId}::${String(occurrence?.scheduledTimeKey || "default").toLowerCase()}`];
        const entryStatus = String(entry?.status || "").trim().toLowerCase();
        const timingMeta = getTodayMedicationTimingMeta({
          schedule: occurrence?.scheduleText || medication?.schedule || "",
          scheduledClockTime: occurrence?.scheduledClockTime || "",
          prn: medication?.prn,
        }, !!entry);

        return {
          medication,
          medicationId,
          entry,
          entryStatus,
          timingMeta,
          scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
          scheduledClockTime: occurrence?.scheduledClockTime || "",
          occurrenceLabel: occurrence?.scheduledLabel || "",
        };
      });

      return {
        medication,
        medicationId,
        occurrences,
      };
    });

    return rows.sort((first: any, second: any) => (
      String(first?.medication?.medicationName || "").localeCompare(String(second?.medication?.medicationName || ""))
    ));
  };

  const medPassRows = useMemo(
    () => buildMedPassRows(activeNonPrnMedicationsScheduledToday),
    [activeNonPrnMedicationsScheduledToday, todayEntryByOccurrenceKey],
  );
  const todayPassOccurrenceRows = useMemo(
    () => medPassRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
      medication: row.medication,
      medicationId: row.medicationId,
      occurrences: [occurrence],
    }))),
    [medPassRows],
  );
  const prnMedPassRows = useMemo(
    () => buildMedPassRows(activePrnMedicationsForMedPass),
    [activePrnMedicationsForMedPass, todayEntryByOccurrenceKey],
  );
  const prnFollowUpPendingCount = useMemo(
    () => prnMedPassRows.reduce(
      (total: number, row: any) => total + row.occurrences.filter((occurrence: any) => isPrnFollowUpPending(occurrence)).length,
      0,
    ),
    [prnMedPassRows],
  );
  const buildDailyTrackingRows = (itemsForToday: any[]) => {
    const rows = itemsForToday
      .map((item: any) => {
        const trackingItemId = String(item?.trackingItemId || "").trim();
        const occurrences = getMedicationScheduleOccurrences(item, todayDate).map((occurrence: any) => {
          const entry = todayDailyTrackingEntryByOccurrenceKey[`${trackingItemId}::${String(occurrence?.scheduledTimeKey || "default").toLowerCase()}`];
          const entryStatus = String(entry?.status || "").trim().toLowerCase();
          const timingMeta = getTodayMedicationTimingMeta({
            schedule: occurrence?.scheduleText || item?.schedule || "",
            scheduledClockTime: occurrence?.scheduledClockTime || "",
            prn: false,
          }, !!entry);
          return {
            item,
            trackingItemId,
            entry,
            entryStatus,
            timingMeta,
            scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
            scheduledClockTime: occurrence?.scheduledClockTime || "",
            occurrenceLabel: occurrence?.scheduledLabel || "",
          };
        });
        return {
          item,
          trackingItemId,
          occurrences,
        };
      })
      .filter((row: any) => row.occurrences.length > 0);
    return rows.sort((first: any, second: any) => String(first?.item?.name || "").localeCompare(String(second?.item?.name || "")));
  };
  const dailyTrackingRows = useMemo(
    () => buildDailyTrackingRows(activeDailyTrackingItems),
    [activeDailyTrackingItems, todayDailyTrackingEntryByOccurrenceKey, todayDate],
  );
  const filteredMedPassRows = useMemo(
    () => filterEmarPassRows(medPassRows, todayPassFilter),
    [medPassRows, todayPassFilter],
  );
  const filteredDailyTrackingRows = useMemo(
    () => filterEmarPassRows(dailyTrackingRows, todayPassFilter),
    [dailyTrackingRows, todayPassFilter],
  );
  const todayTrackingOccurrenceRows = useMemo(
    () => dailyTrackingRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
      item: row.item,
      trackingItemId: row.trackingItemId,
      occurrences: [occurrence],
    }))),
    [dailyTrackingRows],
  );
  const filteredTodayPassOccurrenceRows = useMemo(
    () => filterEmarPassRows(todayPassOccurrenceRows, todayPassFilter),
    [todayPassOccurrenceRows, todayPassFilter],
  );
  const filteredTodayTrackingOccurrenceRows = useMemo(
    () => filterEmarPassRows(todayTrackingOccurrenceRows, todayPassFilter),
    [todayTrackingOccurrenceRows, todayPassFilter],
  );
  const todayPassTimeSections = useMemo(() => {
    const nowTime = Date.now();
    const upcoming: any[] = [];
    const past: any[] = [];
    const combinedRows = [
      ...filteredTodayPassOccurrenceRows.map((row: any) => ({ ...row, timeViewType: "medication" })),
      ...filteredTodayTrackingOccurrenceRows.map((row: any) => ({ ...row, timeViewType: "tracking" })),
    ];

    combinedRows.forEach((row: any) => {
      const occurrence = row?.occurrences?.[0];
      const scheduledTime = occurrence?.timingMeta?.scheduledDate instanceof Date
        ? occurrence.timingMeta.scheduledDate.getTime()
        : null;
      if (scheduledTime !== null && scheduledTime > nowTime) {
        upcoming.push(row);
        return;
      }
      past.push(row);
    });

    const getOccurrenceSortTime = (row: any, fallback: number) => (
      row?.occurrences?.[0]?.timingMeta?.scheduledDate instanceof Date
        ? row.occurrences[0].timingMeta.scheduledDate.getTime()
        : fallback
    );

    upcoming.sort((first: any, second: any) => getOccurrenceSortTime(first, Number.MAX_SAFE_INTEGER) - getOccurrenceSortTime(second, Number.MAX_SAFE_INTEGER));
    past.sort((first: any, second: any) => getOccurrenceSortTime(second, 0) - getOccurrenceSortTime(first, 0));

    return { upcoming, past };
  }, [filteredTodayPassOccurrenceRows, filteredTodayTrackingOccurrenceRows]);
  const todayPassTabCount = medPassRows.length + dailyTrackingRows.length;
  const todaySummaryTrackingCount = filteredDailyTrackingRows.length;

  const renderDailyTrackingRecordSection = (rows: any[] = dailyTrackingRows) => (
    <div>
      <div>
        <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a" }}>Vital Tracker Records</div>
        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "42rem" }}>
          Track blood pressure, glucose, oxygen, pain, weight, and other non-med daily readings that need charting.
        </div>
      </div>
      <div className="mt-3">
        {activeDailyTrackingItems.length === 0 ? (
          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1.5rem 0.75rem" }}>
            <div>No vital tracker items added yet.</div>
            <div className="mt-3">
              <Button
                type="button"
                label="Add New Item"
                icon="pi pi-heart"
                severity="warning"
                rounded
                onClick={() => setShowAddItemDialog(true)}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="block md:hidden">
              <div className="flex flex-column gap-3">
                {rows.map((row: any, index: number) => (
                  <div
                    key={row?.trackingItemId || row?.item?.name}
                    style={{
                      padding: "0.1rem 0",
                      borderBottom: index === rows.length - 1 ? "none" : "1px solid #e2e8f0",
                    }}
                  >
                    {dailyTrackingItemBodyTemplate({ ...row, showControls: false, showEditControl: true, compact: true })}
                    <div className="mt-3">
                      {dailyTrackingStatusBodyTemplate(row)}
                    </div>
                    <div className="mt-3">
                      {dailyTrackingActionsBodyTemplate(row)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <DataTable value={rows} responsiveLayout="scroll" size="small" stripedRows>
                <Column header="Tracking Item" body={(row: any) => dailyTrackingItemBodyTemplate({ ...row, showControls: false, showEditControl: true, compact: true })} style={{ minWidth: "18rem" }} />
                <Column header="Status" body={dailyTrackingStatusBodyTemplate} style={{ minWidth: "17rem" }} />
                <Column header="Actions" body={dailyTrackingActionsBodyTemplate} style={{ minWidth: "20rem" }} />
              </DataTable>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderDailyTrackingManagementSection = () => {
    return (
      <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Vital Tracker</div>
            <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
              Manage vital tracker items here. Edit them to change setup or activate/deactivate them.
            </div>
          </div>
          {displayedDailyTrackingItems.length > 0 ? (
            <Button
              type="button"
              label="Add New Item"
              icon="pi pi-heart"
              severity="warning"
              rounded
              onClick={openCreateDailyTrackingDialog}
            />
          ) : null}
        </div>
        <div className="mt-3">
          {displayedDailyTrackingItems.length === 0 ? (
            <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1.5rem 0.75rem" }}>
              <div>
                {dailyTrackingListFilter === "inactive"
                  ? "No deleted vital tracker items."
                  : dailyTrackingListFilter === "discontinued"
                    ? "No discontinued vital tracker items."
                    : "No vital tracker items added yet."}
              </div>
              <div className="mt-3">
                <Button
                  type="button"
                  label="Add New Item"
                  icon="pi pi-heart"
                  severity="warning"
                  rounded
                  onClick={openCreateDailyTrackingDialog}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                  {dailyTrackingListFilter === "inactive"
                    ? `Deleted items (${inactiveDailyTrackingItems.length})`
                    : dailyTrackingListFilter === "discontinued"
                      ? `Discontinued items (${discontinuedDailyTrackingItems.length})`
                      : `Active items (${activeDailyTrackingItems.length})`}
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                  <span
                    style={{
                      color: "#64748b",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Filter
                  </span>
                  <Dropdown
                    value={dailyTrackingListFilter}
                    options={dailyTrackingListFilterOptions}
                    onChange={(e) => setDailyTrackingListFilter(e.value)}
                    style={{ minWidth: "18rem" }}
                  />
                </div>
              </div>
              <div className="block md:hidden">
                <div className="flex flex-column gap-3">
                  {displayedDailyTrackingItems.map((row: any, index: number) => (
                    <div key={row?.trackingItemId || `${row?.name || "tracking-item"}-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                      {dailyTrackingItemBodyTemplate({ item: row, trackingItemId: row?.trackingItemId, occurrences: [], showControls: false })}
                      <div className="mt-3 flex gap-2 flex-wrap align-items-center">
                        {dailyTrackingManagementStatusBodyTemplate(row)}
                      </div>
                      <div className="mt-3">
                        {dailyTrackingManagementActionsBodyTemplate(row)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hidden md:block">
                <DataTable
                  value={displayedDailyTrackingItems}
                  responsiveLayout="scroll"
                  size="small"
                  stripedRows
                  emptyMessage={
                    dailyTrackingListFilter === "inactive"
                      ? "No deleted vital tracker items."
                      : dailyTrackingListFilter === "discontinued"
                        ? "No discontinued vital tracker items."
                        : "No active vital tracker items yet."
                  }
                >
                  <Column
                    header="Tracking Item"
                    body={(row: any) => dailyTrackingItemBodyTemplate({ item: row, trackingItemId: row?.trackingItemId, occurrences: [], showControls: false })}
                    style={{ minWidth: "18rem" }}
                  />
                  <Column header="Status" body={dailyTrackingManagementStatusBodyTemplate} style={{ width: "10rem" }} />
                  <Column header="Actions" body={dailyTrackingManagementActionsBodyTemplate} style={{ width: "12rem" }} />
                </DataTable>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTodayPassTimeSection = (title: string, rows: any[]) => (
    <div>
      <div style={{ fontSize: "0.96rem", fontWeight: 800, color: "#0f172a" }}>{title}</div>
      <div className="mt-3">
        {rows.length === 0 ? (
          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "0.75rem 0.5rem" }}>
            No items in this section right now.
          </div>
        ) : (
          <>
            <div className="block md:hidden px-0">
              <div className="flex flex-column gap-3">
                {rows.map((row: any, index: number) => {
                  const occurrence = row?.occurrences?.[0];
                  const rowType = String(row?.timeViewType || "medication");
                  const rowKey = `${rowType}::${row?.medicationId || row?.trackingItemId || "item"}::${occurrence?.scheduledTimeKey || "default"}`;
                  return (
                    <div
                      key={rowKey}
                      style={{
                        padding: "0.1rem 0",
                        borderBottom: index === rows.length - 1 ? "none" : "1px solid #e2e8f0",
                      }}
                    >
                      {rowType === "tracking"
                        ? dailyTrackingItemBodyTemplate({ ...row, showControls: false, showEditControl: true, compact: true })
                        : medPassMedicationBodyTemplate(row, true)}
                      <div className="mt-3">
                        {rowType === "tracking" ? dailyTrackingStatusBodyTemplate(row) : medPassStatusBodyTemplate(row)}
                      </div>
                      <div className="mt-3">
                        {rowType === "tracking" ? dailyTrackingActionsBodyTemplate(row) : medPassActionsBodyTemplate(row)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="hidden md:block">
              <DataTable value={rows} responsiveLayout="scroll" size="small" stripedRows>
                <Column
                  header="Item"
                  body={(row: any) => (String(row?.timeViewType || "medication") === "tracking"
                    ? dailyTrackingItemBodyTemplate({ ...row, showControls: false, showEditControl: true, compact: true })
                    : medPassMedicationBodyTemplate(row, true))}
                  style={{ minWidth: "19rem" }}
                />
                <Column
                  header="Status"
                  body={(row: any) => (String(row?.timeViewType || "medication") === "tracking"
                    ? dailyTrackingStatusBodyTemplate(row)
                    : medPassStatusBodyTemplate(row))}
                  style={{ minWidth: "17rem" }}
                />
                <Column
                  header="Actions"
                  body={(row: any) => (String(row?.timeViewType || "medication") === "tracking"
                    ? dailyTrackingActionsBodyTemplate(row)
                    : medPassActionsBodyTemplate(row))}
                  style={{ minWidth: "20rem" }}
                />
              </DataTable>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const medPassMedicationBodyTemplate = (row: any, compact = false) => {
    const occurrenceLabels = row.occurrences
      .map((occurrence: any) => String(occurrence?.occurrenceLabel || "").trim())
      .filter((label: string) => label && label !== "PRN");
    const expiryAlert = getMedicationExpiryAlert(row?.medication?.effectiveEndDate);

    return (
      <div style={{ minWidth: 0 }}>
        <div className="flex align-items-center gap-2 flex-wrap">
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.medication?.medicationName || "Medication"}</div>
          <Button
            type="button"
            icon="pi pi-pencil"
            outlined
            rounded
            severity="warning"
            style={{ color: "#ea580c", background: "#fff7ed" }}
            tooltip="Edit"
            tooltipOptions={{ position: "top" }}
            aria-label={`Edit ${row?.medication?.medicationName || "medication"}`}
            onClick={() => openEditDialog(row?.medication)}
          />
        </div>
        {buildMedicationScheduleText(row?.medication) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            {buildMedicationScheduleText(row?.medication)}
          </div>
        ) : null}
        {row?.medication?.dosage ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Dose:</strong> {row.medication.dosage}
          </div>
        ) : null}
        {row?.medication?.route ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Route:</strong> {row.medication.route}
          </div>
        ) : null}
        {row?.medication?.effectiveEndDate ? (
          <div className="mt-1" style={{ fontSize: "0.82rem", color: expiryAlert?.color || "#64748b", fontWeight: expiryAlert ? 700 : 500 }}>
            {isMedicationExpired(row?.medication?.effectiveEndDate) ? "Expired:" : "Exp:"} {row.medication.effectiveEndDate}
          </div>
        ) : null}
        {row?.medication?.indication ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {row.medication.indication}
          </div>
        ) : null}
        {row?.medication?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Instructions:</strong> {row.medication.instructions}
          </div>
        ) : null}
        {row?.medication?.handlingNotes ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Handling notes:</strong> {row.medication.handlingNotes}
          </div>
        ) : null}
        {row?.medication?.prn ? <PrnMedicationHistory residentId={residentId || ""} medicationId={String(row?.medication?.medicationId || "")} /> : null}
      </div>
    );
  };
  const medPassStatusBodyTemplate = (row: any) => (
    <div style={{ minWidth: 0 }}>
      <div className="flex flex-column gap-2">
        {row.occurrences.map((occurrence: any) => {
          const occurrenceStatus = deriveEmarOccurrenceStatus(occurrence?.entryStatus, occurrence?.timingMeta, row?.medication?.prn === true);
          const statusLabel = formatEmarStatusLabel(occurrenceStatus);
          const statusSeverity = getEmarStatusSeverity(occurrenceStatus, occurrence);
          const detailRows = buildEmarEntryDetailRows(occurrence?.entry, row?.medication).filter((detail: any) => (
            !["recommended action", "resolved dose"].includes(String(detail?.label || "").trim().toLowerCase())
          ));
          const summaryTag = buildOccurrenceSummaryTag(occurrence, occurrenceStatus ? statusLabel : "", statusSeverity);

          return (
            <div
              key={`${row.medicationId}-${occurrence.scheduledTimeKey}`}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.65rem 0.75rem",
                background: "#fff",
              }}
            >
              <div className="flex gap-2 flex-wrap align-items-center">
                {summaryTag ? <Tag value={summaryTag.label} severity={summaryTag.severity as any} rounded /> : null}
              </div>
              {detailRows.length ? (
                <div className="mt-2 flex flex-column gap-1">
                  {detailRows.map((item) => (
                    <div key={`${occurrence.scheduledTimeKey}-${item.label}-${item.value}`} style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                      <strong style={{ color: "#334155" }}>{item.label}:</strong> {item.value}
                    </div>
                  ))}
                </div>
              ) : null}
              {row?.medication?.prn && !detailRows.length && !occurrence?.entryStatus ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  No PRN entry charted yet.
                </div>
              ) : null}
              {!detailRows.length && !occurrence?.entryStatus && occurrence?.timingMeta?.isOverdue ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: "#b91c1c" }}>
                  No charted entry was saved for this scheduled medication pass.
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  const dailyTrackingItemBodyTemplate = (row: any) => {
    const categoryOption = getEmarDailyTrackingCategoryOption(row?.item?.category);
    const protocolSummary = getEmarProtocolSummary(row?.item);
    const isCompact = row?.compact === true;
    const itemName = String(row?.item?.name || "").trim();
    const showCategoryLabel = isCompact
      ? !itemName && Boolean(String(categoryOption?.label || "").trim())
      : !itemName || itemName.toLowerCase() !== String(categoryOption?.label || "").trim().toLowerCase();
    const schema = getEmarDailyTrackingSchema(row?.item);
    const occurrenceLabels = row.occurrences
      .map((occurrence: any) => String(occurrence?.occurrenceLabel || "").trim())
      .filter(Boolean);
    const showControls = row?.showControls !== false;
    const showEditControl = showControls || row?.showEditControl === true;
    return (
      <div style={{ minWidth: 0 }}>
        <div className="flex align-items-center gap-2 flex-wrap">
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.item?.name || categoryOption?.label || "Vital tracker"}</div>
          {showEditControl ? (
            <>
              <Button
                type="button"
                icon="pi pi-pencil"
                outlined
                rounded
                severity="warning"
                style={{ color: "#ea580c", background: "#fff7ed" }}
                tooltip="Edit"
                tooltipOptions={{ position: "top" }}
                aria-label={`Edit ${row?.item?.name || "tracking item"}`}
                onClick={() => openEditDailyTrackingDialog(row?.item)}
              />
              {showControls ? (
                <Button
                  type="button"
                  icon="pi pi-trash"
                  outlined
                  rounded
                  severity="danger"
                  tooltip={deleteItemTooltipMessage}
                  tooltipOptions={{ position: "top" }}
                  aria-label={`Delete ${row?.item?.name || "tracking item"}`}
                  onClick={() => deleteDailyTrackingItem(row?.item)}
                />
              ) : null}
            </>
          ) : null}
        </div>
        {showCategoryLabel ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
            {categoryOption?.label || "Daily tracking"}
          </div>
        ) : null}
        {!isCompact && String(row?.item?.frequency || "").trim() ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Frequency:</strong> {String(row.item.frequency).replaceAll("_", " ")}
          </div>
        ) : null}
        {(row?.item?.effectiveStartDate || row?.item?.effectiveEndDate) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Dates:</strong>{" "}
            {[row?.item?.effectiveStartDate ? `Start ${formatEmarDateOnly(row.item.effectiveStartDate)}` : "", row?.item?.effectiveEndDate ? `End ${formatEmarDateOnly(row.item.effectiveEndDate)}` : ""].filter(Boolean).join(" • ")}
          </div>
        ) : null}
        {!isCompact && schema.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Records:</strong>{" "}
            {schema.map((field: any) => `${field?.label || field?.key}${field?.unit ? ` (${field.unit})` : ""}`).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {isCompact && schema.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Records:</strong>{" "}
            {schema.map((field: any) => `${field?.label || field?.key}${field?.unit ? ` (${field.unit})` : ""}`).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {!isCompact && occurrenceLabels.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Scheduled:</strong> {occurrenceLabels.join(", ")}
          </div>
        ) : null}
        {row?.item?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Instructions:</strong> {row.item.instructions}
          </div>
        ) : null}
        {protocolSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <span><strong style={{ color: "#334155" }}>Action rules:</strong>{` ${protocolSummary.label}`}</span>
          </div>
        ) : null}
        {!isCompact && (
          <div className="mt-2 flex gap-2 flex-wrap">
            <Tag
              value={row?.item?.active === false || row?.item?.discontinued === true ? "Inactive" : "Active"}
              severity={row?.item?.active === false || row?.item?.discontinued === true ? "secondary" : "success"}
              rounded
            />
            {row?.item?.scheduledClockTime ? (
              <Tag value={`Clock ${formatStructuredScheduleTimeLabel(row.item.scheduledClockTime) || row.item.scheduledClockTime}`} severity="info" rounded />
            ) : null}
            {row?.item?.effectiveEndDate ? (
              <Tag value={`Ends ${formatEmarDateOnly(row.item.effectiveEndDate)}`} severity="warning" rounded />
            ) : null}
          </div>
        )}
        {!isCompact && String(row?.item?.modifiedAt || row?.item?.createdAt || "").trim() ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Last updated:</strong> {formatEmarDateTime(row?.item?.modifiedAt || row?.item?.createdAt)}
          </div>
        ) : null}
      </div>
    );
  };

  const dailyTrackingStatusBodyTemplate = (row: any) => (
    <div style={{ minWidth: 0 }}>
      {!row.occurrences.length ? (
        <Tag value="Not scheduled today" severity="secondary" rounded />
      ) : null}
      <div className="flex flex-column gap-2">
        {row.occurrences.map((occurrence: any) => {
          const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
          const effectiveStatus = status || (occurrence?.timingMeta?.isOverdue ? "missed" : "");
          const summaryTag = buildOccurrenceSummaryTag(
            occurrence,
            effectiveStatus ? formatDailyTrackingStatusLabel(effectiveStatus) : "",
            effectiveStatus ? getDailyTrackingStatusSeverity(effectiveStatus) : "",
          );
          return (
            <div key={`${row.trackingItemId}-${occurrence.scheduledTimeKey}`} style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                {summaryTag ? <Tag value={summaryTag.label} severity={summaryTag.severity as any} rounded /> : null}
              </div>
              {occurrence?.entry ? (
                <>
                  <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                    {buildEmarDailyTrackingValueSummary(occurrence.entry, row?.item) || (occurrence?.entry?.note ? occurrence.entry.note : "No recorded values")}
                  </div>
                  {getTrackingRecommendedActionLabel(occurrence?.entry) ? (
                    <div className="mt-2">
                      <Tag value={getTrackingRecommendedActionLabel(occurrence?.entry)} severity="danger" rounded />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  {occurrence?.timingMeta?.isOverdue
                    ? "No charted entry was saved for this scheduled tracking time."
                    : "No entry saved yet for this scheduled tracking time."}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const dailyTrackingActionsBodyTemplate = (row: any) => (
    <div className="flex flex-column gap-2" style={{ minWidth: "18rem" }}>
      {!row.occurrences.length ? (
        <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          This tracking item is active but not scheduled for today.
        </div>
      ) : null}
      {row.occurrences.map((occurrence: any) => {
        const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
        const isRecorded = status === "recorded";
        const canChartScheduledNow = canChartScheduledOccurrenceNow(occurrence);
        const chartingAvailableAtLabel = formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate);
        return (
          <div key={`${row.trackingItemId}-${occurrence.scheduledTimeKey}`} style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
            <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
              {occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || "Scheduled"}
            </div>
            <div className="flex gap-2 flex-wrap">
              {canChartScheduledNow ? (
                <Button
                  type="button"
                  label="Record"
                  size="small"
                  rounded
                  severity="success"
                  outlined={status !== "recorded"}
                  onClick={() => openDailyTrackingEntryDialog(row, occurrence, "recorded")}
                />
              ) : null}
              {!isRecorded ? (
                <>
                  {canChartScheduledNow ? (
                    <Button
                      type="button"
                      label="Refused"
                      size="small"
                      rounded
                      severity="danger"
                      outlined={status !== "refused"}
                      onClick={() => openDailyTrackingEntryDialog(row, occurrence, "refused")}
                    />
                  ) : null}
                  <Button
                    type="button"
                    label="Family"
                    size="small"
                    rounded
                    severity="success"
                    outlined={status !== "family"}
                    onClick={() => openDailyTrackingEntryDialog(row, occurrence, "family")}
                  />
                  <Button
                    type="button"
                    label="Hospitalized"
                    size="small"
                    rounded
                    severity="warning"
                    outlined={status !== "hospitalized"}
                    onClick={() => openDailyTrackingEntryDialog(row, occurrence, "hospitalized")}
                  />
                </>
              ) : null}
            </div>
            {!canChartScheduledNow ? (
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem" }}>
                Record and Refused unlock 1 hour before the scheduled tracking time
                {chartingAvailableAtLabel ? ` (${chartingAvailableAtLabel}).` : "."}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const dailyTrackingManagementStatusBodyTemplate = (row: any) => (
    <Tag
      value={row?.active === false || row?.discontinued === true ? "Inactive" : "Active"}
      severity={row?.active === false || row?.discontinued === true ? "secondary" : "success"}
      rounded
    />
  );

  const dailyTrackingManagementActionsBodyTemplate = (row: any) => (
    <div className="flex gap-2 flex-wrap">
      <Button
        type="button"
        label="Edit"
        size="small"
        outlined
        rounded
        severity="warning"
        icon="pi pi-pencil"
        onClick={() => openEditDailyTrackingDialog(row)}
      />
    </div>
  );

  const medPassActionsBodyTemplate = (row: any) => {
    const expiredMedicationMessage = row?.medication?.effectiveEndDate && isMedicationExpired(row.medication.effectiveEndDate)
      ? `Charting is blocked because this medication expired on ${row.medication.effectiveEndDate}. Update the medication before saving another med pass.`
      : "";
    const isExpiredMedication = !!expiredMedicationMessage;

    const renderActionChip = (
      label: string,
      severityStyles: React.CSSProperties,
      isRowSaving: boolean,
      onClick: () => void,
      isDisabled = false,
    ) => (
      <button
        key={label}
        type="button"
        disabled={isRowSaving || isDisabled}
        onClick={onClick}
        style={{
          ...medPassActionChipStyle,
          ...severityStyles,
          opacity: (isRowSaving || isDisabled) ? 0.7 : 1,
        }}
      >
        {label}
      </button>
    );

    return (
      <div className="flex flex-column gap-2" style={{ minWidth: "16rem" }}>
        {row.occurrences.map((occurrence: any) => {
          const scheduledTime = occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "";
          const scheduledTimeKey = occurrence?.scheduledTimeKey || "default";
          const actionRow = {
            ...row,
            ...occurrence,
            medication: row.medication,
            medicationId: row.medicationId,
          };
          const entryStatus = String(occurrence?.entryStatus || "").toLowerCase();
          const isLockedStatus = ["given", "family", "hospitalized"].includes(entryStatus);
          const isRowSaving = savingEntry && savingEntryKey === `${String(row?.medicationId || "").trim()}::${String(scheduledTimeKey).trim().toLowerCase()}`;
          const canChartScheduledNow = canChartScheduledOccurrenceNow(occurrence);
          const chartingAvailableAtLabel = formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate);
          const showTimedScheduledActions = row?.medication?.prn || canChartScheduledNow;

          return (
            <div
              key={`${row.medicationId}-${scheduledTimeKey}`}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.6rem 0.65rem",
                background: "#fff",
              }}
            >
              <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
                {scheduledTime || "Scheduled"}
              </div>
              <div className="flex gap-2 flex-wrap">
                {isLockedStatus
                  ? renderActionChip(
                    entryStatus === "hospitalized" ? "Hospitalized" : entryStatus === "family" ? "Family" : "Given",
                    entryStatus === "hospitalized"
                      ? { background: "#fef3c7", borderColor: "#d97706", color: "#9a3412" }
                      : entryStatus === "family"
                        ? { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" }
                        : { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" },
                    isRowSaving,
                    () => { },
                    true,
                  )
                  : (
                    <>
                      {showTimedScheduledActions ? renderActionChip(
                        "Given",
                        row?.medication?.prn
                          ? isPrnFollowUpPending(occurrence)
                            ? { background: "#e0f2fe", borderColor: "#0284c7", color: "#0c4a6e" }
                            : occurrence?.entryStatus === "prn_given"
                              ? { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" }
                              : { background: "#fff", borderColor: "#bae6fd", color: "#0c4a6e" }
                          : occurrence?.entryStatus === "given"
                            ? { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" }
                            : { background: "#fff", borderColor: "#bbf7d0", color: "#166534" },
                        isRowSaving,
                        () => {
                          if (row?.medication?.prn) {
                            openMedPassActionDialog(actionRow, "prn_given");
                            return;
                          }
                          openGivenMedPassDialog(actionRow);
                        },
                        isExpiredMedication,
                      ) : null}
                      {!row?.medication?.prn ? renderActionChip(
                        "Family",
                        occurrence?.entryStatus === "family"
                          ? { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" }
                          : { background: "#fff", borderColor: "#bfdbfe", color: "#1d4ed8" },
                        isRowSaving,
                        () => {
                          void openFamilyMedPassDialog(actionRow);
                        },
                        isExpiredMedication,
                      ) : null}
                      {showTimedScheduledActions ? renderActionChip(
                        "Refused",
                        occurrence?.entryStatus === "refused"
                          ? { background: "#fee2e2", borderColor: "#dc2626", color: "#991b1b" }
                          : { background: "#fff", borderColor: "#fecaca", color: "#991b1b" },
                        isRowSaving,
                        () => {
                          openMedPassActionDialog(actionRow, "refused");
                        },
                        isExpiredMedication,
                      ) : null}
                      {showTimedScheduledActions ? renderActionChip(
                        "Not Given",
                        occurrence?.entryStatus === "not_given"
                          ? { background: "#fef3c7", borderColor: "#f59e0b", color: "#92400e" }
                          : { background: "#fff", borderColor: "#fde68a", color: "#92400e" },
                        isRowSaving,
                        () => {
                          openMedPassActionDialog(actionRow, "not_given");
                        },
                        isExpiredMedication,
                      ) : null}
                      {showTimedScheduledActions ? renderActionChip(
                        "Held",
                        occurrence?.entryStatus === "held"
                          ? { background: "#fef3c7", borderColor: "#f59e0b", color: "#92400e" }
                          : { background: "#fff", borderColor: "#fde68a", color: "#92400e" },
                        isRowSaving,
                        () => {
                          openMedPassActionDialog(actionRow, "held");
                        },
                        isExpiredMedication,
                      ) : null}
                      {!row?.medication?.prn ? renderActionChip(
                        "Hospitalized",
                        occurrence?.entryStatus === "hospitalized"
                          ? { background: "#fef3c7", borderColor: "#d97706", color: "#9a3412" }
                          : { background: "#fff", borderColor: "#fcd34d", color: "#9a3412" },
                        isRowSaving,
                        () => {
                          openMedPassActionDialog(actionRow, "hospitalized");
                        },
                        isExpiredMedication,
                      ) : null}
                    </>
                  )}
              </div>
              {isExpiredMedication ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: "#b91c1c" }}>
                  {expiredMedicationMessage}
                </div>
              ) : !row?.medication?.prn && !canChartScheduledNow ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: "#92400e" }}>
                  {`Given, Refused, Held, and Not Given will unlock 1 hour before the scheduled pass time${chartingAvailableAtLabel ? ` (${chartingAvailableAtLabel})` : ""}.`}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };
  const effectiveLatestEmarCompareJobId = String(
    latestEmarCompareJobId
    || emarCompareCompletedJobId
    || (emarCompareResultData ? emarCompareJobId : "")
    || "",
  ).trim();
  const emarCompareHasDifferences = Boolean(
    (emarCompareResultData?.changed || []).length
    || (emarCompareResultData?.newOnUploadedMar || []).length
    || (emarCompareResultData?.removedFromUploadedMar || []).length,
  );
  const formatEmarCompareEntryDetails = (entry: any) => {
    if (!entry) {
      return "—";
    }

    const detailLines = [
      [entry?.dosage, entry?.route, entry?.schedule].filter(Boolean).join(" - "),
      (Array.isArray(entry?.scheduledTimes) ? entry.scheduledTimes : [entry?.scheduledClockTime]).filter(Boolean).length
        ? `time: ${(Array.isArray(entry?.scheduledTimes) ? entry.scheduledTimes : [entry?.scheduledClockTime]).filter(Boolean).join(", ")}`
        : "",
      (Array.isArray(entry?.parameters) ? entry.parameters : Array.isArray(entry?.targetSchema) ? entry.targetSchema : []).length
        ? `parameters: ${(Array.isArray(entry?.parameters) ? entry.parameters : entry?.targetSchema).map((parameter: any) => parameter?.label || parameter?.key).filter(Boolean).join(", ")}`
        : "",
      entry?.indication ? `use: ${entry.indication}` : "",
      entry?.instructions ? `instructions: ${entry.instructions}` : "",
    ].filter(Boolean);

    return detailLines.length ? detailLines.join("\n") : "not listed";
  };
  const getEmarCompareActionLabels = (item: any) => {
    const labels = Array.isArray(item?.differenceLabels) ? item.differenceLabels : [];
    const mapped = labels.map((label: string) => {
      const normalized = String(label || "").trim().toLowerCase();
      if (normalized.includes("dose")) {
        return "Update Dosage";
      }
      if (normalized.includes("route")) {
        return "Update Route";
      }
      if (normalized.includes("schedule") || normalized.includes("frequency")) {
        return "Update Frequency";
      }
      if (normalized.includes("time") || normalized.includes("hour")) {
        return "Update Time";
      }
      if (normalized.includes("parameter")) {
        return "Update Parameters";
      }
      if (normalized.includes("use") || normalized.includes("indication") || normalized.includes("purpose")) {
        return "Update Use";
      }
      if (normalized.includes("instruction")) {
        return "Update Instructions";
      }
      if (normalized.includes("prn")) {
        return "Update PRN";
      }
      return `Update ${String(label || "").trim()}`;
    });

    return Array.from(new Set(mapped.filter(Boolean)));
  };
  const getEmarCompareDifferenceType = (label: string) => {
    const normalized = String(label || "").trim().toLowerCase();
    if (normalized.includes("dose")) {
      return "dosage";
    }
    if (normalized.includes("route")) {
      return "route";
    }
    if (normalized.includes("schedule") || normalized.includes("frequency")) {
      return "frequency";
    }
    if (normalized.includes("time") || normalized.includes("hour")) {
      return "time";
    }
    if (normalized.includes("parameter")) {
      return "parameters";
    }
    if (normalized.includes("use") || normalized.includes("indication") || normalized.includes("purpose")) {
      return "use";
    }
    if (normalized.includes("instruction")) {
      return "instructions";
    }
    if (normalized.includes("prn")) {
      return "prn";
    }
    return normalized || "details";
  };
  const getEmarCompareDifferenceLabel = (differenceType: string) => {
    if (differenceType === "use") {
      return "Usage";
    }
    if (differenceType === "frequency") {
      return "Frequency";
    }
    if (differenceType === "time") {
      return "Time";
    }
    if (differenceType === "parameters") {
      return "Parameters";
    }
    if (differenceType === "instructions") {
      return "Instructions";
    }
    if (differenceType === "dosage") {
      return "Dosage";
    }
    if (differenceType === "route") {
      return "Route";
    }
    if (differenceType === "prn") {
      return "PRN";
    }
    if (differenceType === "medication") {
      return "Medication";
    }
    return String(differenceType || "Details").trim() || "Details";
  };
  const getEmarCompareFieldValue = (entry: any, differenceType: string) => {
    if (!entry) {
      return "not listed";
    }

    if (differenceType === "dosage") {
      return String(entry?.dosage || "").trim() || "not listed";
    }
    if (differenceType === "route") {
      return String(entry?.route || "").trim() || "not listed";
    }
    if (differenceType === "frequency") {
      return String(entry?.frequency || entry?.schedule || "").trim() || "not listed";
    }
    if (differenceType === "time") {
      const times = Array.isArray(entry?.scheduledTimes)
        ? entry.scheduledTimes
        : [entry?.scheduledClockTime];
      return times.map((value: any) => String(value || "").trim()).filter(Boolean).join(", ") || "not listed";
    }
    if (differenceType === "parameters") {
      const parameters = Array.isArray(entry?.parameters)
        ? entry.parameters
        : Array.isArray(entry?.targetSchema)
          ? entry.targetSchema
          : [];
      return parameters.map((parameter: any) => {
        const conditions = Array.isArray(parameter?.conditions) && parameter.conditions.length
          ? ` (${parameter.conditions.join("; ")})`
          : "";
        return `${parameter?.label || parameter?.key || "parameter"}${conditions}`;
      }).join(", ") || "not listed";
    }
    if (differenceType === "use") {
      return String(entry?.indication || "").trim() || "not listed";
    }
    if (differenceType === "instructions") {
      return String(entry?.instructions || "").trim() || "not listed";
    }
    if (differenceType === "prn") {
      return entry?.prn ? "yes" : "no";
    }

    return formatEmarCompareEntryDetails(entry);
  };
  const formatEmarCompareGroupedDetails = (entry: any, differenceTypes: string[]) => {
    const normalizedTypes = Array.from(new Set((differenceTypes || []).filter(Boolean)));
    if (!normalizedTypes.length) {
      return formatEmarCompareEntryDetails(entry);
    }

    const lines = normalizedTypes.map((differenceType: string) => {
      const label = getEmarCompareDifferenceLabel(differenceType);
      const value = getEmarCompareFieldValue(entry, differenceType);
      return `${label}: ${value}`;
    });

    return lines.join("\n");
  };
  const buildChangedEmarCompareRows = (item: any, index: number) => {
    const labels = Array.isArray(item?.differenceLabels) ? item.differenceLabels : [];
    const rawDifferenceTypes = Array.from(
      new Set(labels.map((label: string) => getEmarCompareDifferenceType(label)).filter(Boolean)),
    ) as string[];
    const differenceTypes = rawDifferenceTypes.filter((differenceType: string) => {
      if (differenceType === "medication") {
        return true;
      }
      const nextValue = getEmarCompareFieldValue(item?.uploadedEntry, differenceType);
      return String(nextValue || "").trim().toLowerCase() !== "not listed";
    });

    if (!differenceTypes.length) {
      return [];
    }

    return [{
      rowKey: `changed-${index}`,
      medicationName: item?.uploadedEntry?.medicationName || item?.baselineEntry?.medicationName || "medication",
      psychotropicDrugClass: item?.uploadedEntry?.psychotropicDrugClass || item?.baselineEntry?.psychotropicDrugClass || "",
      differenceTypes,
      currentEmar: formatEmarCompareGroupedDetails(item?.baselineEntry, differenceTypes),
      newMar: formatEmarCompareGroupedDetails(item?.uploadedEntry, differenceTypes),
      actionLabels: differenceTypes.map((differenceType: string) => getEmarCompareDifferenceLabel(differenceType)),
      actionSeverity: "warning",
    }];
  };
  const emarCompareDifferenceRows = [
    ...(emarCompareResultData?.changed || []).flatMap((item: any, index: number) => buildChangedEmarCompareRows(item, index)),
    ...(emarCompareResultData?.newOnUploadedMar || []).map((item: any, index: number) => ({
      rowKey: `new-${index}`,
      medicationName: item?.uploadedEntry?.medicationName || "medication",
      psychotropicDrugClass: item?.uploadedEntry?.psychotropicDrugClass || "",
      differenceTypes: ["medication"],
      currentEmar: "not in current emar",
      newMar: formatEmarCompareEntryDetails(item?.uploadedEntry),
      actionLabels: ["Add medication"],
      actionSeverity: "danger",
    })),
    ...(emarCompareResultData?.removedFromUploadedMar || []).map((item: any, index: number) => ({
      rowKey: `removed-${index}`,
      medicationName: item?.baselineEntry?.medicationName || "medication",
      psychotropicDrugClass: item?.baselineEntry?.psychotropicDrugClass || "",
      differenceTypes: ["medication"],
      currentEmar: formatEmarCompareEntryDetails(item?.baselineEntry),
      newMar: "not found on new mar",
      actionLabels: ["Remove medication"],
      actionSeverity: "secondary",
    })),
  ];
  const psychotropicCompareRows = [
    ...(psychotropicCompareResultData?.changed || []).map((item: any, index: number) => ({
      rowKey: `psychotropic-changed-${index}`,
      medicationName: item?.uploadedEntry?.medicationName || item?.baselineEntry?.medicationName || "Medication",
      activeEmar: formatEmarCompareEntryDetails(item?.uploadedEntry),
      carePlan: formatEmarCompareEntryDetails(item?.baselineEntry),
      difference: Array.isArray(item?.differenceLabels) ? item.differenceLabels.join(", ") : "Review needed",
    })),
    ...(psychotropicCompareResultData?.newOnUploadedMar || []).map((item: any, index: number) => ({
      rowKey: `psychotropic-emar-only-${index}`,
      medicationName: item?.uploadedEntry?.medicationName || "Medication",
      activeEmar: formatEmarCompareEntryDetails(item?.uploadedEntry),
      carePlan: "not in care plan",
      difference: "Missing from care plan",
    })),
    ...(psychotropicCompareResultData?.removedFromUploadedMar || []).map((item: any, index: number) => ({
      rowKey: `psychotropic-care-plan-only-${index}`,
      medicationName: item?.baselineEntry?.medicationName || "Medication",
      activeEmar: "not in active eMAR",
      carePlan: formatEmarCompareEntryDetails(item?.baselineEntry),
      difference: "Missing from active eMAR",
    })),
  ];
  const emarCompareAffectedMedicationCount = Array.from(
    new Set(
      [
        ...(emarCompareResultData?.changed || []).map((item: any) => item?.uploadedEntry?.medicationName || item?.baselineEntry?.medicationName || ""),
        ...(emarCompareResultData?.newOnUploadedMar || []).map((item: any) => item?.uploadedEntry?.medicationName || ""),
        ...(emarCompareResultData?.removedFromUploadedMar || []).map((item: any) => item?.baselineEntry?.medicationName || ""),
      ].map((name: string) => String(name || "").trim()).filter(Boolean),
    ),
  ).length;

  if (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
    return <Unauthorized />;
  }

  const isFamilyAdministrationStatus = (status: any) => String(status || "").trim().toLowerCase() === "family";
  const isGivenLikeAdministrationStatus = (status: any) => {
    const normalizedStatus = String(status || "").trim().toLowerCase();
    return normalizedStatus === "given" || normalizedStatus === "family";
  };
  const deleteItemTooltipMessage = "On Delete, item will not be added to the MAR sheet";

  const dialogFooter = (
    <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
      <div className="flex align-items-center gap-2 flex-wrap">
        <Button
          type="button"
          label={formData.active === false ? "Make Active" : "Delete"}
          severity={formData.active === false ? "success" : "danger"}
          outlined
          rounded
          loading={saving}
          disabled={saving}
          tooltip={formData.active === false ? undefined : deleteItemTooltipMessage}
          tooltipOptions={formData.active === false ? undefined : { position: "top" }}
          onClick={() => { void saveMedication({ active: formData.active === false }); }}
        />
      </div>
      <div className="flex align-items-center gap-2">
        <Button
          label="Close"
          severity="secondary"
          outlined
          onClick={() => {
            setShowProtocolSetupDialog(false);
            setShowEditDialog(false);
          }}
        />
        <Button
          label={formData?.medicationId ? "Update Item" : "Save Item"}
          severity="warning"
          rounded
          loading={saving}
          onClick={() => { void saveMedication(); }}
        />
      </div>
    </div>
  );

  return (
    <div className="container-fluid px-3 py-3 provider-resident-emar-page p-component">
      <Toast ref={toast} />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="eMAR"
        details={EMAR_SUBSCRIPTION_FEATURE_DETAILS}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      {generatingReport ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255, 255, 255, 0.72)",
            backdropFilter: "blur(1px)",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.9rem",
          }}
        >
          <ProgressSpinner style={{ width: "3.2rem", height: "3.2rem" }} />
          <div style={{ color: "#0f172a", fontWeight: 700 }}>Generating eMAR report...</div>
        </div>
      ) : null}
      <style>
        {`
          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }

          .provider-resident-emar-page .resident-workspace-tabs {
            width: 100%;
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            width: 100%;
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav li {
            margin: 0;
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
            transition: all 0.18s ease;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
            box-shadow: 0 0 0 0.18rem rgba(249, 115, 22, 0.16);
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
            border-color: #fdba74;
            background: linear-gradient(180deg, #ffffff 0%, #fff7ed 100%);
            color: #9a3412;
            transform: translateY(-1px);
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          .provider-resident-emar-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
            width: 100%;
          }

          .provider-resident-emar-page .p-datatable .p-datatable-tbody > tr.emar-mar-sheet-row-skipped > td {
            background: #fef2f2 !important;
          }

          .provider-resident-emar-page .p-datatable .p-datatable-tbody > tr.emar-mar-sheet-row-skipped > td:first-child {
            box-shadow: inset 4px 0 0 #ef4444;
          }

        `}
      </style>
      <input
        ref={marSheetFileInputRef}
        type="file"
        accept={MAR_SHEET_FILE_ACCEPT}
        style={{ display: "none" }}
        onChange={importFromMarSheetFile}
      />
      <div style={{ width: "100%" }}>
        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
          {renderSurfaceHeader(
            `${residentDisplayName} eMAR`,
            "Manage meds and chart today's doses.",
            "pi pi-table",
          )}
          {(
            todayPassSummary.missedScheduled + todayPassSummary.missed + dailyTrackingRows.reduce((total: number, row: any) => (
              total + row.occurrences.filter((occurrence: any) => {
                const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
                return status === "missed" || (!status && occurrence?.timingMeta?.isOverdue);
              }).length
            ), 0) > 0
            || todayPassSummary.expiringSoon > 0
            || todayPassSummary.expired > 0
          ) ? (
            <div className="flex gap-2 flex-wrap align-items-center" style={{ justifyContent: "flex-end", maxWidth: "36rem" }}>
              <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Today&apos;s Status:</span>
              {todayPassSummary.missedScheduled + todayPassSummary.missed + dailyTrackingRows.reduce((total: number, row: any) => (
                total + row.occurrences.filter((occurrence: any) => {
                  const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
                  return status === "missed" || (!status && occurrence?.timingMeta?.isOverdue);
                }).length
              ), 0) > 0 ? (
                <Tag
                  value={`${todayPassSummary.missedScheduled + todayPassSummary.missed + dailyTrackingRows.reduce((total: number, row: any) => (
                    total + row.occurrences.filter((occurrence: any) => {
                      const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
                      return status === "missed" || (!status && occurrence?.timingMeta?.isOverdue);
                    }).length
                  ), 0)} missed`}
                  severity="danger"
                  rounded
                />
              ) : null}
              {todayPassSummary.expiringSoon > 0 ? (
                <Tag value={`${todayPassSummary.expiringSoon} expiring soon`} severity="warning" rounded />
              ) : null}
              {todayPassSummary.expired > 0 ? (
                <Tag value={`${todayPassSummary.expired} expired`} severity="danger" rounded />
              ) : null}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="flex justify-content-center py-6">
            <ProgressSpinner style={{ width: "3rem", height: "3rem" }} />
          </div>
        ) : !isAdminUser && !isUserSubscribed ? (
          <div style={{ ...appInnerCardStyle, maxWidth: 760, margin: "0 auto", padding: "1rem 1.1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>eMAR subscription needed</div>
            <div className="mt-2" style={appMutedTextStyle}>
              This page is available with a provider subscription. Open the dialog to see details.
            </div>
          </div>
        ) : (
          <div className="mt-4">
            {medicationExpiryAlerts.length > 0 ? (
              <div
                style={{
                  ...appInnerCardStyle,
                  maxWidth: "52rem",
                  margin: "0 auto 1rem auto",
                  padding: "1rem 1.1rem",
                  border: "1px solid #f59e0b",
                  background: "linear-gradient(180deg, #fff7ed 0%, #fffaf3 100%)",
                  textAlign: "center",
                }}
              >
                <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
                  <div style={{ minWidth: 0, flex: "1 1 22rem", textAlign: "left" }}>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color: "#9a3412" }}>Medication Expiration Alerts</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "36rem" }}>
                      {showMedicationExpiryAlertsExpanded
                        ? "Review these active medications before charting today's passes."
                        : `${medicationExpiryAlerts.slice(0, 3).map((alert) => alert.medicationName).join(", ")}${medicationExpiryAlerts.length > 3 ? ` +${medicationExpiryAlerts.length - 3} more` : ""}`}
                    </div>
                  </div>
                  <Button
                    type="button"
                    label={showMedicationExpiryAlertsExpanded ? "Minimize" : "Show list"}
                    icon={showMedicationExpiryAlertsExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                    size="small"
                    rounded
                    outlined
                    severity="warning"
                    onClick={() => setShowMedicationExpiryAlertsExpanded((current) => !current)}
                  />
                </div>
                {showMedicationExpiryAlertsExpanded ? (
                  <div className="mt-3 flex flex-column gap-2">
                    {medicationExpiryAlerts.map((alert) => (
                      <div
                        key={`${alert.medicationId}-${alert.effectiveEndDate}`}
                        className="flex justify-content-between align-items-center gap-3 flex-wrap"
                        style={{
                          background: "#ffffff",
                          border: `1px solid ${alert.status === "expired" ? "#fecaca" : "#fed7aa"}`,
                          borderRadius: 14,
                          padding: "0.8rem 0.95rem",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ minWidth: 0, flex: "1 1 18rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{alert.medicationName}</div>
                          <div className="mt-1" style={appMutedTextStyle}>
                            {alert.status === "expired" ? "Expired on" : "Expires on"} {formatEmarDateOnly(alert.effectiveEndDate)}
                          </div>
                        </div>
                        <Tag value={alert.status === "expired" ? "Expired" : "Expiring soon"} severity={alert.status === "expired" ? "danger" : "warning"} rounded />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="mt-4">
              <TabView
                className="resident-workspace-tabs"
                scrollable
                activeIndex={activeTabIndex}
                onTabChange={(event) => handleEmarTabChange(event.index)}
              >
                <TabPanel header={`Today's Pass (${todayPassTabCount})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Today&apos;s Pass</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                          Review scheduled meds for today, then mark what happened.
                        </div>
                      </div>
                      <div className="flex align-items-center gap-2 flex-wrap" style={{ marginLeft: "auto" }}>
                        <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Filter</span>
                        <Dropdown
                          value={todayPassFilter}
                          options={[
                            { label: "All passes", value: "all" },
                            { label: "Pending", value: "pending" },
                            { label: "Charted", value: "charted" },
                          ]}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(event) => setTodayPassFilter(event.value as "all" | "pending" | "charted")}
                          style={{ minWidth: "12rem" }}
                        />
                        <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>View by</span>
                        <Button
                          type="button"
                          label="Item"
                          size="small"
                          rounded
                          severity={todayPassViewMode === "medication" ? "warning" : "secondary"}
                          outlined={todayPassViewMode !== "medication"}
                          onClick={() => setTodayPassViewMode("medication")}
                        />
                        <Button
                          type="button"
                          label="Time"
                          size="small"
                          rounded
                          severity={todayPassViewMode === "time" ? "warning" : "secondary"}
                          outlined={todayPassViewMode !== "time"}
                          onClick={() => setTodayPassViewMode("time")}
                        />
                      </div>
                    </div>
                    {todayPassViewMode === "medication" ? (
                      <div className="mt-3 flex flex-column gap-4">
                        <div>
                          <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a" }}>Scheduled Medications</div>
                          {activeNonPrnMedications.length === 0 ? (
                            <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                              Add scheduled meds first. Then today&apos;s charting will show here.
                              <div className="mt-3">
                                <Button
                                  type="button"
                                  label="Add New Medication"
                                  icon="pi pi-file-edit"
                                  severity="warning"
                                  rounded
                                  onClick={() => openCreateDialog()}
                                />
                              </div>
                            </div>
                          ) : filteredMedPassRows.length === 0 ? (
                            <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                              No scheduled medication passes match this filter.
                            </div>
                          ) : (
                            <>
                              <div className="block md:hidden">
                                <div className="flex flex-column gap-3">
                                  {filteredMedPassRows.map((row: any, index: number) => (
                                    <div
                                      key={row?.medicationId || row?.medication?.medicationId}
                                      style={{
                                        padding: "0.1rem 0",
                                        borderBottom: index === filteredMedPassRows.length - 1 ? "none" : "1px solid #e2e8f0",
                                      }}
                                    >
                                      {medPassMedicationBodyTemplate(row, true)}
                                      <div className="mt-3">
                                        {medPassStatusBodyTemplate(row)}
                                      </div>
                                      <div className="mt-3">
                                        {medPassActionsBodyTemplate(row)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="hidden md:block">
                                <DataTable value={filteredMedPassRows} responsiveLayout="scroll" size="small" stripedRows>
                                  <Column header="Medication" body={(row: any) => medPassMedicationBodyTemplate(row, true)} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                                  <Column header="Status" body={medPassStatusBodyTemplate} style={{ minWidth: "17rem" }} />
                                  <Column header="Actions" body={medPassActionsBodyTemplate} style={{ minWidth: "20rem" }} />
                                </DataTable>
                              </div>
                            </>
                          )}
                        </div>
                        {todaySummaryTrackingCount > 0 ? renderDailyTrackingRecordSection(filteredDailyTrackingRows) : null}
                      </div>
                    ) : (
                      <div className="mt-3 flex flex-column gap-3">
                        {activeNonPrnMedications.length === 0 && todaySummaryTrackingCount === 0 ? (
                          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                            Add scheduled meds or vital trackers first. Then today&apos;s charting will show here.
                            <div className="mt-3">
                              <Button
                                type="button"
                                label="Add New Medication"
                                icon="pi pi-file-edit"
                                severity="warning"
                                rounded
                                onClick={() => openCreateDialog()}
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            {renderTodayPassTimeSection("Upcoming", todayPassTimeSections.upcoming)}
                            {renderTodayPassTimeSection("Past", todayPassTimeSections.past)}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </TabPanel>

                <TabPanel header={`PRN Meds (${prnMedPassRows.length})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>PRN Meds</div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                        Review PRN meds here and chart them separately from scheduled meds.
                      </div>
                    </div>
                    <div className="mt-3">
                      {activePrnMedications.length === 0 ? (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          Add PRN meds first. Then PRN charting will show here.
                        </div>
                      ) : (
                        <>
                          <div className="block md:hidden">
                            <div className="flex flex-column gap-3">
                              {prnMedPassRows.map((row: any, index: number) => (
                                <div
                                  key={row?.medicationId || row?.medication?.medicationId}
                                  style={{
                                    padding: "0.1rem 0",
                                    borderBottom: index === prnMedPassRows.length - 1 ? "none" : "1px solid #e2e8f0",
                                  }}
                                >
                                  {medPassMedicationBodyTemplate(row, true)}
                                  <div className="mt-3">
                                    {medPassStatusBodyTemplate(row)}
                                  </div>
                                  <div className="mt-3">
                                    {medPassActionsBodyTemplate(row)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable value={prnMedPassRows} responsiveLayout="scroll" size="small" stripedRows>
                              <Column header="Medication" body={(row: any) => medPassMedicationBodyTemplate(row, true)} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                              <Column header="Last Entry" body={medPassStatusBodyTemplate} style={{ minWidth: "17rem" }} />
                              <Column header="Actions" body={medPassActionsBodyTemplate} style={{ minWidth: "20rem" }} />
                            </DataTable>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </TabPanel>

                {activeMarOnlyItems.length > 0 ? <TabPanel header={`MAR-Only Items (${activeMarOnlyItems.length})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>MAR-Only Items</div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                        Review MAR-only items that should stay on the MAR report but do not need daily charting actions.
                      </div>
                    </div>
                    <div className="mt-3">
                      {activeMarOnlyItems.length === 0 ? (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          No MAR-only items yet.
                        </div>
                      ) : (
                        <>
                          <div className="block md:hidden px-0">
                            <div className="flex flex-column gap-3">
                              {activeMarOnlyItems.map((row: any, index: number) => (
                                <div key={row?.medicationId || `${row?.medicationName || "medication"}-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                                  {detailsBodyTemplate(row)}
                                  <div className="mt-3 flex gap-2 flex-wrap align-items-center">
                                    {sourceBodyTemplate(row)}
                                    {statusBodyTemplate(row)}
                                  </div>
                                  <div className="mt-3">
                                    {actionBodyTemplate(row)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable value={activeMarOnlyItems} responsiveLayout="scroll" size="small" stripedRows>
                              <Column header="Medication" body={detailsBodyTemplate} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                              <Column header="Source" body={sourceBodyTemplate} style={{ minWidth: "10rem" }} />
                              <Column header="Status" body={statusBodyTemplate} style={{ width: "8rem" }} />
                              <Column header="Actions" body={actionBodyTemplate} style={{ width: "12rem" }} />
                            </DataTable>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </TabPanel> : null}

                <TabPanel header={`Vital Tracker (${activeDailyTrackingItems.length})`}>
                  {renderDailyTrackingManagementSection()}
                </TabPanel>

                <TabPanel header={`Med List (${activeDisplayMedications.length})`}>
                  {activeDisplayMedications.length === 0 ? (
                    <div style={{ ...appInnerCardStyle, textAlign: "center", background: "#f8fafc" }}>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Med List</div>
                      <div className="mt-2" style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0f172a" }}>No active medications yet</div>
                      <div className="mt-2" style={{ ...appMutedTextStyle, maxWidth: "40rem", margin: "0 auto" }}>
                        Add a med, add from the care plan, or add from a MAR sheet.
                      </div>
                      <div className="mt-3 flex justify-content-center gap-2 flex-wrap">
                        <Button type="button" label="Add Item" severity="warning" rounded onClick={() => setShowAddItemDialog(true)} />
                        <SplitButton
                          label="Add"
                          severity="warning"
                          rounded
                          outlined
                          style={{ width: "auto" }}
                          menuStyle={{ minWidth: "10rem" }}
                          disabled={importing || importingMarSheetFile}
                          model={[
                            {
                              label: "Add from Care Plan",
                              icon: "pi pi-file-edit",
                              command: () => { void importFromCarePlan(); },
                            },
                            {
                              label: "Add from MAR Sheet",
                              icon: "pi pi-upload",
                              command: () => { triggerMarSheetFileImport(); },
                            },
                          ]}
                          onClick={(event: any) => {
                            const menuButton = event?.currentTarget?.parentElement?.querySelector(".p-splitbutton-menubutton") as HTMLButtonElement | null;
                            menuButton?.click();
                          }}
                        />
                      </div>
                      {inactiveDisplayMedications.length ? (
                        <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
                          {inactiveDisplayMedications.length} inactive medication{inactiveDisplayMedications.length === 1 ? "" : "s"} already exist. Use the toggle below to see them.
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex flex-column gap-3">
                      {inactiveDisplayMedications.length || activeDisplayMedications.length ? (
                        <div style={appInnerCardStyle}>
                          <div className="px-3 pt-3">
                            <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                              <div>
                                <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Med List</div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                                  See all active and inactive meds in one place.
                                </div>
                              </div>
                              <Button
                                type="button"
                                label={resettingMedicationList ? "Resetting..." : "Reset List"}
                                icon={resettingMedicationList ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                                severity="danger"
                                outlined
                                rounded
                                disabled={resettingMedicationList || (activeDisplayMedications.length === 0 && activeDailyTrackingItems.length === 0)}
                                onClick={resetMedicationList}
                              />
                            </div>
                          </div>
                          <div className="flex justify-content-between align-items-center gap-2 flex-wrap px-3 pt-3">
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                              {showArchivedMedications
                                ? showExpiredOnlyMedications
                                  ? `Expired inactive meds (${expiredInactiveMedications.length})`
                                  : `Inactive meds (${inactiveDisplayMedications.length})`
                                : showDiscontinuedMedications
                                  ? `Discontinued meds (${discontinuedMedications.length})`
                                  : showExpiredOnlyMedications
                                    ? `Expired active meds (${expiredActiveMedications.length})`
                                    : `Active meds (${activeDisplayMedications.length})`}
                            </div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                              <span
                                style={{
                                  color: "#64748b",
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Filter
                              </span>
                              <Dropdown
                                value={medicationListFilter}
                                options={medicationListFilterOptions}
                                onChange={(e) => setMedicationListFilter(e.value)}
                                style={{ minWidth: "18rem" }}
                              />
                            </div>
                          </div>
                          <div className="block md:hidden px-3 pb-3">
                            <div className="flex flex-column gap-3">
                              {displayedMedications.map((row: any, index: number) => (
                                <div key={row?.medicationId || `${row?.medicationName || "medication"}-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                                  {detailsBodyTemplate(row)}
                                  <div className="mt-3 flex gap-2 flex-wrap align-items-center">
                                    {sourceBodyTemplate(row)}
                                    {statusBodyTemplate(row)}
                                  </div>
                                  <div className="mt-3">
                                    {actionBodyTemplate(row)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable
                              value={displayedMedications}
                              responsiveLayout="scroll"
                              emptyMessage={
                                showExpiredOnlyMedications
                                  ? showArchivedMedications
                                    ? "No expired inactive meds."
                                    : "No expired active meds."
                                  : showDiscontinuedMedications
                                    ? "No discontinued meds."
                                    : showArchivedMedications
                                      ? "No inactive meds."
                                      : "No active meds yet."
                              }
                              size="small"
                              stripedRows
                            >
                              <Column header="Medication" body={detailsBodyTemplate} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                              <Column header="Source" body={sourceBodyTemplate} style={{ minWidth: "10rem" }} />
                              <Column header="Status" body={statusBodyTemplate} style={{ width: "8rem" }} />
                              <Column header="Actions" body={actionBodyTemplate} style={{ width: "12rem" }} />
                            </DataTable>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </TabPanel>
                <TabPanel header="Past Entries">
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Past Entries</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                          Review medication and vital tracker charting for one day at a time, or chart any missed medication-pass slots from the active medication list for that date.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap align-items-end">
                        {loadingHistory ? <Tag value="Loading..." severity="info" rounded /> : null}
                        <div>
                          <label className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                            Filter
                          </label>
                          <Dropdown
                            value={historyStatusFilter}
                            options={[{ label: "All entries", value: "all" }, { label: "Missed", value: "missed" }]}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(event) => setHistoryStatusFilter(event.value as "all" | "missed")}
                            style={{ minWidth: "12rem" }}
                          />
                        </div>
                        <div>
                          <label className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                            Date
                          </label>
                          <Calendar
                            value={getCalendarDateFromDateKey(effectiveHistoryDate)}
                            minDate={getCalendarDateFromDateKey(earliestPastEntryEditDate) || undefined}
                            maxDate={getCalendarDateFromDateKey(latestPastEntryDate) || undefined}
                            onChange={(event) => {
                              const selectedDate = event.value instanceof Date ? getLosAngelesIsoDateOnly(event.value) : latestPastEntryDate;
                              setHistoryFilterDate(
                                selectedDate > latestPastEntryDate
                                  ? latestPastEntryDate
                                  : selectedDate < earliestPastEntryEditDate
                                    ? earliestPastEntryEditDate
                                    : selectedDate,
                              );
                            }}
                            dateFormat="yy-mm-dd"
                            showIcon
                            readOnlyInput
                            style={{ ...compactFieldStyle, minWidth: "12rem" }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Tag value={`${historySummary.total} entries`} severity="info" rounded />
                      <Tag value={`${historySummary.pending} not charted`} severity={historySummary.pending ? "warning" : "secondary"} rounded />
                      <Tag value={`${historySummary.attention} needs follow-up`} severity={historySummary.attention ? "danger" : "secondary"} rounded />
                      <Tag value={`${historySummary.prn} PRN`} severity={historySummary.prn ? "info" : "secondary"} rounded />
                      <Tag value={`${historySummary.reviewed} reviewed`} severity={historySummary.reviewed ? "success" : "secondary"} rounded />
                      <Tag value={`${historySummary.resolved} closed`} severity={historySummary.resolved ? "success" : "secondary"} rounded />
                    </div>
                    <div className="mt-3">
                      {filteredHistoryEntries.length ? (
                        <>
                          <div className="block md:hidden">
                            <div className="flex flex-column gap-3">
                              {filteredHistoryEntries.map((entry: any, index: number) => {
                                const status = String(entry?.status || "").toLowerCase();
                                const severity = getEmarStatusSeverity(status, entry);
                                const historyCount = Array.isArray(entry?.statusHistory) ? entry.statusHistory.length : 0;
                                const syntheticMissed = entry?.syntheticPending && status === "missed";
                                return (
                                  <div key={entry?.entryId || `${entry?.medicationId || "entry"}-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                      {getPastEntryName(entry)}
                                    </div>
                                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                      {getPastEntryDateLabel(entry)}
                                    </div>
                                    {entry?.medicationSnapshot?.dosage ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        <strong style={{ color: "#334155" }}>Dose:</strong> {entry.medicationSnapshot.dosage}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.route ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        <strong style={{ color: "#334155" }}>Route:</strong> {entry.medicationSnapshot.route}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.indication ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {entry.medicationSnapshot.indication}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.instructions ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Instructions:</strong> {entry.medicationSnapshot.instructions}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.handlingNotes ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Handling notes:</strong> {entry.medicationSnapshot.handlingNotes}
                                      </div>
                                    ) : null}
                                    <div className="mt-3 flex gap-2 flex-wrap">
                                      <Tag value={getPastEntryStatusTagLabel(entry)} severity={getPastEntryStatusTagSeverity(entry) as any} rounded />
                                      {entry?.exceptionReviewed ? <Tag value="Reviewed" severity="info" rounded /> : null}
                                      {entry?.exceptionResolved ? <Tag value="Resolved" severity="success" rounded /> : null}
                                      {historyCount > 1 ? <Tag value={`${historyCount} events`} severity="warning" rounded /> : null}
                                    </div>
                                    <div className="mt-3">
                                      {entry?.syntheticPending && !isDailyTrackingHistoryEntry(entry) ? (
                                        <span style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: syntheticMissed ? "#b91c1c" : undefined }}>
                                          {syntheticMissed
                                            ? "No chart was saved for this scheduled medication pass."
                                            : "No chart saved yet for this scheduled medication pass."}
                                        </span>
                                      ) : (
                                        getPastEntryNotes(entry)
                                      )}
                                      {entry?.exceptionFollowUpNote ? (
                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                          <strong style={{ color: "#334155" }}>Follow-up:</strong> {entry.exceptionFollowUpNote}
                                        </div>
                                      ) : null}
                                      {!entry?.syntheticPending && !entry?.note && !entry?.exceptionFollowUpNote && !isDailyTrackingHistoryEntry(entry) && String(entry?.status || "").toLowerCase() !== "prn_given" ? (
                                        <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>No extra notes</span>
                                      ) : null}
                                    </div>
                                    {entry?.syntheticPending ? null : (
                                      <div className="mt-3" style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                                        {formatEmarDateTime(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt)}
                                      </div>
                                    )}
                                    {entry?.recordedByName ? (
                                      <div className="mt-1" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                                        {entry.recordedByName}
                                        {entry?.recordedByRole ? ` • ${entry.recordedByRole}` : ""}
                                      </div>
                                    ) : null}
                                    <div className="mt-3 flex gap-2 flex-wrap">
                                      {entry?.syntheticPending ? null : (
                                        <Button
                                          type="button"
                                          label="Details"
                                          size="small"
                                          rounded
                                          severity="secondary"
                                          outlined
                                          icon="pi pi-list"
                                          onClick={() => openHistoryDialog(entry)}
                                        />
                                      )}
                                      {isDailyTrackingHistoryEntry(entry) ? (
                                        <Button
                                          type="button"
                                          label={entry?.syntheticPending ? "Chart" : "Fix"}
                                          size="small"
                                          rounded
                                          severity={entry?.syntheticPending ? "success" : "warning"}
                                          outlined
                                          icon={entry?.syntheticPending ? "pi pi-check-circle" : "pi pi-pencil"}
                                          disabled={!canEditPastEntry(entry)}
                                          onClick={() => entry?.syntheticPending
                                            ? openDailyTrackingHistoryChartDialog(entry)
                                            : openDailyTrackingHistoryFixDialog(entry)}
                                        />
                                      ) : (
                                        <Button
                                          type="button"
                                          label={entry?.syntheticPending
                                            ? (isPrnHistoryEntry(entry) ? "Chart PRN" : "Chart")
                                            : (isPrnHistoryEntry(entry) ? "Fix PRN" : "Fix")}
                                          size="small"
                                          rounded
                                          severity={entry?.syntheticPending ? "success" : "warning"}
                                          outlined
                                          icon={entry?.syntheticPending ? "pi pi-check-circle" : "pi pi-pencil"}
                                          disabled={!canEditPastEntry(entry)}
                                          onClick={() => openMedPassCorrectionDialog(entry)}
                                        />
                                      )}
                                      {deletePastEntryButton(entry)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable value={filteredHistoryEntries} responsiveLayout="scroll" size="small" stripedRows>
                              <Column
                                header="Medication"
                                body={(entry: any) => (
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                      {getPastEntryName(entry)}
                                    </div>
                                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                      {getPastEntryDateLabel(entry)}
                                    </div>
                                    {entry?.medicationSnapshot?.dosage ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        <strong style={{ color: "#334155" }}>Dose:</strong> {entry.medicationSnapshot.dosage}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.route ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        <strong style={{ color: "#334155" }}>Route:</strong> {entry.medicationSnapshot.route}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.indication ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {entry.medicationSnapshot.indication}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.instructions ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Instructions:</strong> {entry.medicationSnapshot.instructions}
                                      </div>
                                    ) : null}
                                    {entry?.medicationSnapshot?.handlingNotes ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Handling notes:</strong> {entry.medicationSnapshot.handlingNotes}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                                style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
                              />
                              <Column
                                header="Status"
                                body={(entry: any) => {
                                  const status = String(entry?.status || "").toLowerCase();
                                  const severity = getEmarStatusSeverity(status, entry);
                                  const historyCount = Array.isArray(entry?.statusHistory) ? entry.statusHistory.length : 0;
                                  const syntheticMissed = entry?.syntheticPending && status === "missed";
                                  return (
                                    <div className="flex gap-2 flex-wrap">
                                      <Tag value={getPastEntryStatusTagLabel(entry)} severity={getPastEntryStatusTagSeverity(entry) as any} rounded />
                                      {entry?.exceptionReviewed ? <Tag value="Reviewed" severity="info" rounded /> : null}
                                      {entry?.exceptionResolved ? <Tag value="Resolved" severity="success" rounded /> : null}
                                      {historyCount > 1 ? <Tag value={`${historyCount} events`} severity="warning" rounded /> : null}
                                    </div>
                                  );
                                }}
                                style={{ minWidth: "11rem" }}
                              />
                              <Column
                                header="Notes"
                                body={(entry: any) => (
                                  <div style={{ minWidth: 0 }}>
                                    {entry?.syntheticPending && !isDailyTrackingHistoryEntry(entry) ? (() => {
                                      const syntheticMissed = String(entry?.status || "").toLowerCase() === "missed";
                                      return (
                                        <span style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: syntheticMissed ? "#b91c1c" : undefined }}>
                                          {syntheticMissed
                                            ? "No chart was saved for this scheduled medication pass."
                                            : "No chart saved yet for this scheduled medication pass."}
                                        </span>
                                      );
                                    })() : getPastEntryNotes(entry)}
                                    {entry?.exceptionFollowUpNote ? (
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                        <strong style={{ color: "#334155" }}>Follow-up:</strong> {entry.exceptionFollowUpNote}
                                      </div>
                                    ) : null}
                                    {!entry?.syntheticPending && !entry?.note && !entry?.exceptionFollowUpNote && !isDailyTrackingHistoryEntry(entry) && String(entry?.status || "").toLowerCase() !== "prn_given" ? (
                                      <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>No extra notes</span>
                                    ) : null}
                                  </div>
                                )}
                                style={{ minWidth: "13rem" }}
                              />
                              <Column
                                header="Recorded"
                                body={(entry: any) => (
                                  <div style={{ minWidth: 0 }}>
                                    {entry?.syntheticPending ? (
                                      <div style={{ fontSize: "0.8rem", color: String(entry?.status || "").toLowerCase() === "missed" ? "#b91c1c" : "#b45309", fontWeight: 700 }}>
                                        {String(entry?.status || "").toLowerCase() === "missed" ? "Missed - no chart entry" : "Awaiting chart entry"}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                                        {formatEmarDateTime(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt)}
                                      </div>
                                    )}
                                    {entry?.recordedByName ? (
                                      <div className="mt-1" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                                        {entry.recordedByName}
                                        {entry?.recordedByRole ? ` • ${entry.recordedByRole}` : ""}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                                style={{ minWidth: "11rem" }}
                              />
                              <Column
                                header="Actions"
                                body={(entry: any) => (
                                  <div className="flex gap-2 flex-wrap">
                                    {entry?.syntheticPending ? null : (
                                      <Button
                                        type="button"
                                        label="Details"
                                        size="small"
                                        rounded
                                        severity="secondary"
                                        outlined
                                        icon="pi pi-list"
                                        onClick={() => openHistoryDialog(entry)}
                                      />
                                    )}
                                    {isDailyTrackingHistoryEntry(entry) ? (
                                      <Button
                                        type="button"
                                        label={entry?.syntheticPending ? "Chart" : "Fix"}
                                        size="small"
                                        rounded
                                        severity={entry?.syntheticPending ? "success" : "warning"}
                                        outlined
                                        icon={entry?.syntheticPending ? "pi pi-check-circle" : "pi pi-pencil"}
                                        disabled={!canEditPastEntry(entry)}
                                        onClick={() => entry?.syntheticPending
                                          ? openDailyTrackingHistoryChartDialog(entry)
                                          : openDailyTrackingHistoryFixDialog(entry)}
                                      />
                                    ) : (
                                      <Button
                                        type="button"
                                        label={entry?.syntheticPending
                                          ? (isPrnHistoryEntry(entry) ? "Chart PRN" : "Chart")
                                          : (isPrnHistoryEntry(entry) ? "Fix PRN" : "Fix")}
                                        size="small"
                                        rounded
                                        severity={entry?.syntheticPending ? "success" : "warning"}
                                        outlined
                                        icon={entry?.syntheticPending ? "pi pi-check-circle" : "pi pi-pencil"}
                                        disabled={!canEditPastEntry(entry)}
                                        onClick={() => openMedPassCorrectionDialog(entry)}
                                      />
                                    )}
                                    {deletePastEntryButton(entry)}
                                  </div>
                                )}
                                style={{ minWidth: "8rem" }}
                              />
                            </DataTable>
                          </div>
                        </>
                      ) : (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "0.75rem 0.5rem" }}>
                          No entries found for this date.
                        </div>
                      )}
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header="MAR Compliance">
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <div style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", height: "100%" }}>
                      <div className="flex flex-column justify-content-between gap-3" style={{ height: "100%" }}>
                        <div>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>Compare New Pharmacy MAR Sheet with Active Resident MAR in the Hub</div>
                          <div className="mt-1" style={appMutedTextStyle}>Upload a new pharmacy MAR sheet and review only medication and vital tracker differences.</div>
                        </div>
                        <div className="flex gap-2 flex-wrap align-items-center">
                          {effectiveLatestEmarCompareJobId || loadingLatestEmarCompareJob ? (
                            <Button type="button" label="Last Compare" severity="warning" rounded outlined loading={loadingLatestEmarCompareResult || loadingLatestEmarCompareJob} disabled={!effectiveLatestEmarCompareJobId} onClick={() => { void openLatestEmarCompareResult(); }} />
                          ) : null}
                          <Button type="button" label="Compare New MAR" severity="warning" rounded size="small" style={{ width: "auto", flex: "0 0 auto" }} onClick={() => setShowEmarCompareDialog(true)} />
                        </div>
                      </div>
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", height: "100%" }}>
                      <div className="flex flex-column justify-content-between gap-3" style={{ height: "100%" }}>
                        <div>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>Compare Psychotropic Medications</div>
                          <div className="mt-1" style={appMutedTextStyle}>Compare psychotropic medications in the active eMAR list with the resident&apos;s Care Plan Medication Management section.</div>
                        </div>
                        <Button type="button" label="Compare Psychotropic Meds" severity="warning" rounded size="small" style={{ width: "auto", flex: "0 0 auto", alignSelf: "flex-start" }} loading={loadingPsychotropicCompare} onClick={() => { void runPsychotropicComplianceComparison(); }} />
                      </div>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </TabView>
            </div>

            <div className="mt-4" style={{ display: "none" }}>
              <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Medication History & Audit</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                      Review past medication entries, exception handling, and the full status trail for each charted item.
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap align-items-center">
                    {loadingHistory ? <Tag value="Refreshing..." severity="info" rounded /> : null}
                    <Tag value={formatEmarDateOnly(effectiveHistoryDate) || effectiveHistoryDate} severity="secondary" rounded />
                  </div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Tag value={`${historySummary.total} entries`} severity="info" rounded />
                  <Tag value={`${historySummary.attention} attention`} severity={historySummary.attention ? "danger" : "secondary"} rounded />
                  <Tag value={`${historySummary.prn} PRN`} severity={historySummary.prn ? "info" : "secondary"} rounded />
                  <Tag value={`${historySummary.reviewed} reviewed`} severity={historySummary.reviewed ? "success" : "secondary"} rounded />
                  <Tag value={`${historySummary.resolved} resolved`} severity={historySummary.resolved ? "success" : "secondary"} rounded />
                </div>
                <div className="mt-3">
                  {filteredHistoryEntries.length ? (
                    <>
                      <DataTable value={filteredHistoryEntries} responsiveLayout="scroll" size="small" stripedRows>
                        <Column
                          header="Medication"
                          body={(entry: any) => (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                {getPastEntryName(entry)}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {getPastEntryDateLabel(entry)}
                              </div>
                            </div>
                          )}
                          style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
                        />
                        <Column
                          header="Status"
                          body={(entry: any) => {
                            const status = String(entry?.status || "").toLowerCase();
                            const severity = getEmarStatusSeverity(status, entry);
                            const historyCount = Array.isArray(entry?.statusHistory) ? entry.statusHistory.length : 0;
                            return (
                              <div className="flex gap-2 flex-wrap">
                                <Tag value={getPastEntryStatusTagLabel(entry)} severity={getPastEntryStatusTagSeverity(entry) as any} rounded />
                                {entry?.exceptionReviewed ? <Tag value="Reviewed" severity="info" rounded /> : null}
                                {entry?.exceptionResolved ? <Tag value="Resolved" severity="success" rounded /> : null}
                                {historyCount > 1 ? <Tag value={`${historyCount} events`} severity="warning" rounded /> : null}
                              </div>
                            );
                          }}
                          style={{ minWidth: "14rem" }}
                        />
                        <Column
                          header="Notes"
                          body={(entry: any) => (
                            <div style={{ minWidth: 0 }}>
                              {getPastEntryNotes(entry)}
                              {entry?.exceptionFollowUpNote ? (
                                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                                  <strong style={{ color: "#334155" }}>Follow-up:</strong> {entry.exceptionFollowUpNote}
                                </div>
                              ) : null}
                              {!entry?.note && !entry?.exceptionFollowUpNote && !isDailyTrackingHistoryEntry(entry) && String(entry?.status || "").toLowerCase() !== "prn_given" ? (
                                <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>No extra notes</span>
                              ) : null}
                            </div>
                          )}
                          style={{ minWidth: "20rem" }}
                        />
                        <Column
                          header="Recorded"
                          body={(entry: any) => (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                                {formatEmarDateTime(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt)}
                              </div>
                              {entry?.recordedByName ? (
                                <div className="mt-1" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                                  {entry.recordedByName}
                                  {entry?.recordedByRole ? ` â€¢ ${entry.recordedByRole}` : ""}
                                </div>
                              ) : null}
                            </div>
                          )}
                          style={{ minWidth: "14rem" }}
                        />
                        <Column
                          header="Actions"
                          body={(entry: any) => (
                            <div className="flex gap-2 flex-wrap">
                              {entry?.syntheticPending ? null : (
                                <Button
                                  type="button"
                                  label="Details"
                                  size="small"
                                  outlined
                                  rounded
                                  severity="secondary"
                                  icon="pi pi-list"
                                  onClick={() => openHistoryDialog(entry)}
                                />
                              )}
                              {isDailyTrackingHistoryEntry(entry) ? (
                                <Button
                                  type="button"
                                  label="Fix"
                                  size="small"
                                  outlined
                                  rounded
                                  severity="warning"
                                  icon="pi pi-pencil"
                                  onClick={() => openDailyTrackingHistoryFixDialog(entry)}
                                />
                              ) : (
                                <Button
                                  type="button"
                                  label="Correction"
                                  size="small"
                                  outlined
                                  rounded
                                  severity="warning"
                                  icon="pi pi-pencil"
                                  onClick={() => openMedPassCorrectionDialog(entry)}
                                />
                              )}
                            </div>
                          )}
                          style={{ width: "9rem" }}
                        />
                      </DataTable>
                    </>
                  ) : (
                    <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "0.75rem 0.5rem" }}>
                      No eMAR entry history found for this period yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog
        visible={showMedicationSetupDialog}
        modal
        header={renderSurfaceHeader("Get Started", "Choose how you want to start the eMAR list.", "pi pi-directions-right")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowMedicationSetupDialog(false)}
      >
        <div style={{ display: "grid", gap: "1rem" }}>
          <div style={appNeutralPanelStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              No active meds or vital trackers yet.
            </div>
            <div className="mt-2" style={appMutedTextStyle}>
              Add a medication item or vital sign tracker now, add medications from the care plan, or add from a MAR sheet.
            </div>
          </div>

          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              type="button"
              label="Add Item"
              severity="warning"
              rounded
              onClick={() => {
                setShowMedicationSetupDialog(false);
                setShowAddItemDialog(true);
              }}
            />
            <Button
              type="button"
              label="Add from Care Plan"
              severity="secondary"
              rounded
              outlined
              disabled={importing || importingMarSheetFile}
              onClick={() => {
                setShowMedicationSetupDialog(false);
                void importFromCarePlan();
              }}
            />
            <Button
              type="button"
              label="Add from MAR Sheet"
              severity="secondary"
              rounded
              outlined
              disabled={importing || importingMarSheetFile}
              onClick={() => {
                setShowMedicationSetupDialog(false);
                triggerMarSheetFileImport();
              }}
            />
            <Button
              type="button"
              label="Later"
              severity="secondary"
              outlined
              onClick={() => setShowMedicationSetupDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showCarePlanRequiredDialog}
        modal
        header={renderSurfaceHeader("Care Plan Needed", "Add meds to the care plan before importing them here.", "pi pi-file-edit")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={closeCarePlanRequiredDialog}
      >
        <div style={{ display: "grid", gap: "1rem" }}>
          <div style={appNeutralPanelStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              No meds were found in the resident care plan.
            </div>
            <div className="mt-2" style={appMutedTextStyle}>
              Update the care plan on the NCP page, then come back here and import again.
            </div>
          </div>

          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              type="button"
              label="Open NCP"
              severity="warning"
              rounded
              onClick={() => {
                const resolvedHomeId = String(residentDetails?.homeId || new URLSearchParams(location.search).get("homeId") || "").trim();
                closeCarePlanRequiredDialog();
                if (!resolvedHomeId || !residentId) {
                  return;
                }
                navigate(RouterAddress.getResidentNCPPagePath(resolvedHomeId, residentId));
              }}
            />
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={closeCarePlanRequiredDialog}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showExceptionDialog}
        style={{ width: "38rem", maxWidth: "96vw" }}
        modal
        onHide={closeExceptionDialog}
        header={renderSurfaceHeader(
          exceptionDialogMode === "resolve" ? "Close Medication Issue" : "Follow-up Note",
          exceptionDialogMode === "resolve"
            ? "Add a short note before closing this issue."
            : "Add a short note so the team knows the next step.",
          exceptionDialogMode === "resolve" ? "pi pi-check-circle" : "pi pi-file-edit",
        )}
        footer={renderDialogFooter({
          loading: savingExceptionAction,
          onCancel: closeExceptionDialog,
          onSubmit: saveExceptionDialogAction,
          submitLabel: exceptionDialogMode === "resolve" ? "Close Issue" : "Save Note",
        })}
      >
        <div className="pt-2">
          {selectedAttentionRow?.medicationName ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: "16px",
                padding: "0.85rem 0.95rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ fontWeight: 800, color: "#0f172a" }}>{selectedAttentionRow.medicationName}</div>
              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                {selectedAttentionRow?.detail || "Needs follow-up."}
              </div>
            </div>
          ) : null}
          <label className="block mb-2 font-medium">
            {exceptionDialogMode === "resolve" ? "Closing note" : "Follow-up note"}
          </label>
          <InputTextarea
            value={exceptionFollowUpDraft}
            onChange={(event) => setExceptionFollowUpDraft(event.target.value)}
            rows={5}
            placeholder={exceptionDialogMode === "resolve"
              ? "Example: Reviewed and confirmed. Issue is closed."
              : "Example: Review with caregiver before the next pass and confirm the reason."}
            style={compactFieldStyle}
          />
        </div>
      </Dialog>

      <Dialog
        visible={showMedPassActionDialog}
        style={protocolAssessmentDialogStyle}
        modal
        onHide={closeMedPassActionDialog}
        header={renderSurfaceHeader(
          pendingMedPassAction?.correctionMode
            ? "Fix Entry"
            : pendingMedPassAction?.status === "prn_given"
              ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                ? "PRN Follow-up"
                : "Record PRN Dose"
              : isGivenLikeAdministrationStatus(pendingMedPassAction?.status) && pendingMedPassAction?.showGivenTimeChoiceDialog
                ? `Choose ${isFamilyAdministrationStatus(pendingMedPassAction?.status) ? "Family" : "Given"} Time`
                : "Record Status",
          pendingMedPassAction?.correctionMode
            ? "Update the status, notes, or PRN details for this entry. The change stays in the audit trail."
            : pendingMedPassAction?.historyChartMode
              ? "Select the chart status for this missed med pass. If it was given, choose the scheduled time, current time, or exact time."
              : pendingMedPassAction?.status === "prn_given"
                ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                  ? "Add follow-up, effectiveness, and any side effects for this PRN med."
                  : "Add the reason before saving this PRN entry."
                : isGivenLikeAdministrationStatus(pendingMedPassAction?.status) && pendingMedPassAction?.showGivenTimeChoiceDialog
                  ? `Pick the correct ${isFamilyAdministrationStatus(pendingMedPassAction?.status) ? "family administration" : "administration"} time before saving this entry.`
                  : pendingMedPassAction?.status === "hospitalized"
                    ? "Add a note before saving this hospital status."
                    : "Add a short note before saving.",
          pendingMedPassAction?.status === "prn_given" ? "pi pi-bolt" : "pi pi-file-edit",
        )}
        footer={(() => {
          const protocolSummary = getProtocolSummaryForMedication({
            protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
            protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
            protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
          });
          const requiresProtocolResolution = protocolResolutionRequiredForSave(protocolSummary, pendingMedPassAction?.status);
          const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
          if (requiresProtocolResolution) {
            return (
              <div className="flex justify-content-end gap-2">
                <Button label="Close" severity="secondary" outlined rounded onClick={closeMedPassActionDialog} />
                {actionMeta ? (
                  <Button label={actionMeta.buttonLabel} severity="warning" rounded loading={savingEntry} onClick={saveMedPassActionDialog} />
                ) : null}
              </div>
            );
          }
          return renderDialogFooter({
            loading: savingEntry,
            onCancel: closeMedPassActionDialog,
            onSubmit: saveMedPassActionDialog,
            cancelButtonProps: { rounded: true },
            submitLabel: pendingMedPassAction?.correctionMode
              ? "Save Fix"
              : pendingMedPassAction?.status === "prn_given"
                ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                  ? "Save PRN Follow-up"
                  : "Save PRN"
                : "Save",
          });
        })()}
      >
        <div className="pt-2">
          {pendingMedPassAction?.medicationName && !(isGivenLikeAdministrationStatus(pendingMedPassAction?.status) && pendingMedPassAction?.showGivenTimeChoiceDialog) ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: "16px",
                padding: "0.85rem 0.95rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ fontWeight: 800, color: "#0f172a" }}>{pendingMedPassAction.medicationName}</div>
              <div className="mt-1 flex gap-2 flex-wrap">
                <Tag value={formatEmarStatusLabel(pendingMedPassAction?.status)} severity={getEmarStatusSeverity(pendingMedPassAction?.status) as any} rounded />
                {pendingMedPassAction?.recordedAt ? (
                  <Tag value={formatEmarDateTime(pendingMedPassAction.recordedAt)} severity="secondary" rounded />
                ) : null}
                {pendingMedPassAction?.entryDate ? (
                  <Tag value={formatEmarDateOnly(pendingMedPassAction.entryDate)} severity="secondary" rounded />
                ) : null}
              </div>
              {pendingMedPassAction?.correctionMode ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  {pendingMedPassAction?.recordedAt
                    ? `Originally charted ${formatEmarDateTime(pendingMedPassAction.recordedAt)}`
                    : "This fix will add a new audit event for this entry."}
                  {normalizeValue(pendingMedPassAction?.recordedByName) ? ` by ${normalizeValue(pendingMedPassAction.recordedByName)}.` : "."}
                </div>
              ) : null}
            </div>
          ) : null}
          {(() => {
            const protocolMedication = {
              protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
              protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
              protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
            };
            const protocolSummary = getProtocolSummaryForMedication(protocolMedication);
            const statusKey = String(pendingMedPassAction?.status || "").toLowerCase();
            const shouldShowProtocolSection = !!protocolSummary && ["given", "prn_given"].includes(statusKey);
            const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
            if (!shouldShowProtocolSection) {
              return null;
            }
            return (
              <div
                style={{
                  border: "1px solid #bfdbfe",
                  background: "#f8fbff",
                  borderRadius: "14px",
                  padding: "0.75rem 0.85rem",
                  marginBottom: "0.75rem",
                }}
              >
                <div style={{ fontWeight: 800, color: "#0f172a" }}>Chart Parameters</div>
                <div className="grid mt-2" style={{ rowGap: "0.5rem" }}>
                  {getProtocolAssessmentFields(protocolSummary, protocolAssessmentValues).map((field: any) => {
                    const fieldKey = String(field?.key || "").trim();
                    return (
                      <div className="col-12 md:col-6" key={fieldKey}>
                        <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ whiteSpace: "nowrap", fontSize: "0.84rem" }}>{getProtocolAssessmentFieldLabel(field)}</label>
                        {field?.inputType === "select" ? (
                          <Dropdown
                            value={protocolAssessmentValues[fieldKey] || ""}
                            options={(Array.isArray(field?.options) ? field.options : []).map((option: any) => ({ label: String(option), value: String(option) }))}
                            onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, String(event.value || ""))}
                            placeholder={getProtocolAssessmentFieldPlaceholder()}
                            style={{ ...protocolAssessmentInputStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                            className="p-inputtext-sm"
                          />
                        ) : (
                          <InputText
                            value={protocolAssessmentValues[fieldKey] || ""}
                            onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, event.target.value)}
                            inputMode="decimal"
                            placeholder={getProtocolAssessmentFieldPlaceholder()}
                            style={{ ...protocolAssessmentInputStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                            className="p-inputtext-sm"
                          />
                        )}
                        </div>
                      </div>
                    );
                  })}
                  {!protocolResolution ? (
                    <div className="col-12 flex justify-content-end">
                      <Button
                        type="button"
                        label="Show Recommendation"
                        icon="pi pi-bell"
                        severity="warning"
                        rounded
                        loading={resolvingProtocol}
                        onClick={() => { void resolvePendingProtocol(); }}
                      />
                    </div>
                  ) : null}
                  {protocolSummary?.instructions ? (
                    <div className="col-12" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                      <strong style={{ color: "#334155" }}>Setup:</strong> {protocolSummary.instructions}
                    </div>
                  ) : null}
                  {hasProtocolRecommendation(protocolResolution, pendingMedPassAction?.status, protocolDoseValue) ? (
                    <div className="col-12" style={{ gridColumn: "1 / -1", width: "100%", minWidth: 0 }}>
                          <div style={{ width: "100%", boxSizing: "border-box" }}>
                        <label className="block mb-2 font-medium">Recommendation</label>
                        <InputTextarea
                          value={[
                            shouldShowProtocolAction(protocolResolution, pendingMedPassAction?.status, protocolDoseValue)
                              ? `Recommended action: ${String(actionMeta?.actionLabel).trim()}`
                              : "",
                            String(actionMeta?.dosageLabel || protocolDoseValue || "").trim()
                              ? `Suggested dosage: ${String(actionMeta?.dosageLabel || protocolDoseValue).trim()}`
                              : "",
                            protocolResolution?.decisionNote ? `Why: ${protocolResolution.decisionNote}` : "",
                          ].filter(Boolean).join("\n")}
                          readOnly
                          rows={3}
                          autoResize
                          style={{ ...compactFieldStyle, width: "100%", display: "block", boxSizing: "border-box", background: "#fff7ed", color: "#9a3412", fontWeight: 700 }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })()}
          {pendingMedPassAction?.correctionMode ? (
            <div className="flex flex-column gap-3">
              <div className="emar-prn-fix-meta-grid">
                <div>
                  <label className="block mb-2 font-medium">New date</label>
                  <Calendar
                    value={getMedicationExpiryCalendarValue(pendingMedPassAction?.entryDate)}
                    onChange={(event) => {
                      const nextEntryDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                      setPendingMedPassAction((current: any) => ({
                        ...current,
                        entryDate: nextEntryDate,
                        recordedAt: updateIsoDateTimeParts(current?.recordedAt, nextEntryDate, getLocalTimeValueWithSecondsFromIso(current?.recordedAt)),
                      }));
                    }}
                    dateFormat="mm/dd/yy"
                    placeholder="Select date"
                    showButtonBar
                    maxDate={today}
                    monthNavigator
                    yearNavigator
                    yearRange="2020:2035"
                    style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium">New time</label>
                  <Dropdown
                    value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                    options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                    onChange={(event) => {
                      const nextTimeValue = String(event.value || "");
                      setPendingMedPassAction((current: any) => ({
                        ...current,
                        recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                      }));
                    }}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select time"
                    style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                  />
                </div>
              <div className="field">
                <label className="block mb-2 font-medium">New status</label>
                <Dropdown
                  value={pendingMedPassAction?.status || ""}
                  options={pendingMedPassAction?.isPrnMedication ? PRN_MED_PASS_CORRECTION_STATUS_OPTIONS : MED_PASS_CORRECTION_STATUS_OPTIONS}
                  onChange={(event) => {
                    const nextStatus = String(event.value || "");
                    setPendingMedPassAction((current: any) => ({
                      ...current,
                      status: nextStatus,
                    }));
                  }}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select status"
                  style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                />
              </div>
              </div>
              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                This updates the date and time and keeps the audit trail.
              </div>
              {pendingMedPassAction?.status === "prn_given" ? (
                <div className="emar-prn-fix-grid" style={{ whiteSpace: "nowrap" }}>
                  <div className="field">
                    <label className="block mb-2 font-medium">Reason for PRN</label>
                    <Dropdown
                      value={prnReasonCode}
                      options={PRN_REASON_OPTIONS}
                      onChange={(event) => setPrnReasonCode(String(event.value || ""))}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select a reason"
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field">
                    <label className="block mb-2 font-medium">Dose note</label>
                    <InputTextarea
                      value={medPassActionDraft}
                      onChange={(event) => setMedPassActionDraft(event.target.value)}
                      rows={1}
                      placeholder="Add details about this PRN dose."
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field">
                    <label className="block mb-2 font-medium">Time reassessed</label>
                    <InputText
                      value={prnReassessedAt}
                      onChange={(event) => setPrnReassessedAt(event.target.value)}
                      placeholder="Example: 3:00 PM"
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field">
                    <label className="block mb-2 font-medium">Effectiveness</label>
                    <Dropdown
                      value={prnEffectiveness}
                      options={PRN_EFFECTIVENESS_OPTIONS}
                      onChange={(event) => setPrnEffectiveness(String(event.value || ""))}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select effectiveness"
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field" style={{ order: 3 }}>
                    <label className="block mb-2 font-medium">Side effects seen</label>
                    <Dropdown
                      value={prnSideEffectCode}
                      options={PRN_SIDE_EFFECT_OPTIONS}
                      onChange={(event) => setPrnSideEffectCode(String(event.value || ""))}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select side effects"
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field" style={{ order: 5 }}>
                    <label className="block mb-2 font-medium">Follow-up note</label>
                    <InputTextarea
                      value={prnFollowUpNote}
                      onChange={(event) => setPrnFollowUpNote(event.target.value)}
                      rows={1}
                      placeholder="Example: Pain improved after reassessment."
                      style={compactFieldStyle}
                    />
                  </div>
                  <div className="field" style={{ order: 4 }}>
                    <label className="block mb-2 font-medium">Side effect note</label>
                    <InputTextarea
                      value={prnSideEffectNote}
                      onChange={(event) => setPrnSideEffectNote(event.target.value)}
                      rows={1}
                      placeholder="Add extra side effect context if needed."
                      style={compactFieldStyle}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ width: "100%", flex: "0 0 100%" }}>
                    <label className="block mb-2 font-medium">
                      {isGivenLikeAdministrationStatus(pendingMedPassAction?.status) ? "Change note" : "Note"}
                    </label>
                    <InputTextarea
                      value={medPassActionDraft}
                      onChange={(event) => setMedPassActionDraft(event.target.value)}
                      rows={4}
                      placeholder={isFamilyAdministrationStatus(pendingMedPassAction?.status)
                        ? "Optional: add a note about the family-administered dose."
                        : pendingMedPassAction?.status === "given"
                          ? "Optional: add a note about why this was changed."
                          : pendingMedPassAction?.status === "refused"
                            ? "Example: Corrected after chart review. Resident declined after explanation."
                            : pendingMedPassAction?.status === "not_given"
                              ? "Example: Corrected after chart review. Medication was not available at the scheduled pass."
                              : pendingMedPassAction?.status === "held"
                                ? "Example: Corrected after provider review. Held per instruction."
                                : pendingMedPassAction?.status === "missed"
                                  ? "Example: Corrected after chart review. Resident was away during pass."
                                  : "Example: Corrected after chart review. Resident was hospitalized and medication was not administered."}
                      style={{ ...compactFieldStyle, width: "100%", display: "block", boxSizing: "border-box" }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : pendingMedPassAction?.historyChartMode ? (
            <div className="flex flex-column gap-3">
              <div className="field">
                <label className="block mb-2 font-medium">Status</label>
                <Dropdown
                  value={pendingMedPassAction?.status || ""}
                  options={MED_PASS_CORRECTION_STATUS_OPTIONS}
                  onChange={(event) => {
                    const nextStatus = String(event.value || "");
                    setPendingMedPassAction((current: any) => {
                      if (!["given", "family"].includes(nextStatus)) {
                        return {
                          ...current,
                          status: nextStatus,
                        };
                      }
                      const nextGivenTimeState = buildGivenTimeSelectionState({
                        entryDate: String(current?.entryDate || "").trim() || todayDate,
                        scheduledClockTime: String(current?.scheduledClockTime || "").trim(),
                        baseTimestamp: String(current?.recordedAt || "").trim() || new Date().toISOString(),
                      });
                      return {
                        ...current,
                        status: nextStatus,
                        givenTimeChoice: current?.givenTimeChoice || nextGivenTimeState.givenTimeChoice,
                        recordedAt: current?.recordedAt || (nextGivenTimeState.givenTimeChoice === "scheduled" ? nextGivenTimeState.scheduledRecordedAt : nextGivenTimeState.currentRecordedAt),
                        showGivenTimeChoiceDialog: nextGivenTimeState.showGivenTimeChoiceDialog,
                        givenTimeOptions: nextGivenTimeState.givenTimeOptions,
                      };
                    });
                  }}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select status"
                  style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                />
              </div>
              {pendingMedPassAction?.status === "given" && pendingMedPassAction?.showGivenTimeChoiceDialog ? (
                <>
                  <div>
                    <label className="block mb-2 font-medium">Choose administration time</label>
                    <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 15rem), 1fr))", width: "100%", maxWidth: "36rem", margin: "0 auto", gap: "0.5rem" }}>
                      {(Array.isArray(pendingMedPassAction?.givenTimeOptions) ? pendingMedPassAction.givenTimeOptions : []).map((option: any) => {
                        const isSelected = pendingMedPassAction?.givenTimeChoice === option?.value;
                        return (
                          <div key={String(option?.value || "")} style={{ padding: 0 }}>
                            <button
                              type="button"
                              disabled={option?.disabled}
                              onClick={() => {
                                if (option?.disabled) {
                                  return;
                                }
                                setPendingMedPassAction((current: any) => ({
                                  ...current,
                                  givenTimeChoice: option?.value,
                                  recordedAt: option?.recordedAt || current?.recordedAt || "",
                                }));
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                border: `1px solid ${isSelected ? "#2563eb" : "#dbe3ef"}`,
                                borderRadius: "14px",
                                padding: "0.8rem 0.9rem",
                                background: option?.disabled ? "#f8fafc" : isSelected ? "#eff6ff" : "#ffffff",
                                color: option?.disabled ? "#94a3b8" : "#0f172a",
                                cursor: option?.disabled ? "not-allowed" : "pointer",
                              }}
                            >
                              <div style={{ fontWeight: 700 }}>{option?.label || "Time option"}</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {option?.helper || ""}
                              </div>
                            </button>
                          </div>
                        );
                      })}
                      {pendingMedPassAction?.givenTimeChoice === "custom" ? (
                        <div style={{ padding: 0 }}>
                          <div style={{ padding: 0 }}>
                            <Dropdown
                              value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                              options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                              onChange={(event) => {
                                const nextTimeValue = String(event.value || "");
                                setPendingMedPassAction((current: any) => ({
                                  ...current,
                                  recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                                }));
                              }}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Select time"
                              disabled={pendingMedPassAction?.givenTimeChoice !== "custom"}
                              style={{ ...compactFieldStyle, width: "100%" }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : (
                <div className="field" style={{ width: "100%", flex: "0 0 100%" }}>
                  <label className="block mb-2 font-medium">Note</label>
                  <InputTextarea
                    value={medPassActionDraft}
                    onChange={(event) => setMedPassActionDraft(event.target.value)}
                    rows={4}
                    placeholder={pendingMedPassAction?.status === "family"
                      ? "Optional: add a note about family administration."
                      : pendingMedPassAction?.status === "refused"
                        ? "Example: Resident declined this dose."
                        : pendingMedPassAction?.status === "not_given"
                          ? "Example: Medication was not available at the scheduled pass."
                          : pendingMedPassAction?.status === "held"
                            ? "Example: Held per provider instruction."
                            : pendingMedPassAction?.status === "missed"
                              ? "Example: Resident was away during the med pass."
                              : "Example: Resident was hospitalized and medication was not administered."}
                    style={{ ...compactFieldStyle, width: "100%", display: "block", boxSizing: "border-box" }}
                  />
                </div>
              )}
            </div>
          ) : isGivenLikeAdministrationStatus(pendingMedPassAction?.status) ? (
            <div className="flex flex-column gap-3">
              {pendingMedPassAction?.givenTimePromptReason ? (
                <div
                  style={{
                    border: "1px solid #fde68a",
                    background: "#fffbeb",
                    borderRadius: "14px",
                    padding: "0.9rem 1rem",
                    color: "#92400e",
                    fontSize: "0.9rem",
                    lineHeight: 1.55,
                  }}
                >
                  {pendingMedPassAction.givenTimePromptReason}
                </div>
              ) : null}
              {pendingMedPassAction?.showGivenTimeChoiceDialog ? <div>
                <label className="block mb-2 font-medium">Choose administration time</label>
                <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 15rem), 1fr))", width: "100%", maxWidth: "36rem", margin: "0 auto", gap: "0.5rem" }}>
                  {(Array.isArray(pendingMedPassAction?.givenTimeOptions) ? pendingMedPassAction.givenTimeOptions : []).map((option: any) => {
                    const isSelected = pendingMedPassAction?.givenTimeChoice === option?.value;
                    return (
                      <div key={String(option?.value || "")} style={{ padding: 0 }}>
                        <button
                          type="button"
                          disabled={option?.disabled}
                          onClick={() => {
                            if (option?.disabled) {
                              return;
                            }
                            setPendingMedPassAction((current: any) => ({
                              ...current,
                              givenTimeChoice: option?.value,
                              recordedAt: option?.recordedAt || current?.recordedAt || "",
                            }));
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            border: `1px solid ${isSelected ? "#2563eb" : "#dbe3ef"}`,
                            borderRadius: "14px",
                            padding: "0.8rem 0.9rem",
                            background: option?.disabled ? "#f8fafc" : isSelected ? "#eff6ff" : "#ffffff",
                            color: option?.disabled ? "#94a3b8" : "#0f172a",
                            cursor: option?.disabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{option?.label || "Time option"}</div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                            {option?.helper || ""}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                  {pendingMedPassAction?.givenTimeChoice === "custom" ? (
                    <div style={{ padding: 0 }}>
                      <div style={{ padding: 0 }}>
                        <Dropdown
                          value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                          options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                          onChange={(event) => {
                            const nextTimeValue = String(event.value || "");
                            setPendingMedPassAction((current: any) => ({
                              ...current,
                              recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                            }));
                          }}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select time"
                          disabled={pendingMedPassAction?.givenTimeChoice !== "custom"}
                          style={{ ...compactFieldStyle, width: "100%" }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div> : null}
              {pendingMedPassAction?.showGivenTimeChoiceDialog ? <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                This saves the medication as {isFamilyAdministrationStatus(pendingMedPassAction?.status) ? "administered by family" : "given"} for {formatEmarDateOnly(pendingMedPassAction?.entryDate)} using the selected administration time.
              </div> : null}
              <div className="field">
                <label className="block mb-2 font-medium">Note</label>
                <InputTextarea
                  value={medPassActionDraft}
                  onChange={(event) => setMedPassActionDraft(event.target.value)}
                  rows={4}
                  placeholder="Optional: add a note about this medication pass."
                  style={{ ...compactFieldStyle, width: "100%", display: "block", boxSizing: "border-box" }}
                />
              </div>
            </div>
          ) : pendingMedPassAction?.status === "prn_given" ? (
            <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))", gap: "0.75rem", alignItems: "start", whiteSpace: "nowrap" }}>
              <div className="field">
                <label className="block mb-2 font-medium">Chart time</label>
                <Dropdown
                  value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                  options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                  onChange={(event) => {
                    const nextTimeValue = String(event.value || "");
                    setPendingMedPassAction((current: any) => ({
                      ...current,
                      recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                    }));
                  }}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select time"
                  style={compactFieldStyle}
                />
              </div>
              <div className="field">
                <label className="block mb-2 font-medium">Reason for PRN</label>
                <Dropdown
                  value={prnReasonCode}
                  options={PRN_REASON_OPTIONS}
                  onChange={(event) => setPrnReasonCode(String(event.value || ""))}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select a reason"
                  style={compactFieldStyle}
                />
              </div>
              <div className="field">
                <label className="block mb-2 font-medium">Dose note</label>
                <InputTextarea
                  value={medPassActionDraft}
                  onChange={(event) => setMedPassActionDraft(event.target.value)}
                  rows={1}
                  placeholder="Example: Tylenol PRN given for headache at 2 PM."
                  style={compactFieldStyle}
                />
              </div>
              <div className="field">
                <label className="block mb-2 font-medium">Time reassessed</label>
                <InputText
                  value={prnReassessedAt}
                  onChange={(event) => setPrnReassessedAt(event.target.value)}
                  placeholder="Example: 3:00 PM"
                  style={compactFieldStyle}
                />
              </div>
              <div className="field">
                <label className="block mb-2 font-medium">Effectiveness</label>
                <Dropdown
                  value={prnEffectiveness}
                  options={PRN_EFFECTIVENESS_OPTIONS}
                  onChange={(event) => setPrnEffectiveness(String(event.value || ""))}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select effectiveness"
                  style={compactFieldStyle}
                />
              </div>
              <div className="field" style={{ order: 3 }}>
                <label className="block mb-2 font-medium">Side effects seen</label>
                <Dropdown
                  value={prnSideEffectCode}
                  options={PRN_SIDE_EFFECT_OPTIONS}
                  onChange={(event) => setPrnSideEffectCode(String(event.value || ""))}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select side effects"
                  style={compactFieldStyle}
                />
              </div>
              <div className="field" style={{ order: 5 }}>
                <label className="block mb-2 font-medium">Follow-up note</label>
                <InputTextarea
                  value={prnFollowUpNote}
                  onChange={(event) => setPrnFollowUpNote(event.target.value)}
                  rows={1}
                  placeholder="Example: Pain improved after reassessment."
                  style={compactFieldStyle}
                />
              </div>
              <div className="field" style={{ order: 4 }}>
                <label className="block mb-2 font-medium">Side effect note</label>
                <InputTextarea
                  value={prnSideEffectNote}
                  onChange={(event) => setPrnSideEffectNote(String(event.target.value || ""))}
                  rows={1}
                  placeholder="Add extra side effect context if needed."
                  style={compactFieldStyle}
                />
              </div>
            </div>
          ) : (
            <>
              {pendingMedPassAction?.status === "hospitalized"
                && pendingMedPassAction?.correctionMode !== true
                && pendingMedPassAction?.historyChartMode !== true ? (
                <div
                  style={{
                    border: "1px solid #fde68a",
                    background: "#fffbeb",
                    borderRadius: "14px",
                    padding: "0.9rem 1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div className="flex align-items-start gap-2">
                    <Checkbox
                      inputId="hospitalized-all-meds-toggle"
                      checked={applyHospitalizedToAllForDay}
                      onChange={(event) => setApplyHospitalizedToAllForDay(!!event.checked)}
                    />
                    <div>
                      <label htmlFor="hospitalized-all-meds-toggle" style={{ fontWeight: 700, color: "#92400e", cursor: "pointer" }}>
                        Mark all scheduled meds for this day as hospitalized
                      </label>
                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                        This will save hospitalized status for every scheduled medication pass on {formatEmarDateOnly(pendingMedPassAction?.entryDate)}.
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div style={{ width: "100%", flex: "0 0 100%" }}>
                <label className="block mb-2 font-medium">Note</label>
                <InputTextarea
                  value={medPassActionDraft}
                  onChange={(event) => setMedPassActionDraft(event.target.value)}
                  rows={4}
                  placeholder={pendingMedPassAction?.status === "refused"
                    ? "Example: Resident declined after explanation."
                    : pendingMedPassAction?.status === "not_given"
                      ? "Example: Medication was not available at the scheduled pass."
                      : pendingMedPassAction?.status === "held"
                        ? "Example: Held per provider instruction."
                        : pendingMedPassAction?.status === "missed"
                          ? "Example: Resident was away during pass."
                          : "Example: Resident sent to hospital on 06/09/2026 and medication not administered."}
                  style={{ ...compactFieldStyle, width: "100%", display: "block", boxSizing: "border-box" }}
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={showProtocolConditionsDialog}
        style={{ width: "min(52rem, 96vw)" }}
        modal
        onHide={() => setShowProtocolConditionsDialog(false)}
        header={renderSurfaceHeader(
          "Protocol Conditions",
          "Review all configured parameter conditions and recommendations for this medication.",
          "pi pi-question-circle",
        )}
        footer={(
          <div className="flex justify-content-end">
            <Button label="Close" severity="secondary" outlined onClick={() => setShowProtocolConditionsDialog(false)} />
          </div>
        )}
      >
        {(() => {
          const protocolMedication = {
            protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
            protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
            protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
          };
          const protocolDefinitions = getProtocolSummariesForMedication(protocolMedication);
          return renderProtocolDefinitionsOverview(protocolDefinitions);
        })()}
      </Dialog>

      <CarePlanContactsDialog
        header="Missing Report Details"
        introText="Add the physician and pharmacy details needed for this MAR report. We will save them and continue."
        visible={showReportDetailsDialog}
        style={{ width: "44rem", maxWidth: "96vw" }}
        formData={reportContactForm}
        setFormData={setReportContactForm}
        errors={reportContactErrors}
        saving={savingReportDetails}
        onSave={saveReportDetailsAndContinue}
        saveLabel="Save and Continue"
        hideSaveToCarePlanText
        onHide={() => {
          if (!savingReportDetails) {
            setShowReportDetailsDialog(false);
            setReportContactErrors({});
          }
        }}
      />

      <Dialog
        visible={showGenerateReportDialog}
        style={{ width: "32rem", maxWidth: "96vw" }}
        modal
        onHide={() => {
          if (!generatingReport) {
            setShowGenerateReportDialog(false);
          }
        }}
        header={renderSurfaceHeader(
          "Generate eMAR Report",
          "Choose either a monthly report or an exact date range.",
          "pi pi-calendar",
        )}
      >
        <div className="pt-2 flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
              <Button
                type="button"
                label="Monthly"
                rounded
                outlined
                severity="secondary"
                style={{
                  ...reportRangeButtonStyle,
                  background: reportRangeMode === "monthly" ? "#f1f5f9" : "#fff",
                  color: "#0f172a",
                }}
                onClick={() => setReportRangeMode("monthly")}
              />
              <Button
                type="button"
                label="Exact dates"
                rounded
                outlined
                severity="secondary"
                style={{
                  ...reportRangeButtonStyle,
                  background: reportRangeMode === "exact_dates" ? "#f1f5f9" : "#fff",
                  color: "#0f172a",
                }}
                onClick={() => setReportRangeMode("exact_dates")}
              />
            </div>
            {reportRangeMode === "monthly" ? (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Month</div>
                <Calendar
                  value={reportMonthSelection}
                  onChange={(event) => {
                    const nextValue = event.value instanceof Date ? event.value : today;
                    setReportMonthSelection(nextValue);
                  }}
                  view="month"
                  dateFormat="M yy"
                  maxDate={today}
                  showButtonBar
                  yearNavigator
                  yearRange="2020:2035"
                  showIcon
                  style={{ width: "100%" }}
                />
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  The monthly report generates the full selected month.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Exact date range</div>
                <Calendar
                  value={reportExactDateSelection as Date[] | null}
                  onChange={(event) => {
                    const nextValue = Array.isArray(event.value) ? event.value as (Date | null)[] : null;
                    setReportExactDateSelection(nextValue);
                    const nextStart = nextValue?.[0] instanceof Date ? getIsoDateOnly(nextValue[0]) : "";
                    const nextEnd = nextValue?.[1] instanceof Date ? getIsoDateOnly(nextValue[1]) : "";
                    setReportStartDateSelection(nextStart);
                    setReportEndDateSelection(nextEnd || nextStart);
                  }}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  maxDate={today}
                  showIcon
                  style={{ width: "100%" }}
                />
                <div className="mt-2 flex gap-2 flex-wrap align-items-center">
                  <Tag
                    value={`${getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) || 0} day range`}
                    severity={getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) > 31 ? "danger" : "info"}
                    rounded
                  />
                  {getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) > 31 ? (
                    <Tag value="Max 31 days" severity="danger" rounded />
                  ) : null}
                </div>
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  Choose any exact date range up to 31 days total.
                </div>
              </>
            )}
            <div className="mt-3">
              <label style={{ display: "flex", alignItems: "center", gap: "0.7rem", cursor: "pointer" }}>
                <Checkbox
                  inputId="emar-blank-report-checkbox"
                  checked={blankMarReport}
                  onChange={(event) => setBlankMarReport(Boolean(event.checked))}
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>Generate blank MAR report</span>
              </label>
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                Keep medications, schedules, resident details, and contacts the same, but leave charting and exceptions blank.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              disabled={generatingReport}
              onClick={() => setShowGenerateReportDialog(false)}
            />
            <Button
              type="button"
              label="Download"
              severity="info"
              outlined
              rounded
              loading={generatingReport}
              onClick={async () => {
                await generateReport(false);
                setShowGenerateReportDialog(false);
              }}
            />
            <Button
              type="button"
              label="Add to Binder"
              severity="warning"
              rounded
              loading={generatingReport}
              onClick={async () => {
                await generateReport(true);
                setShowGenerateReportDialog(false);
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!entryToDelete}
        style={{ width: "32rem", maxWidth: "92vw" }}
        modal
        closable={!deletingEntry}
        onHide={() => {
          if (!deletingEntry) {
            setEntryToDelete(null);
          }
        }}
        header={renderSurfaceHeader(
          "Delete Past Entry",
          "This action keeps the entry for audit history but removes it from reports.",
          "pi pi-trash",
        )}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={deletingEntry}
              onClick={() => setEntryToDelete(null)}
            />
            <Button
              type="button"
              label="Mark Deleted"
              severity="danger"
              rounded
              loading={deletingEntry}
              onClick={() => { void markPastEntryDeleted(); }}
            />
          </div>
        )}
      >
        <div className="flex flex-column gap-2">
          <div style={{ fontWeight: 700, color: "#0f172a" }}>
            {getPastEntryName(entryToDelete)}
          </div>
          <div style={{ color: "#334155", lineHeight: 1.5 }}>
            Report will exclude this entry. Are you sure you want to mark it as deleted?
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showHistoryDialog}
        style={{ width: "52rem", maxWidth: "96vw" }}
        modal
        onHide={() => {
          setShowHistoryDialog(false);
          setSelectedHistoryEntry(null);
        }}
        header={renderSurfaceHeader(
          "Entry History",
          "Review the full timeline, notes, and follow-up for this entry.",
          "pi pi-list",
        )}
      >
        <div className="pt-2">
          {selectedHistoryEntry ? (
            <>
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  borderRadius: "16px",
                  padding: "0.9rem 1rem",
                }}
              >
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                      {selectedHistoryEntry?.medicationSnapshot?.medicationName || selectedHistoryEntry?.medicationName || "Medication"}
                    </div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                      {`${formatEmarDateOnly(selectedHistoryEntry?.entryDate)}${normalizeValue(selectedHistoryEntry?.scheduledTime) ? ` • ${normalizeValue(selectedHistoryEntry?.scheduledTime)}` : ""}`}
                    </div>
                    {selectedHistoryEntry?.note ? (
                      <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                        <strong style={{ color: "#334155" }}>Note:</strong> {selectedHistoryEntry.note}
                      </div>
                    ) : null}
                    {renderPrnDetailBlock(selectedHistoryEntry, "0.84rem")}
                    {selectedHistoryEntry?.exceptionFollowUpNote ? (
                      <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                        <strong style={{ color: "#334155" }}>Follow-up:</strong> {selectedHistoryEntry.exceptionFollowUpNote}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-content-end">
                    <Tag value={formatEmarStatusLabel(selectedHistoryEntry?.status)} severity={getEmarStatusSeverity(selectedHistoryEntry?.status, selectedHistoryEntry) as any} rounded />
                    {selectedHistoryEntry?.exceptionReviewed ? <Tag value="Reviewed" severity="info" rounded /> : null}
                    {selectedHistoryEntry?.exceptionResolved ? <Tag value="Resolved" severity="success" rounded /> : null}
                  </div>
                </div>
              </div>

              <div
                className="mt-3"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {(Array.isArray(selectedHistoryEntry?.statusHistory) ? selectedHistoryEntry.statusHistory : []).length ? (
                  ([...(selectedHistoryEntry.statusHistory || [])].sort((first: any, second: any) => {
                    const firstTime = new Date(first?.createdAt || first?.recordedAt || first?.modifiedAt || 0).getTime();
                    const secondTime = new Date(second?.createdAt || second?.recordedAt || second?.modifiedAt || 0).getTime();
                    return secondTime - firstTime;
                  })).map((event: any, index: number) => (
                    <div
                      key={event?.eventId || `history-event-${index}`}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "14px",
                        padding: "0.85rem 0.95rem",
                        background: "#fff",
                      }}
                    >
                      <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            {formatEmarHistoryEventLabel(event?.eventType)}
                            {event?.status ? ` • ${formatEmarStatusLabel(event?.status)}` : ""}
                          </div>
                          {event?.scheduledTime ? (
                            <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                              <strong style={{ color: "#334155" }}>Scheduled time:</strong> {event.scheduledTime}
                            </div>
                          ) : null}
                          {renderPrnDetailBlock(event, "0.84rem")}
                          {event?.followUpNote ? (
                            <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                              <strong style={{ color: "#334155" }}>Follow-up:</strong> {event.followUpNote}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                            {formatEmarDateTime(event?.createdAt)}
                          </div>
                          {event?.createdBy ? (
                            <div className="mt-1" style={{ fontSize: "0.78rem", color: "#64748b" }}>
                              {event.createdBy}
                              {event?.createdByRole ? ` • ${event.createdByRole}` : ""}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "0.75rem 0.5rem" }}>
                    No detailed history found for this entry yet.
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        visible={showAddItemDialog}
        style={{ width: "50rem", maxWidth: "96vw" }}
        modal
        onHide={() => setShowAddItemDialog(false)}
        headerStyle={appDialogHeaderStyle}
        contentStyle={{ ...appDialogContentStyle, padding: "1rem" }}
        header={renderSurfaceHeader(
          "Add to eMAR",
          "Choose what you want to add. Each option is set up a little differently.",
          "pi pi-plus-circle",
        )}
      >
        <div className="grid">
          {[
            {
              title: "Add Medication",
              icon: "pi pi-file-edit",
              description: "Use for medications staff will chart during med pass or PRN workflow.",
              examples: "Examples: Miralax, Aspirin, Insulin",
              actionLabel: "Add Medication",
              onClick: () => {
                setShowAddItemDialog(false);
                openCreateDialog();
              },
            },
            {
              title: "Add Vital Tracker",
              icon: "pi pi-heart",
              description: "Use for readings staff record during today’s pass.",
              examples: "Examples: Blood Pressure, Pulse, Weight",
              actionLabel: "Add Tracking Item",
              onClick: () => {
                setShowAddItemDialog(false);
                openCreateDailyTrackingDialog();
              },
            },
            {
              title: "Add MAR-Only Item",
              icon: "pi pi-list-check",
              description: "Use for items that should appear on the MAR report but do not need daily charting actions.",
              examples: "Examples: Bed rail in place, Compression stockings on, Oxygen setup checked",
              actionLabel: "Add MAR-Only Item",
              onClick: () => {
                setShowAddItemDialog(false);
                openCreateMarOnlyDialog();
              },
            },
          ].map((option) => (
            <div key={option.title} className="col-12 lg:col-4">
              <div style={{ ...appInnerCardStyle, height: "100%", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div className="flex align-items-center gap-2">
                  <i className={option.icon} style={{ color: "#c2410c", fontSize: "1rem" }} />
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>{option.title}</div>
                </div>
                <div style={{ ...appMutedTextStyle, fontSize: "0.9rem", flex: 1 }}>{option.description}</div>
                <div style={{ fontSize: "0.82rem", color: "#475569", lineHeight: 1.5 }}>
                  <strong style={{ color: "#334155" }}>Examples:</strong> {option.examples.replace(/^Examples:\s*/, "")}
                </div>
                <Button type="button" label={option.actionLabel} severity="warning" rounded onClick={option.onClick} />
              </div>
            </div>
          ))}
        </div>
      </Dialog>

      <Dialog
        visible={showEditDialog}
        style={{ width: "46rem", maxWidth: "96vw" }}
        modal
        onHide={() => setShowEditDialog(false)}
        headerStyle={appDialogHeaderStyle}
        contentStyle={{ ...appDialogContentStyle, padding: "1rem 1rem 0.5rem" }}
        header={renderSurfaceHeader(
          formData?.marOnlyItem
            ? (formData?.medicationId ? "Update MAR-Only Item" : "Add MAR-Only Item")
            : (formData?.medicationId ? "Update Item" : "Add Item"),
          formData?.marOnlyItem
            ? "Use this for items that belong on the MAR report but do not need daily charting."
            : "Keep the med entry focused: essentials first, details only where needed.",
          "pi pi-pencil",
        )}
        footer={dialogFooter}
      >
        <div className="grid pt-2" style={{ padding: "0.25rem 0 0.25rem", rowGap: "0.5rem" }}>
          {formData?.marOnlyItem || formData?.discontinued ? null : <div className="col-12 md:col-4" style={medicationDialogToggleCardStyle}>
            <label className="mt-3 flex align-items-center gap-2" htmlFor="psychotropic-medication-toggle" style={{ cursor: "pointer", width: "fit-content" }}>
              <Checkbox
                inputId="psychotropic-medication-toggle"
                checked={psychotropicMedicationMode}
                onChange={(event) => {
                  const nextChecked = !!event.checked;
                  setPsychotropicMedicationMode(nextChecked);
                  if (!nextChecked) {
                    setMedicationSuggestions([]);
                    setFormData((current: any) => ({
                      ...current,
                      psychotropicDrugClass: "",
                    }));
                  }
                }}
              />
              <span style={{ fontWeight: 600, color: "#0f172a" }}>Psychotropic medication</span>
            </label>
            <div className="mt-2" style={medicationDialogFieldHintStyle}>
              Turn this on to search the psychotropic medication catalog and keep the class with the med.
            </div>
          </div>}
          {formData?.marOnlyItem || formData?.discontinued ? null : <div className="col-12 md:col-4" style={medicationDialogToggleCardStyle}>
            <label className="mt-3 flex align-items-center gap-2" htmlFor="prn-medication-toggle" style={{ cursor: "pointer", width: "fit-content" }}>
              <Checkbox
                inputId="prn-medication-toggle"
                checked={!!formData.prn}
                onChange={(event) => {
                  const nextPrn = !!event.checked;
                  const nextFrequency = nextPrn
                    ? "prn"
                    : String(formData?.frequency || "").trim().toLowerCase() === "prn"
                      ? ""
                      : String(formData?.frequency || "").trim().toLowerCase();
                  const nextScheduleTimes = nextPrn
                    ? []
                    : reconcileScheduleTimesForFrequency(nextFrequency, selectedScheduleTimes);
                  setSelectedScheduleTimes(nextScheduleTimes);
                  setFormData((current: any) => ({
                    ...current,
                    prn: nextPrn,
                    frequency: nextFrequency,
                    ...buildFormScheduleFields(nextScheduleTimes, {
                      scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                      scheduleDays: selectedScheduleDays,
                      scheduleMonthDays: selectedScheduleMonthDays,
                    }),
                  }));
                }}
              />
              <span style={{ fontWeight: 600, color: "#0f172a" }}>PRN medication</span>
            </label>
            <div className="mt-2" style={medicationDialogFieldHintStyle}>
              Use PRN for as-needed meds. Scheduled times will clear because PRN meds are charted when needed.
            </div>
          </div>}
          <div className={formData?.marOnlyItem ? "col-12 md:col-6" : "col-12 md:col-4"} style={medicationDialogToggleCardStyle}>
            <label className="mt-3 flex align-items-center gap-2" htmlFor="discontinued-medication-toggle" style={{ cursor: "pointer", width: "fit-content" }}>
              <Checkbox
                inputId="discontinued-medication-toggle"
                checked={!!formData?.discontinued}
                onChange={(event) => setFormData((current: any) => ({
                  ...current,
                  discontinued: !!event.checked,
                  discontinuedAt: event.checked ? (current?.discontinuedAt || todayDate) : "",
                  discontinueReason: event.checked ? current?.discontinueReason || "" : "",
                }))}
              />
              <span style={{ fontWeight: 600, color: "#0f172a" }}>
                {formData?.marOnlyItem ? "Item discontinued" : "Medication discontinued"}
              </span>
            </label>
            <div className="mt-2" style={medicationDialogFieldHintStyle}>
              {formData?.marOnlyItem
                ? "Discontinued items stay on the MAR report history, but future days will remain blank."
                : "Discontinued meds stay on the med list and can still be included in reports, but they will not show as due in today&apos;s med pass."}
            </div>
          </div>
          {formData?.discontinued ? (
            <>
              <div className="col-12" style={medicationDialogSectionStyle}>
                <label className="block mb-2 font-medium">Discontinue reason</label>
                <InputTextarea
                  value={formData.discontinueReason}
                  onChange={(event) => setFormData((current: any) => ({ ...current, discontinueReason: event.target.value }))}
                  rows={3}
                  placeholder="Example: Discontinued per provider order after follow-up."
                  style={compactFieldStyle}
                />
              </div>
              <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
                <label className="block mb-2 font-medium">Discontinued on</label>
                <div className="flex gap-2 flex-wrap align-items-end">
                  <Calendar
                    value={getMedicationExpiryCalendarValue(formData.discontinuedAt)}
                    onChange={(event) => {
                      const nextDiscontinuedDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                      setFormData((current: any) => ({ ...current, discontinuedAt: nextDiscontinuedDate }));
                    }}
                    dateFormat="mm/dd/yy"
                    placeholder="Select discontinue date"
                    showButtonBar
                    monthNavigator
                    yearNavigator
                    yearRange="2020:2035"
                    maxDate={today}
                    style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    outlined
                    rounded
                    aria-label="Clear discontinue date"
                    onClick={() => setFormData((current: any) => ({ ...current, discontinuedAt: "" }))}
                  />
                </div>
              </div>
            </>
          ) : null}
          <div style={{ display: formData?.discontinued ? "none" : "contents" }}>
            <div className="col-12" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">{formData?.marOnlyItem ? "Item name" : "Medication name"}</label>
              {psychotropicMedicationMode ? (
                <>
                  <AutoComplete
                    value={formData.medicationName}
                    suggestions={medicationSuggestions}
                    completeMethod={(event) => { void searchMedicationSuggestions(event); }}
                    field="productName"
                    dropdown={false}
                    delay={250}
                    itemTemplate={renderMedicationSuggestion}
                    onChange={(event) => {
                      const nextValue = typeof event.value === "string"
                        ? event.value
                        : event.value?.productName || "";
                      const nextPsychotropicDrugClass = typeof event.value === "string"
                        ? ""
                        : String(event.value?.drugClass || "").trim();
                      setFormData((current: any) => ({
                        ...current,
                        medicationName: nextValue,
                        psychotropicDrugClass: nextPsychotropicDrugClass,
                      }));
                    }}
                    placeholder="Search psychotropic medication"
                    style={compactFieldStyle}
                    inputClassName="w-full"
                  />
                  <div style={medicationDialogFieldHintStyle}>
                    Search the psychotropic catalog or type a custom medication name.
                  </div>
                </>
              ) : (
                <>
                  <InputText
                    value={formData.medicationName}
                    onChange={(event) => setFormData((current: any) => ({ ...current, medicationName: event.target.value }))}
                    placeholder={formData?.marOnlyItem ? "Example: Bed rail in place" : "Example: Sertraline"}
                    style={compactFieldStyle}
                  />
                  <div style={medicationDialogFieldHintStyle}>
                    {formData?.marOnlyItem ? "Use a short name that should appear clearly on the MAR report." : "Use free text for non-psychotropic medications."}
                  </div>
                </>
              )}
            </div>
            {!formData?.marOnlyItem && psychotropicMedicationMode && formData?.psychotropicDrugClass ? (
              <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
                <label className="block mb-2 font-medium">Psychotropic class</label>
                <InputText
                  value={formData.psychotropicDrugClass}
                  onChange={(event) => {
                    setFormData((current: any) => ({ ...current, psychotropicDrugClass: event.target.value }));
                  }}
                  placeholder="Psychotropic class"
                  style={compactFieldStyle}
                />
              </div>
            ) : null}
            {formData?.marOnlyItem ? null : <div className="col-12" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Dose</label>
              <div className="grid align-items-center">
                <div className="col-12 lg:col-5">
                  <Dropdown
                    value={DOSAGE_OPTIONS.some((option) => option.value === formData.dosage) ? formData.dosage : ""}
                    options={DOSAGE_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Common dose"
                    style={compactFieldStyle}
                    onChange={(event) => setFormData((current: any) => ({ ...current, dosage: event.value || "" }))}
                  />
                </div>
                <div className="col-12 lg:col-6">
                  <InputText
                    value={formData.dosage}
                    onChange={(event) => setFormData((current: any) => ({ ...current, dosage: event.target.value }))}
                    placeholder="Exact dose"
                    style={medicationDialogInlineTextFieldStyle}
                  />
                </div>
                <div className="col-12 lg:col-1 flex lg:justify-content-end">
                  <Button
                    type="button"
                    icon="pi pi-times"
                    outlined
                    rounded
                    aria-label="Clear dose"
                    onClick={() => setFormData((current: any) => ({ ...current, dosage: "" }))}
                  />
                </div>
              </div>
              <div style={medicationDialogFieldHintStyle}>
                Pick a common dose or type the exact MAR wording.
              </div>
            </div>}
            {formData?.marOnlyItem ? null : <div className="col-12" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Route</label>
              <div className="grid align-items-center">
                <div className="col-12 lg:col-5">
                  <Dropdown
                    value={ROUTE_OPTIONS.some((option) => option.value === formData.route) ? formData.route : ""}
                    options={ROUTE_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Common route"
                    style={compactFieldStyle}
                    onChange={(event) => setFormData((current: any) => ({ ...current, route: event.value || "" }))}
                  />
                </div>
                <div className="col-12 lg:col-6">
                  <InputText
                    value={formData.route}
                    onChange={(event) => setFormData((current: any) => ({ ...current, route: event.target.value }))}
                    placeholder="Exact route"
                    style={medicationDialogInlineTextFieldStyle}
                  />
                </div>
                <div className="col-12 lg:col-1 flex lg:justify-content-end">
                  <Button
                    type="button"
                    icon="pi pi-times"
                    outlined
                    rounded
                    aria-label="Clear route"
                    onClick={() => setFormData((current: any) => ({ ...current, route: "" }))}
                  />
                </div>
              </div>
              <div style={medicationDialogFieldHintStyle}>
                Pick a common route or type the exact MAR wording.
              </div>
            </div>}
            {formData?.marOnlyItem || formData?.prn ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Frequency</label>
              <div style={medicationFormPickerRowStyle}>
                <Dropdown
                  value={formData.frequency}
                  options={formData?.prn ? [{ label: "PRN", value: "prn" }] : FREQUENCY_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={formData?.prn ? "PRN" : "Select frequency"}
                  style={compactQuickPickFieldStyle}
                  disabled={!!formData?.prn}
                  onChange={(event) => {
                    const nextFrequency = String(event.value || "").trim().toLowerCase();
                    const nextScheduleTimes = reconcileScheduleTimesForFrequency(nextFrequency, selectedScheduleTimes);
                    setSelectedScheduleTimes(nextScheduleTimes);
                    setFormData((current: any) => ({
                      ...current,
                      frequency: nextFrequency,
                      ...buildFormScheduleFields(nextScheduleTimes, {
                        scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                        scheduleDays: selectedScheduleDays,
                        scheduleMonthDays: selectedScheduleMonthDays,
                      }),
                    }));
                  }}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  outlined
                  rounded
                  disabled={!!formData?.prn}
                  aria-label="Clear frequency"
                  onClick={() => setFormData((current: any) => ({ ...current, frequency: "" }))}
                />
              </div>
              <div style={medicationDialogFieldHintStyle}>
                {formData?.prn
                  ? "PRN meds do not need fixed schedule times."
                  : "Frequency helps suggest the right number of schedule times."}
              </div>
            </div>}
            {formData?.marOnlyItem || formData?.prn ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Schedule days</label>
              <Dropdown
                value={formData?.scheduleDayPattern || "daily"}
                options={SCHEDULE_DAY_PATTERN_OPTIONS}
                optionLabel="label"
                optionValue="value"
                placeholder="Select schedule days"
                style={compactFieldStyle}
                disabled={!!formData?.prn}
                onChange={(event) => {
                  const nextPattern = String(event.value || "daily").trim().toLowerCase();
                  const nextScheduleDays = nextPattern === "specific_days" ? selectedScheduleDays : [];
                  const nextScheduleMonthDays = nextPattern === "specific_month_days" ? selectedScheduleMonthDays : [];
                  setSelectedScheduleDays(nextScheduleDays);
                  setSelectedScheduleMonthDays(nextScheduleMonthDays);
                  setFormData((current: any) => ({
                    ...current,
                    ...buildFormScheduleFields(selectedScheduleTimes, {
                      scheduleDayPattern: nextPattern as any,
                      scheduleDays: nextScheduleDays,
                      scheduleMonthDays: nextScheduleMonthDays,
                    }),
                  }));
                }}
              />
              {String(formData?.scheduleDayPattern || "daily") === "specific_days" ? (
                <div className="mt-2">
                  <MultiSelect
                    value={selectedScheduleDays}
                    options={SCHEDULE_WEEKDAY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select weekdays"
                    selectedItemsLabel="{0} days selected"
                    showSelectAll={false}
                    style={compactFieldStyle}
                    onChange={(event) => {
                      const nextScheduleDays = normalizeMedicationScheduleDays(event.value || []);
                      setSelectedScheduleDays(nextScheduleDays);
                      setFormData((current: any) => ({
                        ...current,
                        ...buildFormScheduleFields(selectedScheduleTimes, {
                          scheduleDayPattern: "specific_days",
                          scheduleDays: nextScheduleDays,
                          scheduleMonthDays: [],
                        }),
                      }));
                    }}
                  />
                </div>
              ) : null}
              {String(formData?.scheduleDayPattern || "daily") === "specific_month_days" ? (
                <div className="mt-2">
                  <MultiSelect
                    value={selectedScheduleMonthDays}
                    options={SCHEDULE_MONTH_DAY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select month dates"
                    selectedItemsLabel="{0} dates selected"
                    showSelectAll={false}
                    style={compactFieldStyle}
                    onChange={(event) => {
                      const nextScheduleMonthDays = normalizeMedicationScheduleMonthDays(event.value || []);
                      setSelectedScheduleMonthDays(nextScheduleMonthDays);
                      setFormData((current: any) => ({
                        ...current,
                        ...buildFormScheduleFields(selectedScheduleTimes, {
                          scheduleDayPattern: "specific_month_days",
                          scheduleDays: [],
                          scheduleMonthDays: nextScheduleMonthDays,
                        }),
                      }));
                    }}
                  />
                </div>
              ) : null}
              <div style={medicationDialogFieldHintStyle}>
                {formData?.prn
                  ? "PRN meds are not limited to scheduled days."
                  : String(formData?.scheduleDayPattern || "daily") === "specific_days"
                    ? "Choose the weekdays this medication should appear on the eMAR."
                    : String(formData?.scheduleDayPattern || "daily") === "specific_month_days"
                      ? "Choose the month dates this medication should appear on the eMAR."
                      : String(formData?.scheduleDayPattern || "daily") === "alternate_days"
                        ? "Alternating days are counted from the effective start date when available."
                        : "Use this when the medication is not taken every day."}
              </div>
            </div>}
            {formData?.marOnlyItem || formData?.prn ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Schedule</label>
              <div style={medicationFormPickerRowStyle}>
                <MultiSelect
                  value={selectedScheduleTimes}
                  options={SCHEDULE_TIME_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  placeholder="Select times"
                  maxSelectedLabels={2}
                  selectedItemsLabel="{0} times selected"
                  showSelectAll={false}
                  style={compactFieldStyle}
                  disabled={!!formData?.prn}
                  onChange={(event) => {
                    const nextScheduleTimes = dedupeAndSortScheduleTimes(event.value || []);
                    setSelectedScheduleTimes(nextScheduleTimes);
                    setFormData((current: any) => ({
                      ...current,
                      ...buildFormScheduleFields(nextScheduleTimes, {
                        scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                        scheduleDays: selectedScheduleDays,
                        scheduleMonthDays: selectedScheduleMonthDays,
                      }),
                    }));
                  }}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  outlined
                  rounded
                  aria-label="Clear schedule"
                  disabled={!!formData?.prn}
                  onClick={() => {
                    setSelectedScheduleTimes([]);
                    setFormData((current: any) => ({
                      ...current,
                      ...buildFormScheduleFields([], {
                        scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                        scheduleDays: selectedScheduleDays,
                        scheduleMonthDays: selectedScheduleMonthDays,
                      }),
                    }));
                  }}
                />
              </div>
              <div className="flex gap-2 flex-wrap" style={{ marginTop: "0.5rem", minHeight: "2rem" }}>
                {selectedScheduleTimes.length ? selectedScheduleTimes.map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => removeScheduleTimeFromForm(time)}
                    style={{
                      border: "1px solid #bfdbfe",
                      borderRadius: "999px",
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      padding: "0.3rem 0.65rem",
                      cursor: "pointer",
                    }}
                  >
                    {formatStructuredScheduleTimeLabel(time)} ×
                  </button>
                )) : (
                  <div style={medicationDialogFieldHintStyle}>
                    {formData?.prn
                      ? "PRN meds do not need fixed schedule times."
                      : "Select one or more med times based on frequency."}
                  </div>
                )}
              </div>
            </div>}
            {formData?.marOnlyItem ? null : <div className="col-12" style={medicationDialogSectionStyle}>
              <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div className="block mb-2 font-medium">Parameters / Sliding scale</div>
                  <div style={medicationDialogFieldHintStyle}>
                    Add one or more parameter sets for insulin scales, hold parameters, PRN assessment protocols, and other meds that need staff to enter values before administrating.
                  </div>
                </div>
                <Button
                  type="button"
                  label="Edit Parameters"
                  icon="pi pi-sliders-h"
                  rounded
                  outlined
                  severity="warning"
                  onClick={openProtocolManagementDialog}
                />
              </div>
              <div className="mt-2" style={medicationDialogFieldHintStyle}>
                Up to {MAX_PROTOCOL_PARAMETERS_PER_MEDICATION} parameters per medication and up to {MAX_PROTOCOL_RULES_PER_PARAMETER} rules in each parameter.
              </div>
              {false && ((Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions : []).length ? (
                <div className="mt-3" style={medicationDialogSummaryCardStyle}>
                  <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div style={{ fontWeight: 700, color: "#9a3412" }}>Parameter sets added</div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, color: "#9a3412", fontSize: "0.84rem" }}>
                        Each set can carry its own instructions and rules for this medication.
                      </div>
                    </div>
                    <Tag
                      value={`${(Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions.length : 0)} parameter${(Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions.length : 0) === 1 ? "" : "s"}`}
                      severity="warning"
                      rounded
                    />
                  </div>
                  <div className="mt-3 flex flex-column gap-2">
                    {(formData.protocolDefinitions || []).map((definition: any, definitionIndex: number) => {
                      const categoryOption = getEmarProtocolCategoryOption(definition?.category);
                      return (
                        <div
                          key={definition?.parameterId || definitionIndex}
                          style={{ border: "1px solid #fdba74", background: "#ffffff", borderRadius: "12px", padding: "0.75rem 0.85rem" }}
                        >
                          <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                            <div>
                              <div style={{ fontWeight: 700, color: "#7c2d12", fontSize: "0.84rem" }}>
                                {categoryOption?.label || `Parameter ${definitionIndex + 1}`}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {categoryOption?.guidanceAction || "Custom recommendation"} • {(Array.isArray(definition?.rules) ? definition.rules.length : 0)} rule{Array.isArray(definition?.rules) && definition.rules.length === 1 ? "" : "s"}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                type="button"
                                label="Edit"
                                outlined
                                rounded
                                onClick={() => openEditProtocolDefinitionDialog(definition, definitionIndex)}
                              />
                              <Button
                                type="button"
                                icon="pi pi-trash"
                                outlined
                                rounded
                                severity="danger"
                                onClick={() => removeProtocolDefinition(definitionIndex)}
                              />
                            </div>
                          </div>
                          {String(definition?.instructions || "").trim() ? (
                            <div className="mt-2" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap", fontSize: "0.82rem" }}>
                              <strong style={{ color: "#9a3412" }}>Instructions:</strong> {definition.instructions}
                            </div>
                          ) : null}
                          {(Array.isArray(definition?.rules) ? definition.rules : []).length ? (
                            <div className="mt-2 flex flex-column gap-2">
                              {(definition.rules || []).map((rule: any, ruleIndex: number) => (
                                <div key={rule?.ruleId || ruleIndex} style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                  <strong style={{ color: "#7c2d12" }}>{String(rule?.label || "").trim() || `Rule ${ruleIndex + 1}`}:</strong>{" "}
                                  {getProtocolRuleSummaryParts(rule, definition?.category || "sliding_scale_insulin").join(" • ")}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex justify-content-end">
                    <Button
                      type="button"
                      label="New Parameter"
                      icon="pi pi-plus"
                      rounded
                      outlined
                      onClick={openNewProtocolDefinitionDialog}
                      disabled={(Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions.length : 0) >= MAX_PROTOCOL_PARAMETERS_PER_MEDICATION}
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-3" style={medicationDialogFieldHintStyle}>
                  No parameter sets added yet. Use New Parameter to add one for this medication.
                </div>
              ))}
              <div className="mt-3" style={medicationDialogFieldHintStyle}>
                {(Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions.length : 0) === 1
                  ? "1 parameter is set."
                  : `${Array.isArray(formData?.protocolDefinitions) ? formData.protocolDefinitions.length : 0} parameters are set.`}
              </div>
            </div>}
            <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Start date</label>
              <div className="flex gap-2 flex-wrap align-items-end">
                <Calendar
                  value={getMedicationExpiryCalendarValue(formData.effectiveStartDate)}
                  onChange={(event) => {
                    const nextStartDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                    setFormData((current: any) => ({ ...current, effectiveStartDate: nextStartDate }));
                  }}
                  dateFormat="mm/dd/yy"
                  placeholder="Select start date"
                  showButtonBar
                  monthNavigator
                  yearNavigator
                  yearRange="2020:2035"
                  style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  outlined
                  rounded
                  aria-label="Clear start date"
                  onClick={() => setFormData((current: any) => ({ ...current, effectiveStartDate: "" }))}
                />
              </div>
            </div>
            {formData?.marOnlyItem ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Expiration</label>
              <div className="flex gap-2 flex-wrap align-items-end">
                <Calendar
                  value={getMedicationExpiryCalendarValue(formData.effectiveEndDate)}
                  onChange={(event) => {
                    const nextExpiryDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                    setFormData((current: any) => ({ ...current, effectiveEndDate: nextExpiryDate }));
                  }}
                  dateFormat="mm/dd/yy"
                  placeholder="Select expiry"
                  showButtonBar
                  monthNavigator
                  yearNavigator
                  yearRange="2020:2035"
                  style={{ ...compactFieldStyle, maxWidth: "14rem" }}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  outlined
                  rounded
                  aria-label="Clear expiration"
                  onClick={() => setFormData((current: any) => ({ ...current, effectiveEndDate: "" }))}
                />
              </div>
            </div>}
            {formData?.marOnlyItem ? null : <div className="col-12" style={{ paddingBottom: 0 }}>
              <div className="flex justify-content-center">
                <Button
                  type="button"
                  label="Use AI to Generate Instructions"
                  icon="pi pi-sparkles"
                  severity="warning"
                  outlined
                  rounded
                  loading={generatingMedicationInstructions}
                  disabled={!String(formData?.medicationName || "").trim() || generatingMedicationInstructions}
                  onClick={() => { void generateMedicationInstructions(); }}
                />
              </div>
              <div className="mt-2 text-center" style={medicationDialogFieldHintStyle}>
                Fills Reason or indication, Instructions, and Handling notes.
              </div>
            </div>}
            {formData?.marOnlyItem ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Reason or indication</label>
              <InputTextarea
                value={formData.indication}
                onChange={(event) => setFormData((current: any) => ({ ...current, indication: event.target.value }))}
                rows={3}
                placeholder="What is this medication for?"
                style={compactFieldStyle}
              />
            </div>}
            <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Instructions</label>
              <InputTextarea
                value={formData.instructions}
                onChange={(event) => setFormData((current: any) => ({ ...current, instructions: event.target.value }))}
                rows={3}
                placeholder="Timing, food instructions, or special directions."
                style={compactFieldStyle}
              />
            </div>
            {formData?.marOnlyItem ? null : <div className="col-12 md:col-6" style={medicationDialogSectionStyle}>
              <label className="block mb-2 font-medium">Handling notes</label>
              <InputTextarea
                value={formData.handlingNotes}
                onChange={(event) => setFormData((current: any) => ({ ...current, handlingNotes: event.target.value }))}
                rows={3}
                placeholder="Storage, crushing, delegation, or handling reminders."
                style={compactFieldStyle}
              />
            </div>}
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showDailyTrackingEditDialog}
        style={{ width: "48rem", maxWidth: "96vw" }}
        modal
        onHide={closeDailyTrackingEditDialog}
        headerStyle={appDialogHeaderStyle}
        contentStyle={{ ...appDialogContentStyle, padding: "1rem 1rem 0.5rem" }}
        header={renderSurfaceHeader(
          dailyTrackingFormData?.trackingItemId ? "Update Vital Tracker" : "Add Vital Tracker",
          "Set up non-med daily charting that should appear on the eMAR workflow.",
          "pi pi-heart",
        )}
        footer={(
          <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
            <div>
              {dailyTrackingFormData?.trackingItemId ? (
                <Button
                  type="button"
                  label="Delete"
                  rounded
                  outlined
                  severity="danger"
                  icon="pi pi-trash"
                  tooltip={deleteItemTooltipMessage}
                  tooltipOptions={{ position: "top" }}
                  onClick={() => {
                    const itemToDelete = { ...dailyTrackingFormData };
                    closeDailyTrackingEditDialog();
                    deleteDailyTrackingItem(itemToDelete);
                  }}
                />
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                outlined
                onClick={closeDailyTrackingEditDialog}
                disabled={savingDailyTrackingItem}
              />
              <Button
                type="button"
                label={dailyTrackingFormData?.trackingItemId ? "Update Vital Tracker" : "Save Vital Tracker"}
                severity="warning"
                rounded
                loading={savingDailyTrackingItem}
                onClick={saveDailyTrackingItem}
              />
            </div>
          </div>
        )}
      >
        <div className="grid pt-2" style={{ padding: "0.25rem 0 0.25rem", rowGap: "0.5rem" }}>
          {dailyTrackingFormData?.discontinued ? null : <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
            <label className="block mb-2 font-medium">Tracking type</label>
            <Dropdown
              value={dailyTrackingFormData?.category || "blood_pressure"}
              options={EMAR_DAILY_TRACKING_CATEGORY_OPTIONS}
              optionLabel="label"
              optionValue="value"
              style={compactFieldStyle}
              onChange={(event) => {
                const nextCategory = String(event.value || "blood_pressure");
                const nextOption = getEmarDailyTrackingCategoryOption(nextCategory);
                setDailyTrackingFormData((current: any) => ({
                  ...current,
                  category: nextCategory,
                  name: current?.trackingItemId ? current?.name : buildEmarDailyTrackingDefaultName(nextCategory),
                  targetSchema: nextOption?.parameterSchema || [],
                  protocolCategory: current?.protocolCategory || getEmarDailyTrackingProtocolCategory(nextCategory),
                }));
              }}
            />
            <div style={medicationDialogFieldHintStyle}>
              {getEmarDailyTrackingCategoryOption(dailyTrackingFormData?.category)?.guidance || "Choose what staff should chart."}
            </div>
          </div>}
          {dailyTrackingFormData?.discontinued ? null : <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
            <label className="block mb-2 font-medium">Vital tracker name</label>
            <InputText
              value={dailyTrackingFormData?.name || ""}
              onChange={(event) => setDailyTrackingFormData((current: any) => ({ ...current, name: event.target.value }))}
              placeholder="Example: Morning blood pressure"
              style={compactFieldStyle}
            />
          </div>}
          <div className="col-12 md:col-6" style={medicationDialogToggleCardStyle}>
            <label className="mt-3 flex align-items-center gap-2" htmlFor="discontinued-daily-tracking-toggle" style={{ cursor: "pointer", width: "fit-content" }}>
              <Checkbox
                inputId="discontinued-daily-tracking-toggle"
                checked={!!dailyTrackingFormData?.discontinued}
                onChange={(event) => setDailyTrackingFormData((current: any) => ({
                  ...current,
                  discontinued: !!event.checked,
                  discontinuedAt: event.checked ? (current?.discontinuedAt || todayDate) : "",
                  discontinueReason: event.checked ? current?.discontinueReason || "" : "",
                }))}
              />
              <span style={{ fontWeight: 600, color: "#0f172a" }}>Vital tracker discontinued</span>
            </label>
            <div className="mt-2" style={medicationDialogFieldHintStyle}>
              Discontinued vital trackers stay in history, but future charting stays blank.
            </div>
          </div>
          {dailyTrackingFormData?.discontinued ? (
            <>
              <div className="col-12" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
                <label className="block mb-2 font-medium">Discontinue reason</label>
                <InputTextarea
                  value={dailyTrackingFormData?.discontinueReason || ""}
                  onChange={(event) => setDailyTrackingFormData((current: any) => ({ ...current, discontinueReason: event.target.value }))}
                  rows={3}
                  placeholder="Explain why this vital tracker was discontinued."
                  style={compactFieldStyle}
                />
              </div>
              <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
                <label className="block mb-2 font-medium">Discontinued on</label>
                <div className="flex gap-2 flex-wrap align-items-end">
                  <Calendar
                    value={getMedicationExpiryCalendarValue(dailyTrackingFormData?.discontinuedAt)}
                    onChange={(event) => {
                      const nextDiscontinuedDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                      setDailyTrackingFormData((current: any) => ({ ...current, discontinuedAt: nextDiscontinuedDate }));
                    }}
                    dateFormat="mm/dd/yy"
                    placeholder="Select discontinue date"
                    showButtonBar
                    monthNavigator
                    yearNavigator
                    yearRange="2020:2035"
                    maxDate={today}
                    style={compactFieldStyle}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    outlined
                    rounded
                    aria-label="Clear discontinue date"
                    onClick={() => setDailyTrackingFormData((current: any) => ({ ...current, discontinuedAt: "" }))}
                  />
                </div>
              </div>
            </>
          ) : null}
          <div style={{ display: dailyTrackingFormData?.discontinued ? "none" : "contents" }}>
            <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">Frequency</label>
              <Dropdown
                value={dailyTrackingFormData?.frequency || ""}
                options={FREQUENCY_OPTIONS}
                optionLabel="label"
                optionValue="value"
                placeholder="Select frequency"
                style={compactFieldStyle}
                onChange={(event) => {
                  const nextFrequency = String(event.value || "").trim().toLowerCase();
                  const nextScheduleTimes = reconcileScheduleTimesForFrequency(nextFrequency, dailyTrackingScheduleTimes);
                  setDailyTrackingScheduleTimes(nextScheduleTimes);
                  setDailyTrackingFormData((current: any) => ({
                    ...current,
                    frequency: nextFrequency,
                    ...buildFormScheduleFields(nextScheduleTimes, {
                      scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                      scheduleDays: dailyTrackingScheduleDays,
                      scheduleMonthDays: dailyTrackingScheduleMonthDays,
                    }),
                  }));
                }}
              />
            </div>
            <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">Schedule days</label>
              <Dropdown
                value={dailyTrackingFormData?.scheduleDayPattern || "daily"}
                options={SCHEDULE_DAY_PATTERN_OPTIONS}
                optionLabel="label"
                optionValue="value"
                style={compactFieldStyle}
                onChange={(event) => {
                  const nextPattern = String(event.value || "daily").trim().toLowerCase();
                  const nextScheduleDays = nextPattern === "specific_days" ? dailyTrackingScheduleDays : [];
                  const nextScheduleMonthDays = nextPattern === "specific_month_days" ? dailyTrackingScheduleMonthDays : [];
                  setDailyTrackingScheduleDays(nextScheduleDays);
                  setDailyTrackingScheduleMonthDays(nextScheduleMonthDays);
                  setDailyTrackingFormData((current: any) => ({
                    ...current,
                    ...buildFormScheduleFields(dailyTrackingScheduleTimes, {
                      scheduleDayPattern: nextPattern as any,
                      scheduleDays: nextScheduleDays,
                      scheduleMonthDays: nextScheduleMonthDays,
                    }),
                  }));
                }}
              />
              {String(dailyTrackingFormData?.scheduleDayPattern || "daily") === "specific_days" ? (
                <div className="mt-2">
                  <MultiSelect
                    value={dailyTrackingScheduleDays}
                    options={SCHEDULE_WEEKDAY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select weekdays"
                    maxSelectedLabels={7}
                    selectedItemsLabel="{0} days selected"
                    showSelectAll={false}
                    style={compactFieldStyle}
                    onChange={(event) => {
                      const nextScheduleDays = normalizeMedicationScheduleDays(event.value || []);
                      setDailyTrackingScheduleDays(nextScheduleDays);
                      setDailyTrackingFormData((current: any) => ({
                        ...current,
                        ...buildFormScheduleFields(dailyTrackingScheduleTimes, {
                          scheduleDayPattern: "specific_days",
                          scheduleDays: nextScheduleDays,
                          scheduleMonthDays: [],
                        }),
                      }));
                    }}
                  />
                </div>
              ) : null}
              {String(dailyTrackingFormData?.scheduleDayPattern || "daily") === "specific_month_days" ? (
                <div className="mt-2">
                  <MultiSelect
                    value={dailyTrackingScheduleMonthDays}
                    options={SCHEDULE_MONTH_DAY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select month dates"
                    selectedItemsLabel="{0} dates selected"
                    showSelectAll={false}
                    style={compactFieldStyle}
                    onChange={(event) => {
                      const nextScheduleMonthDays = normalizeMedicationScheduleMonthDays(event.value || []);
                      setDailyTrackingScheduleMonthDays(nextScheduleMonthDays);
                      setDailyTrackingFormData((current: any) => ({
                        ...current,
                        ...buildFormScheduleFields(dailyTrackingScheduleTimes, {
                          scheduleDayPattern: "specific_month_days",
                          scheduleDays: [],
                          scheduleMonthDays: nextScheduleMonthDays,
                        }),
                      }));
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="col-12" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">Schedule times</label>
              <MultiSelect
                value={dailyTrackingScheduleTimes}
                options={SCHEDULE_TIME_OPTIONS}
                optionLabel="label"
                optionValue="value"
                display="chip"
                placeholder="Select times"
                maxSelectedLabels={8}
                selectedItemsLabel="{0} times selected"
                showSelectAll={false}
                style={compactFieldStyle}
                onChange={(event) => {
                  const nextScheduleTimes = event.value || [];
                  setDailyTrackingScheduleTimes(nextScheduleTimes);
                  setDailyTrackingFormData((current: any) => ({
                    ...current,
                    ...buildFormScheduleFields(nextScheduleTimes, {
                      scheduleDayPattern: String(current?.scheduleDayPattern || "daily") as any,
                      scheduleDays: dailyTrackingScheduleDays,
                      scheduleMonthDays: dailyTrackingScheduleMonthDays,
                    }),
                  }));
                }}
              />
            </div>
            <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">Start date</label>
              <Calendar
                value={getMedicationExpiryCalendarValue(dailyTrackingFormData?.effectiveStartDate)}
                onChange={(event) => {
                  const nextValue = formatMedicationExpiryFromCalendar(event.value as Date | null);
                  setDailyTrackingFormData((current: any) => ({ ...current, effectiveStartDate: nextValue }));
                }}
                dateFormat="mm/dd/yy"
                placeholder="Select start date"
                showButtonBar
                monthNavigator
                yearNavigator
                yearRange="2020:2035"
                style={compactFieldStyle}
              />
            </div>
            <div className="col-12 md:col-6" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">End date</label>
              <Calendar
                value={getMedicationExpiryCalendarValue(dailyTrackingFormData?.effectiveEndDate)}
                onChange={(event) => {
                  const nextValue = formatMedicationExpiryFromCalendar(event.value as Date | null);
                  setDailyTrackingFormData((current: any) => ({ ...current, effectiveEndDate: nextValue }));
                }}
                dateFormat="mm/dd/yy"
                placeholder="Select end date"
                showButtonBar
                monthNavigator
                yearNavigator
                yearRange="2020:2035"
                style={compactFieldStyle}
              />
            </div>
            <div className="col-12" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <label className="block mb-2 font-medium">Instructions</label>
              <InputTextarea
                value={dailyTrackingFormData?.instructions || ""}
                onChange={(event) => setDailyTrackingFormData((current: any) => ({ ...current, instructions: event.target.value }))}
                rows={3}
                placeholder="Example: Check before breakfast and record before insulin decisions."
                style={compactFieldStyle}
              />
            </div>
            <div className="col-12" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <div className="block mb-2 font-medium">Data staff will record</div>
              <div className="flex gap-2 flex-wrap">
                {getEmarDailyTrackingSchema(dailyTrackingFormData).map((field: any) => (
                  <Tag
                    key={String(field?.key || "")}
                    value={`${field?.label || field?.key}${field?.unit ? ` (${field.unit})` : ""}${field?.required ? " *" : ""}`}
                    severity="info"
                    rounded
                  />
                ))}
              </div>
            </div>
            <div className="col-12" style={{ ...medicationDialogSectionStyle, minWidth: 0 }}>
              <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div className="block mb-2 font-medium">Action rules</div>
                  <div style={medicationDialogFieldHintStyle}>
                    Set value thresholds for this tracking item so eMAR can recommend actions like Call Provider or Dial 911.
                  </div>
                </div>
                <Button
                  type="button"
                  label={(Array.isArray(dailyTrackingFormData?.protocolDefinitions) ? dailyTrackingFormData.protocolDefinitions.length : 0) ? "Manage Rules" : "Add Rules"}
                  icon="pi pi-sliders-h"
                  rounded
                  outlined
                  severity="warning"
                  onClick={openDailyTrackingProtocolManagementDialog}
                />
              </div>
              {(Array.isArray(dailyTrackingFormData?.protocolDefinitions) ? dailyTrackingFormData.protocolDefinitions : []).length ? (
                <div className="mt-3">
                  {renderProtocolDefinitionsOverview(
                    (dailyTrackingFormData.protocolDefinitions || []).map((definition: any) => ({
                      ...buildProtocolDefinitionFromParameterSet(definition),
                    })),
                  )}
                </div>
              ) : (
                <div className="mt-3" style={medicationDialogFieldHintStyle}>
                  No action rules are set yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!trackingRecommendedActionDialog}
        style={{ width: "30rem", maxWidth: "94vw" }}
        modal
        onHide={() => setTrackingRecommendedActionDialog(null)}
        header={renderSurfaceHeader(
          "Recommended Action",
          "Use this guidance based on the saved reading and rules.",
          "pi pi-bell",
        )}
        footer={(
          <div className="flex justify-content-end">
            <Button
              type="button"
              label="Close"
              severity="warning"
              rounded
              onClick={() => setTrackingRecommendedActionDialog(null)}
            />
          </div>
        )}
      >
        <div className="pt-2">
          <div
            style={{
              border: "1px solid #fed7aa",
              background: "#fff7ed",
              borderRadius: "16px",
              padding: "1rem",
            }}
          >
            <div style={{ fontWeight: 800, color: "#9a3412", marginBottom: "0.5rem" }}>
              {trackingRecommendedActionDialog?.title || "Recommended Action"}
            </div>
            <div style={{ color: "#7c2d12", lineHeight: 1.55 }}>
              {trackingRecommendedActionDialog?.message || ""}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showDailyTrackingEntryDialog}
        style={{ width: "52rem", maxWidth: "96vw" }}
        modal
        onHide={closeDailyTrackingEntryDialog}
        header={renderSurfaceHeader(
          pendingDailyTrackingEntryAction?.allowStatusChange ? "Fix Vital Tracker" : isDailyTrackingValueEntryStatus(pendingDailyTrackingEntryAction?.status) ? "Record Vital Tracker" : "Save Tracking Status",
          pendingDailyTrackingEntryAction?.allowStatusChange
            ? "Update the status, values, or note before saving the corrected entry."
            : isDailyTrackingValueEntryStatus(pendingDailyTrackingEntryAction?.status)
              ? "Add the reading values for this scheduled tracking item."
              : "Add a short note before saving this tracking status.",
          "pi pi-heart",
        )}
        footer={renderDialogFooter({
          loading: savingDailyTrackingEntry,
          onCancel: closeDailyTrackingEntryDialog,
          onSubmit: saveDailyTrackingEntry,
          submitLabel: pendingDailyTrackingEntryAction?.allowStatusChange ? "Save Changes" : isDailyTrackingValueEntryStatus(pendingDailyTrackingEntryAction?.status) ? "Save Reading" : "Save Status",
        })}
      >
        <div className="pt-2">
          {pendingDailyTrackingEntryAction?.item ? (
            <div style={{ border: "1px solid #e2e8f0", background: "#f8fafc", borderRadius: "16px", padding: "0.85rem 0.95rem", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                {pendingDailyTrackingEntryAction?.item?.name || getEmarDailyTrackingCategoryOption(pendingDailyTrackingEntryAction?.item?.category)?.label || "Vital tracker"}
              </div>
              <div className="mt-1 flex gap-2 flex-wrap">
                <Tag value={pendingDailyTrackingEntryAction?.occurrenceLabel || "Scheduled"} severity="secondary" rounded />
                <Tag value={formatDailyTrackingStatusLabel(pendingDailyTrackingEntryAction?.status)} severity={getDailyTrackingStatusSeverity(pendingDailyTrackingEntryAction?.status) as any} rounded />
              </div>
              {getTrackingRecommendedActionLabel(pendingDailyTrackingEntryAction?.entry) ? (
                <div className="mt-2">
                  <Tag value={getTrackingRecommendedActionLabel(pendingDailyTrackingEntryAction?.entry)} severity="danger" rounded />
                </div>
              ) : null}
            </div>
          ) : null}
          {pendingDailyTrackingEntryAction?.allowStatusChange ? (
            <div className="mb-3">
              <label className="block mb-2 font-medium">Status</label>
              <Dropdown
                value={String(pendingDailyTrackingEntryAction?.status || "recorded").trim().toLowerCase()}
                options={DAILY_TRACKING_ENTRY_STATUS_OPTIONS}
                onChange={(event) => setPendingDailyTrackingEntryAction((current: any) => {
                  const nextStatus = String(event.value || "recorded").trim().toLowerCase();
                  if (!current) {
                    return current;
                  }
                  if (!isDailyTrackingValueEntryStatus(nextStatus)) {
                    return {
                      ...current,
                      status: nextStatus,
                    };
                  }
                  const nextTimeState = buildGivenTimeSelectionState({
                    entryDate: String(current?.entryDate || todayDate).trim() || todayDate,
                    scheduledClockTime: String(current?.scheduledClockTime || "").trim(),
                    baseTimestamp: String(current?.recordedAt || "").trim() || new Date().toISOString(),
                    includePromptReason: true,
                  });
                  return {
                    ...current,
                    status: nextStatus,
                    givenTimeChoice: current?.givenTimeChoice || nextTimeState.givenTimeChoice,
                    recordedAt: current?.recordedAt || (nextTimeState.givenTimeChoice === "scheduled" ? nextTimeState.scheduledRecordedAt : nextTimeState.currentRecordedAt),
                    showGivenTimeChoiceDialog: nextTimeState.showGivenTimeChoiceDialog,
                    givenTimePromptReason: nextTimeState.givenTimePromptReason,
                    givenTimeOptions: nextTimeState.givenTimeOptions,
                  };
                })}
                style={compactFieldStyle}
              />
            </div>
          ) : null}
          {isDailyTrackingValueEntryStatus(pendingDailyTrackingEntryAction?.status) ? (
            <div className="grid">
              {pendingDailyTrackingEntryAction?.showGivenTimeChoiceDialog ? (
                <div className="col-12" style={{ order: 2 }}>
                  <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "16px", padding: "0.85rem 0.95rem", marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose administration time</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                      Choose whether to save it at the scheduled time, the current time, or an exact charting time.
                    </div>
                    {pendingDailyTrackingEntryAction?.givenTimePromptReason ? (
                      <div className="mt-2" style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.8rem 0.9rem", color: "#92400e", fontSize: "0.88rem", lineHeight: 1.5 }}>
                        {pendingDailyTrackingEntryAction.givenTimePromptReason}
                      </div>
                    ) : null}
                    <div className="mt-2 grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 15rem), 15rem))", width: "100%", maxWidth: "36rem", justifyContent: "center", margin: "0.5rem auto 0", gap: "0.5rem" }}>
                      {(Array.isArray(pendingDailyTrackingEntryAction?.givenTimeOptions) ? pendingDailyTrackingEntryAction.givenTimeOptions : []).map((option: any) => {
                        const isSelected = pendingDailyTrackingEntryAction?.givenTimeChoice === option?.value;
                        return (
                          <div className="col-12 md:col-6" key={String(option?.value || "")} style={{ width: "100%", padding: 0 }}>
                            <button
                              type="button"
                              disabled={option?.disabled}
                              onClick={() => {
                                if (option?.disabled) {
                                  return;
                                }
                                setPendingDailyTrackingEntryAction((current: any) => ({
                                  ...current,
                                  givenTimeChoice: option?.value,
                                  recordedAt: option?.recordedAt || current?.recordedAt || "",
                                }));
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                border: isSelected ? "1px solid #2563eb" : "1px solid #dbeafe",
                                borderRadius: 12,
                                padding: "0.7rem 0.8rem",
                                background: isSelected ? "#eff6ff" : "#fff",
                                cursor: option?.disabled ? "not-allowed" : "pointer",
                                opacity: option?.disabled ? 0.6 : 1,
                              }}
                            >
                              <div style={{ fontWeight: 700 }}>{option?.label || "Time option"}</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {option?.helper || ""}
                              </div>
                            </button>
                          </div>
                        );
                      })}
                      <div className="col-12 md:col-6" style={{ width: "100%", padding: 0 }}>
                        <div style={{ minHeight: "5.25rem", border: "1px solid #dbe3ef", borderRadius: "14px", padding: "0.8rem 0.9rem", background: "#fff" }}>
                          <label className="block mb-2 font-medium">Exact charting time</label>
                          <Dropdown
                            value={String(getLocalTimeValueWithSecondsFromIso(pendingDailyTrackingEntryAction?.recordedAt) || "").slice(0, 5)}
                            options={getAvailableChartTimeOptions(pendingDailyTrackingEntryAction?.entryDate)}
                            onChange={(event) => {
                              const nextTimeValue = String(event.value || "");
                              setPendingDailyTrackingEntryAction((current: any) => ({
                                ...current,
                                recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                              }));
                            }}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select time"
                            disabled={pendingDailyTrackingEntryAction?.givenTimeChoice !== "custom"}
                            style={{ width: "100%" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="col-12" style={{ order: 1, fontWeight: 800, color: "#0f172a", marginBottom: "-0.25rem" }}>
                Parameters to track
              </div>
              {getEmarDailyTrackingSchema(pendingDailyTrackingEntryAction?.item).map((field: any) => {
                const fieldKey = String(field?.key || "").trim();
                return (
                  <div className="col-12 lg:col-6" style={{ minWidth: 0, order: 1 }} key={fieldKey}>
                    <label className="block mb-2 font-medium">{field?.label || fieldKey}{field?.required ? " *" : ""}</label>
                    <InputText
                      value={dailyTrackingValueDraft[fieldKey] || ""}
                      onChange={(event) => setDailyTrackingValueDraft((current) => ({ ...current, [fieldKey]: event.target.value }))}
                      placeholder={getEmarDailyTrackingFieldPlaceholder(field)}
                      style={compactFieldStyle}
                    />
                  </div>
                );
              })}
              <div className="col-12" style={{ order: 3 }}>
                <label className="block mb-2 font-medium">Note</label>
                <InputTextarea
                  value={dailyTrackingNoteDraft}
                  onChange={(event) => setDailyTrackingNoteDraft(event.target.value)}
                  rows={3}
                  placeholder="Optional: add charting context for this reading."
                  style={compactFieldStyle}
                />
              </div>
            </div>
          ) : (
            <>
              <label className="block mb-2 font-medium">Note</label>
              <InputTextarea
                value={dailyTrackingNoteDraft}
                onChange={(event) => setDailyTrackingNoteDraft(event.target.value)}
                rows={4}
                placeholder={pendingDailyTrackingEntryAction?.allowStatusChange
                  ? "Add a note about the corrected status."
                  : "Explain what happened for this scheduled tracking item."}
                style={compactFieldStyle}
              />
            </>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={carePlanPreviewDialogVisible}
        style={{ width: "64rem", maxWidth: "96vw" }}
        modal
        onHide={closeCarePlanPreviewDialog}
        header={renderSurfaceHeader(
          "Review Care Plan Medications",
          "Pick the medications you want to bring into eMAR before anything is added.",
          "pi pi-list-check",
        )}
        footer={renderDialogFooter({
          loading: importing,
          onCancel: closeCarePlanPreviewDialog,
          onSubmit: confirmCarePlanImport,
          submitLabel: "Upload Ready Medications",
        })}
      >
        <div className="pt-2">
          <div className="flex gap-2 flex-wrap align-items-center mb-3">
            <div className="flex gap-2 flex-wrap">
              <Tag value={`${carePlanPreviewEntries.length} detected`} severity="info" rounded />
              <Tag
                value={`${carePlanPreviewEntries.filter((item: any) => !item?.duplicate).length} ready to import`}
                severity="success"
                rounded
              />
              <Tag
                value={`${carePlanPreviewEntries.filter((item: any) => item?.duplicate).length} duplicate${carePlanPreviewEntries.filter((item: any) => item?.duplicate).length === 1 ? "" : "s"}`}
                severity="warning"
                rounded
              />
            </div>
          </div>
          <div>
            <DataTable value={carePlanPreviewEntries} responsiveLayout="scroll" emptyMessage="No medications were found in the care plan.">
              <Column
                header="Medication"
                body={(row: any) => (
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.medicationName || "Medication"}</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                      {row?.dosage || "No dose details found"}
                    </div>
                  </div>
                )}
                style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
              />
              <Column
                header="Use / Instructions"
                body={(row: any) => (
                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                    {renderMedicationUseInstructionsText(row)}
                  </div>
                )}
                style={{ minWidth: "16rem" }}
              />
              <Column
                header="Upload Status"
                body={(row: any) => (
                  <Tag
                    value={row?.duplicate ? "Already active in eMAR" : "Ready to import"}
                    severity={row?.duplicate ? "warning" : "success"}
                    rounded
                  />
                )}
                style={{ width: "13rem" }}
              />
            </DataTable>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={marSheetImportProgressDialogVisible}
        style={{ width: "34rem", maxWidth: "94vw" }}
        modal
        closable={!importingMarSheetFile}
        onHide={closeMarSheetImportProgressDialog}
        header={renderSurfaceHeader(
          marSheetImportDialogTitle,
          "",
          "pi pi-spin pi-spinner",
        )}
      >
        <div className="pt-2">
          <div className="text-center" style={{ ...appInnerCardStyle, padding: "1.1rem 1rem" }}>
            <ProgressSpinner style={{ width: "3rem", height: "3rem" }} strokeWidth="5" />
            <div className="mt-3" style={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>
              {marSheetPreviewFileName || "MAR sheet"}
            </div>
            <div className="mt-2" style={{ ...appMutedTextStyle }}>
              {marSheetImportJobStatus === "UPLOADING"
                ? "Uploading your MAR sheet..."
                : "Processing..."}
              {" "}
              {marSheetImportElapsedTime} seconds elapsed
            </div>
            {marSheetImportJobSubStatus ? (
              <div className="mt-2" style={{ ...appMutedTextStyle }}>
                {marSheetImportJobSubStatus}
              </div>
            ) : null}
          </div>
          <div className="mt-3 text-center" style={{ ...appMutedTextStyle }}>
            Please keep this window open while we prepare the medication list for review.
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={marSheetPreviewDialogVisible}
        style={{ width: "64rem", maxWidth: "96vw" }}
        modal
        onHide={closeMarSheetPreviewDialog}
        header={renderSurfaceHeader(
          "Review Medications",
          "Review the medication details before adding them to eMAR.",
          "pi pi-upload",
        )}
        footer={(
          <div
            className="flex align-items-center justify-content-between gap-3 flex-wrap"
            style={{ width: "100%" }}
          >
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic", flex: "1 1 18rem" }}>
              {AI_MESSAGE_FOR_COMPLIANCE}
            </p>
            <div style={{ flexShrink: 0 }}>
              {renderDialogFooter({
                loading: importingMarSheetFile,
                onCancel: closeMarSheetPreviewDialog,
                onSubmit: confirmMarSheetImport,
                submitLabel: "Add to eMAR",
              })}
            </div>
          </div>
        )}
      >
        <div className="pt-2">
          <div style={{ ...appMutedTextStyle, marginBottom: "0.9rem" }}>
            {marSheetPreviewFileName ? `MAR sheet: ${marSheetPreviewFileName}` : "Review the medications below."}
          </div>
          <div>
            <DataTable
              value={marSheetPreviewEntries}
              dataKey="previewKey"
              responsiveLayout="scroll"
              emptyMessage="No medications were found in this MAR sheet."
            >
              <Column
                header="Medication"
                body={(row: any) => (
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.medicationName || row?.name || "Medication"}</div>
                    {row?.psychotropicDrugClass ? <Tag value="Psychotropic" severity="danger" rounded className="mt-1" /> : null}
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                      {row?.entryKind === "vital" ? "Vital tracking item" : row?.dosage || row?.schedule || "No dosage details found"}
                    </div>
                  </div>
                )}
                style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
              />
              <Column
                header="Time"
                body={(row: any) => (
                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                    {row?.prn
                      ? "As needed"
                      : (Array.isArray(row?.scheduledTimeLabels) ? row.scheduledTimeLabels : Array.isArray(row?.scheduledTimes) ? row.scheduledTimes : [row?.scheduledClockTime])
                        .map((value: any) => normalizeValue(value))
                        .filter(Boolean)
                        .join(" · ") || "—"}
                  </div>
                )}
                style={{ minWidth: "10rem" }}
              />
              <Column
                header="Parameters"
                body={(row: any) => {
                  const parameters = Array.isArray(row?.parameters) ? row.parameters : Array.isArray(row?.parameterNames) ? row.parameterNames : [];
                  const labels = parameters
                    .map((parameter: any) => {
                      if (typeof parameter === "string") return normalizeValue(parameter);
                      const label = normalizeValue(parameter?.label || parameter?.name || parameter?.parameterName);
                      const conditions = Array.isArray(parameter?.conditions) ? parameter.conditions.map((condition: any) => normalizeValue(condition)).filter(Boolean) : [];
                      return [label, ...conditions].filter(Boolean).join(conditions.length ? ": " : "");
                    })
                    .filter(Boolean);
                  return <span style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>{labels.join(" · ") || "—"}</span>;
                }}
                style={{ minWidth: "11rem" }}
              />
              <Column
                header="Usage / Instructions"
                body={(row: any) => (
                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
                    {renderMedicationUseInstructionsText(row)}
                  </div>
                )}
                style={{ minWidth: "14rem" }}
              />
            </DataTable>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          "Compare Active eMAR List With New MAR Sheet",
          "Upload the new MAR sheet to review medication, usage, route, or frequency changes against the resident's current active eMAR list before fixing it.",
          "pi pi-file-check",
        )}
        visible={showEmarCompareDialog}
        style={{ width: "72rem", maxWidth: "98vw" }}
        breakpoints={{ "960px": "96vw", "640px": "98vw" }}
        headerStyle={{ ...appDialogHeaderStyle, padding: "1rem clamp(0.85rem, 4vw, 1.25rem) 0.65rem" }}
        contentStyle={{ ...appDialogContentStyle, padding: "clamp(0.85rem, 4vw, 1.25rem)", overflowX: "hidden" }}
        modal
        onHide={closeEmarCompareDialog}
        closeOnEscape={!processingEmarCompare && !applyingEmarCompareChanges}
        footer={(
          <div
            className="flex align-items-center justify-content-between gap-3 flex-wrap"
            style={{ width: "100%" }}
          >
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic", flex: "1 1 18rem" }}>
              {AI_MESSAGE_FOR_COMPLIANCE}
            </p>
            {emarCompareHasDifferences ? (
              <div className="flex flex-wrap justify-content-end gap-2" style={{ flexShrink: 0 }}>
                {/* <Button
                  type="button"
                  label={emarCompareAffectedMedicationCount > 0 ? `Fix Med details (${emarCompareAffectedMedicationCount})` : "Fix Med details"}
                  severity="warning"
                  outlined
                  rounded
                  loading={applyingEmarCompareChanges}
                  disabled={!emarCompareCompletedJobId}
                  onClick={() => {
                    confirmDialog({
                      message: renderConfirmMessage(
                        "Fix active eMAR list",
                        "This will update the active eMAR medication list using the uploaded MAR sheet as the latest source of truth. Continue?",
                      ),
                      header: "Confirmation",
                      icon: "pi pi-exclamation-triangle",
                      acceptLabel: "Fix Med Details",
                      acceptClassName: appConfirmDialogClasses.acceptClassName,
                      rejectLabel: "Cancel",
                      rejectClassName: "p-button-text p-button-rounded",
                      accept: () => { void applyEmarCompareChanges(); },
                    });
                  }}
                /> */}
                <Button
                  type="button"
                  label="Download Comparison"
                  severity="secondary"
                  outlined
                  rounded
                  onClick={() => {
                    const residentFileName = String(residentDisplayName || "resident")
                      .trim()
                      .replace(/[^a-z0-9]+/gi, "-")
                      .replace(/^-+|-+$/g, "")
                      .toLowerCase() || "resident";
                    downloadHtmlContent("eMAR Medication Comparison", emarCompareReportHtml, `${residentFileName}-mar-compliance-report.doc`);
                  }}
                />
              </div>
            ) : <div style={{ flexShrink: 0 }} />}
          </div>
        )}
      >
        <div className="flex flex-column gap-3">
          {loadingLatestEmarCompareResult ? (
            <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                Loading saved comparison
              </div>
              <div className="block mt-2" style={{ color: "#596F80" }}>
                Reopening the most recent completed eMAR comparison result for this resident.
              </div>
            </div>
          ) : null}

          {!processingEmarCompare && !loadingLatestEmarCompareResult && !emarCompareResultData ? (
            <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
              <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Upload the new MAR sheet</div>
              <input
                ref={emarCompareFileInputRef}
                type="file"
                accept={MAR_SHEET_FILE_ACCEPT}
                style={{ display: "none" }}
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setEmarCompareUploadFile(file);
                  setEmarCompareFileName(String(file?.name || "").trim());
                  if (file) {
                    void startEmarCompareReview(file);
                  }
                }}
              />
              <Button
                type="button"
                label={emarCompareUploadFile ? "Replace MAR Sheet" : "Upload MAR Sheet"}
                severity="secondary"
                outlined
                rounded
                onClick={() => emarCompareFileInputRef.current?.click()}
                style={{ borderWidth: "1.5px" }}
              />
              {emarCompareUploadFile && (
                <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.25rem" }}>Selected file</div>
                  <div style={appMutedTextStyle}>{emarCompareUploadFile.name}</div>
                </div>
              )}
            </div>
          ) : null}

          {processingEmarCompare ? (
            <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                Reviewing MAR sheet
              </div>
              <div className="block mt-2" style={{ color: "#596F80" }}>
                <strong>Status:</strong> {emarCompareJobStatus || "Starting"}
              </div>
              <div className="block mt-1" style={{ color: "#596F80" }}>
                <strong>Progress:</strong> {emarCompareJobSubStatus || "Preparing eMAR comparison"}
              </div>
              <div className="block mt-2" style={{ color: "#596F80" }}>
                Elapsed time: {emarCompareElapsedSeconds} sec
              </div>
            </div>
          ) : null}

          {emarCompareResultData ? (
            <div className="flex flex-column gap-3">
              <div className="flex flex-column gap-2">
                {emarCompareDifferenceRows.length ? (
                  <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                    <DataTable
                      value={emarCompareDifferenceRows}
                      dataKey="rowKey"
                      responsiveLayout="scroll"
                      size="small"
                      stripedRows
                    >
                    <Column
                      header="Medication"
                      body={(row: any) => (
                        <div style={{ minWidth: "12rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            {row?.medicationName || "Medication"}
                          </div>
                          {row?.psychotropicDrugClass ? <Tag value="Psychotropic" severity="danger" rounded className="mt-1" /> : null}
                        </div>
                      )}
                      style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
                    />
                    <Column
                      header="Current eMAR"
                      body={(row: any) => (
                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap", minWidth: "15rem" }}>
                          {row?.currentEmar || "—"}
                        </div>
                      )}
                      style={{ minWidth: "16rem" }}
                    />
                    <Column
                      header="New MAR"
                      body={(row: any) => (
                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap", minWidth: "15rem" }}>
                          {row?.newMar || "—"}
                        </div>
                      )}
                      style={{ minWidth: "16rem" }}
                    />
                    <Column
                      header="Fix needed"
                      body={(row: any) => (
                        <div className="flex gap-2 flex-wrap" style={{ minWidth: "13rem" }}>
                          {(row?.actionLabels || []).map((label: string) => (
                            <Tag key={`${row?.rowKey}-${label}-fix`} value={label} severity={row?.actionSeverity || "warning"} rounded />
                          ))}
                        </div>
                      )}
                      style={{ minWidth: "14rem" }}
                    />
                    </DataTable>
                  </div>
                ) : null}

                {!emarCompareHasDifferences ? (
                  <div style={appMutedTextStyle}>No medication differences were found between the current active eMAR list and the new MAR sheet.</div>
                ) : (
                  <div style={appMutedTextStyle}>
                    {`${emarCompareAffectedMedicationCount} medication${emarCompareAffectedMedicationCount === 1 ? "" : "s"} need updates.`}
                  </div>
                )}
              </div>

              {/* {emarCompareHasDifferences ? (
                <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.35rem" }}>If you click Fix Med Details</div>
                  <div style={appMutedTextStyle}>
                    AFH Hub will update the active eMAR medication list to match the uploaded MAR sheet, because the MAR sheet is treated as the latest source of truth from pharmacy.
                  </div>
                  <ul style={{ margin: "0.65rem 0 0", paddingLeft: "1.15rem", color: "#7c2d12" }}>
                    <li>Missing medications from the active eMAR list will be added from the new MAR sheet.</li>
                    <li>Mismatched details like dosage, route, frequency, use, and instructions will be updated.</li>
                    <li>Active eMAR medications not found on the new MAR will be archived from the active list.</li>
                  </ul>
                </div>
              ) : null} */}
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        visible={showPsychotropicCompareDialog}
        style={{ width: "min(76rem, 96vw)" }}
        modal
        onHide={() => !loadingPsychotropicCompare && setShowPsychotropicCompareDialog(false)}
        header={renderSurfaceHeader(
          "Psychotropic Medication Compliance",
          "Compare active psychotropic eMAR medications with the resident Care Plan Medication Management section.",
          "pi pi-shield",
        )}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        footer={psychotropicCompareReportHtml ? (
          <div className="flex justify-content-end" style={{ width: "100%" }}>
            <Button
              type="button"
              label="Download Comparison"
              severity="secondary"
              outlined
              rounded
              onClick={() => {
                const residentFileName = String(residentDisplayName || "resident").trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "resident";
                downloadHtmlContent("Psychotropic Medication Compliance", psychotropicCompareReportHtml, `${residentFileName}-psychotropic-mar-compliance-report.doc`);
              }}
            />
          </div>
        ) : undefined}
      >
        {loadingPsychotropicCompare ? (
          <div className="text-center py-5"><ProgressSpinner role="status" /><div className="mt-3" style={appMutedTextStyle}>Comparing psychotropic medications.</div></div>
        ) : psychotropicCompareRows.length ? (
          <DataTable value={psychotropicCompareRows} dataKey="rowKey" responsiveLayout="scroll" size="small" stripedRows>
            <Column header="Medication" body={(row: any) => <strong>{row?.medicationName || "Medication"}</strong>} style={{ minWidth: "12rem" }} />
            <Column header="Active eMAR" body={(row: any) => <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap", minWidth: "15rem" }}>{row?.activeEmar}</div>} style={{ minWidth: "16rem" }} />
            <Column header="Care Plan" body={(row: any) => <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap", minWidth: "15rem" }}>{row?.carePlan}</div>} style={{ minWidth: "16rem" }} />
            <Column header="Difference" body={(row: any) => <Tag value={row?.difference || "Review needed"} severity="warning" rounded />} style={{ minWidth: "13rem" }} />
          </DataTable>
        ) : (
          <div style={appMutedTextStyle}>No psychotropic medication differences were found.</div>
        )}
      </Dialog>

      <Dialog
        visible={showProtocolSetupDialog}
        style={{ width: "68rem", maxWidth: "98vw" }}
        modal
        onHide={() => {
          setShowProtocolSetupDialog(false);
          setEditingProtocolDefinitionIndex(-1);
          setActiveProtocolRuleIndex(0);
          setShowProtocolRuleEditor(false);
        }}
        header={renderSurfaceHeader(
          "Manage Parameter",
          protocolSetupTarget === "tracking"
            ? "Review all parameter sets for this tracking item, then add or update matching rules."
            : "Review all parameter sets for this medication, then add or update matching rules.",
          "pi pi-sliders-h",
        )}
        footer={(
          <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => {
                setShowProtocolSetupDialog(false);
                setEditingProtocolDefinitionIndex(-1);
                setActiveProtocolRuleIndex(0);
                setShowProtocolRuleEditor(false);
                setExpandedProtocolEditorIds([]);
              }}
            />
            <Button
              type="button"
              label={protocolSetupTarget === "tracking" ? "Update Rules" : "Update Parameters"}
              severity="warning"
              rounded
              onClick={saveProtocolDefinitionDraft}
              disabled={(Array.isArray(protocolDialogDefinitions) ? protocolDialogDefinitions : []).some((definition: any) => !isProtocolDialogDefinitionReady(definition))}
            />
          </div>
        )}
      >
        <div className="grid pt-2">
          <div className="col-12">
            <div style={medicationDialogSummaryCardStyle}>
              <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>
                    {protocolDialogDefinitions.length} parameter{protocolDialogDefinitions.length === 1 ? "" : "s"} are set.
                  </div>
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                  {protocolSetupTarget !== "tracking" ? (
                    <Button
                      type="button"
                      label="Add New Parameter"
                      icon="pi pi-plus"
                      rounded
                      outlined
                      severity="warning"
                      onClick={startNewProtocolDefinition}
                      disabled={protocolDialogDefinitions.length >= MAX_PROTOCOL_PARAMETERS_PER_MEDICATION}
                    />
                  ) : null}
                </div>
              </div>
              {protocolDialogDefinitions.length ? (
                <div className="mt-3 flex flex-column gap-2">
                  {protocolDialogDefinitions.map((definition: any, definitionIndex: number) => {
                    const categoryOption = getEmarProtocolCategoryOption(definition?.category);
                    const definitionRules = Array.isArray(definition?.rules) ? definition.rules : [];
                    const visibleDefinitionRules = getVisibleProtocolRulesForDraft(definition);
                    const parameterId = String(definition?.parameterId || `parameter_${definitionIndex + 1}`);
                    const isExpanded = expandedProtocolEditorIds.includes(parameterId);
                    const definitionRule = getVisibleProtocolRuleForDraft(definition);
                    const categoryOptions = getEmarProtocolEditorCategoryOptions(definition?.category);
                    return (
                      <div
                        key={definition?.parameterId || definitionIndex}
                        ref={editingProtocolDefinitionIndex === definitionIndex ? protocolEditorSectionRef : undefined}
                        style={{
                          ...protocolManagementGridRowStyle,
                          border: `1px solid ${isExpanded ? "#fb923c" : "#ffedd5"}`,
                          background: isExpanded ? "#fff7ed" : "#ffffff",
                        }}
                      >
                        {isExpanded ? (
                          <div className="mt-1 flex flex-column gap-2">
                            <div style={{ padding: 0 }}>
                              <div className="flex justify-content-between align-items-end gap-3 flex-wrap">
                                <div style={{ flex: "1 1 18rem", minWidth: 0 }}>
                                  <label className="block mb-2 font-medium">Parameter</label>
                                  {protocolSetupTarget === "tracking" ? (
                                    <div style={{ ...appMutedTextStyle, fontSize: "0.9rem", fontWeight: 700, color: "#7c2d12" }}>
                                      {categoryOption?.label || "Tracking Parameter"}
                                    </div>
                                  ) : (
                                    <Dropdown
                                      value={definition?.category || ""}
                                      options={categoryOptions}
                                      optionLabel="label"
                                      optionValue="value"
                                      placeholder="Select parameter"
                                      style={{ ...compactProtocolInlineFieldStyle, width: "100%", maxWidth: "24rem" }}
                                      onChange={(event) => {
                                        const nextCategory = String(event.value || "");
                                        updateProtocolDialogDefinition(definitionIndex, (current: any) => ({
                                          ...current,
                                          category: nextCategory,
                                          rules: [createEmptyProtocolRule(nextCategory, 0)],
                                        }));
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="flex justify-content-end gap-2 flex-wrap">
                                  <Button
                                    type="button"
                                    label="Delete"
                                    severity="danger"
                                    outlined
                                    rounded
                                    onClick={() => removeProtocolDefinition(definitionIndex)}
                                  />
                                  <Button
                                    type="button"
                                    label="Save"
                                    severity="warning"
                                    outlined
                                    rounded
                                    disabled={!isProtocolDialogDefinitionReady(definition)}
                                    onClick={() => saveProtocolDialogSection(definitionIndex)}
                                  />
                                </div>
                              </div>
                              <div className="mt-1 flex flex-column gap-1">
                                {visibleDefinitionRules.map((rule: any, ruleIndex: number) => (
                                  <div key={`${parameterId}-rule-${ruleIndex}`} style={{ borderTop: ruleIndex > 0 ? "1px solid #ffedd5" : undefined, padding: "0.4rem 0 0.15rem" }}>
                                    <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                                      <div style={{ fontWeight: 700, color: "#9a3412" }}>{`Rule ${ruleIndex + 1}`}</div>
                                      <div className="flex gap-2 flex-wrap">
                                        <Button
                                          type="button"
                                          label="Add Rule"
                                          icon="pi pi-plus"
                                          rounded
                                          outlined
                                          severity="warning"
                                          disabled={visibleDefinitionRules.length >= MAX_PROTOCOL_RULES_PER_PARAMETER}
                                          onClick={() => addProtocolDialogRule(definitionIndex)}
                                        />
                                        {visibleDefinitionRules.length > 1 ? (
                                          <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            rounded
                                            outlined
                                            severity="danger"
                                            onClick={() => removeProtocolDialogRule(definitionIndex, ruleIndex)}
                                          />
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className="mt-1 grid align-items-center" style={{ rowGap: "0.25rem" }}>
                                      <div className="col-12 md:col-4 lg:col-3">
                                        <div style={protocolRulesGridHeaderStyle}>Measurement</div>
                                      </div>
                                      <div className="col-12 md:col-4 lg:col-3">
                                        <div style={protocolRulesGridHeaderStyle}>Condition</div>
                                      </div>
                                      <div className="col-12 md:col-4 lg:col-6">
                                        <div style={protocolRulesGridHeaderStyle}>Value</div>
                                      </div>
                                    </div>
                                    <div className="mt-1 flex flex-column gap-1">
                                      {getEmarProtocolParameterSchema(definition?.category, visibleDefinitionRules).map((field: any) => {
                                        const fieldKey = String(field?.key || "").trim();
                                        const condition = rule?.conditions?.[fieldKey] || {};
                                        const operator = getProtocolConditionOperator(condition);
                                        const operatorOptions = field?.inputType === "number"
                                          ? PROTOCOL_CONDITION_OPERATOR_OPTIONS
                                          : PROTOCOL_CONDITION_OPERATOR_OPTIONS.filter((option) => ["equals", "list", "required"].includes(option.value));
                                        const valueFieldStyle = field?.inputType === "number"
                                          ? compactProtocolNumericFieldStyle
                                          : compactProtocolTextValueFieldStyle;
                                        return (
                                          <div
                                            className="grid align-items-center"
                                            style={{
                                              rowGap: "0.5rem",
                                              padding: "0.35rem 0.45rem",
                                              border: "1px solid #ffedd5",
                                              borderRadius: "8px",
                                              background: operator === "none" ? "#ffffff" : "#fff7ed",
                                            }}
                                            key={`${parameterId}-${ruleIndex}-${fieldKey}`}
                                          >
                                            <div className="col-12 md:col-4 lg:col-3">
                                              <div style={{ fontWeight: 700, color: "#7c2d12", fontSize: "0.9rem" }}>
                                                {field?.label || fieldKey}
                                              </div>
                                              {field?.unit ? (
                                                <div style={{ ...appMutedTextStyle, fontSize: "0.76rem" }}>{field.unit}</div>
                                              ) : null}
                                            </div>
                                            <div className="col-12 md:col-4 lg:col-3">
                                              <Dropdown
                                                value={operator}
                                                options={operatorOptions}
                                                optionLabel="label"
                                                optionValue="value"
                                                style={compactProtocolDropdownFieldStyle}
                                                onChange={(event) => {
                                                  const nextOperator = String(event.value || "equals");
                                                  const currentEqualsValue = String(condition?.equalsValue || "").trim();
                                                  updateProtocolDialogFieldCondition(
                                                    definitionIndex,
                                                    ruleIndex,
                                                    fieldKey,
                                                    nextOperator,
                                                    nextOperator === "list" && !currentEqualsValue
                                                      ? { equalsValue: getProtocolListDefaultValue(field) }
                                                      : undefined,
                                                  );
                                                }}
                                              />
                                            </div>
                                            <div className="col-12 md:col-4 lg:col-6">
                                              <div className="flex align-items-center gap-2 flex-wrap">
                                                {operator === "none" ? (
                                                  <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>No value needed</span>
                                                ) : operator === "required" ? (
                                                  <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Value required during charting</span>
                                                ) : operator === "list" ? (
                                                  <InputTextarea
                                                    value={String(condition?.equalsValue || "")}
                                                    onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { equalsValue: event.target.value })}
                                                    rows={2}
                                                    autoResize
                                                    placeholder="Comma-separated values"
                                                    style={{ width: "100%", minWidth: 0, maxWidth: "none", display: "block", boxSizing: "border-box" }}
                                                  />
                                                ) : operator === "equals" ? (
                                                  field?.inputType === "select" ? (
                                                    <Dropdown
                                                      value={String(condition?.equalsValue || "")}
                                                      options={(Array.isArray(field?.options) ? field.options : []).map((option: any) => ({ label: String(option), value: String(option) }))}
                                                      onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { equalsValue: String(event.value || "") })}
                                                      optionLabel="label"
                                                      optionValue="value"
                                                      placeholder="Select an option"
                                                      style={{ width: "100%", minWidth: 0, maxWidth: "none" }}
                                                    />
                                                  ) : (
                                                  <InputText
                                                    value={String(condition?.equalsValue || "")}
                                                    onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { equalsValue: event.target.value })}
                                                    placeholder={field?.inputType === "number" ? "Value" : "Exact text"}
                                                    type={field?.inputType === "number" ? "number" : "text"}
                                                    inputMode={field?.inputType === "number" ? "decimal" : "text"}
                                                    step={field?.inputType === "number" ? String(getProtocolNumberFieldConfig(field).step) : undefined}
                                                    style={valueFieldStyle}
                                                  />
                                                  )
                                                ) : operator === "less_than" ? (
                                                  <InputText
                                                    value={String(condition?.maxValue || "")}
                                                    onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { maxValue: event.target.value })}
                                                    placeholder="Max"
                                                    type="number"
                                                    inputMode="decimal"
                                                    step={String(getProtocolNumberFieldConfig(field).step)}
                                                    style={compactProtocolNumericFieldStyle}
                                                  />
                                                ) : operator === "more_than" ? (
                                                  <InputText
                                                    value={String(condition?.minValue || "")}
                                                    onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { minValue: event.target.value })}
                                                    placeholder="Min"
                                                    type="number"
                                                    inputMode="decimal"
                                                    step={String(getProtocolNumberFieldConfig(field).step)}
                                                    style={compactProtocolNumericFieldStyle}
                                                  />
                                                ) : (
                                                  <>
                                                    <InputText
                                                      value={String(condition?.minValue || "")}
                                                      onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { minValue: event.target.value })}
                                                      placeholder="Min"
                                                      type="number"
                                                      inputMode="decimal"
                                                      step={String(getProtocolNumberFieldConfig(field).step)}
                                                      style={compactProtocolNumericFieldStyle}
                                                    />
                                                    <InputText
                                                      value={String(condition?.maxValue || "")}
                                                      onChange={(event) => updateProtocolDialogFieldCondition(definitionIndex, ruleIndex, fieldKey, operator, { maxValue: event.target.value })}
                                                      placeholder="Max"
                                                      type="number"
                                                      inputMode="decimal"
                                                      step={String(getProtocolNumberFieldConfig(field).step)}
                                                      style={compactProtocolNumericFieldStyle}
                                                    />
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="grid align-items-start mt-1" style={{ rowGap: "0.4rem" }}>
                                      <div className="col-12 md:col-6">
                                        <label className="block mb-1 font-medium" style={{ fontSize: "0.78rem", color: "#475569" }}>
                                          {protocolSetupTarget === "tracking" ? "Action" : "Suggested dosage/action"}
                                        </label>
                                        {protocolSetupTarget === "tracking" ? (
                                          <Dropdown
                                            value={String(rule?.action || "alert")}
                                            options={EMAR_PROTOCOL_RULE_ACTION_OPTIONS.filter((option) => option.value !== "give")}
                                            optionLabel="label"
                                            optionValue="value"
                                            style={{ width: "100%", minWidth: 0, maxWidth: "none", boxSizing: "border-box" }}
                                            onChange={(event) => updateProtocolDialogRuleMeta(definitionIndex, ruleIndex, { action: String(event.value || "alert") })}
                                          />
                                        ) : (
                                          <InputTextarea
                                            value={String(rule?.doseValue || "")}
                                            onChange={(event) => updateProtocolDialogRuleMeta(definitionIndex, ruleIndex, { doseValue: event.target.value })}
                                            placeholder="Optional"
                                            rows={2}
                                            autoResize
                                            style={{ width: "100%", minWidth: 0, maxWidth: "none", display: "block", boxSizing: "border-box" }}
                                          />
                                        )}
                                      </div>
                                      <div className="col-12 md:col-6">
                                        <label className="block mb-1 font-medium" style={{ fontSize: "0.78rem", color: "#475569" }}>
                                          {protocolSetupTarget === "tracking" && String(rule?.action || "") === "custom_action" ? "Other action details" : "Note"}
                                        </label>
                                        <InputTextarea
                                          value={String(rule?.note || "")}
                                          onChange={(event) => updateProtocolDialogRuleMeta(definitionIndex, ruleIndex, { note: event.target.value })}
                                          placeholder={protocolSetupTarget === "tracking" && String(rule?.action || "") === "custom_action" ? "Example: Notify hospice nurse" : "Optional"}
                                          rows={2}
                                          autoResize
                                          style={{ width: "100%", minWidth: 0, maxWidth: "none", display: "block", boxSizing: "border-box" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid align-items-start" style={{ rowGap: "0.6rem" }}>
                            <div className="col-12 md:col-4 lg:col-3">
                              <div style={protocolRulesGridHeaderStyle}>Parameter</div>
                              <div className="mt-2" style={{ fontWeight: 700, color: "#7c2d12", fontSize: "0.9rem" }}>
                                {categoryOption?.label || `Parameter ${definitionIndex + 1}`}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {categoryOption?.guidanceAction || "Custom recommendation"}
                              </div>
                            </div>
                            <div className="col-12 md:col-5 lg:col-6">
                              <div style={protocolRulesGridHeaderStyle}>Rules</div>
                              <div className="mt-2 flex flex-column gap-2">
                                {definitionRules.length ? definitionRules.map((rule: any, ruleIndex: number) => (
                                  <div
                                    key={rule?.ruleId || ruleIndex}
                                    style={{
                                      border: "1px solid #fed7aa",
                                      borderRadius: "12px",
                                      background: "#ffffff",
                                      padding: "0.6rem 0.7rem",
                                    }}
                                  >
                                    <div style={{ fontWeight: 700, color: "#9a3412", fontSize: "0.82rem" }}>
                                      {String(rule?.label || "").trim() || `Rule ${ruleIndex + 1}`}
                                    </div>
                                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                      {getProtocolRuleSummaryParts(rule, definition?.category || "sliding_scale_insulin").join(" • ")}
                                    </div>
                                  </div>
                                )) : (
                                  <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>No rules set yet.</div>
                                )}
                              </div>
                            </div>
                            <div className="col-12 md:col-3 lg:col-3">
                              <div style={protocolRulesGridHeaderStyle}>Actions</div>
                              <div className="mt-2 flex gap-2 flex-wrap justify-content-start md:justify-content-end">
                                <Button
                                  type="button"
                                  label="Edit"
                                  outlined
                                  rounded
                                  onClick={() => startEditProtocolDefinition(definition, definitionIndex)}
                                />
                                <Button
                                  type="button"
                                  icon="pi pi-trash"
                                  outlined
                                  rounded
                                  severity="danger"
                                  onClick={() => removeProtocolDefinition(definitionIndex)}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3" style={medicationDialogFieldHintStyle}>
                  {protocolSetupTarget === "tracking"
                    ? "No action rules are set yet. Add your first rule for this tracking item."
                    : "No parameter sets added yet. Use Add New Parameter to create the first one."}
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={bulkScheduleDialogVisible}
        style={{ width: "60rem", maxWidth: "96vw" }}
        modal
        onHide={() => closeBulkScheduleDialog()}
        header={renderSurfaceHeader(
          "Review Med Schedules",
          "We prefilled 8:00 AM and 8:00 PM for unscheduled meds so you can quickly review and adjust them here.",
          "pi pi-clock",
        )}
        footer={renderDialogFooter({
          loading: savingBulkSchedule,
          onCancel: () => closeBulkScheduleDialog(),
          onSubmit: saveBulkScheduleAssignments,
          submitLabel: "Save Reviewed Schedules",
        })}
      >
        <div className="pt-2">
          <div style={{ ...appMutedTextStyle, marginBottom: "0.9rem" }}>
            Review {bulkScheduleRows.length} medication{bulkScheduleRows.length === 1 ? "" : "s"} and update any times that need to change.
          </div>
          <div
            style={{
              border: "1px solid #fed7aa",
              background: "#fffaf0",
              borderRadius: "14px",
              padding: "0.9rem 1rem",
              marginBottom: "0.9rem",
            }}
          >
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.35rem" }}>Quick update all</div>
            <div style={{ ...appMutedTextStyle, color: "#9a3412", marginBottom: "0.6rem", fontSize: "0.84rem" }}>
              Default times start at 8:00 AM and 8:00 PM. Change them here once if most medications should follow the same schedule.
            </div>
            <div className="flex gap-2 align-items-center flex-wrap">
              <MultiSelect
                value={bulkScheduleApplyTimes}
                options={SCHEDULE_TIME_OPTIONS}
                optionLabel="label"
                optionValue="value"
                display="chip"
                placeholder="Choose shared med times"
                maxSelectedLabels={3}
                showSelectAll={false}
                style={{ ...compactFieldStyle, maxWidth: "20rem" }}
                onChange={(event) => {
                  const nextScheduleTimes = dedupeAndSortScheduleTimes(event.value || []);
                  setBulkScheduleApplyTimes(nextScheduleTimes);
                  setBulkScheduleRows((currentRows: any[]) => currentRows.map((row: any) => ({
                    ...row,
                    selectedTimes: nextScheduleTimes,
                  })));
                }}
              />
              <Button
                type="button"
                label="Reset All"
                icon="pi pi-times"
                outlined
                rounded
                onClick={() => {
                  setBulkScheduleApplyTimes(DEFAULT_BULK_SCHEDULE_TIMES);
                  setBulkScheduleRows((currentRows: any[]) => currentRows.map((row: any) => ({
                    ...row,
                    selectedTimes: DEFAULT_BULK_SCHEDULE_TIMES,
                  })));
                }}
              />
            </div>
          </div>
          <div className="flex flex-column gap-3">
            {bulkScheduleRows.map((row: any) => (
              <div key={row.medicationId} style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{row.medicationName}</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                      {[row.dosage, row.route].filter(Boolean).join(" • ") || "Dose / route not added yet"}
                    </div>
                  </div>
                  <Tag value="Review schedule" severity="warning" rounded />
                </div>
                <div className="mt-3 flex gap-3 flex-wrap align-items-end">
                  <div style={{ flex: "1 1 20rem", minWidth: "18rem" }}>
                    <label className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                      Scheduled times
                    </label>
                    <MultiSelect
                      value={row.selectedTimes}
                      options={SCHEDULE_TIME_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      display="chip"
                      placeholder="Choose daily med times"
                      maxSelectedLabels={3}
                      showSelectAll={false}
                      style={{ ...compactFieldStyle, maxWidth: "22rem" }}
                      onChange={(event) => {
                        const nextScheduleTimes = dedupeAndSortScheduleTimes(event.value || []);
                        setBulkScheduleRows((currentRows: any[]) => currentRows.map((currentRow: any) => (
                          currentRow.medicationId === row.medicationId
                            ? { ...currentRow, selectedTimes: nextScheduleTimes }
                            : currentRow
                        )));
                      }}
                    />
                  </div>
                  <div style={{ flex: "1 1 18rem", minWidth: "16rem" }}>
                    <label className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                      Expiration
                    </label>
                    <div className="flex gap-2 align-items-end flex-wrap">
                      <Calendar
                        value={getMedicationExpiryCalendarValue(row.effectiveEndDate)}
                        onChange={(event) => {
                          const nextExpiryDate = formatMedicationExpiryFromCalendar(event.value as Date | null);
                          setBulkScheduleRows((currentRows: any[]) => currentRows.map((currentRow: any) => (
                            currentRow.medicationId === row.medicationId
                              ? { ...currentRow, effectiveEndDate: nextExpiryDate }
                              : currentRow
                          )));
                        }}
                        dateFormat="mm/dd/yy"
                        placeholder="Select expiry"
                        showButtonBar
                        monthNavigator
                        yearNavigator
                        yearRange="2020:2035"
                        style={{ ...compactFieldStyle, maxWidth: "12rem" }}
                      />
                      <Button
                        type="button"
                        icon="pi pi-times"
                        outlined
                        rounded
                        aria-label="Clear expiration"
                        onClick={() => {
                          setBulkScheduleRows((currentRows: any[]) => currentRows.map((currentRow: any) => (
                            currentRow.medicationId === row.medicationId
                              ? { ...currentRow, effectiveEndDate: "" }
                              : currentRow
                          )));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {row.selectedTimes.length ? row.selectedTimes.map((time: string) => (
                    <button
                      key={`${row.medicationId}-${time}`}
                      type="button"
                      onClick={() => {
                        setBulkScheduleRows((currentRows: any[]) => currentRows.map((currentRow: any) => (
                          currentRow.medicationId === row.medicationId
                            ? { ...currentRow, selectedTimes: currentRow.selectedTimes.filter((currentTime: string) => currentTime !== time) }
                            : currentRow
                        )));
                      }}
                      style={{
                        border: "1px solid #bfdbfe",
                        borderRadius: "999px",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        padding: "0.35rem 0.7rem",
                        cursor: "pointer",
                      }}
                    >
                      {formatStructuredScheduleTimeLabel(time)} ×
                    </button>
                  )) : (
                    <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                      Choose one or more daily med times for this medication.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProviderResidentEmarPage;
